import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatTenure, formatLiveTenure, liveTenureYears } from './utils/tenure';
import { NewFromRefreshBadge } from './components/NewFromRefreshBadge';
import { IS_RECMAPPER } from './product';
import { createPortal } from 'react-dom';
import FilterBar from './FilterBar';
import { FilterBarV2 } from './FilterBarV2';
import { ColumnSelectorPanel } from './ColumnSelectorPanel';
import { AISearchBar } from './AISearchBar';
import AISearchPreviewModal from './AISearchPreviewModal';
import { FilterState, FilterGroup, RadiusSearch } from './types/filters';
import { ConnectionsModal } from './components/ConnectionsModal';
import HmDiscoveryModal from './components/HmDiscoveryModal';
import FeatureApprovalPrompt from './components/FeatureApprovalPrompt';
import { JobSpecMatchV2Modal, MatchBreakdownPopover, JsSpec, JsResult } from './JobSpecMatchV2Modal';
import { useAuth } from './AuthContext';
import { useAdaContext, AdaActions } from './contexts/AdaContext';
import CardView from './CardView';
import EntityDetailPanel, { prefetchEntity } from './EntityDetailPanel';
import AIColumnModal from './components/AIColumnModal';
import OrgChartModal from './components/OrgChartModal';
import FormulaColumnModal from './components/FormulaColumnModal';
import { extractFormulaRefs, compileFormula, formulaValueToString } from './utils/formulaEval';
import { parseSearchTerms } from './utils/searchTerms';
import { tourEvent, useLessonAction } from './tour/tourEvents';
import { useTour } from './tour/TourProvider';

// Auto-discovered hiring managers: a light row colour per kind, so a
// company's discovered contacts read as a group (violet = hiring managers,
// sky = talent acquisition, amber = owner / founder).
const DISCOVERY_ROW_CLASS: Record<string, string> = {
  hiring_manager: 'bg-violet-50 border-l-4 border-l-violet-300',
  talent_acquisition: 'bg-sky-50 border-l-4 border-l-sky-300',
  owner_founder: 'bg-amber-50 border-l-4 border-l-amber-300',
};
const DISCOVERY_CHIP_CLASS: Record<string, string> = {
  hiring_manager: 'bg-violet-100 text-violet-800 border-violet-300',
  talent_acquisition: 'bg-sky-100 text-sky-800 border-sky-300',
  owner_founder: 'bg-amber-100 text-amber-800 border-amber-300',
};

// Pseudo-column meaning "match across ALL profile columns" (mirrors the search
// bar). Its `values` are OR phrases, matched in any column — must stay in sync
// with the backend (utils/searchFields.js) and AISearchPreviewModal.

// Hidden for now — the code is kept so either can come back by flipping the flag.
//   Enrich Companies (candidates tab): Clay company enrichment of unenriched employers.
//   Find Hiring Managers (companies tab): opens a LinkedIn Classic people search;
//   superseded by Find Contacts (universe).
const SHOW_ENRICH_COMPANIES_BUTTON = false;
const SHOW_LINKEDIN_FIND_HIRING_MANAGERS = false;

const ANY_FIELD_COL = 'any_field';

// Optional profile fields that can be pushed to lemlist as {{variables}}.
// Headline & skills are always sent, so they're not listed here.
const LEMLIST_FIELD_OPTIONS: { key: string; label: string }[] = [
  { key: 'summary', label: 'Profile summary' },
  { key: 'current_position_description', label: 'Current role description' },
  { key: 'previous_company_1', label: 'Previous company 1' },
  { key: 'previous_company_2', label: 'Previous company 2' },
  { key: 'previous_company_3', label: 'Previous company 3' },
  { key: 'previous_position_1', label: 'Previous title 1' },
  { key: 'previous_position_2', label: 'Previous title 2' },
  { key: 'previous_position_3', label: 'Previous title 3' },
  { key: 'previous_position_1_description', label: 'Previous role 1 description' },
  { key: 'previous_position_2_description', label: 'Previous role 2 description' },
  { key: 'previous_position_3_description', label: 'Previous role 3 description' },
];

// If a string looks like a URL, return a canonical href for it; otherwise null.
// Used to render custom-column values that contain URLs (e.g. LinkedIn or
// website columns from a CSV upload) as clickable links instead of plain text.
const detectUrlHref = (value: string): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
  return null;
};

// Tenure columns: months under 2 years, then years (utils/tenure.ts)
const TENURE_COLUMNS = new Set([
  'years_at_current_company', 'average_tenure',
  'previous_position_1_years', 'previous_position_2_years', 'previous_position_3_years'
]);


interface Company {
  id: number;
  company_name: string;
  company_domain?: string;
  name?: string;
  website?: string;
  employee_count?: number;
  industry?: string;
  subindustry?: string[] | string;
  category?: string;
  slogan?: string;
  primary_category?: string;
  specialties?: string;
  description?: string;
  about?: string;
  url?: string;
  founded?: number;
  follower_count?: number;
  investors?: string;
  funding?: string;
  headquarters?: string;
  crunchbase_url?: string;
  type?: string;
  office_address?: string;
  company_size_formatted?: string;
  annual_revenue?: string;
  business_type?: string[] | string;
  business_stage?: string;
  primary_offerings?: string[] | string;
  revenue_streams?: string[] | string;
  pattern_tags?: string[] | string;
  scale_scope?: string;
  candidate_count?: number;
  hiring_manager_count?: number;
  created_at?: string;
  updated_at?: string;
  latitude?: number;
  longitude?: number;
  formatted_address?: string;
  logo_url?: string;
  pin_color?: string;
  tags?: string[];
  custom_data?: any;
  org_id?: string;
}

interface Candidate {
  id: number;
  name: string;
  tags?: string[];
  position?: string;
  company?: string;
  company_linkedin_url?: string;
  current_company?: string;
  address?: string;
  li_profile_url?: string;
  summary?: string;
  headline?: string;
  employment_type?: string;
  company_location?: string;
  phone?: string;
  email?: string;
  personal_email?: string;
  domain?: string;
  first_name?: string;
  last_name?: string;
  working_style?: string;
  all_skills?: string[];
  current_role_description?: string;
  current_role_skills?: string[];
  experience_years?: number;
  created_at?: string;
  updated_at?: string;
  enrichment_status?: 'in_progress' | 'found' | 'not_found' | null;
  enriched_at?: string | null;
  email_searched?: boolean;
  personal_email_searched?: boolean;
  phone_searched?: boolean;
  connection_count?: number;
  match_score?: number | null;
  match_reasoning?: string | null;
}

interface HiringManager {
  id: number;
  name: string;
  first_name?: string;
  last_name?: string;
  tags?: string[];
  job_title?: string;
  org?: string;
  li_profile_url?: string;
  li_company_url?: string;
  summary?: string;
  headline?: string;
  location?: string;
  email?: string;
  personal_email?: string;
  phone?: string;
  department?: string;
  seniority_level?: string;
  is_starred?: boolean;
  // Auto-discovery (Find Contacts → AI): kind, level, confidence, reasons.
  discovery_kind?: 'hiring_manager' | 'talent_acquisition' | 'owner_founder' | null;
  discovery_level?: number | null;
  discovery_confidence?: number | null;
  discovery_why?: string | null;
  enrichment_status?: 'in_progress' | 'found' | 'not_found' | null;
  enriched_at?: string | null;
  email_searched?: boolean;
  personal_email_searched?: boolean;
  phone_searched?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface FilterCondition {
  id: string;
  columns: string[];
  operator: string;
  value: string;
  entityType: 'company' | 'candidate';
}

interface TableViewProps {
  selectedProject: {
    id: number;
    name: string;
    created_by_agent?: boolean;
  };
  onNavigateToAnalytics?: (filterState: FilterState) => void;
  onNavigateToMap?: (filterState: FilterState) => void;
  initialFilters?: FilterState | null;
  initialSearch?: string;
  initialDataType?: 'companies' | 'candidates' | 'managers';
  adaWorking?: boolean;
  backToJobs?: () => void;
  // When set, the table is scoped to a campaign's members (Leads List embed).
  campaignScopeId?: number;
  // Jobs → Job Spec Match handoff: open the modal on this spec at mount.
  initialJobSpecId?: number | null;
  onInitialJobSpecConsumed?: () => void;
}

// Render reasoning text, replacing [LI:url] markers with small clickable LinkedIn icons
function renderReasoningWithLinkedIn(text: string): React.ReactNode {
  if (!text) return null;
  const parts: React.ReactNode[] = [];
  const regex = /\[LI:([^\]]+)\]/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const url = match[1];
    parts.push(
      <a
        key={`li-${key++}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title="Open LinkedIn profile"
        className="inline-flex items-center justify-center w-4 h-4 ml-1 align-middle text-[#0A66C2] hover:text-[#004182]"
        onClick={e => e.stopPropagation()}
      >
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      </a>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

// Default sort per tab. Candidates: date added, newest first (26 Oct 2026 —
// alphabetical said nothing about a market; with "Keep growing" the newest
// people are on top). Companies: most candidates. Managers: name. Agent
// (Ada) sub-projects: match score.
const defaultSortFor = (type: string, agentProject: boolean): { column: string; direction: 'asc' | 'desc' } => {
  if (type === 'companies') return { column: 'candidate_count', direction: 'desc' };
  if (agentProject) return { column: 'match_score', direction: 'desc' };
  if (type === 'candidates') return { column: 'created_at', direction: 'desc' };
  return { column: 'name', direction: 'asc' };
};

const TableView: React.FC<TableViewProps> = ({ selectedProject, onNavigateToAnalytics, onNavigateToMap, initialFilters, initialSearch, initialDataType, adaWorking, backToJobs, campaignScopeId, initialJobSpecId, onInitialJobSpecConsumed }) => {
  const { user } = useAuth();
  const isAdmin = user?.email === 'alex@marketmapr.com' || localStorage.getItem('adminEmail') === 'alex@marketmapr.com';
  const [activeDataType, setActiveDataType] = useState<'companies' | 'candidates' | 'managers'>(initialDataType || 'candidates');

  // View mode: table or card (persisted in localStorage per user)
  const userId = useAuth()?.user?.id;
  const viewModeKey = `dataViewMode_${userId || 'guest'}`;
  const cardViewDismissedKey = `cardViewNewDismissed_${userId || 'guest'}`;
  const [viewMode, setViewMode] = useState<'table' | 'card'>(() => {
    // Phones open on cards (the wide table needs a laptop) unless the user
    // picked a view on this device; laptops default to the table as before.
    const saved = localStorage.getItem(viewModeKey) as 'table' | 'card' | null;
    if (saved) return saved;
    try { if (window.matchMedia('(max-width: 767px)').matches) return 'card'; } catch { /* ignore */ }
    return 'table';
  });
  const [showCardViewHint, setShowCardViewHint] = useState(() => {
    return !localStorage.getItem(cardViewDismissedKey);
  });
  const handleSetViewMode = (mode: 'table' | 'card') => {
    setViewMode(mode);
    localStorage.setItem(viewModeKey, mode);
    if (mode === 'card' && showCardViewHint) {
      setShowCardViewHint(false);
      localStorage.setItem(cardViewDismissedKey, 'true');
    }
  };

  // Track when viewing candidates/HMs drilled down from a company card
  const [companyDrilldown, setCompanyDrilldown] = useState<string | null>(null);

  // Full unfiltered datasets (source of truth)
  const [fullCompanies, setFullCompanies] = useState<Company[]>([]);
  const [fullCandidates, setFullCandidates] = useState<Candidate[]>([]);
  const [fullHiringManagers, setFullHiringManagers] = useState<HiringManager[]>([]);
  
  // Filtered datasets (displayed in tables)
  const [companies, setCompanies] = useState<Company[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [hiringManagers, setHiringManagers] = useState<HiringManager[]>([]);
  
  const [isFiltered, setIsFiltered] = useState(false);
  const [displayData, setDisplayData] = useState<any[]>([]);
  const displayDataRef = useRef<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [customColumns, setCustomColumns] = useState<any[]>([]);
  // Campaign-scoped AI columns (only when embedded as a campaign Leads List).
  // Kept separate from project customColumns so project column edit/delete flows
  // never touch them. Values arrive merged into each row's custom_data.
  const [campaignColumns, setCampaignColumns] = useState<any[]>([]);
  const [customColMenu, setCustomColMenu] = useState<string | null>(null);
  // Screen coordinates where the custom-column dropdown should appear.
  // Rendered via React Portal at document.body to escape the sticky <th>
  // stacking context (dropdowns rendered inside a sticky cell get clipped
  // by adjacent sticky cells even at higher z-index).
  const [customColMenuPos, setCustomColMenuPos] = useState<{ top: number; right: number } | null>(null);
  // Ada-created criteria columns can be grouped together (one group per
  // run_criteria_scoring call). Users can collapse a group to hide its
  // individual columns behind a single combined-verdict column.
  const [collapsedColumnGroups, setCollapsedColumnGroups] = useState<Set<string>>(new Set());

  // Group key -> ordered list of column keys in that group
  const columnGroupMembers = useMemo(() => {
    const map: Record<string, string[]> = {};
    customColumns.forEach(col => {
      if (col.column_group_key) {
        if (!map[col.column_group_key]) map[col.column_group_key] = [];
        map[col.column_group_key].push(col.column_key);
      }
    });
    return map;
  }, [customColumns]);

  // Column key -> group key (quick lookup)
  const columnToGroupKey = useMemo(() => {
    const map: Record<string, string> = {};
    customColumns.forEach(col => {
      if (col.column_group_key) map[col.column_key] = col.column_group_key;
    });
    return map;
  }, [customColumns]);

  // Group key -> group label
  const columnGroupLabels = useMemo(() => {
    const map: Record<string, string> = {};
    customColumns.forEach(col => {
      if (col.column_group_key && col.column_group_label) {
        map[col.column_group_key] = col.column_group_label;
      }
    });
    return map;
  }, [customColumns]);

  // Helper: is a column hidden because its group is collapsed and it's
  // not the group leader (leader = the column that represents the whole
  // group in collapsed mode).
  const isColumnHiddenByGroup = (colKey: string): boolean => {
    const groupKey = columnToGroupKey[colKey];
    if (!groupKey) return false;
    if (!collapsedColumnGroups.has(groupKey)) return false;
    // First member is the leader; everything else is hidden
    return columnGroupMembers[groupKey]?.[0] !== colKey;
  };

  // Helper: is this column the leader (first) of a collapsed group?
  const isCollapsedGroupLeader = (colKey: string): boolean => {
    const groupKey = columnToGroupKey[colKey];
    if (!groupKey) return false;
    if (!collapsedColumnGroups.has(groupKey)) return false;
    return columnGroupMembers[groupKey]?.[0] === colKey;
  };

  // Get the 0-100 overall score for a collapsed group on a given row.
  // Backend stores this at custom_data[`${groupKey}__score`].
  // Returns null if not yet scored (cell shows dash).
  const getGroupScore = (row: any, groupKey: string): number | null => {
    if (!row.custom_data) return null;
    const raw = row.custom_data[`${groupKey}__score`];
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') {
      const n = parseInt(raw, 10);
      return isNaN(n) ? null : n;
    }
    return null;
  };

  const getGroupReasoning = (row: any, groupKey: string): string | null => {
    if (!row.custom_data) return null;
    const raw = row.custom_data[`${groupKey}__reasoning`];
    return typeof raw === 'string' && raw.length > 0 ? raw : null;
  };

  // Step 5: read overall confidence for a collapsed group. Stored in a
  // parallel `<groupKey>__score_confidence` field.
  const getGroupConfidence = (row: any, groupKey: string): 'high' | 'medium' | 'low' | null => {
    if (!row.custom_data) return null;
    const raw = row.custom_data[`${groupKey}__score_confidence`];
    if (typeof raw !== 'string') return null;
    const lower = raw.toLowerCase();
    return (lower === 'high' || lower === 'medium' || lower === 'low') ? lower : null;
  };

  const toggleColumnGroup = (groupKey: string) => {
    setCollapsedColumnGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };
  const [renameModal, setRenameModal] = useState<{ columnKey: string; currentLabel: string } | null>(null);
  const [descriptionModal, setDescriptionModal] = useState<{ columnLabel: string; description: string } | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [verdictModal, setVerdictModal] = useState<{
    entityName: string;
    columnLabel: string;
    verdict: 'Match' | 'Near-Match' | 'Miss' | 'Unclear';
    reasoning: string;
    confidence?: 'high' | 'medium' | 'low' | null;
    score?: number | null;
  } | null>(null);
  // AI-column "why" modal (value + the model's reasoning for a cell).
  const [aiReasoningModal, setAiReasoningModal] = useState<{
    entityName: string;
    columnLabel: string;
    columnKey: string;
    rowId?: number;
    value: string;
    reasoning: string;
    meta?: { confidence?: string; sources?: Array<{ url: string; title?: string }>; searches?: number } | null;
  } | null>(null);
  const [explainBusy, setExplainBusy] = useState(false);
  const [companyCandidateCounts, setCompanyCandidateCounts] = useState<Map<number, {total: number, filtered: number}>>(new Map());
  const [companyManagerCounts, setCompanyManagerCounts] = useState<Map<number, {total: number, filtered: number}>>(new Map());

  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
// const [appliedFilters, setAppliedFilters] = useState<FilterGroup[]>([]); // REMOVED - using aiFilterState instead
  const [appliedEmploymentTypes, setAppliedEmploymentTypes] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: 'asc' | 'desc' }>(
    () => defaultSortFor(initialDataType || 'candidates', false));

  // Auto-sort by match_score DESC for agent-created subprojects
  useEffect(() => {
    if (selectedProject?.created_by_agent && activeDataType === 'candidates') {
      setSortConfig({ column: 'match_score', direction: 'desc' });
    }
  }, [selectedProject?.id]);

  const [startRow, setStartRow] = useState(0);
  const [rowLimit, setRowLimit] = useState(250);
  const [currentPage, setCurrentPage] = useState(1);

  // Server-side pagination state (disabled - loading all data now)
  const [serverPagination, setServerPagination] = useState<{
    enabled: boolean;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  }>({ enabled: false, total: 0, totalPages: 0, hasNext: false, hasPrev: false });
  // Store server totals for all data types (for tab count display)
  const [serverTotals, setServerTotals] = useState<{
    companies: number;
    candidates: number;
    managers: number;
  }>({ companies: 0, candidates: 0, managers: 0 });

  // Publishing the table's current view state to the Ada chat context is
  // declared further down, once aiFilterState has been initialised.
  // Store original unfiltered project totals (never changes until project changes)
  // Used for "from X" display to show what counts are being filtered from
  const [originalProjectTotals, setOriginalProjectTotals] = useState<{
    companies: number;
    candidates: number;
    managers: number;
  }>({ companies: 0, candidates: 0, managers: 0 });
  // Keyed by "${dataType}-${pageNumber}" to prevent cross-tab data contamination
  const [prefetchedData, setPrefetchedData] = useState<Map<string, any[]>>(new Map());
  const [pageLoading, setPageLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const lastSelectedIndexRef = useRef<number | null>(null);

  // Company association filter (uses junction table for current employee matching)
  const [associatedCompanyFilter, setAssociatedCompanyFilter] = useState<string | null>(null);

  // Quick search state
  const [searchTerm, setSearchTerm] = useState(initialSearch || '');
  const [searchInputValue, setSearchInputValue] = useState(initialSearch || ''); // For controlled input
  const [searchOriginTab, setSearchOriginTab] = useState<'companies' | 'candidates' | 'managers' | null>(initialSearch ? initialDataType || null : null); // Track which tab initiated the search
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const prevSearchTermRef = useRef<string>(''); // Track previous search term to detect clearing - start empty to ensure first search runs
  const initialSearchAppliedRef = useRef<string>(''); // Track which initialSearch we've applied

  // Update search state when initialSearch changes (e.g., navigating from Jobs to company).
  // Two-phase: set the visible input + active tab immediately so the user sees
  // the navigation took effect, but defer setSearchTerm() until serverPagination
  // has enabled — otherwise the search-trigger effect at line ~2040 hits its
  // `if (!serverPagination.enabled) return` early-return and swallows the change.
  useEffect(() => {
    if (!initialSearch) return;
    if (initialSearch === initialSearchAppliedRef.current) return;

    // Visible state — apply immediately so the input box shows the company name.
    setSearchInputValue(initialSearch);
    setSearchOriginTab(initialDataType || null);
    if (initialDataType) {
      setActiveDataType(initialDataType);
    }

    // Trigger the actual search only when the data layer is ready. Otherwise
    // the search-trigger effect early-returns and we lose the fetch.
    if (serverPagination.enabled) {
      console.log('📍 Applying initial search:', initialSearch, 'dataType:', initialDataType);
      prevSearchTermRef.current = '';
      initialSearchAppliedRef.current = initialSearch;
      setSearchTerm(initialSearch);
    }
  }, [initialSearch, initialDataType, serverPagination.enabled]);

  // Track the last page that was loaded from server (to detect navigation back to page 1)
  const lastLoadedPageRef = useRef<number>(0);

  // Track previous filter state to detect when filters are actually cleared vs just switching tabs
  const prevFilterStateRef = useRef<{
    hasFilters: boolean;
    activeDataType: 'companies' | 'candidates' | 'managers';
  }>({ hasFilters: false, activeDataType: 'candidates' });

  // Track when AI search with radius is in progress - to prevent the filter useEffect from overwriting counts
  const aiRadiusSearchInProgressRef = useRef<boolean>(false);
  const [showEnrichMenu, setShowEnrichMenu] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichmentMessage, setEnrichmentMessage] = useState<string | null>(null);
  // Companies tab: when user clicks Export CSV we open a small modal
  // asking whether to expand multi-office companies into one row per
  // office (each row carries Clay's is_primary flag). Off keeps the
  // legacy one-row-per-company shape.
  const [showExportOptionsModal, setShowExportOptionsModal] = useState(false);
const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
const [deleting, setDeleting] = useState(false);
const [deleteMessage, setDeleteMessage] = useState<string | null>(null);
  const [showStickyHeaders, setShowStickyHeaders] = useState(false);
const [showTagModal, setShowTagModal] = useState(false);
const [showRemoveTagModal, setShowRemoveTagModal] = useState(false);
const [showColorModal, setShowColorModal] = useState(false);
const [showAddSingleModal, setShowAddSingleModal] = useState(false);
const [addSingleUrl, setAddSingleUrl] = useState('');
const [addSingleEnrich, setAddSingleEnrich] = useState(true);
const [addingSingle, setAddingSingle] = useState(false);
// Manual HM entry state
const [addHmMode, setAddHmMode] = useState<'linkedin' | 'manual'>('manual');
const [manualHm, setManualHm] = useState({ firstName: '', lastName: '', jobTitle: '', companyId: 0, email: '', linkedinUrl: '' });
const [companySearch, setCompanySearch] = useState('');
const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
const [newTag, setNewTag] = useState('');
const [tagToRemove, setTagToRemove] = useState('');
const [removingTag, setRemovingTag] = useState(false);
const [selectedColor, setSelectedColor] = useState('#3B82F6');
const [colorPalette, setColorPalette] = useState<Array<{name: string, hex: string}>>([]);
const [allTags, setAllTags] = useState<string[]>([]);
const [tagging, setTagging] = useState(false);
const [changingColor, setChangingColor] = useState(false);
const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
const [showSalesNavModal, setShowSalesNavModal] = useState(false);
const [salesNavTitles, setSalesNavTitles] = useState('');
const [showFindContactsModal, setShowFindContactsModal] = useState(false);
// Universe Find Contacts: holds the selected companies' entries while the
// user picks what they're hunting (candidates vs hiring managers).
const [contactIntentEntries, setContactIntentEntries] = useState<string[] | null>(null);
// The ticked company ids behind the Find Contacts modal (null = every
// company in the project) — what auto-discovery runs on.
const [contactIntentCompanyIds, setContactIntentCompanyIds] = useState<number[] | null>(null);
const [discoverCompanyIds, setDiscoverCompanyIds] = useState<number[] | null | undefined>(undefined);
// Non-admins go through an approval prompt first (FeatureApprovalPrompt); holds the company ids they clicked with.
const [discoverApprovalFor, setDiscoverApprovalFor] = useState<number[] | null | undefined>(undefined);
const [findContactsTitles, setFindContactsTitles] = useState('');
// Clay "find contacts" (auto-map hiring managers) criteria + templates.
const [contactCriteria, setContactCriteria] = useState<{ jobTitleKeywords: string; excludeKeywords: string; locations: string; excludeLocations: string; headlineKeywords: string; summaryKeywords: string; roleDescriptionKeywords: string; minMonths: number; limit: number }>({
  jobTitleKeywords: '', excludeKeywords: '', locations: '', excludeLocations: '', headlineKeywords: '', summaryKeywords: '', roleDescriptionKeywords: '', minMonths: 0, limit: 10,
});
const [contactTemplates, setContactTemplates] = useState<any[]>([]);
const [findingContacts, setFindingContacts] = useState(false);
const [findContactsMsg, setFindContactsMsg] = useState<string | null>(null);

const loadContactTemplates = async () => {
  try {
    const res = await fetch('/api/clay/contact-templates', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    if (res.ok) setContactTemplates(await res.json());
  } catch { /* non-fatal */ }
};

const handleFindViaClay = async () => {
  if (!selectedProject?.id) return;
  const companyIds = Array.from(selectedRows);
  if (companyIds.length === 0) return;
  setFindingContacts(true);
  setFindContactsMsg(null);
  try {
    const res = await fetch('/api/clay/find-contacts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ projectId: selectedProject.id, companyIds, criteria: contactCriteria }),
    });
    const data = await res.json();
    if (!res.ok) {
      // Never surface our data-provider's name to non-admins.
      if (data.code === 'contacts_not_configured') {
        throw new Error(isAdmin
          ? 'Contacts search isn’t set up yet — add the webhook in Settings.'
          : 'Contacts search isn’t set up yet — please reach out to your account holder.');
      }
      throw new Error(data.error || 'Failed to start search');
    }
    if ((data.total ?? 0) === 0) {
      setFindContactsMsg('None of the selected companies matched — try de-selecting and re-selecting, then run again.');
    } else if (data.queued < data.total) {
      setFindContactsMsg(`Only ${data.queued}/${data.total} sent — the webhook rejected the rest (check the contacts webhook URL in Settings).`);
    } else {
      setFindContactsMsg(`Searching ${data.queued} compan${data.queued === 1 ? 'y' : 'ies'} — contacts will appear shortly (1 credit each, only if found).`);
    }
  } catch (e: any) {
    setFindContactsMsg(e.message);
  } finally {
    setFindingContacts(false);
  }
};

const saveContactTemplate = async () => {
  const name = window.prompt('Template name:');
  if (!name || !name.trim()) return;
  try {
    const res = await fetch('/api/clay/contact-templates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ name: name.trim(), criteria: contactCriteria }),
    });
    if (res.ok) loadContactTemplates();
  } catch { /* non-fatal */ }
};
const [aiSearchLoading, setAiSearchLoading] = useState(false);
const [aiFilterState, setAiFilterState] = useState<FilterState>(
  initialFilters || {
    groups: [],
    employmentTypes: [],
  }
);

// AdaContext publisher lives further down the file, after all the filter
// state it needs to read is declared (radius states are declared below).
const { setAdaContext } = useAdaContext();

const [aiExplanation, setAiExplanation] = useState<string | null>(null);
const [canWidenSearch, setCanWidenSearch] = useState<boolean>(false);
const [isWidenedSearch, setIsWidenedSearch] = useState<boolean>(false);
const [currentSearchQuery, setCurrentSearchQuery] = useState<string>('');
const [showEnrichCompaniesModal, setShowEnrichCompaniesModal] = useState(false);
const [enrichmentProgress, setEnrichmentProgress] = useState<{
  current: number; total: number; isEnriching: boolean;
  operationId?: string; phase?: string; cachedCount?: number; enrichedCount?: number; creditsUsed?: number;
}>({ current: 0, total: 0, isEnriching: false });
const enrichmentPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
const [unenrichedCompanyCount, setUnenrichedCompanyCount] = useState<number>(0);

  // Filter history state for back button functionality
  const [filterHistory, setFilterHistory] = useState<Array<{
    dataType: 'companies' | 'candidates' | 'managers';
    filterState: FilterState;
    description: string;
    page: number;
    associatedCompany?: string | null;
    sort?: { column: string; direction: 'asc' | 'desc' };
  }>>([]);

  // Starred managers state
  const [starredManagerIds, setStarredManagerIds] = useState<Set<number>>(new Set());

  // Cell modal state
  const [cellModal, setCellModal] = useState<{show: boolean, content: string, title: string, rowId?: number, columnKey?: string, isEditing?: boolean, editValue?: string, isSaving?: boolean}>({show: false, content: '', title: ''});

  // Entity detail panel state (slide-out panel on name click)
  const [detailPanel, setDetailPanel] = useState<{ open: boolean; entityType: 'candidate' | 'company' | 'contact'; entityId: number | null }>({ open: false, entityType: 'candidate', entityId: null });
  const detailPanelRef = useRef(detailPanel);
  detailPanelRef.current = detailPanel;

  // Shortlist state
  const [showShortlistDropdown, setShowShortlistDropdown] = useState(false);
  const [shortlistJobs, setShortlistJobs] = useState<{id: number; title: string; company_name: string}[]>([]);
  const [shortlisting, setShortlisting] = useState(false);
  const [shortlistMessage, setShortlistMessage] = useState<string | null>(null);

  // BD Campaign state
  const [showBdCampaignDropdown, setShowBdCampaignDropdown] = useState(false);
  const [bdCampaignsList, setBdCampaignsList] = useState<{id: number; name: string; status: string; stages: {id: number; name: string; position: number}[] | null}[]>([]);
  const [addingToCampaign, setAddingToCampaign] = useState(false);
  const [bdCampaignMessage, setBdCampaignMessage] = useState<string | null>(null);

  // Column resizing state
  const [columnWidths, setColumnWidths] = useState<{[key: string]: number}>({});
  const [resizing, setResizing] = useState<{columnKey: string, startX: number, startWidth: number} | null>(null);

  // Frozen (pinned-left) columns — user-chosen via the column menu. Persisted
  // per project + data type. The leading checkbox + # gutter is always pinned;
  // frozen data columns stack to the right of it and stay visible on horizontal
  // scroll (e.g. keep Company Name in view while scanning across).
  const [frozenColumns, setFrozenColumns] = useState<Set<string>>(new Set());
  const FROZEN_DEFAULT_WIDTH = 200;
  const LEADING_GUTTER_WIDTH = 96; // checkbox (48) + # (48), both always pinned

  useEffect(() => {
    if (!selectedProject?.id) { setFrozenColumns(new Set()); return; }
    try {
      const raw = localStorage.getItem(`frozenCols_${selectedProject.id}_${activeDataType}`);
      setFrozenColumns(raw ? new Set(JSON.parse(raw)) : new Set());
    } catch {
      setFrozenColumns(new Set());
    }
  }, [selectedProject?.id, activeDataType]);

  const toggleFreezeColumn = (colKey: string) => {
    tourEvent('column-frozen');
    const next = new Set(frozenColumns);
    if (next.has(colKey)) {
      next.delete(colKey);
    } else {
      next.add(colKey);
      // Give it a deterministic width so the sticky-left offsets are stable.
      if (!columnWidths[colKey]) {
        setColumnWidths(prev => ({ ...prev, [colKey]: FROZEN_DEFAULT_WIDTH }));
      }
    }
    setFrozenColumns(next);
    if (selectedProject?.id) {
      try { localStorage.setItem(`frozenCols_${selectedProject.id}_${activeDataType}`, JSON.stringify(Array.from(next))); } catch {}
    }
  };

  // Column order state
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  // The person's own default candidate column order (server-side, follows
  // them between machines; migration 213). Loaded before the order is
  // initialised; a browser copy gives an instant first paint.
  type TablePrefs = { loaded: boolean; order: string[] | null; savedAt: string | null; resetBefore: string | null };
  const tablePrefsKey = `mm.tablePrefs.${userId || 'anon'}`;
  const [tablePrefs, setTablePrefs] = useState<TablePrefs>(() => {
    try {
      const cached = localStorage.getItem(tablePrefsKey);
      if (cached) return { loaded: true, ...JSON.parse(cached) };
    } catch { /* no cache */ }
    return { loaded: false, order: null, savedAt: null, resetBefore: null };
  });
  const [defaultOrderNote, setDefaultOrderNote] = useState<string | null>(null);
  const [resetOtherProjects, setResetOtherProjects] = useState(false);
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    fetch('/api/table-preferences', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!alive) return;
        const next = { loaded: true, order: p && Array.isArray(p.candidateColumnOrder) ? p.candidateColumnOrder : null, savedAt: p ? p.savedAt : null, resetBefore: p ? p.resetBefore : null };
        setTablePrefs(next);
        try { localStorage.setItem(tablePrefsKey, JSON.stringify({ order: next.order, savedAt: next.savedAt, resetBefore: next.resetBefore })); } catch { /* ignore */ }
      })
      .catch(() => { if (alive) setTablePrefs((t) => ({ ...t, loaded: true })); });
    return () => { alive = false; };
  }, [userId]);
  // Save the current candidate column order as this person's default for
  // every project (optionally overriding the projects arranged differently).
  const saveDefaultColumnOrder = async (resetProjects: boolean) => {
    const order = columnOrder.filter((k) => visibleColumns.includes(k));
    if (!order.length) return;
    try {
      const r = await fetch('/api/table-preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ candidateColumnOrder: order, resetProjects }),
      });
      const p = await r.json();
      if (!r.ok) throw new Error(p.error || 'Could not save');
      const next = { loaded: true, order: p.candidateColumnOrder, savedAt: p.savedAt, resetBefore: p.resetBefore };
      setTablePrefs(next);
      try { localStorage.setItem(tablePrefsKey, JSON.stringify({ order: next.order, savedAt: next.savedAt, resetBefore: next.resetBefore })); } catch { /* ignore */ }
      if (resetProjects) {
        // This browser's per-project orders go now; other machines ignore theirs via resetBefore.
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith('columnOrder_') && k.endsWith('_candidates')) localStorage.removeItem(k);
        }
      }
      setDefaultOrderNote(resetProjects ? 'Saved as your default and applied to all your projects.' : 'Saved as your default. Projects you\'ve arranged yourself keep their own order.');
      setTimeout(() => setDefaultOrderNote(null), 6000);
    } catch (e: any) {
      setDefaultOrderNote(e.message || 'Could not save your default order');
    }
  };
  const clearDefaultColumnOrder = async () => {
    try {
      const r = await fetch('/api/table-preferences', { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const p = await r.json();
      const next = { loaded: true, order: null, savedAt: null, resetBefore: p.resetBefore || null };
      setTablePrefs(next);
      try { localStorage.setItem(tablePrefsKey, JSON.stringify({ order: null, savedAt: null, resetBefore: next.resetBefore })); } catch { /* ignore */ }
      setDefaultOrderNote('Your default order has been cleared.');
      setTimeout(() => setDefaultOrderNote(null), 6000);
    } catch { setDefaultOrderNote('Could not clear your default order'); }
  };
  // Per-column header colour (Clay-style). key -> hex. Persisted in
  // localStorage per project+datatype, same as column order/widths.
  const [columnColors, setColumnColors] = useState<Record<string, string>>({});
  // AI Column creation modal (opened from the column-header "Use AI" entry).
  const [aiColumnModalOpen, setAiColumnModalOpen] = useState(false);
  // The column whose ⋮ menu launched the AI-column modal — the new column is
  // inserted directly to its right (Clay-style), not appended at the far end.
  const [aiColumnAnchorKey, setAiColumnAnchorKey] = useState<string | null>(null);
  // Pending reposition once the new column appears in columnOrder. `side`
  // controls whether it lands just after (right, default) or before (left) the
  // anchor — AI columns always insert right; manual "Insert column" can do both.
  const [aiColumnReposition, setAiColumnReposition] = useState<{ key: string; anchor: string | null; side?: 'left' | 'right' } | null>(null);
  // The AI-column background job currently running for this project (if any).
  // Owned here (not the modal) so it keeps polling + filling cells after the
  // modal closes, and reconnects when the user returns to the project.
  const [activeAiJob, setActiveAiJob] = useState<any | null>(null);
  // Which custom columns were produced by an AI-column run (so the header menu
  // can offer "Re-run with new prompt").
  const [aiColumnKeys, setAiColumnKeys] = useState<Set<string>>(new Set());
  // Re-run prefill: config for an existing AI column + its key. Null = create-new.
  const [aiReRunInitialSpec, setAiReRunInitialSpec] = useState<any | null>(null);
  const [aiReRunColumnKey, setAiReRunColumnKey] = useState<string | null>(null);
  // Per-cell run (Clay-style ▶): cells currently running, keyed `${colKey}:${rowId}`.
  const [runningCells, setRunningCells] = useState<Set<string>>(new Set());
  // Column-header ▶ run menu (blank vs all): the AI column its menu is open for.
  const [headerRunMenu, setHeaderRunMenu] = useState<{ key: string; label: string } | null>(null);
  const aiColPending = (colKey: string): boolean =>
    !!activeAiJob && ['pending', 'running'].includes(activeAiJob.status) && activeAiJob.columnKey === colKey;
  // Rows being re-processed by a re-run (they already have a value, so they get
  // a "refreshing" treatment rather than the empty-cell spinner).
  const [aiEnrichingRowIds, setAiEnrichingRowIds] = useState<Set<number>>(new Set());
  const aiCellRefreshing = (colKey: string, rowId: number): boolean =>
    aiColPending(colKey) && aiEnrichingRowIds.has(rowId);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [showColumnOrderPanel, setShowColumnOrderPanel] = useState(false);
  const [scrollInterval, setScrollInterval] = useState<NodeJS.Timeout | null>(null);
  const [draggedPanelColumn, setDraggedPanelColumn] = useState<string | null>(null);

  // Match highlighting state
  const [matchData, setMatchData] = useState<Map<number, {columns: Set<string>, secondaryColumns: Set<string>, terms: Set<string>, count: number}>>(new Map());

  // Radius search state - SEPARATE filters for company offices and candidate locations
  const [showRadiusSearch, setShowRadiusSearch] = useState(false);

  // Company office radius filter (filter companies by office location, cross-filter candidates at those companies)
  const [companyOfficeRadius, setCompanyOfficeRadius] = useState<{
    location: string;
    distance: number;
    coords: { lat: number; lng: number } | null;
  }>({ location: '', distance: 25, coords: null });

  // Candidate location radius filter (filter candidates by their personal location)
  const [candidateLocationRadius, setCandidateLocationRadius] = useState<{
    location: string;
    distance: number;
    coords: { lat: number; lng: number } | null;
  }>({ location: '', distance: 25, coords: null });

  // Hiring manager location radius filter — direct filter on the Managers tab,
  // no cross-filter to Companies / Candidates tabs.
  const [managerLocationRadius, setManagerLocationRadius] = useState<{
    location: string;
    distance: number;
    coords: { lat: number; lng: number } | null;
  }>({ location: '', distance: 25, coords: null });

  // Legacy state for backwards compatibility (will be phased out)
  const [radiusLocation, setRadiusLocation] = useState('');
  const [radiusDistance, setRadiusDistance] = useState(25);
  const [radiusType, setRadiusType] = useState<'company' | 'candidate'>('company');
  const [radiusCoords, setRadiusCoords] = useState<{ lat: number; lng: number } | null>(null);

  // Ada action handlers. The real implementations reference high-level
  // functions declared much later in this file (applyDualRadiusSearch,
  // handleDataTypeChange, etc.), so we use a ref pattern: stable wrapper
  // functions registered now delegate to whatever's attached to the ref,
  // which a later useEffect populates with the real handlers each render.
  // This lets AdaSidebar consume stable references but always call the
  // current implementation.
  const adaActionsRef = useRef<AdaActions>({
    applyFilterGroups: () => {},
    switchTab: () => {},
    applyRadiusSearch: () => {},
    setKeywordSearch: () => {},
    setEmploymentTypes: () => {},
    runSmartSearch: async () => ({ applied: false, error: 'not wired' }),
  });
  const adaActions = useMemo<AdaActions>(() => ({
    applyFilterGroups: (groups, mode) => adaActionsRef.current.applyFilterGroups(groups, mode),
    switchTab: (tab) => adaActionsRef.current.switchTab(tab),
    applyRadiusSearch: (args) => adaActionsRef.current.applyRadiusSearch(args),
    setKeywordSearch: (term) => adaActionsRef.current.setKeywordSearch(term),
    setEmploymentTypes: (types) => adaActionsRef.current.setEmploymentTypes(types),
    runSmartSearch: (query) => adaActionsRef.current.runSmartSearch(query),
  }), []);

  // Smart Search modal driven by Ada — the user gets the same preview card
  // as the manual AI Smart Search bar at the top of the table, but launched
  // from a chat action. Resolves the pending promise when the modal closes.
  type AdaSmartSearchState = {
    open: boolean;
    query: string;
    loading: boolean;
    filterGroups: any[] | null;
    explanation: string | null;
    radius: any | null;
    companyRadius: any | null;
    candidateRadius: any | null;
    canWiden: boolean;
  };
  const [adaSmartSearchModal, setAdaSmartSearchModal] = useState<AdaSmartSearchState>({
    open: false,
    query: '',
    loading: false,
    filterGroups: null,
    explanation: null,
    radius: null,
    companyRadius: null,
    candidateRadius: null,
    canWiden: false,
  });
  const adaSmartSearchResolveRef = useRef<((r: any) => void) | null>(null);

  // When Ada runs a smart search, briefly type the query into the visible
  // AI Smart Search bar at the top of the table before the modal opens.
  // This is the educational moment — the user sees the manual UI path Ada
  // is using on their behalf, so they learn the workflow. null = no override,
  // internal AISearchBar state takes over.
  const [aiSearchBarOverride, setAiSearchBarOverride] = useState<string | null>(null);

  // Publish the table's current view state to the Ada chat context so the
  // sidebar always knows what the user is looking at without prop-drilling.
  // We include every on-screen filter: filter builder groups, junction-table
  // associatedCompany, the keyword search bar, employment types, and any of
  // the three radius types. Each is published as null/empty when inactive.
  useEffect(() => {
    const radius: any = {};
    if (candidateLocationRadius.coords) {
      radius.candidateLat = candidateLocationRadius.coords.lat;
      radius.candidateLng = candidateLocationRadius.coords.lng;
      radius.candidateDistance = candidateLocationRadius.distance;
    }
    if (managerLocationRadius.coords) {
      radius.managerLat = managerLocationRadius.coords.lat;
      radius.managerLng = managerLocationRadius.coords.lng;
      radius.managerDistance = managerLocationRadius.distance;
    }
    if (companyOfficeRadius.coords) {
      radius.companyLat = companyOfficeRadius.coords.lat;
      radius.companyLng = companyOfficeRadius.coords.lng;
      radius.companyDistance = companyOfficeRadius.distance;
    }

    setAdaContext({
      projectId: selectedProject?.id ?? null,
      projectName: selectedProject?.name ?? null,
      dataType: activeDataType,
      filterGroups: aiFilterState.groups || [],
      associatedCompany: (activeDataType === 'candidates' || activeDataType === 'managers') ? (associatedCompanyFilter || null) : null,
      searchTerm: searchTerm?.trim() ? searchTerm.trim() : null,
      employmentTypes: aiFilterState.employmentTypes || [],
      radius: Object.keys(radius).length > 0 ? radius : null,
      visibleTotal:
        activeDataType === 'companies'
          ? serverTotals.companies
          : activeDataType === 'candidates'
            ? serverTotals.candidates
            : activeDataType === 'managers'
              ? serverTotals.managers
              : null,
      actions: adaActions,
    });
  }, [
    selectedProject?.id, selectedProject?.name, activeDataType,
    aiFilterState.groups, aiFilterState.employmentTypes,
    associatedCompanyFilter, searchTerm,
    candidateLocationRadius.coords, candidateLocationRadius.distance,
    managerLocationRadius.coords, managerLocationRadius.distance,
    companyOfficeRadius.coords, companyOfficeRadius.distance,
    serverTotals, setAdaContext,
  ]);

  // Local state for radius search form (to prevent re-renders on typing)
  const [tempCompanyOfficeRadius, setTempCompanyOfficeRadius] = useState<{
    location: string;
    distance: number;
  }>({ location: '', distance: 25 });
  const [tempCandidateLocationRadius, setTempCandidateLocationRadius] = useState<{
    location: string;
    distance: number;
  }>({ location: '', distance: 25 });
  const [tempManagerLocationRadius, setTempManagerLocationRadius] = useState<{
    location: string;
    distance: number;
  }>({ location: '', distance: 25 });

  // Connections modal state
  const [showConnectionsModal, setShowConnectionsModal] = useState(false);
  const [selectedCandidateForConnections, setSelectedCandidateForConnections] = useState<{ id: number, name: string } | null>(null);

  // Connections filter state
  const [connectionsFilterQuery, setConnectionsFilterQuery] = useState('');
  const [connectionsSuggestions, setConnectionsSuggestions] = useState<Array<{
    id: number,
    name: string,
    position?: string,
    company?: string,
    previous_company_1?: string,
    previous_company_2?: string,
    previous_company_3?: string
  }>>([]);
  const [selectedConnectionsFilter, setSelectedConnectionsFilter] = useState<{
    id: number,
    name: string,
    company?: string,
    previousCompanies: string[]  // All previous companies the selected candidate worked at
  } | null>(null);
  const [connectedCandidateIds, setConnectedCandidateIds] = useState<number[]>([]);
  const [connectionCompanyMap, setConnectionCompanyMap] = useState<Record<number, string>>({}); // Maps candidateId to company they worked together at
  const [connectionPositionMap, setConnectionPositionMap] = useState<Record<number, string>>({}); // Maps candidateId to their position at that company
  const [hiddenConnectionCompanies, setHiddenConnectionCompanies] = useState<Set<string>>(new Set()); // Companies hidden via eye icon
  const [showConnectionsSuggestions, setShowConnectionsSuggestions] = useState(false);
  const [excludePreviousCompanies, setExcludePreviousCompanies] = useState(false); // Toggle to exclude companies where selected candidate worked

  // Create project from filtered data modal state
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [projectCreationMode, setProjectCreationMode] = useState<'move' | 'copy' | 'copy-existing'>('copy');
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [userProjects, setUserProjects] = useState<Array<{id: number; name: string}>>([]);
  const [selectedExistingProjectId, setSelectedExistingProjectId] = useState<number | null>(null);

  // Saved searches state
  const [savedSearches, setSavedSearches] = useState<Array<{id: number; name: string; data_type: string; filter_state: any; search_term: string | null; radius_search: any; sort_config: any; created_at: string}>>([]);
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [showSavedSearchesDropdown, setShowSavedSearchesDropdown] = useState(false);

  // Job Spec Match modal state
  const [showJobSpecModal, setShowJobSpecModal] = useState(false);
  const [isJobSpecMatchMode, setIsJobSpecMatchMode] = useState(false); // Track if filters came from job spec (shows priority toggles)
  const [preferredFilterGroups, setPreferredFilterGroups] = useState<FilterGroup[]>([]); // Store preferred filters for scoring
  const [candidateMatchScores, setCandidateMatchScores] = useState<Record<number, number>>({}); // Store match scores by candidate ID
  // Job Spec Match v2: AI judgment scores (0-100 composites from stored
  // per-criterion Jev judgments). When set, the Match column shows these
  // instead of keyword scores and the keyword recompute effect stands down.
  const [jevMatch, setJevMatch] = useState<{ specId: number; title: string } | null>(null);
  // Ref mirror of jevMatch for fetchPaginatedData: set synchronously when a
  // match is applied so the very first _matchScore fetch already carries the
  // spec id (state alone would still be null inside that same tick).
  const jevMatchRef = useRef<{ specId: number; title: string } | null>(null);
  // Match column click-through: which candidate's breakdown popover is open.
  const [matchPopover, setMatchPopover] = useState<{ candidateId: number; name?: string; x: number; y: number } | null>(null);
  // Jobs-tab handoff: open the modal focused on a freshly created spec.
  const [jobSpecModalFocusId, setJobSpecModalFocusId] = useState<number | null>(null);
  useEffect(() => {
    if (initialJobSpecId) {
      setJobSpecModalFocusId(initialJobSpecId);
      setShowJobSpecModal(true);
      if (onInitialJobSpecConsumed) onInitialJobSpecConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialJobSpecId]);

  // Compute effective filter groups for backend queries
  // In job spec match mode, exclude "preferred" filters (they're only used for scoring, not filtering)
  const effectiveFilterGroups = useMemo(() => {
    if (isJobSpecMatchMode) {
      return aiFilterState.groups.filter(g => g.priority === 'required' || !g.priority);
    }
    return aiFilterState.groups;
  }, [aiFilterState.groups, isJobSpecMatchMode]);

  // SourceWhale integration state
  const [showSourceWhaleModal, setShowSourceWhaleModal] = useState(false);
  const [sourceWhaleConnected, setSourceWhaleConnected] = useState<boolean | null>(null);
  const [sourceWhaleCampaigns, setSourceWhaleCampaigns] = useState<any[]>([]);
  const [sourceWhaleProjects, setSourceWhaleProjects] = useState<any[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [sendImmediately, setSendImmediately] = useState(true);
  const [sourceWhaleLoading, setSourceWhaleLoading] = useState(false);
  const [sourceWhaleSending, setSourceWhaleSending] = useState(false);

  // Athena CRM integration state
  const [athenaConnected, setAthenaConnected] = useState(false);
  const [athenaPushing, setAthenaPushing] = useState(false);
  // Add selected candidates to the native ATS/CRM (user 1 while gated)
  const [atsAddBusy, setAtsAddBusy] = useState(false);
  const [atsAddOpen, setAtsAddOpen] = useState(false);

  const handleAddToAts = async (as: 'candidate' | 'contact') => {
    if (selectedRows.size === 0) return;
    setAtsAddOpen(false);
    setAtsAddBusy(true);
    try {
      const res = await fetch('/api/ats/people/from-candidates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ candidateIds: Array.from(selectedRows), as }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      alert(`ATS/CRM: ${data.added} added, ${data.merged} merged into existing records (as ${as}s). Matched people pull their full profile automatically.`);
    } catch (e: any) {
      alert(e.message || 'Failed to add to ATS');
    } finally {
      setAtsAddBusy(false);
    }
  };
  // Spott ATS integration — button shows for whoever has connected Spott in
  // Settings → Integrations (connection itself is gated server-side).
  const [spottConnected, setSpottConnected] = useState(false);
  const [spottPushing, setSpottPushing] = useState(false);
  const [spottProgress, setSpottProgress] = useState('');
  // v2: companies → Spott clients, hiring managers → client contacts
  const [spottV2Pushing, setSpottV2Pushing] = useState<null | 'companies' | 'contacts'>(null);
  const [spottV2Progress, setSpottV2Progress] = useState('');
  // Atlas ATS/CRM — same idea as Spott: buttons show once connected in
  // Settings → Integrations. One push at a time; kind = which tab it's from.
  const [atlasConnected, setAtlasConnected] = useState(false);
  const [atlasPushing, setAtlasPushing] = useState<null | 'candidates' | 'companies' | 'contacts'>(null);
  const [atlasProgress, setAtlasProgress] = useState('');
  // Giig ATS/CRM — same flow as Atlas.
  const [giigConnected, setGiigConnected] = useState(false);
  const [giigPushing, setGiigPushing] = useState<null | 'candidates' | 'companies' | 'contacts'>(null);
  const [giigProgress, setGiigProgress] = useState('');
  // Org chart — AI-inferred reporting structure (gated server-side, user 1
  // during rollout; /api/org-chart/status says whether to show the button).
  // Scope {} = whole project (candidates tab, single-company projects);
  // {companyId, companyName} = one company (companies tab, the normal case).
  const [orgChartEnabled, setOrgChartEnabled] = useState(false);
  const [orgChartScope, setOrgChartScope] = useState<null | { companyId?: number; companyName?: string }>(null);

  const handleSendToSpottV2 = async (kind: 'companies' | 'contacts') => {
    if (selectedRows.size === 0 || !selectedProject?.id) return;
    const ids = Array.from(selectedRows);
    const what = kind === 'companies' ? 'company' : 'hiring manager';
    const target = kind === 'companies' ? 'clients' : 'client contacts (linked to their client company)';
    if (!window.confirm(`Push ${ids.length} selected ${what}${ids.length === 1 ? '' : 's'} to Spott as ${target}? Already-sent records are skipped.`)) return;
    setSpottV2Pushing(kind);
    setSpottV2Progress('');
    try {
      const endpoint = kind === 'companies' ? '/api/spott/push-companies' : '/api/spott/push-contacts';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ projectId: selectedProject.id, ids }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start Spott push');
      const poll = setInterval(async () => {
        try {
          const p = await fetch(`/api/spott/push-progress/${data.operationId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          });
          if (!p.ok) return;
          const op = await p.json();
          if (op.status === 'running') {
            setSpottV2Progress(`${op.done.toLocaleString()}/${op.total.toLocaleString()}${op.created ? ` · ${op.created.toLocaleString()} new` : ''}`);
          } else {
            clearInterval(poll);
            setSpottV2Pushing(null);
            setSpottV2Progress('');
            alert(`Spott: ${op.created} created, ${op.existing} already in Spott${op.asCandidates ? `, ${op.asCandidates} skipped (already Spott candidates)` : ''}${op.failed ? `, ${op.failed} failed` : ''}.` +
              (op.errors?.length ? `\n${op.errors.join('\n')}` : ''));
          }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e: any) {
      setSpottV2Pushing(null);
      alert(e.message || 'Failed to push to Spott');
    }
  };
  const handleSendToAtlas = async (kind: 'candidates' | 'companies' | 'contacts') => {
    if (selectedRows.size === 0 || !selectedProject?.id) return;
    const ids = Array.from(selectedRows);
    const what = kind === 'candidates' ? 'candidate' : kind === 'companies' ? 'company' : 'hiring manager';
    const as = kind === 'candidates' ? 'people (with their career history)' : kind === 'companies' ? 'companies' : 'company contacts, linked to their company';
    if (!window.confirm(`Send ${ids.length} selected ${what}${ids.length === 1 ? '' : 's'} to Atlas as ${as}? Anyone already in Atlas is linked, not overwritten, and already-sent records are skipped.`)) return;
    setAtlasPushing(kind);
    setAtlasProgress('');
    try {
      const res = await fetch(`/api/atlas/push-${kind}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ projectId: selectedProject.id, ids }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start the Atlas push');
      const poll = setInterval(async () => {
        try {
          const p = await fetch(`/api/atlas/push-progress/${data.operationId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          });
          if (!p.ok) return;
          const op = await p.json();
          if (op.status === 'running') {
            setAtlasProgress(`${op.done.toLocaleString()}/${op.total.toLocaleString()}${op.created ? ` · ${op.created.toLocaleString()} new` : ''}`);
          } else {
            clearInterval(poll);
            setAtlasPushing(null);
            setAtlasProgress('');
            alert(`Atlas: ${op.created} added, ${op.existing} already in Atlas (linked)${op.failed ? `, ${op.failed} not sent` : ''}.` +
              (op.companies ? `\n${op.companies} of their companies were added to Atlas too.` : '') +
              (data.capped ? `\nOnly the first ${data.total.toLocaleString()} were sent — send the rest again to continue.` : '') +
              (op.errors?.length ? `\n\n${op.errors.join('\n')}` : ''));
          }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e: any) {
      setAtlasPushing(null);
      alert(e.message || 'Failed to send to Atlas');
    }
  };
  const handleSendToGiig = async (kind: 'candidates' | 'companies' | 'contacts') => {
    if (selectedRows.size === 0 || !selectedProject?.id) return;
    const ids = Array.from(selectedRows);
    const what = kind === 'candidates' ? 'candidate' : kind === 'companies' ? 'company' : 'hiring manager';
    if (!window.confirm(`Send ${ids.length} selected ${what}${ids.length === 1 ? '' : 's'} to Giig? Anyone already in Giig is linked, not duplicated, and already-sent records are skipped.`)) return;
    setGiigPushing(kind);
    setGiigProgress('');
    try {
      const res = await fetch(`/api/giig/push-${kind}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ projectId: selectedProject.id, ids }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start the Giig push');
      const poll = setInterval(async () => {
        try {
          const p = await fetch(`/api/giig/push-progress/${data.operationId}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
          if (!p.ok) return;
          const op = await p.json();
          if (op.status === 'running') {
            setGiigProgress(`${op.done.toLocaleString()}/${op.total.toLocaleString()}${op.created ? ` · ${op.created.toLocaleString()} new` : ''}`);
          } else {
            clearInterval(poll);
            setGiigPushing(null);
            setGiigProgress('');
            alert(`Giig: ${op.created} added, ${op.existing} already in Giig${op.failed ? `, ${op.failed} not sent` : ''}.` +
              (op.errors?.length ? `\n\n${op.errors.join('\n')}` : ''));
          }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e: any) {
      setGiigPushing(null);
      alert(e.message || 'Failed to send to Giig');
    }
  };
  useEffect(() => {
    if (IS_RECMAPPER) return; // MarketMapr-only integrations
    const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
    fetch('/api/atlas/status', { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => setAtlasConnected(!!d?.connected))
      .catch(() => {});
    fetch('/api/giig/status', { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => setGiigConnected(!!d?.connected))
      .catch(() => {});
    fetch('/api/athena/status', { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => setAthenaConnected(!!d?.connected))
      .catch(() => {});
    fetch('/api/spott/status', { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => setSpottConnected(!!d?.connected))
      .catch(() => {});
    fetch('/api/org-chart/status', { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => setOrgChartEnabled(!!d?.enabled))
      .catch(() => {});
  }, []);

  // ── Prospect pages (admin only): 👁 indicators + create-from-table ─────
  const isProspectAdmin = !IS_RECMAPPER && (userId === 1 || (user as any)?.role === 'admin');
  // A RecMapper country library is shared and read-only (contact unlocks come
  // with per-team overlays) — its write actions are hidden.
  const isLibrary = IS_RECMAPPER && !!(selectedProject as any)?.is_library;
  const [prospectLinks, setProspectLinks] = useState<Record<number, string>>({});
  const [prospectGenBusy, setProspectGenBusy] = useState(false);

  useEffect(() => {
    if (!isProspectAdmin || activeDataType !== 'candidates' || displayData.length === 0) return;
    const ids = displayData.map((r: any) => r.id).filter(Boolean);
    fetch('/api/pitch-decks/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ candidateIds: ids }),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.links) setProspectLinks(prev => ({ ...prev, ...d.links })); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProspectAdmin, activeDataType, displayData]);

  // Learn tab — "Find hiring managers" lesson: "Select them for me", and move
  // on once every company in view is ticked (however the user did it).
  const { stepId: lessonStep } = useTour();
  useLessonAction('project-select-all-companies', () => {
    if (activeDataType === 'companies') setSelectedRows(new Set(displayData.map((row: any) => row.id)));
  });
  useEffect(() => {
    if (lessonStep === 'hm-select-companies' && activeDataType === 'companies'
      && displayData.length > 0 && displayData.every((row: any) => selectedRows.has(row.id))) {
      tourEvent('lesson-companies-selected');
    }
  }, [lessonStep, activeDataType, displayData, selectedRows]);

  // Learn tab — "Your project" lessons. Events fire however the user acts.
  const prevPanelIdRef = useRef<number | null>(null);
  useEffect(() => {
    const id = detailPanel.open ? detailPanel.entityId : null;
    if (id && !prevPanelIdRef.current) tourEvent('profile-opened');
    else if (id && prevPanelIdRef.current && id !== prevPanelIdRef.current) tourEvent('profile-moved');
    prevPanelIdRef.current = id;
  }, [detailPanel.open, detailPanel.entityId]);
  useEffect(() => {
    if (viewMode === 'card') tourEvent('view-mode-card');
    else tourEvent('view-mode-table');
  }, [viewMode]);
  const hadSearchRef = useRef(false);
  useEffect(() => {
    if (searchTerm.trim()) { hadSearchRef.current = true; tourEvent('quick-search-done'); }
    else if (hadSearchRef.current) { hadSearchRef.current = false; tourEvent('quick-search-cleared'); }
  }, [searchTerm]);
  // "Type one for me": the first name of the first candidate in view.
  useLessonAction('lesson-quick-search-example', () => {
    const first = String((displayData[0] as any)?.name || (displayData[0] as any)?.company_name || '').trim().split(/\s+/)[0];
    if (!first) return;
    setSearchInputValue(first);
    setSearchTerm(first);
  });
  // Tags lesson: tick the first three rows, and name the tag.
  useLessonAction('lesson-select-rows', () => {
    setSelectedRows(new Set(displayData.slice(0, 3).map((row: any) => row.id)));
  });
  useLessonAction('lesson-tag-name', () => setNewTag('Shortlist'));
  useEffect(() => { if (selectedRows.size > 0) tourEvent('rows-selected'); }, [selectedRows]);
  // Save search lesson: name it (the user presses Save).
  useLessonAction('lesson-save-search-name', () => setSaveSearchName('GenAI engineers'));
  // Radius lesson: fill the candidate box (the user presses Apply).
  useLessonAction('lesson-radius-example', () => {
    setTempCandidateLocationRadius((prev: any) => ({ ...prev, location: 'Copenhagen, Denmark', distance: 15 }));
  });
  useLessonAction('lesson-back-to-table', () => {
    setDetailPanel((p) => ({ ...p, open: false }));
    handleSetViewMode('table');
  });

  const handleCreateProspectPages = async () => {
    if (selectedRows.size === 0 || !selectedProject?.id) return;
    const ids = Array.from(selectedRows).filter(id => !prospectLinks[id]).slice(0, 25);
    if (ids.length === 0) { alert('All selected candidates already have prospect pages.'); return; }
    if (!window.confirm(`Create prospect pages for ${ids.length} selected candidate${ids.length === 1 ? '' : 's'}? Each takes ~15-30s to generate.`)) return;
    setProspectGenBusy(true);
    try {
      const res = await fetch('/api/pitch-decks/bulk-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ projectId: selectedProject.id, candidateIds: ids }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start generation');
      // Poll until complete, then refresh the 👁 indicators.
      const poll = setInterval(async () => {
        try {
          const p = await fetch(`/api/pitch-decks/bulk-progress/${data.operationId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          });
          if (!p.ok) return;
          const op = await p.json();
          if (op.status === 'completed') {
            clearInterval(poll);
            setProspectGenBusy(false);
            const fresh: Record<number, string> = {};
            for (const r of op.results || []) if (r.status === 'created' && r.url) fresh[r.candidateId] = r.url;
            setProspectLinks(prev => ({ ...prev, ...fresh }));
            alert(`Prospect pages: ${op.created} created${op.skipped ? `, ${op.skipped} skipped` : ''}${op.failed ? `, ${op.failed} failed` : ''}.`);
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e: any) {
      setProspectGenBusy(false);
      alert(e.message || 'Failed to create prospect pages');
    }
  };

  const handleSendToSpott = async () => {
    if (selectedRows.size === 0 || !selectedProject?.id) return;
    const ids = Array.from(selectedRows);
    if (!window.confirm(`Push ${ids.length} selected candidate${ids.length === 1 ? '' : 's'} to Spott with full career history? Duplicates (by LinkedIn URL, email or phone) are skipped automatically.`)) return;
    const updateExisting = window.confirm(
      'Also UPDATE candidates already in Spott with the latest MarketMapr data?\n\n' +
      'OK = refresh their career history, education, skills and profile note (overwrites manual edits to those sections in Spott).\n' +
      'Cancel = skip already-sent candidates (faster).'
    );
    setSpottPushing(true);
    try {
      const res = await fetch('/api/spott/push-candidates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ projectId: selectedProject.id, candidateIds: ids, updateExisting }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start Spott push');
      const poll = setInterval(async () => {
        try {
          const p = await fetch(`/api/spott/push-progress/${data.operationId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          });
          if (!p.ok) return;
          const op = await p.json();
          if (op.status === 'running') {
            setSpottProgress(
              `${op.done.toLocaleString()}/${op.total.toLocaleString()}` +
              `${op.created ? ` · ${op.created.toLocaleString()} new` : ''}` +
              `${op.updated ? ` · ${op.updated.toLocaleString()} refreshed` : ''}`
            );
          }
          if (op.status !== 'running') {
            clearInterval(poll);
            setSpottPushing(false);
            setSpottProgress('');
            const processed = (op.created || 0) + (op.existing || 0) + (op.failed || 0);
            const shortfall = ids.length - processed;
            alert(`Spott: ${op.created} created, ${op.existing} already in Spott${op.updated ? ` (${op.updated} refreshed with latest data)` : ''}${op.failed ? `, ${op.failed} failed` : ''}.` +
              (shortfall > 0 ? `\n${shortfall.toLocaleString()} of your selection were not processed this run — push the same selection again to continue (already-sent candidates are skipped automatically).` : '') +
              (op.errors?.length ? `\n${op.errors.join('\n')}` : ''));
          }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e: any) {
      setSpottPushing(false);
      alert(e.message || 'Failed to push to Spott');
    }
  };

  const handleSendToAthena = async () => {
    if (selectedRows.size === 0 || !selectedProject?.id) return;
    setAthenaPushing(true);
    try {
      const res = await fetch('/api/athena/push/candidates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ projectId: selectedProject.id, candidateIds: Array.from(selectedRows) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      alert(`Athena: ${data.created} created, ${data.updated} updated${data.failed ? `, ${data.failed} failed` : ''}.` +
        (data.experiencesSynced || data.degreesSynced ? `\nCareer history: ${data.experiencesSynced || 0} roles + ${data.degreesSynced || 0} degrees synced${data.careerFailed ? ` (${data.careerFailed} candidates' history failed)` : ''}.` : '') +
        (data.errors?.length ? `\n${data.errors.join('\n')}` : ''));
    } catch (e: any) {
      alert(e.message || 'Failed to push to Athena');
    } finally {
      setAthenaPushing(false);
    }
  };

  // Check SourceWhale connection status on mount
  useEffect(() => {
    const checkSourceWhaleStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/sourcewhale/status', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setSourceWhaleConnected(data.connected);
        }
      } catch (error) {
        // Silently fail - just means button won't show
        setSourceWhaleConnected(false);
      }
    };
    if (!IS_RECMAPPER) checkSourceWhaleStatus();
  }, []);

  // lemlist integration state
  const [showLemlistModal, setShowLemlistModal] = useState(false);
  const [lemlistConnected, setLemlistConnected] = useState<boolean | null>(null);
  const [lemlistCampaigns, setLemlistCampaigns] = useState<any[]>([]);
  const [selectedLemlistCampaignId, setSelectedLemlistCampaignId] = useState<string>('');
  const [lemlistLoading, setLemlistLoading] = useState(false);
  const [lemlistSending, setLemlistSending] = useState(false);
  // Optional profile fields to push as lemlist variables (mirrors the Launch tab).
  const [lemlistFields, setLemlistFields] = useState<Set<string>>(new Set(['summary', 'current_position_description']));
  const [showLemlistFields, setShowLemlistFields] = useState(false);

  // Check lemlist connection status on mount
  useEffect(() => {
    const checkLemlistStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/lemlist/status', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setLemlistConnected(data.connected);
        }
      } catch (error) {
        setLemlistConnected(false);
      }
    };
    if (!IS_RECMAPPER) checkLemlistStatus();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Saved searches functions
  const fetchSavedSearches = useCallback(async () => {
    if (!selectedProject?.id) return;
    try {
      const response = await fetch(`/api/projects/${selectedProject.id}/saved-searches`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setSavedSearches(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch saved searches:', err);
    }
  }, [selectedProject?.id]);

  const handleSaveSearch = async (name: string) => {
    if (!selectedProject?.id || !name.trim()) return;
    try {
      const radiusData = radiusCoords ? {
        type: radiusType,
        location: radiusLocation,
        distance: radiusDistance,
        lat: radiusCoords.lat,
        lng: radiusCoords.lng
      } : null;

      const response = await fetch(`/api/projects/${selectedProject.id}/saved-searches`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: name.trim(),
          dataType: activeDataType,
          filterState: aiFilterState,
          searchTerm: searchTerm || null,
          radiusSearch: radiusData,
          sortConfig
        })
      });
      if (response.ok) {
        await fetchSavedSearches();
        tourEvent('search-saved');
        setShowSaveSearchModal(false);
        setSaveSearchName('');
      }
    } catch (err) {
      console.error('Failed to save search:', err);
    }
  };

  const handleLoadSavedSearch = async (search: typeof savedSearches[0]) => {
    // Restore filter state
    const filterState = search.filter_state || { groups: [], employmentTypes: [] };
    setAiFilterState(filterState);

    // Restore search term
    if (search.search_term) {
      setSearchTerm(search.search_term);
    }

    // Restore sort config
    if (search.sort_config) {
      setSortConfig(search.sort_config);
    }

    // Restore radius search
    if (search.radius_search) {
      const r = search.radius_search;
      setRadiusType(r.type || 'candidate');
      setRadiusLocation(r.location || '');
      setRadiusDistance(r.distance || 25);
      if (r.lat && r.lng) {
        setRadiusCoords({ lat: r.lat, lng: r.lng });
        if (r.type === 'candidate') {
          setCandidateLocationRadius({ location: r.location, distance: r.distance, coords: { lat: r.lat, lng: r.lng } });
        } else {
          setCompanyOfficeRadius({ location: r.location, distance: r.distance, coords: { lat: r.lat, lng: r.lng } });
        }
        // Trigger the radius search to actually apply it
        await applyRadiusSearch(r.type, r.location, r.distance);
      }
    }

    setShowSavedSearchesDropdown(false);

    // If no radius search, trigger a re-fetch with the restored filters
    if (!search.radius_search) {
      setCurrentPage(1);
    }
  };

  const handleDeleteSavedSearch = async (searchId: number) => {
    if (!selectedProject?.id) return;
    try {
      const response = await fetch(`/api/projects/${selectedProject.id}/saved-searches/${searchId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setSavedSearches(prev => prev.filter(s => s.id !== searchId));
      }
    } catch (err) {
      console.error('Failed to delete saved search:', err);
    }
  };

  // Fetch saved searches on project load
  React.useEffect(() => {
    fetchSavedSearches();
  }, [fetchSavedSearches]);

  // Track user activity for admin dashboard
  const trackActivity = async () => {
    try {
      await fetch('/api/auth/activity', {
        method: 'POST',
        headers: getAuthHeaders()
      });
    } catch (error) {
      // Silently fail - activity tracking is non-critical
    }
  };

  const getCompanyColumns = (): Array<{ key: string; label: string; type: 'text' | 'number' | 'date' | 'array' | 'boolean'; isLocked?: boolean; isPrimary?: boolean }> => [
    { key: 'company_name', label: 'Company Name', type: 'text' as const, isLocked: true, isPrimary: true },
    { key: 'candidate_count', label: 'Candidates', type: 'number' as const, isLocked: true },
    { key: 'hiring_manager_count', label: 'Hiring Managers', type: 'number' as const, isLocked: true },
    { key: 'tags', label: 'Tags', type: 'array' as const, isPrimary: true },
    { key: 'company_domain', label: 'Domain', type: 'text' as const },
    { key: 'added_via_monitoring', label: 'Added by Monitoring', type: 'boolean' as const },
    { key: 'added_via_jobs', label: 'Added through Jobs', type: 'boolean' as const },
    { key: 'name', label: 'Name', type: 'text' as const },
    { key: 'website', label: 'Website', type: 'text' as const },
    { key: 'employee_count', label: 'Employees', type: 'number' as const, isPrimary: true },
    { key: 'industry', label: 'Industry', type: 'text' as const, isPrimary: true },
    { key: 'subindustry', label: 'Subindustry', type: 'array' as const, isPrimary: true },
    { key: 'category', label: 'Country', type: 'text' as const },
    { key: 'slogan', label: 'Slogan', type: 'text' as const },
    { key: 'primary_category', label: 'Primary Category', type: 'text' as const },
    { key: 'specialties', label: 'Specialties', type: 'text' as const, isPrimary: true },
    { key: 'description', label: 'Description', type: 'text' as const, isPrimary: true },
    { key: 'about', label: 'About', type: 'text' as const },
    { key: 'url', label: 'LinkedIn', type: 'text' as const },
    { key: 'founded', label: 'Founded', type: 'number' as const, isPrimary: true },
    { key: 'follower_count', label: 'Followers', type: 'number' as const },
    { key: 'investors', label: 'Investors', type: 'text' as const },
    { key: 'funding', label: 'Funding', type: 'text' as const },
    { key: 'headquarters', label: 'Headquarters', type: 'text' as const },
    { key: 'crunchbase_url', label: 'Crunchbase', type: 'text' as const },
    { key: 'type', label: 'Type', type: 'text' as const },
    { key: 'office_address', label: 'Office Address', type: 'text' as const },
    { key: 'company_size_formatted', label: 'Company Size', type: 'text' as const },
    { key: 'annual_revenue', label: 'Annual Revenue', type: 'text' as const },
    { key: 'business_type', label: 'Business Type', type: 'array' as const },
    { key: 'business_stage', label: 'Business Stage', type: 'text' as const },
    { key: 'primary_offerings', label: 'Primary Offerings', type: 'array' as const },
    { key: 'revenue_streams', label: 'Revenue Streams', type: 'array' as const },
    { key: 'pattern_tags', label: 'Pattern Tags', type: 'array' as const },
    { key: 'scale_scope', label: 'Scale/Scope', type: 'text' as const },
    { key: 'org_id', label: 'Org ID', type: 'text' as const },
    { key: 'logo_url', label: 'Logo', type: 'text' as const },
    { key: 'created_at', label: 'Created', type: 'date' as const },
    { key: 'updated_at', label: 'Updated', type: 'date' as const }
  ];

// Default column order version - increment this when changing the default order
const CANDIDATE_COLUMN_ORDER_VERSION = 4;

// Default column order for candidates (used when no saved order or version mismatch)
// Name, current role, company first — the rule for everyone (Elliot at Koda,
// 21 Oct 2026). A project's own saved order, or a person's default
// ("Make this my default" in the column menu), takes precedence.
const DEFAULT_CANDIDATE_COLUMN_ORDER = [
  'name', 'current_position', 'current_company',
  'tags', 'connection_count', 'worked_together_at', 'position_at_company',
  'email', 'personal_email', 'phone',
  'current_contract_type', 'current_position_description',
  'location_full', 'location_country', 'current_company_location',
  'years_at_current_company', 'average_tenure', 'headline', 'summary', 'li_profile_url',
  'previous_position_1', 'previous_position_1_description', 'previous_company_1', 'previous_position_1_years',
  'previous_position_2', 'previous_position_2_description', 'previous_company_2', 'previous_position_2_years',
  'previous_position_3', 'previous_position_3_description', 'previous_company_3', 'previous_position_3_years',
  'languages', 'total_experience_years', 'education_1', 'degree_1', 'field_of_study_1',
  'all_skills_text', 'first_name', 'last_name', 'created_at', 'updated_at'
];

const getCandidateColumns = (): Array<{
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'array' | 'boolean' | 'language' | 'role_window';
  isPrimary?: boolean;
  isLocked?: boolean;
  category?: string;
  hiddenByDefault?: boolean;
}> => [
  // ========== PRIMARY COLUMNS (visible by default, toggleable) ==========
  // 'name' is the only truly always-visible column — it's the row identifier.
  // Everything else marked isPrimary is shown by default but can be hidden
  // via the column selector (locked columns can't be hidden).
  { key: 'name', label: 'Name', type: 'text' as const, isLocked: true },
  { key: 'tags', label: 'Tags', type: 'array' as const, isPrimary: true },
  { key: 'connection_count', label: 'Connections', type: 'number' as const, isPrimary: true },
  { key: 'email', label: 'Work Email', type: 'text' as const, isPrimary: true },
  { key: 'personal_email', label: 'Personal Email', type: 'text' as const, isPrimary: true },
  { key: 'phone', label: 'Phone', type: 'text' as const, isPrimary: true },
  { key: 'current_contract_type', label: 'Employment Type', type: 'text' as const, isPrimary: true },
  { key: 'current_position', label: 'Current Role', type: 'text' as const, isPrimary: true },
  { key: 'current_position_description', label: 'Role Description', type: 'text' as const, isPrimary: true },
  { key: 'current_company', label: 'Current Company', type: 'text' as const, isPrimary: true },
  { key: 'location_full', label: 'Location', type: 'text' as const, isPrimary: true },
  { key: 'current_company_location', label: 'Work Location', type: 'text' as const, isPrimary: true },
  { key: 'years_at_current_company', label: 'Current Tenure', type: 'number' as const, isPrimary: true },
  { key: 'average_tenure', label: 'Avg Tenure', type: 'number' as const, isPrimary: true },
  { key: 'headline', label: 'Headline', type: 'text' as const, isPrimary: true },
  { key: 'summary', label: 'Summary', type: 'text' as const, isPrimary: true },
  { key: 'li_profile_url', label: 'LinkedIn', type: 'text' as const, isPrimary: true },

  // ========== CAREER HISTORY (Previous roles) ==========
  { key: 'previous_position_1', label: 'Previous Role 1', type: 'text' as const, category: 'career' },
  { key: 'previous_position_1_description', label: 'Previous Role 1 Description', type: 'text' as const, category: 'career' },
  { key: 'previous_company_1', label: 'Previous Company 1', type: 'text' as const, category: 'career' },
  { key: 'previous_position_1_years', label: 'Years at Previous 1', type: 'number' as const, category: 'career' },
  { key: 'previous_position_2', label: 'Previous Role 2', type: 'text' as const, category: 'career' },
  { key: 'previous_position_2_description', label: 'Previous Role 2 Description', type: 'text' as const, category: 'career' },
  { key: 'previous_company_2', label: 'Previous Company 2', type: 'text' as const, category: 'career' },
  { key: 'previous_position_2_years', label: 'Years at Previous 2', type: 'number' as const, category: 'career' },
  { key: 'previous_position_3', label: 'Previous Role 3', type: 'text' as const, category: 'career' },
  { key: 'previous_position_3_description', label: 'Previous Role 3 Description', type: 'text' as const, category: 'career' },
  { key: 'previous_company_3', label: 'Previous Company 3', type: 'text' as const, category: 'career' },
  { key: 'previous_position_3_years', label: 'Years at Previous 3', type: 'number' as const, category: 'career' },

  // ========== ADDITIONAL VISIBLE BY DEFAULT ==========
  { key: 'languages', label: 'Languages', type: 'array' as const, category: 'profile' },
  { key: 'language_min_proficiency', label: 'Language + min. proficiency', type: 'language' as const, category: 'profile' },
  // MarketMapr's own record id: a stable key for matching exports with other
  // systems (Bullhorn, ZoomInfo…) — the same id Claude's tools return.
  { key: 'id', label: 'MarketMapr ID', type: 'number' as const, category: 'profile' },
  { key: 'role_in_last_years', label: 'Any role (title/keyword) in last N years', type: 'role_window' as const, category: 'career' },
  { key: 'phone_searched', label: 'Phone searched?', type: 'boolean' as const, category: 'profile' },
  { key: 'email_searched', label: 'Work email searched?', type: 'boolean' as const, category: 'profile' },
  { key: 'personal_email_searched', label: 'Personal email searched?', type: 'boolean' as const, category: 'profile' },
  { key: 'total_experience_years', label: 'Total Experience', type: 'number' as const, category: 'career' },
  { key: 'education_1', label: 'Education', type: 'text' as const, category: 'education' },
  { key: 'education_notes', label: 'Education Notes', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'degree_1', label: 'Degree', type: 'text' as const, category: 'education' },
  { key: 'field_of_study_1', label: 'Field of Study', type: 'text' as const, category: 'education' },
  { key: 'education_1_years', label: 'Education 1 Years', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'education_1_description', label: 'Education 1 Description', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'education_1_grade', label: 'Education 1 Grade', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'education_2', label: 'Education 2', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'degree_2', label: 'Degree 2', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'field_of_study_2', label: 'Field of Study 2', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'education_2_years', label: 'Education 2 Years', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'education_2_description', label: 'Education 2 Description', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'education_2_grade', label: 'Education 2 Grade', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'education_3', label: 'Education 3', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'degree_3', label: 'Degree 3', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'field_of_study_3', label: 'Field of Study 3', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'education_3_years', label: 'Education 3 Years', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'education_3_description', label: 'Education 3 Description', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'education_3_grade', label: 'Education 3 Grade', type: 'text' as const, category: 'education', hiddenByDefault: true },
  { key: 'all_skills_text', label: 'All Skills (Text)', type: 'text' as const, category: 'skills' },
  { key: 'first_name', label: 'First Name', type: 'text' as const },
  { key: 'last_name', label: 'Last Name', type: 'text' as const },
  { key: 'created_at', label: 'Created', type: 'date' as const },
  { key: 'updated_at', label: 'Updated', type: 'date' as const },

  // ========== HIDDEN BY DEFAULT ==========
  { key: 'total_positions', label: 'Total Roles', type: 'number' as const, category: 'career', hiddenByDefault: true },
  { key: 'current_position_start_date', label: 'Current Role Started', type: 'text' as const, category: 'career', hiddenByDefault: true },
  { key: 'top_skills_5', label: 'Top 5 Skills', type: 'text' as const, category: 'skills', hiddenByDefault: true },
  { key: 'top_skills_10', label: 'Top 10 Skills', type: 'text' as const, category: 'skills', hiddenByDefault: true },
  { key: 'all_skills', label: 'All Skills (Array)', type: 'array' as const, hiddenByDefault: true },
  { key: 'position', label: 'Position (Legacy)', type: 'text' as const, hiddenByDefault: true },
  { key: 'company', label: 'Company (Legacy)', type: 'text' as const, hiddenByDefault: true },
  { key: 'address', label: 'Address', type: 'text' as const, hiddenByDefault: true },

  // ========== OTHER COLUMNS (not in main list but available) ==========
  { key: 'total_education', label: '# Education', type: 'number' as const, category: 'education' },
  { key: 'certification_1', label: 'Top Certification', type: 'text' as const, category: 'education' },
  { key: 'certification_1_org', label: 'Cert Organization', type: 'text' as const, category: 'education' },
  { key: 'total_certifications', label: '# Certifications', type: 'number' as const, category: 'education' },
  { key: 'total_skills', label: '# Skills', type: 'number' as const, category: 'skills' },
  { key: 'linkedin_premium', label: 'Premium', type: 'boolean' as const, category: 'profile' },
  { key: 'open_to_work', label: 'Open to Work', type: 'boolean' as const, category: 'profile' },
  { key: 'verified_badge', label: 'Verified', type: 'boolean' as const, category: 'profile' },
  { key: 'follower_count', label: 'Followers', type: 'number' as const, category: 'profile' },
  { key: 'company_size', label: 'Company Size', type: 'number' as const, category: 'company' },
  { key: 'company_industry', label: 'Industry', type: 'text' as const, category: 'company' },
  { key: 'company_website', label: 'Company Website', type: 'text' as const, category: 'company' },
  { key: 'company_followers', label: 'Company Followers', type: 'number' as const, category: 'company' },
  { key: 'company_founded_year', label: 'Company Founded', type: 'number' as const, category: 'company' },
  { key: 'location_city', label: 'City', type: 'text' as const, category: 'location' },
  { key: 'location_country', label: 'Country', type: 'text' as const, category: 'location' },

  // ========== AGENT COLUMNS (only shown for agent-created projects) ==========
  { key: 'match_score', label: 'Match Score', type: 'number' as const, category: 'agent', hiddenByDefault: true },
  { key: 'match_reasoning', label: 'Match Reasoning', type: 'text' as const, category: 'agent', hiddenByDefault: true }
];

  const getManagerColumns = () => [
    { key: 'is_starred', label: '⭐', type: 'text' as const, isPrimary: true },
    { key: 'name', label: 'Name', type: 'text' as const, isPrimary: true },
    { key: 'first_name', label: 'First Name', type: 'text' as const },
    { key: 'last_name', label: 'Last Name', type: 'text' as const },
    { key: 'job_title', label: 'Job Title', type: 'text' as const, isPrimary: true },
    { key: 'org', label: 'Organization', type: 'text' as const, isPrimary: true },
    { key: 'tags', label: 'Tags', type: 'array' as const, isPrimary: true },
    { key: 'email', label: 'Work Email', type: 'text' as const, isPrimary: true },
    { key: 'personal_email', label: 'Personal Email', type: 'text' as const, isPrimary: true },
    { key: 'phone', label: 'Phone', type: 'text' as const, isPrimary: true },
    { key: 'location', label: 'Location', type: 'text' as const },
    { key: 'hm_country', label: 'Country', type: 'text' as const, isPrimary: true },
    { key: 'department', label: 'Department', type: 'text' as const },
    { key: 'seniority_level', label: 'Seniority', type: 'text' as const },
    // Auto-discovery: how and why each pick was made.
    { key: 'discovery_why', label: 'Why (auto-discovered)', type: 'text' as const },
    { key: 'discovery_confidence', label: 'Confidence %', type: 'number' as const },
    { key: 'headline', label: 'Headline', type: 'text' as const },
    { key: 'summary', label: 'Summary', type: 'text' as const },
    { key: 'li_profile_url', label: 'LinkedIn Profile', type: 'text' as const },
    { key: 'li_company_url', label: 'Company LinkedIn', type: 'text' as const },
    { key: 'id', label: 'MarketMapr ID', type: 'number' as const },
    { key: 'created_at', label: 'Created', type: 'date' as const },
    { key: 'updated_at', label: 'Updated', type: 'date' as const }
  ];

const getCurrentColumns = () => {
  let baseColumns;
  if (activeDataType === 'companies') baseColumns = getCompanyColumns();
  else if (activeDataType === 'candidates') baseColumns = getCandidateColumns();
  else baseColumns = getManagerColumns();

  // Campaign Leads List: show each member's lemlist status (rendered as a badge
  // in the cell renderer) right after their name.
  if (campaignScopeId && activeDataType !== 'companies') {
    const nameIdx = baseColumns.findIndex((c: any) => c.key === 'name');
    const lemCol: any = { key: 'lemlist_status', label: 'Lemlist', type: 'text' };
    baseColumns = nameIdx >= 0
      ? [...baseColumns.slice(0, nameIdx + 1), lemCol, ...baseColumns.slice(nameIdx + 1)]
      : [lemCol, ...baseColumns];
  }

  // Campaign-scoped AI columns for the current tab (candidate cols on the
  // Candidates tab, contact cols on the Contacts/managers tab). Project custom
  // columns are candidate-oriented and never belong on the managers tab.
  const activeCampaignEntity = activeDataType === 'candidates' ? 'candidate'
    : (activeDataType === 'managers' ? 'contact' : null);
  const campaignColsForTab = (campaignScopeId && activeCampaignEntity)
    ? campaignColumns.filter((c: any) => (c.entity_type || 'candidate') === activeCampaignEntity)
    : [];
  const projectCustomCols = activeDataType === 'managers' ? [] : customColumns;

  // Add custom columns (with group metadata for Ada criteria scoring)
  const customCols = [...projectCustomCols, ...campaignColsForTab].map(col => ({
    key: col.column_key,
    label: col.column_label,
    type: col.column_type,
    isCustom: true,
    isCampaign: !!col.entity_type || campaignColsForTab.includes(col),
    groupKey: col.column_group_key || null,
    groupLabel: col.column_group_label || null
  }));

  // Build a map: groupKey -> [column keys in display order]
  // Used to identify the "leader" column of a group (first in visual order)
  // and to compute combined verdicts from all group members.
  const groupMembers: Record<string, string[]> = {};
  customCols.forEach(c => {
    if (c.groupKey) {
      if (!groupMembers[c.groupKey]) groupMembers[c.groupKey] = [];
      groupMembers[c.groupKey].push(c.key);
    }
  });

  // For agent-created projects, promote Match Score & Match Reasoning to visible primary columns
  if (selectedProject?.created_by_agent && activeDataType === 'candidates') {
    baseColumns = baseColumns.map(col => {
      if (col.key === 'match_score' || col.key === 'match_reasoning') {
        return { ...col, isPrimary: true, hiddenByDefault: false };
      }
      return col;
    });
    // Move match_score and match_reasoning right after 'name'
    const nameIdx = baseColumns.findIndex(col => col.key === 'name');
    const agentCols = baseColumns.filter(col => col.key === 'match_score' || col.key === 'match_reasoning');
    const otherCols = baseColumns.filter(col => col.key !== 'match_score' && col.key !== 'match_reasoning');
    baseColumns = [
      ...otherCols.slice(0, nameIdx + 1),
      ...agentCols,
      ...otherCols.slice(nameIdx + 1)
    ];
  }

  // Position custom columns (from Ada scoring) right after name (or after match_score/match_reasoning if present)
  // This ensures score/label/notes columns appear at position 2/3/4, not at the end
  if (customCols.length > 0 && (activeDataType === 'candidates' || activeDataType === 'companies' || activeDataType === 'managers')) {
    const nameIdx = baseColumns.findIndex(col => col.key === 'name' || col.key === 'company_name');
    if (nameIdx !== -1) {
      // Find insert point: after name and any match_score/match_reasoning columns
      let insertIdx = nameIdx + 1;
      while (insertIdx < baseColumns.length &&
             (baseColumns[insertIdx].key === 'match_score' || baseColumns[insertIdx].key === 'match_reasoning')) {
        insertIdx++;
      }
      baseColumns = [
        ...baseColumns.slice(0, insertIdx),
        ...customCols,
        ...baseColumns.slice(insertIdx)
      ];
    } else {
      baseColumns = [...baseColumns, ...customCols];
    }

    // Add "Worked Together At" column when connections filter is active
    if (selectedConnectionsFilter && activeDataType === 'candidates') {
      const workedNameIdx = baseColumns.findIndex(col => col.key === 'name');
      const workedTogetherCol = {
        key: 'worked_together_at',
        label: 'Worked Together At',
        type: 'text' as const,
        isPrimary: true,
        isConnectionColumn: true
      };
      const positionAtCompanyCol = {
        key: 'position_at_company',
        label: 'Position at Company',
        type: 'text' as const,
        isPrimary: true,
        isConnectionColumn: true
      };
      baseColumns.splice(workedNameIdx + 1, 0, workedTogetherCol, positionAtCompanyCol);
    }

    return baseColumns; // custom cols already included
  }

  // Add "Worked Together At" column when connections filter is active (for candidates)
  if (selectedConnectionsFilter && activeDataType === 'candidates') {
    // Insert after 'name' column
    const nameIndex = baseColumns.findIndex(col => col.key === 'name');
    const workedTogetherCol = {
      key: 'worked_together_at',
      label: 'Worked Together At',
      type: 'text' as const,
      isPrimary: true,
      isConnectionColumn: true
    };
    const positionAtCompanyCol = {
      key: 'position_at_company',
      label: 'Position at Company',
      type: 'text' as const,
      isPrimary: true,
      isConnectionColumn: true
    };
    const columnsWithConnectionCol = [
      ...baseColumns.slice(0, nameIndex + 1),
      workedTogetherCol,
      positionAtCompanyCol,
      ...baseColumns.slice(nameIndex + 1)
    ];
    return [...columnsWithConnectionCol, ...customCols];
  }

  return [...baseColumns, ...customCols];
};

// Refs that scope the visibility init to a (project, dataType) pair. The
// effect below uses these to distinguish "first time on this project/tab"
// (set defaults / load saved) from "data refreshed" (additive only — never
// remove a column the user has chosen to show, never re-add a column they
// have explicitly hidden).
const lastVisibilityProjectRef = useRef<number | null | undefined>(undefined);
const lastVisibilityDataTypeRef = useRef<typeof activeDataType | undefined>(undefined);
const lastConnectionsFilterRef = useRef<typeof selectedConnectionsFilter>(null);

const visibilityStorageKey = (projectId: number | undefined, dataType: typeof activeDataType) =>
  projectId ? `columnVisibility_${projectId}_${dataType}_v1` : null;

const loadSavedVisibility = (projectId: number | undefined, dataType: typeof activeDataType): string[] | null => {
  const key = visibilityStorageKey(projectId, dataType);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

useEffect(() => {
  const columns = getCurrentColumns();
  const allData: any[] = activeDataType === 'companies' ? companies : activeDataType === 'candidates' ? candidates : hiringManagers;

  // Type assertion to handle union type
  const typedColumns = columns as Array<{
    key: string;
    label: string;
    type: string;
    isPrimary?: boolean;
    isLocked?: boolean;
    hiddenByDefault?: boolean;
  }>;

  // Default visibility: locked + primary by default, plus any other column
  // that has data. Used for first-load defaults and to determine which
  // brand-new columns should auto-appear when they show up later.
  const computeDefaults = () => typedColumns.filter(col => {
    if ((col as any).hiddenByDefault) return false;
    if (col.isLocked) return true;
    if (col.isPrimary) return true;
    // Campaign Leads List: always surface custom columns (project + campaign),
    // even when the members' cells are still empty — users expect the columns
    // they created to "come with" the people they add to a campaign. Ada
    // scoring columns (grouped) keep the has-data rule to avoid clutter.
    if (campaignScopeId && (col as any).isCustom && !(col as any).groupKey) return true;
    if ((col.key === 'current_contract_type' || col.key === 'employment_type') &&
        aiFilterState.employmentTypes.length > 0) {
      return true;
    }
    return allData.some((item: any) => {
      const value = (col as any).isCustom
        ? item.custom_data?.[col.key]
        : item[col.key];
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined && value !== '';
    });
  });

  const columnsWithData = computeDefaults();

  // Insert worked_together_at column right after connection_count when connections filter is active
  // Also position current_company and current_position right after worked_together_at
  let finalColumns = columnsWithData.map(c => c.key);
  if (selectedConnectionsFilter && activeDataType === 'candidates') {
    // Remove connection columns from wherever they currently sit
    finalColumns = finalColumns.filter(key => key !== 'worked_together_at' && key !== 'position_at_company');
    // Insert them right after connection_count
    const connectionIndex = finalColumns.indexOf('connection_count');
    if (connectionIndex !== -1) {
      finalColumns.splice(connectionIndex + 1, 0, 'worked_together_at', 'position_at_company');
    }

    // Position current_company and current_position right after position_at_company
    const posAtCompanyIndex = finalColumns.indexOf('position_at_company');
    if (posAtCompanyIndex !== -1) {
      finalColumns = finalColumns.filter(key => key !== 'current_company' && key !== 'current_position');
      finalColumns.splice(posAtCompanyIndex + 1, 0, 'current_company', 'current_position');
    }
  } else {
    // Remove connection columns if connections filter is cleared
    finalColumns = finalColumns.filter(key => key !== 'worked_together_at' && key !== 'position_at_company');
  }

  // Decide whether this is a first-load (project / dataType changed) or just
  // a data refresh. On first load: load saved visibility or fall back to
  // defaults. On refresh: only ADD new columns that didn't exist before, and
  // adjust connection-special columns based on filter toggle. Never remove a
  // column the user has explicitly toggled on, never re-add one they hid.
  const projectId = selectedProject?.id;
  const isInitOrSwitch =
    lastVisibilityProjectRef.current !== projectId ||
    lastVisibilityDataTypeRef.current !== activeDataType;
  const connectionsFilterChanged =
    lastConnectionsFilterRef.current !== selectedConnectionsFilter;

  if (isInitOrSwitch) {
    lastVisibilityProjectRef.current = projectId;
    lastVisibilityDataTypeRef.current = activeDataType;
    lastConnectionsFilterRef.current = selectedConnectionsFilter;

    const saved = loadSavedVisibility(projectId, activeDataType);
    if (saved && saved.length > 0) {
      // Trust saved order, but ensure any locked column is present and add
      // brand-new columns (e.g. custom columns added since last visit).
      const lockedKeys = typedColumns.filter(c => c.isLocked).map(c => c.key);
      const knownKeys = new Set(typedColumns.map(c => c.key));
      const filteredSaved = saved.filter(k => knownKeys.has(k));
      const newKeys = finalColumns.filter(k => !filteredSaved.includes(k));
      const merged = [
        ...lockedKeys.filter(k => !filteredSaved.includes(k)),
        ...filteredSaved,
        ...newKeys.filter(k => !lockedKeys.includes(k))
      ];
      // Also remove the connection-special columns when the filter is off,
      // since they're meaningless without it.
      const noConnections = !selectedConnectionsFilter || activeDataType !== 'candidates';
      const cleaned = noConnections
        ? merged.filter(k => k !== 'worked_together_at' && k !== 'position_at_company')
        : merged;
      setVisibleColumns(cleaned);
    } else {
      setVisibleColumns(finalColumns);
    }
  } else if (connectionsFilterChanged) {
    // Connections filter toggled — surgically add/remove the two columns
    // that depend on it without touching the user's other choices.
    lastConnectionsFilterRef.current = selectedConnectionsFilter;
    setVisibleColumns(prev => {
      const filterActive = !!selectedConnectionsFilter && activeDataType === 'candidates';
      let next = prev.filter(k => k !== 'worked_together_at' && k !== 'position_at_company');
      if (filterActive) {
        let idx = next.indexOf('connection_count');
        if (idx === -1) idx = next.indexOf('name');
        if (idx !== -1) next.splice(idx + 1, 0, 'worked_together_at', 'position_at_company');
        else next.unshift('worked_together_at', 'position_at_company');
      }
      return next;
    });
  } else {
    // Same project/tab, same connections filter — just add new columns.
    setVisibleColumns(prev => {
      if (prev.length === 0) return finalColumns;
      const newKeys = finalColumns.filter(k => !prev.includes(k));
      if (newKeys.length === 0) return prev;
      return [...prev, ...newKeys];
    });
  }
}, [activeDataType, companies, candidates, hiringManagers, selectedConnectionsFilter, selectedProject?.id]);

// Persist visibility to localStorage so the user's choice survives reloads.
useEffect(() => {
  const projectId = selectedProject?.id;
  if (!projectId) return;
  if (lastVisibilityProjectRef.current !== projectId || lastVisibilityDataTypeRef.current !== activeDataType) return;
  if (visibleColumns.length === 0) return;
  const key = visibilityStorageKey(projectId, activeDataType);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(visibleColumns));
  } catch {
    // localStorage may be full or unavailable — non-fatal
  }
}, [visibleColumns, selectedProject?.id, activeDataType]);

// Helper function to fetch paginated data
  const fetchPaginatedData = async (
    dataType: 'companies' | 'candidates' | 'managers',
    page: number,
    pageSize: number = 250,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    filterGroups?: any[],
    employmentTypes?: string[],
    companyNames?: string[], // For connections filter - filter companies by name
    candidateIds?: number[], // For connections filter - filter companies by candidate IDs (more reliable)
    excludeCompany?: string, // Exclude a specific company (e.g., selected candidate's company)
    radiusParams?: {
      lat: number; lng: number; distance: number; type?: string;  // Legacy single filter
      // New dual filter support
      companyLat?: number; companyLng?: number; companyDistance?: number;
      candidateLat?: number; candidateLng?: number; candidateDistance?: number;
      // Direct manager radius (managers tab only, no cross-filter)
      managerLat?: number; managerLng?: number; managerDistance?: number;
    }, // For radius search
    search?: string, // Quick text search across multiple fields
    crossFilterCompanies?: string[], // Cross-filter candidates by company names (when search from Companies tab)
    crossFilterOrgs?: string[], // Cross-filter managers by org names (when search from Companies/Candidates tab)
    associatedCompanyName?: string, // Filter candidates by company association (uses junction table, current employees only)
    startRowOverride?: number // Row-range control: absolute start row; falls back to startRow state
  ) => {
    const startRowAnchor = Math.max(0, startRowOverride !== undefined ? startRowOverride : startRow);
    const endpoint = dataType === 'companies'
      ? 'companies'
      : dataType === 'candidates'
        ? 'candidates'
        : 'hiring-managers';

    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(startRowAnchor > 0 && { startRow: startRowAnchor.toString() }),
      ...(sortBy && { sortBy }),
      ...(sortOrder && { sortOrder }),
      ...(search && { search }),
      // Job Spec Match: lets the server sort by the spec's stored composite
      // when sortBy === '_matchScore' (cross-page ranking).
      ...(dataType === 'candidates' && jevMatchRef.current && { matchSpecId: String(jevMatchRef.current.specId) })
    });

    // For managers: any `org equals "<company>"` condition emitted by Ada
    // (or anywhere else) is semantically "managers at this company". Route
    // it through the junction-based associatedCompanyName param instead of
    // an org-text equality, so it bridges cases like HM org "Gomboc.ai"
    // vs company "Gomboc AI" — same matcher as the company → HM
    // click-through. Mutates a local copy; the caller's filterGroups stays
    // untouched so the filter chip UI still shows what Ada produced.
    let adaAssociatedCompany: string | null = null;
    let workingFilterGroups = filterGroups;
    if (dataType === 'managers' && filterGroups && filterGroups.length > 0) {
      workingFilterGroups = filterGroups
        .map(g => {
          if (g.entityType !== 'manager') return g;
          const kept = (g.conditions || []).filter((c: any) => {
            const isOrgEquals = c.column === 'org' && c.operator === 'equals' && typeof c.value === 'string' && c.value.trim();
            if (isOrgEquals && !adaAssociatedCompany) adaAssociatedCompany = c.value.trim();
            return !isOrgEquals;
          });
          return { ...g, conditions: kept };
        })
        .filter(g => (g.conditions || []).length > 0);
    }

    // Add filter groups if provided
    if (workingFilterGroups && workingFilterGroups.length > 0) {
      // For companies and candidates, send ALL filter groups to enable cross-filtering
      // The backend will separate them and apply cross-filtering as needed
      if (dataType === 'companies' || dataType === 'candidates') {
        params.append('filterGroups', JSON.stringify(workingFilterGroups));
      } else {
        // For managers: send manager-specific filter groups directly
        const managerGroups = workingFilterGroups.filter(g => g.entityType === 'manager');
        if (managerGroups.length > 0) {
          params.append('filterGroups', JSON.stringify(managerGroups));
        }
        // For managers: when candidate filters are active, pass ALL filter
        // groups so the backend can cross-filter (find matching candidates →
        // get their companies → filter managers by those company orgs).
        const candidateGroups = workingFilterGroups.filter(g => g.entityType === 'candidate' || !g.entityType);
        if (candidateGroups.length > 0) {
          params.append('candidateFilterGroups', JSON.stringify(candidateGroups));
        }
        // For managers: when a COMPANY filter is active (e.g. employees < 20),
        // pass it so the backend restricts managers to the matching companies'
        // orgs. Without this the company filter was silently dropped and the
        // managers table showed everyone despite a filtered tab count.
        const companyGroups = workingFilterGroups.filter(g => g.entityType === 'company');
        if (companyGroups.length > 0) {
          params.append('companyFilterGroups', JSON.stringify(companyGroups));
        }
      }
    }

    // Add employment type filter for candidates AND companies (for cross-filtering)
    if ((dataType === 'candidates' || dataType === 'companies') && employmentTypes && employmentTypes.length > 0) {
      params.append('employmentTypes', employmentTypes.join(','));
    }

    // Add company names filter (for connections filter)
    if (dataType === 'companies' && companyNames && companyNames.length > 0) {
      params.append('companyNames', companyNames.join(','));
    }

    // Add candidate IDs filter (for connections filter - more reliable than name matching)
    if (dataType === 'companies' && candidateIds && candidateIds.length > 0) {
      params.append('candidateIds', candidateIds.join(','));
    }

    // Add exclude company filter
    if (dataType === 'companies' && excludeCompany) {
      params.append('excludeCompany', excludeCompany);
    }

    // Add radius filter params for all data types (cross-filtering)
    // Skip this entire block when manager radius is active — managerRadius*
    // is the only filter we want to send (it applies directly on
    // hiring_managers.latitude/longitude with no cross-filter), and the
    // legacy radiusLat/Lng/Distance fields would otherwise be interpreted
    // as a company-office radius and exclude managers at companies whose
    // offices aren't near the requested point.
    const isManagerRadiusOnly = dataType === 'managers' && radiusParams?.managerLat !== undefined;
    if (radiusParams && !isManagerRadiusOnly) {
      // Check for new dual filter params first
      if (radiusParams.companyLat !== undefined || radiusParams.candidateLat !== undefined) {
        // Use new dual-filter format
        if (radiusParams.companyLat !== undefined && radiusParams.companyLng !== undefined && radiusParams.companyDistance !== undefined) {
          params.append('companyRadiusLat', radiusParams.companyLat.toString());
          params.append('companyRadiusLng', radiusParams.companyLng.toString());
          params.append('companyRadiusDistance', radiusParams.companyDistance.toString());
        }
        if (radiusParams.candidateLat !== undefined && radiusParams.candidateLng !== undefined && radiusParams.candidateDistance !== undefined) {
          params.append('candidateRadiusLat', radiusParams.candidateLat.toString());
          params.append('candidateRadiusLng', radiusParams.candidateLng.toString());
          params.append('candidateRadiusDistance', radiusParams.candidateDistance.toString());
        }
      } else {
        // Legacy single-filter format
        params.append('radiusLat', radiusParams.lat.toString());
        params.append('radiusLng', radiusParams.lng.toString());
        params.append('radiusDistance', radiusParams.distance.toString());
        if (radiusParams.type) {
          params.append('radiusType', radiusParams.type);
        }
      }
    }

    // Direct manager-location radius filter — managers tab only.
    if (
      dataType === 'managers' &&
      radiusParams?.managerLat !== undefined &&
      radiusParams?.managerLng !== undefined &&
      radiusParams?.managerDistance !== undefined
    ) {
      params.append('managerRadiusLat', radiusParams.managerLat.toString());
      params.append('managerRadiusLng', radiusParams.managerLng.toString());
      params.append('managerRadiusDistance', radiusParams.managerDistance.toString());
    }

    // Add cross-filter by company names for candidates
    if (dataType === 'candidates' && crossFilterCompanies && crossFilterCompanies.length > 0) {
      params.append('crossFilterCompanies', crossFilterCompanies.join(','));
    }

    // Add cross-filter by org names for managers
    if (dataType === 'managers' && crossFilterOrgs && crossFilterOrgs.length > 0) {
      params.append('crossFilterOrgs', crossFilterOrgs.join(','));
    }

    // Add associated company name filter (uses junction table for full position history matching)
    // Use explicit parameter if provided, otherwise fall back to component
    // state, otherwise to whatever we extracted from an Ada-generated
    // `org equals` manager filter (managers tab only — adaAssociatedCompany
    // is null for other tabs).
    const effectiveAssocCompany = associatedCompanyName || associatedCompanyFilter || adaAssociatedCompany;
    if (dataType === 'candidates' && effectiveAssocCompany) {
      params.append('associatedCompanyName', effectiveAssocCompany);
    }
    // Managers: same junction-table association filter (company -> HM
    // click-through). Resolves via hiring_manager_company_associations so
    // it catches HMs linked by LinkedIn ID / normalized name, not just
    // exact org text (e.g. org "Gomboc.ai" under company "Gomboc AI").
    if (dataType === 'managers' && effectiveAssocCompany) {
      params.append('associatedCompanyName', effectiveAssocCompany);
    }

    // POST with the params in the body rather than GET with a query string:
    // AI-search filterGroups can be tens of KB once expansions are included,
    // which blows past nginx's request-URI limit (414) as a query string.
    // Scope every entity fetch to the campaign's members when embedded as a
    // campaign Leads List.
    if (campaignScopeId) params.append('campaignId', String(campaignScopeId));
    const requestBody = paramsToObject(params);
    // Remember the current tab's filter body so "select all matching" reuses it
    // (only for the active tab, so a background cross-tab fetch can't overwrite
    // it). Restrict to page === 1: filter/search/initial loads all fetch page 1
    // and carry the full context, whereas the background page-2 PREFETCH calls
    // fetchPaginatedData WITHOUT the search term — letting it write the ref
    // dropped `search`, so "select all" reselected the whole filtered set
    // (e.g. 12,812) instead of the search-narrowed set (3,397).
    if (dataType === activeDataType && page === 1) lastMainFetchBodyRef.current = requestBody;
    const response = await fetch(
      `/api/${endpoint}/${selectedProject.id}/paginated`,
      {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) throw new Error(`Failed to fetch ${dataType}`);
    return response.json();
  };

  // Convert URLSearchParams to a plain object for POST bodies. None of our
  // param sets use repeated keys, so a flat object is lossless.
  function paramsToObject(params: URLSearchParams): Record<string, string> {
    const obj: Record<string, string> = {};
    params.forEach((value, key) => { obj[key] = value; });
    return obj;
  }

  // Prefetch next page in background
  const prefetchNextPage = async (dataType: 'companies' | 'candidates' | 'managers', nextPage: number, filterGroups?: any[], employmentTypes?: string[], radiusParams?: {
    lat: number; lng: number; distance: number; type?: string;
    companyLat?: number; companyLng?: number; companyDistance?: number;
    candidateLat?: number; candidateLng?: number; candidateDistance?: number;
    managerLat?: number; managerLng?: number; managerDistance?: number;
  }, sortBy?: string, sortOrder?: 'asc' | 'desc') => {
    try {
      const effectiveSortBy = sortBy || sortConfig.column;
      const effectiveSortOrder = sortOrder || sortConfig.direction;
      // Carry the active quick-search into the prefetch for a DIRECT search on
      // this tab (searchOriginTab is unset or equals this tab). Without it, the
      // prefetched next page held filter-only rows and navigating there dropped
      // the search narrowing. Cross-tab search uses crossFilterCompanies (not a
      // candidate-field search), so we deliberately omit it there.
      const prefetchSearch = (searchTerm && searchTerm.trim() && (!searchOriginTab || searchOriginTab === dataType))
        ? searchTerm.trim()
        : undefined;
      const result = await fetchPaginatedData(dataType, nextPage, rowLimit, effectiveSortBy, effectiveSortOrder, filterGroups, employmentTypes, undefined, undefined, undefined, radiusParams, prefetchSearch);
      // Use composite key including sort to prevent stale prefetch data
      const cacheKey = `${dataType}-${nextPage}-${effectiveSortBy}-${effectiveSortOrder}`;
      setPrefetchedData(prev => new Map(prev).set(cacheKey, result.data));
    } catch (error) {
      console.log('Prefetch failed (non-critical):', error);
    }
  };

// Always use server-side filtering for consistent behavior across all projects
const LARGE_DATASET_THRESHOLD = 0;

// Initial data fetch - loads ALL data upfront for reliable filtering/sorting
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      // Reset pagination tracking for new project
      lastLoadedPageRef.current = 0;
      try {
        const timestamp = new Date().getTime();

        // First, check project stats to determine if we need server-side mode
        const statsResponse = await fetch(`/api/projects/${selectedProject.id}`, {
          headers: getAuthHeaders(),
          cache: 'no-store'
        });

        let useServerSideMode = false;
        let totalCandidates = 0;
        let totalCompanies = 0;
        let totalManagers = 0;

        if (statsResponse.ok) {
          const projectData = await statsResponse.json();
          totalCandidates = projectData.stats?.candidates || 0;
          totalCompanies = projectData.stats?.companies || 0;
          totalManagers = projectData.stats?.hiring_managers || 0;

          // Enable server-side mode for large datasets (check both candidates AND companies)
          useServerSideMode = totalCandidates >= LARGE_DATASET_THRESHOLD || totalCompanies >= LARGE_DATASET_THRESHOLD;
        }

        // Campaign Leads List: the tab counts must reflect this campaign's
        // membership, not the whole project. Override the totals with the
        // campaign-scoped counts and always use the (campaignId-scoped)
        // server-side path so we never load the full project.
        if (campaignScopeId) {
          try {
            const cRes = await fetch(`/api/projects/${selectedProject.id}/filtered-counts`, {
              method: 'POST',
              headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
              cache: 'no-store',
              body: JSON.stringify({ campaignId: campaignScopeId }),
            });
            if (cRes.ok) {
              const cc = await cRes.json();
              totalCandidates = cc.candidates ?? 0;
              totalCompanies = cc.companies ?? 0;
              totalManagers = cc.managers ?? 0;
            }
          } catch { /* fall back to project stats */ }
          useServerSideMode = true;

          if (useServerSideMode) {
            console.log(`📊 Large dataset detected (${totalCandidates} candidates, ${totalCompanies} companies) - using server-side filtering`);
          }
        }

        if (useServerSideMode) {
          // SERVER-SIDE MODE: Load first page only, filter on server
          setServerPagination({
            enabled: true,
            total: totalCandidates,
            totalPages: Math.ceil(totalCandidates / rowLimit),
            hasNext: totalCandidates > rowLimit,
            hasPrev: false
          });

          // Initialize server totals (will be updated with unique counts from paginated responses)
          setServerTotals({
            companies: totalCompanies,  // Will be updated with uniqueTotal below
            candidates: totalCandidates,
            managers: totalManagers
          });

          // Initialize original project totals (these never change until project changes)
          // Used for "from X" display to show what counts are being filtered from
          setOriginalProjectTotals({
            companies: totalCompanies,  // Will be updated with uniqueTotal below
            candidates: totalCandidates,
            managers: totalManagers
          });

          // Fetch first page of companies
          try {
            const companiesResult = await fetchPaginatedData('companies', 1, rowLimit, 'candidate_count', 'desc');
            setFullCompanies(companiesResult.data);
            setCompanies(companiesResult.data);

            // Update companies count with unique total from paginated response
            if (companiesResult.pagination?.uniqueTotal !== undefined) {
              setServerTotals(prev => ({
                ...prev,
                companies: companiesResult.pagination.uniqueTotal
              }));
              // Also update original totals with unique count
              setOriginalProjectTotals(prev => ({
                ...prev,
                companies: companiesResult.pagination.uniqueTotal
              }));
            }
          } catch (err) {
            console.error('Failed to fetch companies:', err);
          }

          // Fetch first page of candidates
          try {
            // Same order the sort control shows (defaultSortFor) — loading by
            // name here while the control said "date added" made header
            // clicks look dead and page 2 come in a different order.
            const candSort = defaultSortFor('candidates', false);
            const candidatesResult = await fetchPaginatedData('candidates', 1, rowLimit, candSort.column, candSort.direction);
            setFullCandidates(candidatesResult.data);
            setCandidates(candidatesResult.data);
            lastLoadedPageRef.current = 1; // Mark page 1 as loaded
            console.log(`✅ Loaded first page of candidates (${candidatesResult.data.length} of ${totalCandidates})`);

            // Prefetch page 2 in background
            if (candidatesResult.pagination.hasNext) {
              prefetchNextPage('candidates', 2, [], undefined, undefined, candSort.column, candSort.direction);
            }
          } catch (err) {
            console.error('Failed to fetch candidates:', err);
          }

          // Fetch first page of managers
          try {
            const managersResult = await fetchPaginatedData('managers', 1, rowLimit, 'name', 'asc');
            setFullHiringManagers(managersResult.data);
            setHiringManagers(managersResult.data);
            const starredIds = new Set<number>(
              managersResult.data.filter((m: any) => m.is_starred).map((m: any) => m.id)
            );
            setStarredManagerIds(starredIds);
          } catch (err) {
            console.error('Failed to fetch managers:', err);
          }

        } else {
          // CLIENT-SIDE MODE: Load all data (existing behavior for smaller datasets)
          console.log(`📊 Standard dataset (${totalCandidates} candidates) - loading all data for client-side filtering`);

          // Ensure server pagination is disabled
          setServerPagination({ enabled: false, total: 0, totalPages: 0, hasNext: false, hasPrev: false });

          // Fetch all companies
          const companiesResponse = await fetch(`/api/companies/${selectedProject.id}?_=${timestamp}`, {
            headers: getAuthHeaders(),
            cache: 'no-store'
          });

          if (companiesResponse.ok) {
            const companiesData = await companiesResponse.json();
            setFullCompanies(companiesData);
            setCompanies(companiesData);
          }

          // Fetch all candidates in batches (handles large datasets)
          const batchSize = 2000;
          let allCandidates: Candidate[] = [];
          let offset = 0;
          let hasMore = true;

          console.log('Loading candidates...');

          while (hasMore) {
            const candidatesResponse = await fetch(
              `/api/candidates/${selectedProject.id}?limit=${batchSize}&offset=${offset}&_=${timestamp}`,
              {
                headers: getAuthHeaders(),
                cache: 'no-store'
              }
            );

            if (candidatesResponse.ok) {
              const batch = await candidatesResponse.json();
              allCandidates = [...allCandidates, ...batch];

              if (batch.length < batchSize) {
                hasMore = false;
              } else {
                offset += batchSize;
              }
            } else {
              console.error('Failed to fetch candidate batch');
              hasMore = false;
            }
          }

          setFullCandidates(allCandidates);
          setCandidates(allCandidates);
          console.log(`✅ Total candidates loaded: ${allCandidates.length}`);

          // Fetch all managers
          const managersResponse = await fetch(`/api/hiring-managers/${selectedProject.id}?_=${timestamp}`, {
            headers: getAuthHeaders(),
            cache: 'no-store'
          });

          if (managersResponse.ok) {
            const managersData = await managersResponse.json();
            setFullHiringManagers(managersData);
            setHiringManagers(managersData);

            // Initialize starred managers Set
            const starredIds = new Set<number>(
              managersData.filter((m: any) => m.is_starred).map((m: any) => m.id)
            );
            setStarredManagerIds(starredIds);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (selectedProject?.id) {
      fetchData();
    }
  }, [selectedProject?.id, refreshTrigger]);

  // Live polling: silently refresh data when Ada is working (scoring in progress)
  useEffect(() => {
    if (!adaWorking || !selectedProject?.id) return;

    const pollInterval = setInterval(async () => {
      try {
        const sortColumn = sortConfig.column || (activeDataType === 'companies' ? 'candidate_count' : 'name');
        if (serverPagination.enabled) {
          const result = await fetchPaginatedData(
            activeDataType, currentPage, rowLimit, sortColumn, sortConfig.direction,
            effectiveFilterGroups, aiFilterState.employmentTypes
          );
          if (result) {
            if (activeDataType === 'candidates') setCandidates(result.data);
            else if (activeDataType === 'companies') setCompanies(result.data);
            else setHiringManagers(result.data);
          }
        }
        // Also refresh custom columns in case Ada created new ones
        const colRes = await fetch(`/api/custom-columns/${selectedProject.id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (colRes.ok) {
          const cols = await colRes.json();
          setCustomColumns(cols);
        }
        if (campaignScopeId) {
          const campRes = await fetch(`/api/campaigns/${campaignScopeId}/columns`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (campRes.ok) setCampaignColumns(await campRes.json());
        }
      } catch (err) {
        // Silent fail - don't interrupt user
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(pollInterval);
  }, [adaWorking, selectedProject?.id, activeDataType, currentPage, rowLimit, sortConfig, serverPagination.enabled]);

  // Handle server-side page changes
  useEffect(() => {
    if (!serverPagination.enabled || !selectedProject?.id) return;

    // Skip if AI radius search is in progress - it will handle the fetches
    if (aiRadiusSearchInProgressRef.current) {
      console.log('📄 Skipping page fetch - AI radius search in progress');
      return;
    }

    // Skip page 1 ONLY on initial load (when lastLoadedPageRef is 0)
    // If lastLoadedPageRef > 0, user has navigated and is coming back to page 1
    const isInitialPage1 = currentPage === 1 && lastLoadedPageRef.current === 0;

    const handleServerPageChange = async () => {
      // Check if we have prefetched data for this page, data type, AND current sort
      const cacheKey = `${activeDataType}-${currentPage}-${sortConfig.column}-${sortConfig.direction}`;
      const prefetched = prefetchedData.get(cacheKey);

      if (prefetched) {
        console.log(`📄 Using prefetched data for ${activeDataType} page ${currentPage}`);
        // Track this page as loaded
        lastLoadedPageRef.current = currentPage;

        // Use prefetched data
        if (activeDataType === 'companies') {
          setCompanies(prefetched);
        } else if (activeDataType === 'candidates') {
          setCandidates(prefetched);
        } else {
          setHiringManagers(prefetched);
          setStarredManagerIds(new Set(prefetched.filter((m: any) => m.is_starred).map((m: any) => m.id)));
        }

        // Remove used prefetch and prefetch next page
        setPrefetchedData(prev => {
          const newMap = new Map(prev);
          newMap.delete(cacheKey);
          return newMap;
        });

        if (currentPage < serverPagination.totalPages) {
          // Include radius params for candidates and companies prefetch
          // Check for BOTH filters being active and use dual-param format if so
          const hasBothFilters = companyOfficeRadius.coords && candidateLocationRadius.coords;
          const hasAnyFilter = (activeDataType === 'candidates' || activeDataType === 'companies' || activeDataType === 'managers') && radiusCoords && radiusDistance > 0;
          const hasManagerFilter = activeDataType === 'managers' && managerLocationRadius.coords;

          let prefetchRadiusParams;
          if (hasManagerFilter) {
            prefetchRadiusParams = {
              lat: managerLocationRadius.coords!.lat, lng: managerLocationRadius.coords!.lng, distance: managerLocationRadius.distance,
              managerLat: managerLocationRadius.coords!.lat,
              managerLng: managerLocationRadius.coords!.lng,
              managerDistance: managerLocationRadius.distance
            };
          } else if (hasBothFilters) {
            prefetchRadiusParams = {
              lat: radiusCoords!.lat, lng: radiusCoords!.lng, distance: radiusDistance,
              companyLat: companyOfficeRadius.coords!.lat,
              companyLng: companyOfficeRadius.coords!.lng,
              companyDistance: companyOfficeRadius.distance,
              candidateLat: candidateLocationRadius.coords!.lat,
              candidateLng: candidateLocationRadius.coords!.lng,
              candidateDistance: candidateLocationRadius.distance
            };
          } else if (hasAnyFilter) {
            prefetchRadiusParams = { lat: radiusCoords!.lat, lng: radiusCoords!.lng, distance: radiusDistance, type: radiusType };
          }

          prefetchNextPage(activeDataType, currentPage + 1, effectiveFilterGroups, aiFilterState.employmentTypes, prefetchRadiusParams);
        }
      } else if (!isInitialPage1) {
        // Fetch from server for any page that doesn't have prefetched data
        // BUT skip if connections filter is active - we already have the data
        if (selectedConnectionsFilter && connectedCandidateIds.length > 0) {
          if (activeDataType === 'candidates') {
            console.log('📄 Skipping fetch - using connected candidates from connections filter');
            return;
          }
          if (activeDataType === 'companies') {
            console.log('📄 Skipping fetch - using connected companies from connections filter');
            // Companies are fetched via handleDataTypeChange with proper candidateIds/excludeCompany
            return;
          }
        }

        console.log(`📄 Fetching page ${currentPage} from server`);
        setPageLoading(true);
        try {
          const sortColumn = sortConfig.column || (activeDataType === 'companies' ? 'candidate_count' : 'name');
          // Include radius params for candidates and companies if radius filter is active
          // Check for BOTH filters being active and use dual-param format if so
          const hasBothFilters = companyOfficeRadius.coords && candidateLocationRadius.coords;
          const hasAnyFilter = (activeDataType === 'candidates' || activeDataType === 'companies' || activeDataType === 'managers') && radiusCoords && radiusDistance > 0;
          const hasManagerFilter = activeDataType === 'managers' && managerLocationRadius.coords;

          let radiusParams;
          if (hasManagerFilter) {
            radiusParams = {
              lat: managerLocationRadius.coords!.lat, lng: managerLocationRadius.coords!.lng, distance: managerLocationRadius.distance,
              managerLat: managerLocationRadius.coords!.lat,
              managerLng: managerLocationRadius.coords!.lng,
              managerDistance: managerLocationRadius.distance
            };
            console.log('📄 Using manager radius params for pagination:', radiusParams);
          } else if (hasBothFilters) {
            // Dual filter mode
            radiusParams = {
              lat: radiusCoords!.lat, lng: radiusCoords!.lng, distance: radiusDistance,
              companyLat: companyOfficeRadius.coords!.lat,
              companyLng: companyOfficeRadius.coords!.lng,
              companyDistance: companyOfficeRadius.distance,
              candidateLat: candidateLocationRadius.coords!.lat,
              candidateLng: candidateLocationRadius.coords!.lng,
              candidateDistance: candidateLocationRadius.distance
            };
            console.log('📄 Using dual filter params for pagination:', radiusParams);
          } else if (hasAnyFilter) {
            // Single filter mode - use legacy format
            radiusParams = { lat: radiusCoords!.lat, lng: radiusCoords!.lng, distance: radiusDistance, type: radiusType };
          }

          const result = await fetchPaginatedData(activeDataType, currentPage, rowLimit, sortColumn, sortConfig.direction, effectiveFilterGroups, aiFilterState.employmentTypes, undefined, undefined, undefined, radiusParams);

          if (activeDataType === 'companies') {
            setCompanies(result.data);
            // Update the company count to match what we actually fetched
            setServerTotals(prev => ({
              ...prev,
              companies: result.pagination.uniqueTotal || result.pagination.total
            }));
            // Update serverPagination to match fetched results
            setServerPagination(prev => ({
              ...prev,
              total: result.pagination.uniqueTotal || result.pagination.total,
              totalPages: result.pagination.totalPages,
              hasNext: result.pagination.hasNext,
              hasPrev: result.pagination.hasPrev
            }));
          } else if (activeDataType === 'candidates') {
            setCandidates(result.data);
            // Update serverPagination to match fetched results (important for dual filters)
            setServerPagination(prev => ({
              ...prev,
              total: result.pagination.total,
              totalPages: result.pagination.totalPages,
              hasNext: result.pagination.hasNext,
              hasPrev: result.pagination.hasPrev
            }));
          } else {
            setHiringManagers(result.data);
            setStarredManagerIds(new Set(result.data.filter((m: any) => m.is_starred).map((m: any) => m.id)));
            // Update serverPagination to match fetched results
            setServerPagination(prev => ({
              ...prev,
              total: result.pagination.total,
              totalPages: result.pagination.totalPages,
              hasNext: result.pagination.hasNext,
              hasPrev: result.pagination.hasPrev
            }));
          }

          // Track this page as loaded
          lastLoadedPageRef.current = currentPage;

          // Prefetch next page with filters and radius params
          if (result.pagination.hasNext) {
            prefetchNextPage(activeDataType, currentPage + 1, effectiveFilterGroups, aiFilterState.employmentTypes, radiusParams);
          }
        } catch (error) {
          console.error('Failed to fetch page:', error);
        } finally {
          setPageLoading(false);
        }
      }
    };

    handleServerPageChange();
  }, [currentPage, serverPagination.enabled, activeDataType, rowLimit, radiusCoords, radiusDistance, radiusType, companyOfficeRadius.coords, candidateLocationRadius.coords, managerLocationRadius.coords]);

  // When filters change, refetch data with server-side filtering
  // ONLY when server-side pagination is enabled
  useEffect(() => {
    // Skip if server-side pagination is disabled - client-side filtering handles this
    if (!serverPagination.enabled) return;

    // Check for any active filters including connections filter and radius search
    const hasFilters = aiFilterState.groups.length > 0 || aiFilterState.employmentTypes.length > 0;
    const hasRadiusSearch = radiusLocation !== '' && radiusLocation !== null;
    const hasConnectionsFilter = selectedConnectionsFilter !== null;
    const hasAnyFilter = hasFilters || hasRadiusSearch;

    // Detect if we're just switching tabs (activeDataType changed)
    // handleDataTypeChange already handles fetching with all filters including radius
    const prevDataType = prevFilterStateRef.current.activeDataType;
    const justSwitchedTabs = prevDataType !== activeDataType;

    // Update the ref for next time
    prevFilterStateRef.current = { hasFilters: hasAnyFilter, activeDataType };

    // Don't refetch if we're just switching tabs - handleDataTypeChange handles that
    if (justSwitchedTabs) {
      console.log('Just switched tabs - skipping filter useEffect refetch (handleDataTypeChange handles it)');
      return;
    }

    // Skip refetch ONLY if a NEW AI radius search is in progress (being set up)
    // This handles the case where filters are set BEFORE radius geocoding completes
    // We DO want to run when hasRadiusSearch is true but no new radius search is starting
    // (e.g., user has existing radius filter and adds a new AI filter without radius)
    if (aiRadiusSearchInProgressRef.current) {
      console.log('AI radius search in progress - skipping filter useEffect refetch (applyRadiusSearch handles it)');
      return;
    }

    if (selectedProject?.id && hasAnyFilter) {
      console.log('Filters applied - refetching with server-side filtering');

      const refetchWithFilters = async () => {
        setPageLoading(true);
        setPrefetchedData(new Map());
        setCurrentPage(1);

        try {
          // Fetch first page with filters for the active data type
          // Use effectiveFilterGroups to exclude preferred filters in job spec match mode
          const sortColumn = sortConfig.column || (activeDataType === 'companies' ? 'candidate_count' : 'name');

          // Build radius params if radius search is active
          const hasBothRadiusFilters = companyOfficeRadius.coords && candidateLocationRadius.coords;
          const hasManagerOnlyRadius = activeDataType === 'managers' && managerLocationRadius.coords;
          let filterRadiusParams;
          if (hasManagerOnlyRadius) {
            filterRadiusParams = {
              lat: managerLocationRadius.coords!.lat, lng: managerLocationRadius.coords!.lng, distance: managerLocationRadius.distance,
              managerLat: managerLocationRadius.coords!.lat,
              managerLng: managerLocationRadius.coords!.lng,
              managerDistance: managerLocationRadius.distance
            };
          } else if (hasBothRadiusFilters) {
            filterRadiusParams = {
              lat: radiusCoords!.lat, lng: radiusCoords!.lng, distance: radiusDistance,
              companyLat: companyOfficeRadius.coords!.lat,
              companyLng: companyOfficeRadius.coords!.lng,
              companyDistance: companyOfficeRadius.distance,
              candidateLat: candidateLocationRadius.coords!.lat,
              candidateLng: candidateLocationRadius.coords!.lng,
              candidateDistance: candidateLocationRadius.distance
            };
          } else if (radiusCoords && radiusDistance > 0) {
            filterRadiusParams = { lat: radiusCoords.lat, lng: radiusCoords.lng, distance: radiusDistance, type: radiusType };
          }

          // Kick off the cross-filtered tab counts request IN PARALLEL with
          // the data fetch — both run the same heavy filter SQL server-side
          // and awaiting them serially used to double the apply latency.
          const countsParams = new URLSearchParams();
          if (effectiveFilterGroups.length > 0) {
            countsParams.append('filterGroups', JSON.stringify(effectiveFilterGroups));
          }
          if (aiFilterState.employmentTypes.length > 0) {
            countsParams.append('employmentTypes', aiFilterState.employmentTypes.join(','));
          }
          // Add radius search parameters for cross-filtering
          if (radiusCoords && radiusDistance > 0) {
            countsParams.append('radiusLat', radiusCoords.lat.toString());
            countsParams.append('radiusLng', radiusCoords.lng.toString());
            countsParams.append('radiusDistance', radiusDistance.toString());
            countsParams.append('radiusType', radiusType);
          }
          // Use searchOriginTab if search is active, otherwise use activeDataType
          countsParams.append('activeTab', searchOriginTab || activeDataType);
          if (campaignScopeId) countsParams.append('campaignId', String(campaignScopeId));

          const countsPromise = fetch(
            `/api/projects/${selectedProject.id}/filtered-counts`,
            {
              method: 'POST',
              headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
              cache: 'no-store',
              body: JSON.stringify(paramsToObject(countsParams))
            }
          );

          const result = await fetchPaginatedData(
            activeDataType,
            1,
            rowLimit,
            sortColumn,
            sortConfig.direction,
            effectiveFilterGroups,
            aiFilterState.employmentTypes,
            undefined,
            undefined,
            undefined,
            filterRadiusParams
          );

          // Render rows as soon as the data arrives — don't wait for counts.
          if (activeDataType === 'companies') {
            setCompanies(result.data);
          } else if (activeDataType === 'candidates') {
            setCandidates(result.data);
          } else {
            setHiringManagers(result.data);
            setStarredManagerIds(new Set(result.data.filter((m: any) => m.is_starred).map((m: any) => m.id)));
          }

          setServerPagination({
            enabled: true,
            total: result.pagination.total,
            totalPages: result.pagination.totalPages,
            hasNext: result.pagination.hasNext,
            hasPrev: result.pagination.hasPrev
          });

          // Rows are visible — drop the loading state now rather than after
          // the (slower) counts request settles below.
          setPageLoading(false);

          // Prefetch page 2 with filters and radius params
          if (result.pagination.hasNext) {
            prefetchNextPage(activeDataType, 2, effectiveFilterGroups, aiFilterState.employmentTypes, filterRadiusParams);
          }

          // Tab counts land whenever the parallel request finishes.
          try {
            const countsResponse = await countsPromise;
            if (countsResponse.ok) {
              const counts = await countsResponse.json();
              setServerTotals({
                companies: counts.companies,
                candidates: counts.candidates,
                managers: counts.managers
              });
            }
          } catch (countsError) {
            console.error('Error fetching cross-filtered counts:', countsError);
            // Fallback to just updating active type
            setServerTotals(prev => ({
              ...prev,
              [activeDataType]: activeDataType === 'companies'
                ? (result.pagination.uniqueTotal || result.pagination.total)
                : result.pagination.total
            }));
          }
        } catch (error) {
          console.error('Error fetching filtered data:', error);
        } finally {
          setPageLoading(false);
        }
      };

      refetchWithFilters();
    } else if (selectedProject?.id && !hasAnyFilter && !hasConnectionsFilter && !justSwitchedTabs && !searchTerm) {
      // Filters were cleared - reset to original unfiltered data and counts
      // But don't reset if:
      // 1. Connections filter is still active
      // 2. We're just switching tabs (not actually clearing filters)
      console.log('Filters cleared - resetting to unfiltered data');

      const resetToUnfiltered = async () => {
        setPageLoading(true);
        setPrefetchedData(new Map());
        setCurrentPage(1);

        try {
          // Fetch first page without filters for the active data type
          const sortColumn = sortConfig.column || (activeDataType === 'companies' ? 'candidate_count' : 'name');
          const result = await fetchPaginatedData(
            activeDataType,
            1,
            rowLimit,
            sortColumn,
            sortConfig.direction,
            [], // No filter groups
            []  // No employment types
          );

          if (activeDataType === 'companies') {
            setCompanies(result.data);
            setFullCompanies(result.data);
          } else if (activeDataType === 'candidates') {
            setCandidates(result.data);
            setFullCandidates(result.data);
          } else {
            setHiringManagers(result.data);
            setFullHiringManagers(result.data);
            setStarredManagerIds(new Set(result.data.filter((m: any) => m.is_starred).map((m: any) => m.id)));
          }

          setServerPagination({
            enabled: true,
            total: result.pagination.total,
            totalPages: result.pagination.totalPages,
            hasNext: result.pagination.hasNext,
            hasPrev: result.pagination.hasPrev
          });

          // Reset counts to original totals (no cross-filtering).
          // In a campaign Leads List, "original totals" are the campaign's
          // member counts, so keep the campaignId scope here too.
          try {
            const countsResponse = await fetch(
              `/api/projects/${selectedProject.id}/filtered-counts${campaignScopeId ? `?campaignId=${campaignScopeId}` : ''}`,
              { headers: getAuthHeaders(), cache: 'no-store' }
            );

            if (countsResponse.ok) {
              const counts = await countsResponse.json();
              setServerTotals({
                companies: counts.companies,
                candidates: counts.candidates,
                managers: counts.managers
              });
            }
          } catch (countsError) {
            console.error('Error fetching reset counts:', countsError);
            // Fallback to using the active type's count
            setServerTotals(prev => ({
              ...prev,
              [activeDataType]: activeDataType === 'companies'
                ? (result.pagination.uniqueTotal || result.pagination.total)
                : result.pagination.total
            }));
          }

          // Prefetch page 2
          if (result.pagination.hasNext) {
            prefetchNextPage(activeDataType, 2, [], []);
          }
        } catch (error) {
          console.error('Error resetting to unfiltered data:', error);
        } finally {
          setPageLoading(false);
        }
      };

      resetToUnfiltered();
    }
  }, [effectiveFilterGroups, aiFilterState.employmentTypes, activeDataType, serverPagination.enabled, selectedConnectionsFilter, isJobSpecMatchMode]);

  // Handle quick search term changes
  useEffect(() => {
    if (!serverPagination.enabled || !selectedProject?.id) return;

    const hadPreviousSearch = prevSearchTermRef.current && prevSearchTermRef.current.trim().length > 0;
    const hasCurrentSearch = searchTerm && searchTerm.trim().length > 0;

    // Update the ref for next comparison
    prevSearchTermRef.current = searchTerm;

    // If search was cleared (had search before, now empty), reset to unfiltered totals
    if (!hasCurrentSearch && hadPreviousSearch) {
      console.log('🔍 Search cleared - resetting to unfiltered totals');

      const resetAfterSearchCleared = async () => {
        setPageLoading(true);
        setPrefetchedData(new Map());
        setCurrentPage(1);

        try {
          // Fetch first page without search for the active data type
          const sortColumn = sortConfig.column || (activeDataType === 'companies' ? 'candidate_count' : 'name');

          // Include radius params if radius search is still active
          const hasBothRadiusFilters = companyOfficeRadius.coords && candidateLocationRadius.coords;
          let searchClearRadiusParams;
          if (hasBothRadiusFilters) {
            searchClearRadiusParams = {
              lat: radiusCoords!.lat, lng: radiusCoords!.lng, distance: radiusDistance,
              companyLat: companyOfficeRadius.coords!.lat,
              companyLng: companyOfficeRadius.coords!.lng,
              companyDistance: companyOfficeRadius.distance,
              candidateLat: candidateLocationRadius.coords!.lat,
              candidateLng: candidateLocationRadius.coords!.lng,
              candidateDistance: candidateLocationRadius.distance
            };
          } else if (radiusCoords && radiusDistance > 0) {
            searchClearRadiusParams = { lat: radiusCoords.lat, lng: radiusCoords.lng, distance: radiusDistance, type: radiusType };
          }

          const result = await fetchPaginatedData(
            activeDataType,
            1,
            rowLimit,
            sortColumn,
            sortConfig.direction,
            aiFilterState.groups,
            aiFilterState.employmentTypes,
            undefined,
            undefined,
            undefined,
            searchClearRadiusParams
          );

          if (activeDataType === 'companies') {
            setCompanies(result.data);
          } else if (activeDataType === 'candidates') {
            setCandidates(result.data);
          } else {
            setHiringManagers(result.data);
          }

          setServerPagination({
            enabled: true,
            total: result.pagination.total,
            totalPages: result.pagination.totalPages,
            hasNext: result.pagination.hasNext,
            hasPrev: result.pagination.hasPrev
          });

          // Reset counts to original totals (respecting any active filters and radius, but not search)
          try {
            const countsParams = new URLSearchParams();
            if (effectiveFilterGroups.length > 0) {
              countsParams.append('filterGroups', JSON.stringify(effectiveFilterGroups));
            }
            if (aiFilterState.employmentTypes.length > 0) {
              countsParams.append('employmentTypes', aiFilterState.employmentTypes.join(','));
            }
            // Include radius params for accurate counts
            if (radiusCoords && radiusDistance > 0) {
              countsParams.append('radiusLat', radiusCoords.lat.toString());
              countsParams.append('radiusLng', radiusCoords.lng.toString());
              countsParams.append('radiusDistance', radiusDistance.toString());
              countsParams.append('radiusType', radiusType);
            }
            if (campaignScopeId) countsParams.append('campaignId', String(campaignScopeId));

            const countsResponse = await fetch(
              `/api/projects/${selectedProject.id}/filtered-counts`,
              {
                method: 'POST',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                cache: 'no-store',
                body: JSON.stringify(paramsToObject(countsParams))
              }
            );

            if (countsResponse.ok) {
              const counts = await countsResponse.json();
              setServerTotals({
                companies: counts.companies,
                candidates: counts.candidates,
                managers: counts.managers
              });
            }
          } catch (countsError) {
            console.error('Error fetching reset counts:', countsError);
          }

          // Prefetch page 2 with radius params
          if (result.pagination.hasNext) {
            prefetchNextPage(activeDataType, 2, effectiveFilterGroups, aiFilterState.employmentTypes, searchClearRadiusParams);
          }
        } catch (error) {
          console.error('Error resetting after search cleared:', error);
        } finally {
          setPageLoading(false);
        }
      };

      resetAfterSearchCleared();
      return;
    }

    // Don't run without search - let filter useEffect handle normal tab switches
    if (!hasCurrentSearch) return;

    const performSearch = async () => {
      const effectiveOrigin = searchOriginTab || activeDataType;
      const isCrossFiltering = effectiveOrigin !== activeDataType;

      console.log(`🔍 Searching for "${searchTerm}" (origin: ${effectiveOrigin}, current: ${activeDataType}, crossFilter: ${isCrossFiltering})`);
      setPageLoading(true);
      setPrefetchedData(new Map());
      setCurrentPage(1);

      try {
        const sortColumn = sortConfig.column || (activeDataType === 'companies' ? 'candidate_count' : 'name');
        let result;

        if (isCrossFiltering) {
          // Cross-filtering: First get matching company names from the origin tab, then filter current tab
          let crossFilterCompanyNames: string[] = [];

          if (effectiveOrigin === 'companies') {
            // Search companies → get company names → filter candidates/managers by those companies
            const companiesResult = await fetchPaginatedData(
              'companies', 1, 10000, 'company_name', 'asc',
              effectiveFilterGroups, aiFilterState.employmentTypes,
              undefined, undefined, undefined, undefined, searchTerm
            );
            crossFilterCompanyNames = companiesResult.data.map((c: any) => c.company_name).filter(Boolean);
            console.log(`Found ${crossFilterCompanyNames.length} companies matching "${searchTerm}"`);
          } else if (effectiveOrigin === 'candidates') {
            // Search candidates → get their company names → filter companies/managers by those companies
            const candidatesResult = await fetchPaginatedData(
              'candidates', 1, 10000, 'name', 'asc',
              effectiveFilterGroups, aiFilterState.employmentTypes,
              undefined, undefined, undefined, undefined, searchTerm
            );
            crossFilterCompanyNames = candidatesResult.data
              .map((c: any) => c.company || c.current_company)
              .filter(Boolean);
            // Deduplicate
            crossFilterCompanyNames = Array.from(new Set(crossFilterCompanyNames));
            console.log(`Found ${crossFilterCompanyNames.length} unique companies from matching candidates`);
          } else if (effectiveOrigin === 'managers') {
            // Search managers → get their org names → filter companies/candidates by those orgs
            const managersResult = await fetchPaginatedData(
              'managers', 1, 10000, 'name', 'asc',
              effectiveFilterGroups, [], // managers don't have employmentTypes
              undefined, undefined, undefined, undefined, searchTerm
            );
            crossFilterCompanyNames = managersResult.data
              .map((m: any) => m.org)
              .filter(Boolean);
            // Deduplicate
            crossFilterCompanyNames = Array.from(new Set(crossFilterCompanyNames));
            console.log(`Found ${crossFilterCompanyNames.length} unique orgs from matching managers`);
          }

          // Now fetch the current tab filtered by those company names
          if (crossFilterCompanyNames.length > 0) {
            if (activeDataType === 'candidates') {
              result = await fetchPaginatedData(
                'candidates', 1, rowLimit, sortColumn, sortConfig.direction,
                effectiveFilterGroups, aiFilterState.employmentTypes,
                undefined, undefined, undefined, undefined,
                undefined, // No search - use cross-filter instead
                crossFilterCompanyNames, // crossFilterCompanies
                undefined // crossFilterOrgs
              );
            } else if (activeDataType === 'managers') {
              result = await fetchPaginatedData(
                'managers', 1, rowLimit, sortColumn, sortConfig.direction,
                effectiveFilterGroups, [],
                undefined, undefined, undefined, undefined,
                undefined, // No search - use cross-filter instead
                undefined, // crossFilterCompanies
                crossFilterCompanyNames // crossFilterOrgs (orgs = company names)
              );
            } else if (activeDataType === 'companies') {
              result = await fetchPaginatedData(
                'companies', 1, rowLimit, sortColumn, sortConfig.direction,
                effectiveFilterGroups, aiFilterState.employmentTypes,
                crossFilterCompanyNames, // companyNames filter
                undefined, undefined, undefined,
                undefined // No search - use cross-filter instead
              );
            }
          } else {
            // No matches from origin tab - show empty results
            result = { data: [], pagination: { total: 0, totalPages: 0, hasNext: false, hasPrev: false } };
          }
        } else {
          // Direct search on current tab (no cross-filtering needed)
          result = await fetchPaginatedData(
            activeDataType,
            1,
            rowLimit,
            sortColumn,
            'asc',
            effectiveFilterGroups,
            aiFilterState.employmentTypes,
            undefined, // companyNames
            undefined, // candidateIds
            undefined, // excludeCompany
            undefined, // radiusParams
            searchTerm || undefined // search
          );
        }

        if (activeDataType === 'companies') {
          setCompanies(result.data);
        } else if (activeDataType === 'candidates') {
          setCandidates(result.data);
        } else {
          setHiringManagers(result.data);
        }

        setServerPagination({
          enabled: true,
          total: result.pagination.total,
          totalPages: result.pagination.totalPages,
          hasNext: result.pagination.hasNext,
          hasPrev: result.pagination.hasPrev
        });

        // Fetch cross-filtered counts with search (context-aware based on active tab)
        try {
          const countsParams = new URLSearchParams();
          if (effectiveFilterGroups.length > 0) {
            countsParams.append('filterGroups', JSON.stringify(effectiveFilterGroups));
          }
          if (aiFilterState.employmentTypes.length > 0) {
            countsParams.append('employmentTypes', aiFilterState.employmentTypes.join(','));
          }
          if (searchTerm) {
            countsParams.append('search', searchTerm);
          }
          // Add radius search parameters for cross-filtering
          if (radiusCoords && radiusDistance > 0) {
            countsParams.append('radiusLat', radiusCoords.lat.toString());
            countsParams.append('radiusLng', radiusCoords.lng.toString());
            countsParams.append('radiusDistance', radiusDistance.toString());
            countsParams.append('radiusType', radiusType);
          }
          // Pass origin tab so backend knows which entity type to search and cross-filter from
          // Use searchOriginTab to maintain context when switching tabs
          countsParams.append('activeTab', searchOriginTab || activeDataType);
          if (campaignScopeId) countsParams.append('campaignId', String(campaignScopeId));

          const countsResponse = await fetch(
            `/api/projects/${selectedProject.id}/filtered-counts`,
            {
              method: 'POST',
              headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
              cache: 'no-store',
              body: JSON.stringify(paramsToObject(countsParams))
            }
          );

          if (countsResponse.ok) {
            const counts = await countsResponse.json();
            setServerTotals({
              companies: counts.companies,
              candidates: counts.candidates,
              managers: counts.managers
            });
          }
        } catch (countsError) {
          console.error('Error fetching search counts:', countsError);
        }

        // Prefetch page 2
        if (result.pagination.hasNext) {
          prefetchNextPage(activeDataType, 2, effectiveFilterGroups, aiFilterState.employmentTypes);
        }
      } catch (error) {
        console.error('Error performing search:', error);
      } finally {
        setPageLoading(false);
      }
    };

    performSearch();
  }, [searchTerm, selectedProject?.id, serverPagination.enabled, activeDataType, searchOriginTab]);

  // Reset column order when switching data types or projects
  useEffect(() => {
    setColumnOrder([]);
  }, [activeDataType, selectedProject?.id]);

  // Initialize column order when visible columns change
  useEffect(() => {
    if (activeDataType === 'candidates' && !tablePrefs.loaded) return; // the person's default decides the start
    if (visibleColumns.length > 0 && columnOrder.length === 0) {
      // Try to load saved column order from localStorage
      const savedOrder = loadColumnOrder();
      let finalOrder: string[];

      if (savedOrder) {
        // Validate saved order against current visible columns
        const validSavedColumns = savedOrder.filter((col: string) => visibleColumns.includes(col));
        const newColumns = visibleColumns.filter(col => !savedOrder.includes(col));
        finalOrder = [...validSavedColumns, ...newColumns];
      } else {
        // No saved order - the person's own default, else the system default,
        // for candidates; visible columns order for others
        if (activeDataType === 'candidates') {
          const base = tablePrefs.order && tablePrefs.order.length ? tablePrefs.order : DEFAULT_CANDIDATE_COLUMN_ORDER;
          // Start with that order, filtered to only visible columns
          const defaultFiltered = base.filter(col => visibleColumns.includes(col));
          // Add any visible columns not in it at the end
          const extraColumns = visibleColumns.filter(col => !base.includes(col));
          finalOrder = [...defaultFiltered, ...extraColumns];
        } else {
          finalOrder = [...visibleColumns];
        }
      }

      // Ensure worked_together_at and position_at_company are properly positioned
      if (visibleColumns.includes('worked_together_at')) {
        // Remove both connection columns from wherever they are
        finalOrder = finalOrder.filter(c => c !== 'worked_together_at' && c !== 'position_at_company');
        // Insert after connection_count or after name
        let insertIndex = finalOrder.indexOf('connection_count');
        if (insertIndex === -1) insertIndex = finalOrder.indexOf('name');
        if (insertIndex !== -1) {
          finalOrder.splice(insertIndex + 1, 0, 'worked_together_at', 'position_at_company');
        } else {
          finalOrder.unshift('worked_together_at', 'position_at_company');
        }
      }

      // When connections filter is active, position current_company and current_position after position_at_company
      if (selectedConnectionsFilter && finalOrder.includes('position_at_company')) {
        const posAtIdx = finalOrder.indexOf('position_at_company');
        finalOrder = finalOrder.filter(key => key !== 'current_company' && key !== 'current_position');
        finalOrder.splice(posAtIdx + 1, 0, 'current_company', 'current_position');
      }

      // Position Ada scoring columns (and the agent's match_score / reasoning)
      // right after 'name' for visibility. ONLY Ada-generated custom columns
      // get force-positioned — those are flagged by a non-null column_group_key
      // (per migration 062). User-uploaded custom columns keep whatever order
      // the CSV / saved layout / drag-and-drop puts them in.
      {
        const agentCols = selectedProject?.created_by_agent ? ['match_score', 'match_reasoning'] : [];
        const scoringCustomCols = customColumns
          .filter(c => c.column_group_key)
          .map(c => c.column_key);
        const allSpecialCols = [...agentCols, ...scoringCustomCols];
        const toInsert = allSpecialCols.filter(c => visibleColumns.includes(c) && finalOrder.includes(c));
        if (toInsert.length > 0) {
          finalOrder = finalOrder.filter(c => !allSpecialCols.includes(c));
          const nameKey = activeDataType === 'companies' ? 'company_name' : 'name';
          const nameIdx = finalOrder.indexOf(nameKey);
          if (nameIdx !== -1) {
            finalOrder.splice(nameIdx + 1, 0, ...toInsert);
          }
        }
      }

      // Ensure locked company columns are always first in the correct order
      if (activeDataType === 'companies') {
        const lockedKeys = ['company_name', 'candidate_count', 'hiring_manager_count'];
        finalOrder = [...lockedKeys.filter(k => finalOrder.includes(k)), ...finalOrder.filter(k => !lockedKeys.includes(k))];
      }

      setColumnOrder(finalOrder);
    } else if (visibleColumns.length > 0) {
      // Add any new columns to the end, remove any that are no longer visible
      const newColumns = visibleColumns.filter(col => !columnOrder.includes(col));
      let validColumns = columnOrder.filter(col => visibleColumns.includes(col));

      // Special handling for connection columns - ensure they're positioned correctly
      if (visibleColumns.includes('worked_together_at')) {
        // Remove both connection columns from current positions
        validColumns = validColumns.filter(c => c !== 'worked_together_at' && c !== 'position_at_company');
        const filteredNewColumns = newColumns.filter(c => c !== 'worked_together_at' && c !== 'position_at_company');

        // Find where to insert - after connection_count, or after name
        let insertIndex = validColumns.indexOf('connection_count');
        if (insertIndex === -1) {
          const newColsWithConnection = [...validColumns, ...filteredNewColumns];
          insertIndex = newColsWithConnection.indexOf('connection_count');
          if (insertIndex !== -1) {
            const finalOrder = [...newColsWithConnection];
            finalOrder.splice(insertIndex + 1, 0, 'worked_together_at', 'position_at_company');
            setColumnOrder(finalOrder);
            return;
          }
          insertIndex = validColumns.indexOf('name');
        }

        if (insertIndex !== -1) {
          validColumns.splice(insertIndex + 1, 0, 'worked_together_at', 'position_at_company');
        } else {
          validColumns.unshift('worked_together_at', 'position_at_company');
        }

        // Position current_company and current_position after position_at_company
        const posAtIdx = validColumns.indexOf('position_at_company');
        if (posAtIdx !== -1) {
          validColumns = validColumns.filter(key => key !== 'current_company' && key !== 'current_position');
          const filteredNew = filteredNewColumns.filter(key => key !== 'current_company' && key !== 'current_position');
          validColumns.splice(posAtIdx + 1, 0, 'current_company', 'current_position');
          setColumnOrder([...validColumns, ...filteredNew]);
          return;
        }

        setColumnOrder([...validColumns, ...filteredNewColumns]);
        return;
      }

      // Place late-arriving columns at their SAVED position, not the end.
      // Custom columns are fetched async, so on project load the first
      // columnOrder init runs before they exist and drops them from the saved
      // order; when they arrive a beat later they used to be appended to the
      // far end — losing the position the user chose with Insert column. If
      // the saved order knows the column, splice it back in after its nearest
      // saved neighbour that's already present.
      const savedForPlacement = loadColumnOrder();
      let finalOrder = [...validColumns];
      for (const nk of newColumns) {
        const si = savedForPlacement ? savedForPlacement.indexOf(nk) : -1;
        if (si === -1) { finalOrder.push(nk); continue; }
        let placed = false;
        for (let j = si - 1; j >= 0; j--) {
          const idx = finalOrder.indexOf(savedForPlacement![j]);
          if (idx !== -1) { finalOrder.splice(idx + 1, 0, nk); placed = true; break; }
        }
        if (!placed) finalOrder.unshift(nk);
      }

      // Ensure locked company columns are always first in the correct order
      if (activeDataType === 'companies') {
        const lockedKeys = ['company_name', 'candidate_count', 'hiring_manager_count'];
        finalOrder = [...lockedKeys.filter(k => finalOrder.includes(k)), ...finalOrder.filter(k => !lockedKeys.includes(k))];
      }

      // Position Ada scoring columns (and the agent's match_score / reasoning)
      // right after 'name'. Only Ada-generated custom columns (column_group_key
      // non-null) get force-positioned; user-uploaded custom columns keep
      // their saved/drag-and-dropped position.
      {
        const agentCols = selectedProject?.created_by_agent ? ['match_score', 'match_reasoning'] : [];
        const scoringCustomCols = customColumns
          .filter(c => c.column_group_key)
          .map(c => c.column_key);
        const allSpecialCols = [...agentCols, ...scoringCustomCols];
        const colsInOrder = allSpecialCols.filter(c => finalOrder.includes(c));
        if (colsInOrder.length > 0) {
          finalOrder = finalOrder.filter(c => !allSpecialCols.includes(c));
          const nameKey = activeDataType === 'companies' ? 'company_name' : 'name';
          const nameIdx = finalOrder.indexOf(nameKey);
          if (nameIdx !== -1) {
            finalOrder.splice(nameIdx + 1, 0, ...colsInOrder);
          }
        }
      }

      setColumnOrder(finalOrder);
    }
  }, [visibleColumns, selectedConnectionsFilter, tablePrefs.loaded]);

  // ============================================================================
  // COLUMN INTERACTION HANDLERS
  // ============================================================================

  // Handle cell double-click to open modal
  const handleCellDoubleClick = (content: string, columnLabel: string, rowId?: number, columnKey?: string) => {
    setCellModal({
      show: true,
      content: content || '-',
      title: columnLabel,
      rowId: rowId,
      columnKey: columnKey,
      isEditing: false,
      editValue: content || '',
      isSaving: false
    });
  };

  // Insert a new, manually-editable custom column next to an anchor column.
  // `side` places it left/right of the anchor; `type` sets how cells format
  // (text / url / number). The column is created empty, then repositioned once
  // it appears in columnOrder, and the rename modal opens so it can be named.
  const handleInsertColumn = async (
    anchorKey: string,
    side: 'left' | 'right',
    type: 'text' | 'url' | 'number'
  ) => {
    setCustomColMenu(null);
    setCustomColMenuPos(null);
    if (!selectedProject?.id) return;
    const columnKey = `col_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const defaultLabel = type === 'url' ? 'New link' : type === 'number' ? 'New number' : 'New column';
    try {
      const response = await fetch(`/api/custom-columns/${selectedProject.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ columnKey, columnLabel: defaultLabel, columnType: type }),
      });
      if (!response.ok) throw new Error('Failed to create column');
      // Refresh custom columns so the new one enters the table's column set.
      const refreshed = await fetch(`/api/custom-columns/${selectedProject.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (refreshed.ok) setCustomColumns(await refreshed.json());
      // A brand-new column has no cell data yet, so the default-visibility logic
      // (which only auto-shows columns that have data) would drop it — leaving it
      // invisible and un-editable. Force it visible; the columnOrder effect then
      // appends it and aiColumnReposition slots it beside the anchor.
      setVisibleColumns(prev => (prev.includes(columnKey) ? prev : [...prev, columnKey]));
      setAiColumnReposition({ key: columnKey, anchor: anchorKey, side });
      // Prompt for a name straight away (empty field, default as placeholder).
      setRenameModal({ columnKey, currentLabel: defaultLabel });
      setRenameValue('');
    } catch (error) {
      console.error('Failed to insert column:', error);
      alert('Failed to insert column. Please try again.');
    }
  };

  // Formula column generator modal (opened from the ⋮ menu). Holds the anchor
  // column + side so the new column lands beside it, like Insert column.
  const [formulaModal, setFormulaModal] = useState<{ anchorKey: string | null; side: 'left' | 'right' } | null>(null);

  // Derived "across all roles" columns for candidates — flatten current +
  // previous roles (and the full position_history when present) into one
  // searchable string, so a formula can ask "has this person EVER worked a
  // contract role / mentioned 'data center' in any title" with a plain
  // .includes(), without arrays. `sourceKeys` are the raw DB columns the apply
  // pass must fetch to compute them for every row.
  const normalizeHistory = (row: any): any[] => {
    let h = row?.position_history;
    if (typeof h === 'string') { try { h = JSON.parse(h); } catch { h = null; } }
    return Array.isArray(h) ? h : [];
  };
  const uniqJoin = (parts: any[]): string => {
    const seen = new Set<string>(); const out: string[] = [];
    parts.forEach(v => {
      const s = (v == null ? '' : String(v)).trim();
      if (s && !seen.has(s.toLowerCase())) { seen.add(s.toLowerCase()); out.push(s); }
    });
    return out.join(' | ');
  };
  const CANDIDATE_VIRTUAL_COLUMNS: any[] = [
    { key: '__all_job_titles', label: 'All Job Titles (all roles)', type: 'text', isCustom: false, isVirtual: true,
      sourceKeys: ['current_position', 'previous_position_1', 'previous_position_2', 'previous_position_3', 'position_history'],
      virtual: (r: any) => uniqJoin([r.current_position, r.previous_position_1, r.previous_position_2, r.previous_position_3, ...normalizeHistory(r).map((p: any) => p.title)]) },
    { key: '__all_employment_types', label: 'All Employment Types (all roles)', type: 'text', isCustom: false, isVirtual: true,
      sourceKeys: ['current_contract_type', 'employment_type', 'position_history'],
      virtual: (r: any) => uniqJoin([r.current_contract_type, r.employment_type, ...normalizeHistory(r).map((p: any) => p.contractType)]) },
    { key: '__all_companies', label: 'All Companies (all roles)', type: 'text', isCustom: false, isVirtual: true,
      sourceKeys: ['current_company', 'previous_company_1', 'previous_company_2', 'previous_company_3', 'position_history'],
      virtual: (r: any) => uniqJoin([r.current_company, r.previous_company_1, r.previous_company_2, r.previous_company_3, ...normalizeHistory(r).map((p: any) => p.companyName)]) },
    { key: '__all_role_descriptions', label: 'All Role Descriptions (all roles)', type: 'text', isCustom: false, isVirtual: true,
      sourceKeys: ['current_position_description', 'previous_position_1_description', 'previous_position_2_description', 'previous_position_3_description', 'position_history'],
      virtual: (r: any) => uniqJoin([r.current_position_description, r.previous_position_1_description, r.previous_position_2_description, r.previous_position_3_description, ...normalizeHistory(r).map((p: any) => p.description)]) },
  ];

  // Every column a formula can reference: all defined columns (system + custom,
  // visible or not) plus the derived candidate columns above.
  const getFormulaColumns = (): any[] => {
    const base = (getCurrentColumns() as any[])
      .filter(c => c.key && c.label)
      .map(c => ({ key: c.key, label: c.label, type: c.type, isCustom: !!c.isCustom }));
    return activeDataType === 'candidates' ? [...CANDIDATE_VIRTUAL_COLUMNS, ...base] : base;
  };

  // Reference columns the formula generator can use, and the loaded rows it
  // previews against.
  const buildRefColumns = () => getFormulaColumns();

  const buildPreviewRows = () => {
    const rows: any[] = activeDataType === 'companies' ? companies : candidates;
    return rows.slice(0, 12).map(r => ({
      id: (r as any).id,
      name: activeDataType === 'companies' ? (r as any).company_name : (r as any).name,
      row: r,
    }));
  };

  // Compute a formula column's value for EVERY row in the project (not just the
  // loaded page — the table is server-paginated), persist in chunked bulk
  // writes, and merge into local state so it behaves like any other column.
  const applyFormulaColumn = async (columnKey: string, formula: string): Promise<number> => {
    if (!selectedProject?.id || activeDataType === 'managers') return 0;
    const refs = extractFormulaRefs(formula);
    let compiled: (v: any[]) => any;
    try { compiled = compileFormula(formula, refs); } catch { return 0; }

    // Map each referenced label to its column (custom / standard / virtual).
    const colMap = new Map<string, any>();
    getFormulaColumns().forEach(c => { if (c.label) colMap.set(c.label.toLowerCase(), c); });
    const refCols = refs.map(label => colMap.get(label.toLowerCase()) || { key: '', isCustom: false });
    // Standard DB columns to fetch: directly-referenced ones + every source
    // column the referenced virtual columns depend on.
    const standardKeys = Array.from(new Set([
      ...refCols.filter(c => c.key && !c.isCustom && !c.isVirtual).map(c => c.key),
      ...refCols.filter(c => c.isVirtual).flatMap(c => c.sourceKeys || []),
    ]));

    // Pull id + referenced values for ALL rows.
    let sourceRows: any[] = [];
    try {
      const res = await fetch(`/api/custom-columns/${selectedProject.id}/formula-source`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ entity: activeDataType, standardKeys }),
      });
      if (!res.ok) throw new Error('source fetch failed');
      sourceRows = (await res.json()).rows || [];
    } catch (e) {
      console.error('Formula source fetch failed, falling back to loaded rows:', e);
      sourceRows = (activeDataType === 'companies' ? fullCompanies : fullCandidates) as any[];
    }

    const resolve = (row: any, rc: any) => {
      if (rc.isVirtual && rc.virtual) return rc.virtual(row);
      if (!rc.key) return undefined;
      return rc.isCustom ? row?.custom_data?.[rc.key] : row?.[rc.key];
    };
    const values = sourceRows.map(r => ({
      id: r.id,
      value: formulaValueToString(compiled(refCols.map(rc => resolve(r, rc)))),
    }));

    // Persist in chunks to keep payloads bounded.
    const CHUNK = 2000;
    for (let i = 0; i < values.length; i += CHUNK) {
      try {
        await fetch(`/api/custom-columns/${selectedProject.id}/bulk-values`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ entity: activeDataType, columnKey, values: values.slice(i, i + CHUNK) }),
        });
      } catch (e) { console.error('Formula bulk write chunk failed:', e); }
    }

    // Merge into whatever rows are currently loaded so the user sees values now.
    const valById = new Map(values.map(v => [v.id, v.value]));
    const patch = (row: any) => valById.has(row.id)
      ? { ...row, custom_data: { ...(row.custom_data || {}), [columnKey]: valById.get(row.id) } }
      : row;
    if (activeDataType === 'companies') { setCompanies(prev => prev.map(patch)); setFullCompanies(prev => prev.map(patch)); }
    else { setCandidates(prev => prev.map(patch)); setFullCandidates(prev => prev.map(patch)); }
    return values.length;
  };

  // After the generator saves the column: refresh columns, make it visible,
  // slot it beside the anchor, then compute + persist its values.
  const handleFormulaSaved = async (info: { columnKey: string; columnLabel: string; formula: string; formulaPrompt: string; refs: string[] }) => {
    const anchor = formulaModal?.anchorKey || null;
    const side = formulaModal?.side || 'right';
    setFormulaModal(null);
    if (!selectedProject?.id) return;
    try {
      const refreshed = await fetch(`/api/custom-columns/${selectedProject.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (refreshed.ok) setCustomColumns(await refreshed.json());
    } catch { /* noop */ }
    setVisibleColumns(prev => (prev.includes(info.columnKey) ? prev : [...prev, info.columnKey]));
    if (anchor) setAiColumnReposition({ key: info.columnKey, anchor, side });
    await applyFormulaColumn(info.columnKey, info.formula);
  };

  // Recompute a formula column from its stored formula (⋮ menu action).
  const recomputeFormulaColumn = async (columnKey: string) => {
    const col = customColumns.find((c: any) => c.column_key === columnKey);
    if (!col?.formula) return;
    setCustomColMenu(null); setCustomColMenuPos(null);
    const n = await applyFormulaColumn(columnKey, col.formula);
    console.log(`Recomputed formula column ${columnKey} for ${n} rows`);
  };

  // Editable fields whitelist for companies
  const editableCompanyFields = [
    'company_name', 'industry', 'description', 'website', 'domain',
    'employee_count', 'founded', 'headquarters', 'type', 'formatted_address',
    'locality', 'region', 'country', 'notes', 'slogan', 'about', 'specialties'
  ];

  // Editable fields whitelist for candidates
  const editableCandidateFields = [
    'name', 'first_name', 'last_name', 'position', 'company', 'address', 'headline', 'summary',
    'email', 'personal_email', 'phone', 'current_position', 'current_company',
    'current_company_location', 'current_contract_type', 'current_position_description',
    'location_city', 'location_state', 'location_country', 'location_full',
    'employment_type', 'working_style', 'company_location'
  ];

  // A custom (CSV/AI) column lives in custom_data JSONB, keyed by column_key.
  const isCustomColumnKey = (columnKey: string | undefined): boolean =>
    !!columnKey && customColumns.some((c: any) => c.column_key === columnKey);

  // Check if current cell is editable
  const isFieldEditable = (columnKey: string | undefined): boolean => {
    if (!columnKey) return false;
    // Custom columns are editable inline for companies & candidates — they write
    // into custom_data (handled by the backend /field endpoint).
    if (isCustomColumnKey(columnKey)) return true;
    if (activeDataType === 'companies') {
      return editableCompanyFields.includes(columnKey);
    } else if (activeDataType === 'candidates') {
      return editableCandidateFields.includes(columnKey);
    }
    return false;
  };

  // Handle saving cell edit
  const handleSaveCellEdit = async () => {
    if (!cellModal.rowId || !cellModal.columnKey || !selectedProject?.id) return;

    setCellModal(prev => ({ ...prev, isSaving: true }));

    try {
      const token = localStorage.getItem('token');
      let endpoint = '';

      if (activeDataType === 'companies') {
        endpoint = `/api/companies/${selectedProject.id}/company/${cellModal.rowId}/field`;
      } else if (activeDataType === 'candidates') {
        endpoint = `/api/candidates/${selectedProject.id}/candidate/${cellModal.rowId}/field`;
      } else {
        throw new Error('Invalid data type');
      }

      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          field: cellModal.columnKey,
          value: cellModal.editValue || ''
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save');
      }

      // Update local data. Custom columns live in custom_data JSONB, so merge
      // the new value into custom_data rather than onto a top-level field.
      const key = cellModal.columnKey!;
      const isCustom = isCustomColumnKey(key);
      const applyPatch = (row: any) =>
        row.id === cellModal.rowId
          ? (isCustom
              ? { ...row, custom_data: { ...(row.custom_data || {}), [key]: cellModal.editValue } }
              : { ...row, [key]: cellModal.editValue })
          : row;

      if (activeDataType === 'companies') {
        setCompanies(prev => prev.map(applyPatch));
        setFullCompanies(prev => prev.map(applyPatch));
      } else if (activeDataType === 'candidates') {
        setCandidates(prev => prev.map(applyPatch));
        setFullCandidates(prev => prev.map(applyPatch));
      }

      // Close modal after successful save
      setCellModal({ show: false, content: '', title: '' });
    } catch (error) {
      console.error('Error saving cell edit:', error);
      alert(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setCellModal(prev => ({ ...prev, isSaving: false }));
    }
  };

  // Handle column resize start
  const handleResizeStart = (e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.target as HTMLElement).closest('th');
    if (th) {
      setResizing({
        columnKey,
        startX: e.clientX,
        startWidth: th.offsetWidth
      });
    }
  };

  // Handle column resize move (global)
  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - resizing.startX;
      const newWidth = Math.max(100, resizing.startWidth + diff);
      setColumnWidths(prev => ({
        ...prev,
        [resizing.columnKey]: newWidth
      }));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing]);

  // Handle column drag and drop
  const handleDragStart = (e: React.DragEvent, columnKey: string) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // Auto-scroll when near edges
    const container = document.getElementById('main-table-container');
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX;
    const edgeThreshold = 50;
    const scrollSpeed = 12;

    // Check if near left edge
    if (mouseX - rect.left < edgeThreshold && container.scrollLeft > 0) {
      if (!scrollInterval) {
        const interval = setInterval(() => {
          container.scrollLeft -= scrollSpeed;
        }, 50);
        setScrollInterval(interval);
      }
    }
    // Check if near right edge
    else if (rect.right - mouseX < edgeThreshold &&
             container.scrollLeft < container.scrollWidth - container.clientWidth) {
      if (!scrollInterval) {
        const interval = setInterval(() => {
          container.scrollLeft += scrollSpeed;
        }, 50);
        setScrollInterval(interval);
      }
    }
    // Clear interval if not near edges
    else if (scrollInterval) {
      clearInterval(scrollInterval);
      setScrollInterval(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetColumnKey: string) => {
    e.preventDefault();

    // Clear any active scroll interval
    if (scrollInterval) {
      clearInterval(scrollInterval);
      setScrollInterval(null);
    }

    if (!draggedColumn || draggedColumn === targetColumnKey) {
      setDraggedColumn(null);
      return;
    }

    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumnKey);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedColumn);
      setColumnOrder(newOrder);
      saveColumnOrder(newOrder);
    }

    setDraggedColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    // Clear any active scroll interval
    if (scrollInterval) {
      clearInterval(scrollInterval);
      setScrollInterval(null);
    }
  };

  // Helper functions for column order persistence
  const saveColumnOrder = (order: string[]) => {
    const data = {
      version: activeDataType === 'candidates' ? CANDIDATE_COLUMN_ORDER_VERSION : 1,
      order: order,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(`columnOrder_${selectedProject.id}_${activeDataType}`, JSON.stringify(data));
  };

  // --- Per-column header colours (Clay-style) -----------------------------
  const columnColorsKey = () =>
    selectedProject?.id ? `columnColors_${selectedProject.id}_${activeDataType}` : null;

  const persistColumnColors = (colors: Record<string, string>) => {
    const key = columnColorsKey();
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(colors));
    } catch {
      // localStorage full/unavailable — non-fatal
    }
  };

  const handleColumnColor = (colKey: string, color: string | null) => {
    setColumnColors(prev => {
      const next = { ...prev };
      if (!color) {
        delete next[colKey];
      } else {
        next[colKey] = color;
      }
      persistColumnColors(next);
      return next;
    });
  };

  const loadColumnOrder = (): string[] | null => {
    const saved = localStorage.getItem(`columnOrder_${selectedProject.id}_${activeDataType}`);
    if (!saved) return null;

    try {
      const parsed = JSON.parse(saved);

      // Handle new versioned format
      if (parsed && typeof parsed === 'object' && 'version' in parsed && 'order' in parsed) {
        // For candidates, check version matches
        if (activeDataType === 'candidates') {
          if (parsed.version !== CANDIDATE_COLUMN_ORDER_VERSION) {
            // Version mismatch - return null to use new default
            return null;
          }
          // "Also reset my other projects" (saved as the person's default on
          // another machine, or before this browser caught up): a project
          // order saved before that moment no longer counts.
          if (tablePrefs.resetBefore && (!parsed.savedAt || parsed.savedAt < tablePrefs.resetBefore)) return null;
        }
        return parsed.order;
      }

      // Handle old format (just an array) - treat as outdated for candidates
      if (Array.isArray(parsed)) {
        if (activeDataType === 'candidates') {
          // Old format without version - use new default
          return null;
        }
        return parsed;
      }

      return null;
    } catch {
      return null;
    }
  };

  const moveColumnInPanel = (fromIndex: number, toIndex: number) => {
    const newOrder = [...columnOrder];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    setColumnOrder(newOrder);
    saveColumnOrder(newOrder);
  };

  // Handle ESC key to close modals
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (cellModal.show) {
          setCellModal({ show: false, content: '', title: '' });
        } else if (showColumnOrderPanel) {
          setShowColumnOrderPanel(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [cellModal.show, showColumnOrderPanel]);

  // ============================================================================
  // MATCH DETECTION AND HIGHLIGHTING
  // ============================================================================

  /**
   * Helper: Check if a field belongs to companies
   */
  const isCompanyField = (fieldKey: string): boolean => {
    const companyFields = getCompanyColumns().map(col => col.key);
    return companyFields.includes(fieldKey);
  };

  /**
   * Helper: Check if a field belongs to candidates
   */
  const isCandidateField = (fieldKey: string): boolean => {
    const candidateFields = getCandidateColumns().map(col => col.key);
    return candidateFields.includes(fieldKey);
  };

  /**
   * Whole-word "contains" test mirroring the backend's buildContainsPattern
   * (utils/searchTerms.js) — keep the two in sync. Left word boundary unless
   * the term starts with a non-word char (".NET"); right word boundary with
   * an optional plural "s" unless the term ends with a non-word char ("C#").
   * So "AI" doesn't match "daily"/"air", but "agent" matches "agents".
   */
  const filterTermMatches = (text: string, term: string): boolean => {
    if (!term) return false;
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const isWordChar = (ch: string) => /[A-Za-z0-9_]/.test(ch);
    const prefix = isWordChar(term[0]) ? '(?:^|[^A-Za-z0-9_])' : '';
    const suffix = isWordChar(term[term.length - 1]) ? '(?:s)?(?![A-Za-z0-9_])' : '';
    return new RegExp(`${prefix}${escaped}${suffix}`, 'i').test(text);
  };

  /**
   * Analyze which fields in a row match the current filters AND search term
   * STRICT: Only highlights terms from filters that match the current activeDataType
   * AND only in the specific columns that were filtered
   */
  const analyzeMatches = (row: any, filters: FilterState, searchTermToHighlight?: string) => {
    const matches = {
      columns: new Set<string>(),
      secondaryColumns: new Set<string>(),
      terms: new Set<string>(),
      count: 0
    };

    // Common words to exclude from highlighting
    const stopWords = new Set(['and', 'the', 'for', 'with', 'from', 'into', 'about', 'that', 'this', 'these', 'those']);

    // Track filter conditions (including multi-word terms as groups)
    type FilterInfo = {
      terms: string[];  // All terms that must be present (for multi-word)
      filteredColumns: Set<string>;  // Columns that are explicitly filtered
      anyColumn?: boolean;  // any_field: a hit in ANY column is a primary match
    };
    const filterConditions: FilterInfo[] = [];

    filters.groups?.forEach(group => {
      group.conditions?.forEach(condition => {
        if (condition.operator === 'contains' || condition.operator === 'equals') {
          // STRICT: Only include if entityType matches current view. A missing
          // entityType is treated as candidate — matching the backend, which
          // routes entityType-less conditions to the candidate filter.
          const isRelevantFilter =
            (activeDataType === 'companies' && condition.entityType === 'company') ||
            (activeDataType === 'candidates' && (condition.entityType === 'candidate' || !condition.entityType)) ||
            (activeDataType === 'managers' && condition.entityType === 'manager');

          if (isRelevantFilter && condition.column) {
            // "Any field" pseudo-column: `values` are OR phrases matched across
            // ALL columns. Push each phrase as its OWN condition (so ANY match
            // highlights, not ALL) and flag anyColumn so a hit in any cell is a
            // primary (green) match — mirroring the backend expansion.
            if (condition.column === ANY_FIELD_COL) {
              // The value can be a pasted boolean ('"data center" OR colo OR …')
              // or an array of phrases. Parse via parseSearchTerms so quoted
              // phrases and OR/comma separators expand into individual keywords —
              // otherwise the whole boolean string is treated as one term and
              // never matches, so nothing highlights (this was the bug).
              const raw = Array.isArray(condition.values)
                ? condition.values
                : (condition.value ? [condition.value] : []);
              const phrases = raw
                .flatMap((v: any) => parseSearchTerms(String(v)))
                .map((v: string) => v.toLowerCase())
                .filter((t: string) => t.length >= 2 && !stopWords.has(t));
              phrases.forEach((p: string) =>
                filterConditions.push({ terms: [p], filteredColumns: new Set(), anyColumn: true }));
              return;
            }

            let allTerms: string[] = [];

            // Handle both single value and multiple values (for multi-word terms)
            if (condition.values && Array.isArray(condition.values)) {
              // Multi-word term: each value is a separate term that ALL must be present
              allTerms = condition.values.map((v: any) => String(v).toLowerCase());
            } else if (condition.value && typeof condition.value === 'string') {
              // Single value: split into terms
              allTerms = condition.value.toLowerCase().split(/\s+/);
            }

            // Filter out stop words and very short terms (allow 2+ chars for abbreviations like "MM", "UI", "AI")
            const terms = allTerms.filter((t: string) => t.length >= 2 && !stopWords.has(t));

            if (terms.length > 0) {
              filterConditions.push({
                terms,
                filteredColumns: new Set([condition.column])
              });
            }
          }
        }
      });
    });

    // Only return early if no filters AND no search term
    if (filterConditions.length === 0 && !searchTermToHighlight?.trim()) return matches;

    // Check each column in the row
    const columnsToCheck = getCurrentColumns();
    const primaryMatches = new Set<string>();  // Columns in filter with all terms
    const secondaryMatches = new Set<string>();  // Other columns with all terms
    const allMatchedTerms = new Set<string>();

    columnsToCheck.forEach((col: any) => {
      const value = row[col.key];
      if (!value) return;

      const valueStr = String(value).toLowerCase();

      // Check each filter condition
      filterConditions.forEach(filterInfo => {
        // Check if ALL terms from this filter are present in this column.
        // Whole-word match (mirrors the backend's buildContainsPattern ~*
        // regex) so the UI agrees with what the database actually matched.
        const allTermsPresent = filterInfo.terms.every(term => {
          return filterTermMatches(valueStr, term);
        });

        if (allTermsPresent) {
          // All terms found in this column!
          filterInfo.terms.forEach(t => allMatchedTerms.add(t));

          if (filterInfo.anyColumn || filterInfo.filteredColumns.has(col.key)) {
            // any_field, or this column is explicitly in the filter — primary match
            primaryMatches.add(col.key);
            matches.count++;  // Only count primary matches for weighting
          } else {
            // This column is not in the filter - secondary match
            secondaryMatches.add(col.key);
          }
        }
      });
    });

    matches.columns = primaryMatches;
    matches.secondaryColumns = secondaryMatches;
    matches.terms = allMatchedTerms;

    // Also check for search term matches (if provided).
    // Multi-keyword: a row matches if ANY parsed term appears in ANY column.
    const searchKeywords = parseSearchTerms(searchTermToHighlight);
    if (searchKeywords.length > 0) {
      const columnsToCheck = getCurrentColumns();

      columnsToCheck.forEach((col: any) => {
        const value = row[col.key];
        if (!value) return;

        const valueStr = String(value);
        searchKeywords.forEach(kw => {
          // Whole-word match (mirrors the backend's ~* / buildContainsPattern),
          // so a column only goes green on a genuine word match — not "colo"
          // inside "colonel" or "AI" inside "training". Keeps the green column
          // highlight consistent with what actually matched and with the yellow
          // in-cell highlight.
          if (filterTermMatches(valueStr, kw)) {
            primaryMatches.add(col.key);
            matches.columns.add(col.key);
            matches.terms.add(kw.toLowerCase());
            matches.count++;
          }
        });
      });
    }

    return matches;
  };

  /**
   * Highlight matched terms within text
   * For filter terms: use word boundary matching
   * For search term: use substring matching (highlights partial matches)
   */
  const highlightMatchedText = (text: string, rowId: number): React.ReactNode => {
    const matches = matchData.get(rowId);
    if (!matches || matches.terms.size === 0) {
      return text;
    }

    const terms = Array.from(matches.terms) as string[];
    let result: React.ReactNode[] = [];
    let lastIndex = 0;

    // Find all occurrences of all terms
    const searchKeywordSet = new Set(parseSearchTerms(searchTerm).map(k => k.toLowerCase()));
    const occurrences: {index: number, length: number, term: string}[] = [];
    terms.forEach((term: string) => {
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      // Whole-word matching, mirroring the backend's buildContainsPattern so
      // the highlights agree with what the database actually matched. Edge
      // boundaries are only enforced where the term's own edge is a word
      // char, so ".NET" / "C#" still highlight. Filter terms additionally
      // allow a plural "s" ("agent" highlights "agents"). Manual
      // neighbour-char checks rather than \b / lookbehind because lookbehind
      // breaks on Safari < 16.4.
      const isSearchTerm = searchKeywordSet.has(term.toLowerCase());
      const isWordChar = (ch: string | undefined) => !!ch && /\w/.test(ch);
      const enforceBefore = isWordChar(term[0]);
      const enforceAfter = isWordChar(term[term.length - 1]);
      const checkBoundary = (match: RegExpExecArray): boolean => {
        const before = match.index > 0 ? text[match.index - 1] : undefined;
        const after = text[match.index + match[0].length];
        return (!enforceBefore || !isWordChar(before)) && (!enforceAfter || !isWordChar(after));
      };

      // Filter terms get the optional plural; quick-search terms highlight
      // exactly what was typed (matching their substring-style row matching).
      const pattern = isSearchTerm || !enforceAfter ? escapedTerm : `${escapedTerm}(?:s)?`;
      const regex = new RegExp(pattern, 'gi');
      let match;
      while ((match = regex.exec(text)) !== null) {
        if (checkBoundary(match)) {
          occurrences.push({index: match.index, length: match[0].length, term});
        }
        if (match[0].length === 0) regex.lastIndex++;
      }
    });

    // Sort by position and remove overlapping occurrences
    occurrences.sort((a, b) => a.index - b.index);

    // Build result with highlights
    occurrences.forEach(({index, length}) => {
      // Skip if this occurrence overlaps with the previous one
      if (index < lastIndex) return;

      if (index > lastIndex) {
        result.push(text.substring(lastIndex, index));
      }
      result.push(<mark key={`${index}-${length}`} className="bg-yellow-300">{text.substring(index, index + length)}</mark>);
      lastIndex = index + length;
    });

    if (lastIndex < text.length) {
      result.push(text.substring(lastIndex));
    }

    return result.length > 0 ? <>{result}</> : text;
  };

  // Every term currently being searched or filtered (quick-search keywords +
  // AI/manual filter condition values). Used to highlight the expanded-cell
  // modal directly, so the popup highlights matches regardless of which column
  // the per-row match analysis attributed the hit to.
  const getActiveHighlightTerms = (): string[] => {
    const terms = new Set<string>();
    parseSearchTerms(searchTerm).forEach(t => { const s = (t || '').trim(); if (s) terms.add(s); });
    (aiFilterState.groups || []).forEach((g: any) => (g.conditions || []).forEach((c: any) => {
      // Only highlight terms from conditions relevant to the current view, so a
      // COMPANY filter's value (e.g. "data center") doesn't light up while
      // you're looking at the candidates table. any_field values are candidate
      // (or company) terms and pass this the same way.
      const relevant =
        (activeDataType === 'companies' && c.entityType === 'company') ||
        (activeDataType === 'candidates' && (c.entityType === 'candidate' || !c.entityType)) ||
        (activeDataType === 'managers' && c.entityType === 'manager');
      if (!relevant) return;
      // Parse each value so a pasted boolean ('"data center" OR colo OR …')
      // expands into individual highlightable keywords; plain phrases pass
      // through unchanged (parseSearchTerms only splits on OR/comma/newline).
      if (Array.isArray(c.values)) c.values.forEach((v: string) => parseSearchTerms(String(v)).forEach(s => { if (s) terms.add(s); }));
      else if (c.value != null && String(c.value).trim()) parseSearchTerms(String(c.value)).forEach(s => { if (s) terms.add(s); });
    }));
    return Array.from(terms);
  };

  // Highlight the given terms wherever they appear in `text` as WHOLE WORDS
  // (mirrors the backend's whole-word ~* matching, so "AI" doesn't light up
  // "michigAn"/"trAIning" and "Ada" doesn't light up "Adaptability"). Boundaries
  // are enforced only on an edge that's itself a word char, so ".NET"/"C#"/"T5"
  // still match. Longest terms first so phrases beat sub-words. Standalone from
  // highlightMatchedText because that one is gated on per-row matchData; this is
  // for the expanded-cell modal where we always want the active terms lit up.
  const highlightTermsInText = (text: string, terms: string[]): React.ReactNode => {
    if (!text || terms.length === 0) return text;
    const isWordChar = (ch?: string) => !!ch && /\w/.test(ch);
    const sorted = terms.slice().sort((a, b) => b.length - a.length);
    const occ: { index: number; length: number }[] = [];
    sorted.forEach(term => {
      if (!term) return;
      const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const enforceBefore = isWordChar(term[0]);
      const enforceAfter = isWordChar(term[term.length - 1]);
      const re = new RegExp(escaped, 'gi');
      let m: RegExpExecArray | null;
      while ((m = re.exec(text)) !== null) {
        const before = m.index > 0 ? text[m.index - 1] : undefined;
        const after = text[m.index + m[0].length];
        const ok = (!enforceBefore || !isWordChar(before)) && (!enforceAfter || !isWordChar(after));
        if (ok) occ.push({ index: m.index, length: m[0].length });
        if (m[0].length === 0) re.lastIndex++;
      }
    });
    if (occ.length === 0) return text;
    occ.sort((a, b) => a.index - b.index);
    const out: React.ReactNode[] = [];
    let last = 0;
    occ.forEach(({ index, length }, i) => {
      if (index < last) return; // overlapping — skip
      if (index > last) out.push(text.substring(last, index));
      out.push(<mark key={i} className="bg-yellow-300">{text.substring(index, index + length)}</mark>);
      last = index + length;
    });
    if (last < text.length) out.push(text.substring(last));
    return <>{out}</>;
  };

  // ============================================================================
  // CENTRALIZED FILTERING LOGIC - Single source of truth for all three tables
  // ============================================================================

const applyOperator = (itemValue: string, filterValue: string, operator: string, rawValue: any) => {
  switch (operator) {
    case 'contains':
      // Use word boundary matching to avoid matching "MM" in "programme" or "SAP" in "asap"
      // Escape special regex characters in filterValue
      const escapedValue = filterValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const wordBoundaryRegex = new RegExp(`\\b${escapedValue}\\b`, 'i');
      return wordBoundaryRegex.test(itemValue);
    case 'not_contains':
      const escapedNotValue = filterValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const notWordBoundaryRegex = new RegExp(`\\b${escapedNotValue}\\b`, 'i');
      return !notWordBoundaryRegex.test(itemValue);
    case 'equals':
      return itemValue === filterValue;
    case 'not_equals':
      return itemValue !== filterValue;  // ADD THIS
    case 'starts_with':
      return itemValue.startsWith(filterValue);
    case 'ends_with':
      return itemValue.endsWith(filterValue);
    case 'is_empty':
      return !rawValue || rawValue === '';
    case 'is_not_empty':
      return rawValue && rawValue !== '';
    case 'greater_than':
      return Number(rawValue) > Number(filterValue);
    case 'less_than':
      return Number(rawValue) < Number(filterValue);
    case 'greater_equal':
      return Number(rawValue) >= Number(filterValue);
    case 'less_equal':
      return Number(rawValue) <= Number(filterValue);
    case 'after':
      return new Date(rawValue) > new Date(filterValue);
    case 'before':
      return new Date(rawValue) < new Date(filterValue);
    default:
      return true;
  }
};

  /**
   * Helper: Check if a single item matches a single filter condition
   */
  const itemMatchesFilterCondition = (item: any, filter: any): boolean => {
  // Handle both old format (columns array) and new format (single column)
  const columnsToCheck = filter.columns || [filter.column];

  return columnsToCheck.some((colKey: string) => {
    // Check custom_data first, then standard columns
    let value = item.custom_data?.[colKey] ?? item[colKey];

    if (!value && value !== 0) return filter.operator === 'is_empty';
    if (filter.operator === 'is_not_empty') return true;

    let itemValue = String(value).toLowerCase();

    if (Array.isArray(value)) {
      itemValue = value.join(' ').toLowerCase();
    }

    // Support multiple values (ALL must be present for multi-word terms)
    if (filter.values && Array.isArray(filter.values)) {
      // Check if ALL values appear in this field
      return filter.values.every((val: string) => {
        const filterValue = val.toLowerCase();
        return applyOperator(itemValue, filterValue, filter.operator, value);
      });
    }

    // Single value check (original behavior)
    const filterValue = filter.value.toLowerCase();
    return applyOperator(itemValue, filterValue, filter.operator, value);
  });
};

  /**
   * Search candidates by name for autocomplete
   */
  const searchCandidatesForConnections = async (query: string) => {
    if (query.length < 2) {
      setConnectionsSuggestions([]);
      setShowConnectionsSuggestions(false);
      return;
    }

    try {
      const headers = getAuthHeaders();
      const response = await fetch(
        `/api/candidates/${selectedProject?.id}/search?query=${encodeURIComponent(query)}`,
        { headers, cache: 'no-store' }
      );

      if (response.ok) {
        const data = await response.json();
        setConnectionsSuggestions(data.candidates || []);
        setShowConnectionsSuggestions(true);
      }
    } catch (error) {
      console.error('Error searching candidates:', error);
    }
  };

  /**
   * Fetch connections for a specific candidate
   */
  const fetchConnectionsForFilter = async (
    candidateId: number,
    candidateName: string,
    candidateCompany?: string,
    previousCompanies?: string[]  // Previous companies the candidate worked at
  ) => {
    try {
      console.log('🔍 Fetching connections for:', candidateId, candidateName);
      const headers = getAuthHeaders();
      const response = await fetch(`/api/connections/${candidateId}`, {headers, cache: 'no-store'});

      console.log('📡 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Connections data:', data);
        const connectionIds = data.connections.map((conn: any) => conn.candidateId);
        console.log('👥 Connection IDs:', connectionIds);

        // Build maps of candidateId -> company and candidateId -> position at that company
        const companyMap: Record<number, string> = {};
        const positionMap: Record<number, string> = {};
        data.connections.forEach((conn: any) => {
          companyMap[conn.candidateId] = conn.company;
          if (conn.connectedPosition) {
            positionMap[conn.candidateId] = conn.connectedPosition;
          }
        });

        // In server-side mode, we need to fetch the full candidate data for all connections
        // because we only have page 1 loaded in memory
        if (serverPagination.enabled && selectedProject?.id && connectionIds.length > 0) {
          console.log('🔄 Server-side mode: fetching full candidate data for connections');
          const byIdsResponse = await fetch(`/api/candidates/${selectedProject.id}/by-ids`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ candidateIds: connectionIds })
          });

          if (byIdsResponse.ok) {
            const byIdsData = await byIdsResponse.json();
            console.log('✅ Fetched connected candidates:', byIdsData.candidates.length);

            // Set the connected candidates directly as fullCandidates for display
            // This bypasses the normal server pagination
            setFullCandidates(byIdsData.candidates);
            setCandidates(byIdsData.candidates);

            // Calculate unique company count from connected candidates' CURRENT companies
            // Exclude the selected candidate's company (they want to see where to market connections)
            const uniqueCompanies = new Set(
              byIdsData.candidates
                .map((c: any) => c.company || c.current_company)
                .filter(Boolean)
            );
            // Remove selected candidate's company from count if present
            if (candidateCompany) {
              uniqueCompanies.delete(candidateCompany);
            }
            const connectedCompanyCount = uniqueCompanies.size;

            // Update pagination to reflect connection count (single page)
            setServerPagination(prev => ({
              ...prev,
              total: byIdsData.candidates.length,
              totalPages: Math.ceil(byIdsData.candidates.length / rowLimit),
              hasNext: byIdsData.candidates.length > rowLimit,
              hasPrev: false
            }));
            setCurrentPage(1);

            // Update server totals for ALL tabs (cross-filtering)
            setServerTotals(prev => ({
              ...prev,
              candidates: byIdsData.candidates.length,
              companies: connectedCompanyCount
            }));
          }
        }

        setConnectedCandidateIds(connectionIds);
        setConnectionCompanyMap(companyMap);
        setConnectionPositionMap(positionMap);
        setSelectedConnectionsFilter({
          id: candidateId,
          name: candidateName,
          company: candidateCompany,
          previousCompanies: previousCompanies || []
        });
        setConnectionsFilterQuery(candidateName);
        setShowConnectionsSuggestions(false);
        setExcludePreviousCompanies(false); // Reset toggle when selecting new candidate
        setHiddenConnectionCompanies(new Set()); // Reset hidden companies
      } else {
        const errorData = await response.json();
        console.error('❌ Backend error:', errorData);
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
    }
  };

  /**
   * Clear connections filter
   */
  const clearConnectionsFilter = async () => {
    setSelectedConnectionsFilter(null);
    setConnectedCandidateIds([]);
    setConnectionCompanyMap({});
    setConnectionPositionMap({});
    setConnectionsFilterQuery('');
    setConnectionsSuggestions([]);
    setShowConnectionsSuggestions(false);
    setExcludePreviousCompanies(false);

    // In server-side mode, we need to refetch the original paginated data
    // since we replaced fullCandidates with just the connected candidates
    if (serverPagination.enabled && selectedProject?.id) {
      console.log('🔄 Server-side mode: refetching original candidate data');
      setPageLoading(true);
      try {
        const sortColumn = sortConfig.column && typeof sortConfig.column === 'string' ? sortConfig.column : 'name';
        const result = await fetchPaginatedData('candidates', 1, rowLimit, sortColumn, sortConfig.direction, effectiveFilterGroups, aiFilterState.employmentTypes);

        if (result) {
          setFullCandidates(result.data);
          setCandidates(result.data);
          setServerPagination(prev => ({
            ...prev,
            total: result.pagination.total,
            totalPages: result.pagination.totalPages,
            hasNext: result.pagination.hasNext,
            hasPrev: false
          }));
          setCurrentPage(1);

          // Update server totals
          setServerTotals(prev => ({
            ...prev,
            candidates: result.pagination.total
          }));

          // Prefetch page 2
          if (result.pagination.hasNext) {
            prefetchNextPage('candidates', 2, effectiveFilterGroups, aiFilterState.employmentTypes);
          }
        }
      } catch (error) {
        console.error('Error refetching candidates:', error);
      } finally {
        setPageLoading(false);
      }
    }
  };

/**
   * Apply aiFilterState to filter candidates/companies/managers
   * This runs client-side filtering based on filter groups
   */
  const applyClientSideFilters = (): {
    filteredCompanies: Company[],
    filteredCandidates: Candidate[],
    filteredManagers: HiringManager[]
  } => {
    let filteredCandidates = [...fullCandidates];
    let filteredCompanies = [...fullCompanies];
    let filteredManagers = [...fullHiringManagers];

// 1. Apply Employment Type filters (candidates only)
    if (aiFilterState.employmentTypes.length > 0) {
      filteredCandidates = filteredCandidates.filter(candidate => {
        const candidateType = (candidate as any).current_contract_type || (candidate as any).employment_type || '';
        return aiFilterState.employmentTypes.some(type => 
          candidateType.toLowerCase().includes(type.toLowerCase())
        );
      });
    }

    // 2. Apply Filter Groups
    if (aiFilterState.groups.length > 0) {
      // Filter groups are connected with AND logic between them
      for (const group of aiFilterState.groups) {
        if (group.conditions.length === 0) continue;

        if (group.entityType === 'candidate') {
          const beforeCount = filteredCandidates.length;

          // Filter candidates based on this group
          filteredCandidates = filteredCandidates.filter(candidate => {
            // Within a group, conditions use the group's logic (AND/OR)
            if (group.logic === 'OR') {
              // At least ONE condition must match
              const matches = group.conditions.some(condition => {
                return itemMatchesFilterCondition(candidate, {
                  ...condition,
                  columns: [condition.column] // Convert single column to array for compatibility
                });
              });
              return matches;
            } else {
              // ALL conditions must match
              return group.conditions.every(condition => {
                return itemMatchesFilterCondition(candidate, {
                  ...condition,
                  columns: [condition.column]
                });
              });
            }
          });

          console.log(`🔍 Group "${group.name}" (${group.logic}): ${beforeCount} → ${filteredCandidates.length} candidates`);
        } else if (group.entityType === 'company') {
          // Filter companies based on this group
          filteredCompanies = filteredCompanies.filter(company => {
            if (group.logic === 'OR') {
              return group.conditions.some(condition => {
                return itemMatchesFilterCondition(company, {
                  ...condition,
                  columns: [condition.column]
                });
              });
            } else {
              return group.conditions.every(condition => {
                return itemMatchesFilterCondition(company, {
                  ...condition,
                  columns: [condition.column]
                });
              });
            }
          });
        } else if (group.entityType === 'manager') {
          // Filter managers based on this group
          filteredManagers = filteredManagers.filter(manager => {
            if (group.logic === 'OR') {
              return group.conditions.some(condition => {
                return itemMatchesFilterCondition(manager, {
                  ...condition,
                  columns: [condition.column]
                });
              });
            } else {
              return group.conditions.every(condition => {
                return itemMatchesFilterCondition(manager, {
                  ...condition,
                  columns: [condition.column]
                });
              });
            }
          });
        }
      }
    }

    // 3. Apply Connections Filter (candidates only)
    if (selectedConnectionsFilter && connectedCandidateIds.length > 0) {
      filteredCandidates = filteredCandidates.filter(candidate =>
        connectedCandidateIds.includes(candidate.id)
      );
    }

// 4. Connect the three tables after filtering
    // If companies are filtered, only show candidates/managers from those companies
    if (filteredCompanies.length < fullCompanies.length) {
      const companyNames = new Set(filteredCompanies.map(c => c.company_name));

      filteredCandidates = filteredCandidates.filter(cand =>
        companyNames.has(cand.company || '') || companyNames.has((cand as any).current_company || '')
      );

      filteredManagers = filteredManagers.filter(mgr =>
        companyNames.has(mgr.org || '')
      );
    }

    // If candidates are filtered, only show their companies AND then filter managers to match
    if (filteredCandidates.length < fullCandidates.length) {
      const candidateCompanies = new Set(
        filteredCandidates.map(c => c.company || (c as any).current_company).filter(Boolean)
      );

      filteredCompanies = filteredCompanies.filter(comp =>
        candidateCompanies.has(comp.company_name)
      );

      // Also filter managers to match the filtered companies
      filteredManagers = filteredManagers.filter(mgr =>
        candidateCompanies.has(mgr.org || '')
      );
    }

    return {
      filteredCompanies,
      filteredCandidates,
      filteredManagers
    };
  };

// Apply filters whenever aiFilterState or full datasets change
  useEffect(() => {
    // Update isFiltered flag regardless of mode
    const hasFilters = aiFilterState.groups.length > 0 ||
      aiFilterState.employmentTypes.length > 0 ||
      selectedConnectionsFilter !== null;
    setIsFiltered(hasFilters);

    // Skip client-side filtering when server-side pagination is enabled
    // The server already handles filtering in that case
    if (serverPagination.enabled) {
      console.log('🔍 Skipping client-side filters (server-side mode)');
      return;
    }

    console.log('🔍 Applying filters...', {
      filterGroups: aiFilterState.groups.length,
      employmentTypes: aiFilterState.employmentTypes.length,
      connectionsFilter: selectedConnectionsFilter?.name || 'none',
      connectedCandidatesCount: connectedCandidateIds.length
    });

    const { filteredCompanies, filteredCandidates, filteredManagers } = applyClientSideFilters();

    console.log('✅ Filter results:', {
      companies: `${filteredCompanies.length} / ${fullCompanies.length}`,
      candidates: `${filteredCandidates.length} / ${fullCandidates.length}`,
      managers: `${filteredManagers.length} / ${fullHiringManagers.length}`
    });

    setCompanies(filteredCompanies);
    setCandidates(filteredCandidates);
    setHiringManagers(filteredManagers);
  }, [aiFilterState, fullCompanies, fullCandidates, fullHiringManagers, selectedConnectionsFilter, connectedCandidateIds, serverPagination.enabled]);

  // Calculate match data when filters, search term, or display data change
  useEffect(() => {
    const hasFilters = aiFilterState && aiFilterState.groups.length > 0;
    const hasSearch = searchTerm && searchTerm.trim().length > 0;

    if (!hasFilters && !hasSearch) {
      setMatchData(new Map());
      return;
    }

    const newMatchData = new Map();
    const data: any[] = activeDataType === 'companies' ? companies : activeDataType === 'candidates' ? candidates : hiringManagers;

    data.forEach(row => {
      const matches = analyzeMatches(row, aiFilterState, searchTerm);
      // Include row if ANY terms were matched (in primary OR secondary columns)
      // This ensures highlighting works even if the term appears in a different column than filtered
      if (matches.terms.size > 0 || matches.columns.size > 0 || matches.secondaryColumns.size > 0) {
        newMatchData.set(row.id, matches);
      }
    });

    setMatchData(newMatchData);
  }, [companies, candidates, hiringManagers, aiFilterState, activeDataType, searchTerm]);

// Fetch custom columns for the selected project
useEffect(() => {
  const fetchCustomColumns = async () => {
    if (!selectedProject?.id) return;
    
    try {
      const response = await fetch(`/api/custom-columns/${selectedProject.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const cols = await response.json();
        setCustomColumns(cols);
        console.log('Loaded custom columns:', cols);
      }
      // When embedded as a campaign Leads List, also load the campaign's
      // AI columns so they render alongside project columns.
      if (campaignScopeId) {
        const campRes = await fetch(`/api/campaigns/${campaignScopeId}/columns`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (campRes.ok) setCampaignColumns(await campRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch custom columns:', error);
    }
  };

  fetchCustomColumns();
}, [selectedProject?.id, campaignScopeId]);

// Load per-column header colours when project or tab changes
useEffect(() => {
  if (!selectedProject?.id) { setColumnColors({}); return; }
  const saved = localStorage.getItem(`columnColors_${selectedProject.id}_${activeDataType}`);
  if (!saved) { setColumnColors({}); return; }
  try {
    const parsed = JSON.parse(saved);
    setColumnColors(parsed && typeof parsed === 'object' ? parsed : {});
  } catch {
    setColumnColors({});
  }
}, [selectedProject?.id, activeDataType]);

// On entering a project, reconnect to any AI-column job still running (so the
// progress pill + cell spinners resume after navigating away and back).
useEffect(() => {
  if (!selectedProject?.id) { setActiveAiJob(null); return; }
  // Campaign embed: no campaign jobs-list endpoint, and project jobs aren't ours
  // to resume here. A running campaign job still fills in live via the poll while
  // the tab is open; we just don't reconnect its pill after a remount.
  if (campaignScopeId) return;
  if (IS_RECMAPPER) return; // no AI columns in RecMapper
  let cancelled = false;
  (async () => {
    try {
      const res = await fetch(`/api/ai-columns/${selectedProject.id}/jobs`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) return;
      const jobs = await res.json();
      if (Array.isArray(jobs) && !cancelled) {
        setAiColumnKeys(new Set(jobs.map((j: any) => j.column_key).filter(Boolean)));
      }
      const running = Array.isArray(jobs) && jobs.find((j: any) => ['pending', 'running'].includes(j.status));
      if (running && !cancelled) {
        setActiveAiJob({
          id: running.id, columnKey: running.column_key, columnLabel: running.column_label,
          status: running.status, total_rows: running.total_rows, processed_rows: running.processed_rows,
          failed_rows: running.failed_rows, credits_charged: running.credits_charged,
        });
      }
    } catch { /* non-fatal */ }
  })();
  return () => { cancelled = true; };
}, [selectedProject?.id, campaignScopeId]);

// Poll a running AI-column job: update progress + reload data so cells fill in
// live. Runs regardless of whether the creation modal is open.
useEffect(() => {
  if (!activeAiJob || !selectedProject?.id) return;
  if (!['pending', 'running'].includes(activeAiJob.status)) return;
  let tick = 0;
  const iv = setInterval(async () => {
    try {
      const jobUrl = campaignScopeId
        ? `/api/campaigns/${campaignScopeId}/ai-columns/jobs/${activeAiJob.id}`
        : `/api/ai-columns/${selectedProject.id}/jobs/${activeAiJob.id}`;
      const res = await fetch(jobUrl, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setActiveAiJob((prev: any) => prev ? {
        ...prev, status: data.status, total_rows: data.total_rows,
        processed_rows: data.processed_rows, failed_rows: data.failed_rows,
        credits_charged: data.credits_charged, error: data.error,
      } : prev);
      const terminal = ['completed', 'failed', 'cancelled'].includes(data.status);
      // Reload data every other tick (~6s) so new cell values appear, and once
      // more on completion.
      tick++;
      if (tick % 2 === 0 || terminal) await reloadAfterAiColumn();
      if (terminal) { clearInterval(iv); setAiEnrichingRowIds(new Set()); }
    } catch { /* keep polling */ }
  }, 3000);
  return () => clearInterval(iv);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeAiJob?.id, activeAiJob?.status, selectedProject?.id, campaignScopeId]);

// Outside-click closing is handled by the full-screen backdrop rendered with
// the menu portal. A document-level listener would fire on in-menu clicks
// (e.g. picking a colour swatch, which should keep the menu open), so we rely
// on the backdrop instead.

const handleRenameCustomColumn = async () => {
  if (!renameModal || !renameValue.trim() || !selectedProject?.id) return;
  try {
    const response = await fetch(`/api/custom-columns/${selectedProject.id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        columnKey: renameModal.columnKey,
        columnLabel: renameValue.trim(),
        columnType: customColumns.find(c => c.column_key === renameModal.columnKey)?.column_type || 'text'
      })
    });
    if (response.ok) {
      setCustomColumns(prev => prev.map(c =>
        c.column_key === renameModal.columnKey ? { ...c, column_label: renameValue.trim() } : c
      ));
    }
  } catch (error) {
    console.error('Failed to rename column:', error);
  }
  setRenameModal(null);
  setRenameValue('');
};

// Sort a column from the header menu (mirrors the header-click sort logic
// but with an explicit direction).
const handleMenuSort = (col: { key: string; type?: string; isCustom?: boolean }, direction: 'asc' | 'desc') => {
  const colGroupKey = (col as any).groupKey as string | null;
  const isGroupCollapsedNow = !!colGroupKey && collapsedColumnGroups.has(colGroupKey);
  const sortKey = isGroupCollapsedNow && colGroupKey
    ? `custom:${colGroupKey}__score`
    : (col.isCustom ? `custom:${col.key}` : col.key);
  handleApplySort(sortKey, direction);
  setCustomColMenu(null);
  setCustomColMenuPos(null);
};

// "Filter on this column" — prompt for a value and add a contains-filter
// Toggle "only cache-added companies": clicking a ⚡ Cache badge filters the
// companies tab to rows populated from the company cache, so they can be
// reviewed (and removed) as a set. Click any badge again to clear.
const CACHE_FILTER_GROUP_ID = 'cache-added-companies';
const cacheFilterActive = aiFilterState.groups.some((g: any) => g.id === CACHE_FILTER_GROUP_ID);
const toggleCacheFilter = () => {
  pushToHistory(cacheFilterActive ? 'Clear cache filter' : 'Filter: added from cache');
  setAiFilterState({
    groups: cacheFilterActive
      ? aiFilterState.groups.filter((g: any) => g.id !== CACHE_FILTER_GROUP_ID)
      : [...aiFilterState.groups, {
          id: CACHE_FILTER_GROUP_ID,
          name: '⚡ Added from cache',
          logic: 'AND' as const,
          entityType: 'company' as const,
          conditions: [{
            id: `${CACHE_FILTER_GROUP_ID}-c`,
            column: 'cached_from_project_id',
            operator: 'is_not_empty' as const,
            value: '',
            entityType: 'company' as const,
          }],
        }],
    employmentTypes: aiFilterState.employmentTypes,
  });
  setCurrentPage(1);
};

// group scoped to this column on the current tab.
const handleFilterOnColumn = (col: { key: string; label: string }) => {
  setCustomColMenu(null);
  setCustomColMenuPos(null);
  const entityType: 'candidate' | 'company' | 'manager' =
    activeDataType === 'candidates' ? 'candidate'
    : activeDataType === 'managers' ? 'manager'
    : 'company';
  const value = window.prompt(`Filter "${col.label}" contains:`, '');
  if (value === null) return; // cancelled
  const trimmed = value.trim();
  if (!trimmed) return;
  const group: FilterGroup = {
    id: `col-${Date.now()}`,
    name: `${col.label}: ${trimmed}`,
    logic: 'AND' as const,
    entityType,
    conditions: [{
      id: Date.now().toString(),
      column: col.key,
      operator: 'contains' as const,
      value: trimmed,
      entityType
    }]
  };
  pushToHistory(`Filter: ${col.label}`);
  setAiFilterState({
    groups: [...aiFilterState.groups, group],
    employmentTypes: aiFilterState.employmentTypes
  });
  setCurrentPage(1);
};

// Snapshot the current view's scope so an AI-column run targets exactly the
// rows the user is looking at (same filters/search/associations/radius).
const buildAiColumnScope = () => ({
  filterGroups: aiFilterState.groups || [],
  employmentTypes: aiFilterState.employmentTypes || [],
  searchTerm: searchTerm || null,
  associatedCompany: (activeDataType === 'candidates' || activeDataType === 'managers')
    ? (associatedCompanyFilter || null) : null,
  radius: radiusCoords
    ? (radiusType === 'candidate'
        ? { candidateLat: radiusCoords.lat, candidateLng: radiusCoords.lng, candidateDistance: radiusDistance }
        : { companyLat: radiusCoords.lat, companyLng: radiusCoords.lng, companyDistance: radiusDistance })
    : null,
});

// After an AI column finishes (or as it progresses), refetch the custom-column
// definitions and reload the current page so the new column + values appear.
const reloadAfterAiColumn = async () => {
  if (!selectedProject?.id) return;
  try {
    const colRes = await fetch(`/api/custom-columns/${selectedProject.id}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (colRes.ok) setCustomColumns(await colRes.json());
    // Campaign embed: the new column is a campaign column — refetch those too.
    if (campaignScopeId) {
      const campRes = await fetch(`/api/campaigns/${campaignScopeId}/columns`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (campRes.ok) setCampaignColumns(await campRes.json());
    }
    const effectiveFilterGroups = aiFilterState.groups || [];
    const sortColumn = sortConfig.column || (activeDataType === 'companies' ? 'candidate_count' : 'name');
    const result = await fetchPaginatedData(
      activeDataType, currentPage, rowLimit, sortColumn, sortConfig.direction,
      effectiveFilterGroups, aiFilterState.employmentTypes
    );
    if (result) {
      if (activeDataType === 'candidates') setCandidates(result.data);
      else if (activeDataType === 'companies') setCompanies(result.data);
      else setHiringManagers(result.data);
    }
  } catch {
    // non-fatal
  }
};

// Per-cell ▶: run one row through the column's saved recipe, synchronously,
// and drop the value straight into the cell. (Project data table only for now.)
const runSingleCell = async (colKey: string, rowId: number) => {
  const cellId = `${colKey}:${rowId}`;
  if (runningCells.has(cellId)) return;
  setRunningCells(prev => new Set(prev).add(cellId));
  try {
    const res = await fetch(`/api/ai-columns/${selectedProject.id}/cell/run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ columnKey: colKey, rowId }),
    });
    const data = await res.json();
    if (!res.ok) { alert(data.error || 'Failed to run this cell'); return; }
    if (data.result) {
      const patch = (r: any) => r.id === rowId
        ? { ...r, custom_data: { ...(r.custom_data || {}), [colKey]: data.result.value, [`${colKey}__reasoning`]: data.result.reasoning } }
        : r;
      if (activeDataType === 'candidates') setCandidates(prev => prev.map(patch));
      else if (activeDataType === 'companies') setCompanies(prev => prev.map(patch));
      else setHiringManagers(prev => prev.map(patch));
    }
  } catch { alert('Failed to run this cell'); }
  finally { setRunningCells(prev => { const n = new Set(prev); n.delete(cellId); return n; }); }
};

// Column-header ▶: run the saved recipe over blank cells ('empty') or every row
// ('all', overwrites). Reuses the background job + progress pill.
const runColumn = async (colKey: string, colLabel: string, mode: 'empty' | 'all') => {
  setHeaderRunMenu(null);
  const what = mode === 'empty' ? 'blank cells only' : 'ALL cells (overwrites existing values)';
  if (!window.confirm(`Run “${colLabel}” on ${what}? This uses credits, one per row processed.`)) return;
  try {
    const res = await fetch(`/api/ai-columns/${selectedProject.id}/column/run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ columnKey: colKey, mode }),
    });
    const data = await res.json();
    if (!res.ok) { alert(data.error || 'Failed to start run'); return; }
    setAiColumnAnchorKey(colKey);
    handleAiJobStarted({ id: data.jobId, columnKey: data.columnKey, columnLabel: data.columnLabel || colLabel, total: 0 });
  } catch { alert('Failed to start run'); }
};

// The ▶ / spinner affordance shown on hover inside an AI-column cell. Only for
// real AI columns (aiColumnKeys) on the project data table (not campaign yet).
const renderCellRunBtn = (colKey: string, rowId: number) => {
  if (campaignScopeId || !aiColumnKeys.has(colKey)) return null;
  const running = runningCells.has(`${colKey}:${rowId}`);
  return (
    <button
      onClick={(e) => { e.stopPropagation(); runSingleCell(colKey, rowId); }}
      disabled={running}
      title="Run AI for this cell"
      className={`absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-5 h-5 rounded text-violet-600 hover:bg-violet-100 ${running ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
    >
      {running
        ? <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"/></svg>
        : <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>}
    </button>
  );
};

// Modal hands off the started job here; we own progress from now on.
const handleAiJobStarted = (j: { id: number; columnKey: string; columnLabel: string; total: number }) => {
  setActiveAiJob({
    id: j.id, columnKey: j.columnKey, columnLabel: j.columnLabel, status: 'pending',
    total_rows: j.total, processed_rows: 0, failed_rows: 0, credits_charged: 0,
  });
  setAiColumnKeys(prev => new Set(prev).add(j.columnKey)); // eligible for re-run immediately
  // Re-run: the in-scope rows already have values, so mark the ones currently
  // in view as "refreshing" (dimmed + spinner) until the run overwrites them.
  if (aiReRunColumnKey === j.columnKey) {
    setAiEnrichingRowIds(new Set((displayDataRef.current || []).map((r: any) => r.id)));
  } else {
    setAiEnrichingRowIds(new Set());
  }
  // Position the new column to the right of the one its menu was launched from.
  setAiColumnReposition({ key: j.columnKey, anchor: aiColumnAnchorKey });
  reloadAfterAiColumn(); // pull in the (empty) column so its spinner cells render
};

// Once the new column has appeared in columnOrder, move it next to its anchor
// column — just after it ("insert right", Clay-style) or just before it
// ("insert left") — then clear the request.
useEffect(() => {
  if (!aiColumnReposition) return;
  const { key, anchor, side = 'right' } = aiColumnReposition;
  if (!columnOrder.includes(key)) return; // hasn't appeared yet — wait
  if (!anchor) { setAiColumnReposition(null); return; } // no anchor — leave at end
  const anchorIdx = columnOrder.indexOf(anchor);
  if (anchorIdx === -1) { setAiColumnReposition(null); return; } // anchor gone — leave at end
  const desiredIdx = side === 'left' ? anchorIdx : anchorIdx + 1;
  if (columnOrder.indexOf(key) === desiredIdx) { setAiColumnReposition(null); return; } // already placed
  const next = columnOrder.filter(k => k !== key);
  // Recompute the anchor index after removing `key` (it may sit before the anchor).
  const anchorAfter = next.indexOf(anchor);
  next.splice(side === 'left' ? anchorAfter : anchorAfter + 1, 0, key);
  setColumnOrder(next);
  if (selectedProject?.id) saveColumnOrder(next);
  setAiColumnReposition(null);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [columnOrder, aiColumnReposition]);

const cancelActiveAiJob = async () => {
  if (!activeAiJob || !selectedProject?.id) return;
  try {
    // Campaign AI-column jobs have no cancel endpoint yet — dismiss the pill
    // locally (the background run finishes on its own).
    if (!campaignScopeId) {
      await fetch(`/api/ai-columns/${selectedProject.id}/jobs/${activeAiJob.id}/cancel`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
    }
  } catch { /* non-fatal */ }
  setActiveAiJob((prev: any) => prev ? { ...prev, status: 'cancelled' } : prev);
};

// On-demand written explanation for one AI cell (judgment-engine columns store
// a structural confidence line as reasoning; this replaces it with prose).
const writeCellExplanation = async () => {
  if (!selectedProject?.id || !aiReasoningModal?.rowId || explainBusy) return;
  const { columnKey, rowId } = aiReasoningModal;
  setExplainBusy(true);
  try {
    const res = await fetch(`/api/ai-columns/${selectedProject.id}/cell/explain`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ columnKey, rowId }),
    });
    const data = await res.json();
    if (!res.ok) { alert(data.error || 'Failed to write the explanation'); return; }
    setAiReasoningModal(prev => prev ? { ...prev, reasoning: data.reasoning } : prev);
    const patch = (r: any) => r.id === rowId
      ? { ...r, custom_data: { ...(r.custom_data || {}), [`${columnKey}__reasoning`]: data.reasoning } }
      : r;
    if (activeDataType === 'candidates') setCandidates(prev => prev.map(patch));
    else if (activeDataType === 'companies') setCompanies(prev => prev.map(patch));
    else setHiringManagers(prev => prev.map(patch));
  } catch { alert('Failed to write the explanation'); }
  finally { setExplainBusy(false); }
};

// Materialise an AI column's stored reasoning as its own visible column.
// The reasoning already lives in custom_data under `${key}__reasoning`, so we
// just register that key as a real column (no data copy, no cost) and drop it
// to the right of the AI column.
const materializeReasoningColumn = async (aiColKey: string, baseLabel: string) => {
  if (!selectedProject?.id) return;
  const reasoningKey = `${aiColKey}__reasoning`;
  try {
    await fetch(`/api/custom-columns/${selectedProject.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ columnKey: reasoningKey, columnLabel: `${baseLabel} — why`, columnType: 'text' }),
    });
    const colRes = await fetch(`/api/custom-columns/${selectedProject.id}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (colRes.ok) setCustomColumns(await colRes.json());
    setAiColumnReposition({ key: reasoningKey, anchor: aiColKey });
  } catch { /* non-fatal */ }
  setAiReasoningModal(null);
};

// Open the AI-column modal in re-run mode for an existing AI column: fetch its
// last config, prefill, and target the same column. Combined with the current
// filter, this re-runs a (possibly edited) prompt on just the filtered subset.
const openReRunAiColumn = async (colKey: string) => {
  if (!selectedProject?.id) return;
  setCustomColMenu(null);
  setCustomColMenuPos(null);
  try {
    const res = await fetch(`/api/ai-columns/${selectedProject.id}/columns/${encodeURIComponent(colKey)}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (!res.ok) return;
    const cfg = await res.json();
    setAiReRunInitialSpec(cfg);
    setAiReRunColumnKey(colKey);
    setAiColumnAnchorKey(null); // same column — no reposition
    setAiColumnModalOpen(true);
  } catch { /* non-fatal */ }
};

// Render for an empty custom cell that's actively being filled: violet
// "AI enriching" spinner for an AI-column job, blue for Ada, else a dash.
const renderPendingCell = (adaBusy: boolean, aiPending: boolean) => {
  if (aiPending) {
    return (
      <span className="inline-flex items-center gap-1 text-violet-500 text-[11px]">
        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
        </svg>
        AI enriching
      </span>
    );
  }
  if (adaBusy) {
    return (
      <span className="inline-flex items-center gap-1 text-blue-400 text-xs">
        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
        </svg>
      </span>
    );
  }
  return <>-</>;
};

const handleDeleteCustomColumn = async (columnKey: string, columnLabel: string) => {
  if (!selectedProject?.id) return;
  if (!window.confirm(`Delete column "${columnLabel}"? The column will be removed from the table. Data in existing records is preserved.`)) return;
  try {
    const response = await fetch(`/api/custom-columns/${selectedProject.id}/${columnKey}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (response.ok) {
      setCustomColumns(prev => prev.filter(c => c.column_key !== columnKey));
    }
  } catch (error) {
    console.error('Failed to delete column:', error);
  }
  setCustomColMenu(null);
};

// ============================================================================
  // DISPLAY DATA PREPARATION (Sorting & Pagination)
  // ============================================================================
  
  useEffect(() => {
    // Companies are already deduplicated by the backend (DISTINCT ON company_name)
    let data: any[] = activeDataType === 'companies' ? [...companies] : activeDataType === 'candidates' ? [...candidates] : [...hiringManagers];

    // Filter out previous employers when toggle is checked (companies view only)
    if (activeDataType === 'companies' && excludePreviousCompanies && selectedConnectionsFilter?.previousCompanies?.length) {
      const normalizedPreviousCompanies = selectedConnectionsFilter.previousCompanies.map(
        c => c.toLowerCase().replace(/[™®©]/g, '').trim()
      );
      data = data.filter(company => {
        const normalizedName = (company.company_name || '').toLowerCase().replace(/[™®©]/g, '').trim();
        return !normalizedPreviousCompanies.includes(normalizedName);
      });
    }

    // Filter out candidates/companies hidden via eye icon on "Worked Together At"
    if (hiddenConnectionCompanies.size > 0 && selectedConnectionsFilter) {
      if (activeDataType === 'candidates') {
        data = data.filter((candidate: any) => {
          const company = connectionCompanyMap[candidate.id];
          return !company || !hiddenConnectionCompanies.has(company.toLowerCase());
        });
      } else if (activeDataType === 'companies') {
        data = data.filter((company: any) => {
          const normalizedName = (company.company_name || '').toLowerCase();
          return !hiddenConnectionCompanies.has(normalizedName);
        });
      }
    }

    // Only apply client-side sorting when NOT using server-side pagination
    // Server already returns data in the correct sort order
    if (!serverPagination.enabled) {
    // Apply sorting
    if (sortConfig.column) {
      data.sort((a, b) => {
        let aVal, bVal;

        if (sortConfig.column === 'candidate_count' && activeDataType === 'companies') {
          const countsA = companyCandidateCounts.get(a.id);
          const countsB = companyCandidateCounts.get(b.id);
          // Sort by filtered count when filters are active, otherwise by total.
          // Radius counts as an active filter — the backend already applies it.
          const hasRadiusFilter = !!(radiusCoords && radiusDistance > 0) ||
            !!companyOfficeRadius.coords || !!candidateLocationRadius.coords || !!managerLocationRadius.coords;
          const hasActiveFilters = aiFilterState.employmentTypes.length > 0
            || aiFilterState.groups.length > 0
            || hasRadiusFilter;
          aVal = hasActiveFilters ? (countsA?.filtered || 0) : (countsA?.total || 0);
          bVal = hasActiveFilters ? (countsB?.filtered || 0) : (countsB?.total || 0);
        } else if (sortConfig.column.startsWith('custom:')) {
          // Custom data JSONB field sorting
          const customKey = sortConfig.column.substring(7);
          const rawA = (a as any).custom_data?.[customKey];
          const rawB = (b as any).custom_data?.[customKey];
          // Convert criteria verdicts to sortable numbers, or parse numeric values
          const toSortVal = (v: any) => {
            if (v === null || v === undefined) return Infinity;
            const s = String(v);
            if (s.startsWith('Match:')) return 1;
            if (s.startsWith('Unclear:')) return 2;
            if (s.startsWith('Miss:')) return 3;
            const n = parseFloat(s);
            return isNaN(n) ? s : n;
          };
          aVal = toSortVal(rawA);
          bVal = toSortVal(rawB);
        } else if (sortConfig.column === 'years_at_current_company') {
          aVal = liveTenureYears(a);
          bVal = liveTenureYears(b);
        } else {
          aVal = (a as any)[sortConfig.column];
          bVal = (b as any)[sortConfig.column];
        }

        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        let comparison = 0;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          comparison = aVal - bVal;
        } else {
          comparison = String(aVal).localeCompare(String(bVal));
        }

        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }


    // Apply match-based sorting when filters are active
    if (matchData.size > 0) {
      data.sort((a, b) => {
        const matchA = matchData.get(a.id)?.count || 0;
        const matchB = matchData.get(b.id)?.count || 0;
        if (matchA !== matchB) {
          return matchB - matchA; // Most matches first
        }
        // Fall back to existing sort if match counts are equal
        return 0;
      });
    }

    // Apply match score sorting when in job spec match mode with candidates
    if (isJobSpecMatchMode && activeDataType === 'candidates' && Object.keys(candidateMatchScores).length > 0) {
      data.sort((a, b) => {
        const scoreA = candidateMatchScores[a.id] ?? 0;
        const scoreB = candidateMatchScores[b.id] ?? 0;
        // Sort by match score descending (highest scores first)
        return scoreB - scoreA;
      });
    }
    } // end of !serverPagination.enabled

    // Apply pagination - skip if server-side pagination is enabled (data is already paginated)
    if (serverPagination.enabled) {
      // Server already gave us the correct page of data
      setDisplayData(data);
      displayDataRef.current = data;
    } else {
      // Client-side pagination (anchored at startRow from the row-range control)
      const start = startRow + (currentPage - 1) * rowLimit;
      const paginatedData = data.slice(start, start + rowLimit);
      setDisplayData(paginatedData);
      displayDataRef.current = paginatedData;
    }
  }, [companies, candidates, hiringManagers, activeDataType, sortConfig, currentPage, rowLimit, startRow, companyCandidateCounts, matchData, aiFilterState, serverPagination.enabled, excludePreviousCompanies, selectedConnectionsFilter, isJobSpecMatchMode, candidateMatchScores, hiddenConnectionCompanies, connectionCompanyMap]);

// Sync top scrollbar width with table on data changes
useEffect(() => {
  const updateScrollbarWidth = () => {
    const mainContainer = document.getElementById('main-table-container');
    const topContent = document.getElementById('top-scrollbar-content');
    const table = mainContainer?.querySelector('table');
    
    if (table && topContent) {
      topContent.style.width = `${table.scrollWidth}px`;
    }
  };
  
  // Update on mount and whenever display data changes
  setTimeout(updateScrollbarWidth, 100);
}, [displayData, visibleColumns]);

  // Fetch candidate counts for companies (with filter support)
  useEffect(() => {
    const fetchCandidateCounts = async () => {
      if (activeDataType !== 'companies') return;

      try {
        const hasFilters = aiFilterState.employmentTypes.length > 0 || aiFilterState.groups.length > 0;
        const hasRadiusFilter = radiusCoords && radiusDistance > 0;
        const hasDualRadius = companyOfficeRadius.coords || candidateLocationRadius.coords;

        // Build filter params if filters are applied
        const params = new URLSearchParams();

        if (aiFilterState.employmentTypes.length > 0) {
          params.append('employmentTypes', aiFilterState.employmentTypes.join(','));
        }

        if (effectiveFilterGroups.length > 0) {
          params.append('filterGroups', JSON.stringify(effectiveFilterGroups));
        }

        // Include radius filter params for accurate counts
        if (hasDualRadius) {
          if (companyOfficeRadius.coords) {
            params.append('companyRadiusLat', companyOfficeRadius.coords.lat.toString());
            params.append('companyRadiusLng', companyOfficeRadius.coords.lng.toString());
            params.append('companyRadiusDistance', companyOfficeRadius.distance.toString());
          }
          if (candidateLocationRadius.coords) {
            params.append('candidateRadiusLat', candidateLocationRadius.coords.lat.toString());
            params.append('candidateRadiusLng', candidateLocationRadius.coords.lng.toString());
            params.append('candidateRadiusDistance', candidateLocationRadius.distance.toString());
          }
        } else if (hasRadiusFilter) {
          params.append('radiusLat', radiusCoords!.lat.toString());
          params.append('radiusLng', radiusCoords!.lng.toString());
          params.append('radiusDistance', radiusDistance.toString());
          params.append('radiusType', radiusType);
        }

        // POST — filterGroups can exceed nginx's request-URI limit as a query string
        const response = await fetch(`/api/companies/${selectedProject.id}/candidate-counts`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(paramsToObject(params))
        });

        if (response.ok) {
          const data = await response.json();
          const counts = new Map<number, {total: number, filtered: number}>();

          Object.keys(data).forEach(companyId => {
            counts.set(parseInt(companyId), data[companyId]);
          });

          setCompanyCandidateCounts(counts);
        }
      } catch (error) {
        console.error('Error fetching candidate counts:', error);
      }
    };

    fetchCandidateCounts();
  }, [activeDataType, selectedProject.id, aiFilterState, radiusCoords, radiusDistance, radiusType, companyOfficeRadius.coords, candidateLocationRadius.coords]);

  // Calculate hiring manager counts for companies (client-side)
  useEffect(() => {
    if (activeDataType !== 'companies' || !hiringManagers.length) return;

    const counts = new Map<number, {total: number, filtered: number}>();

    // Group managers by company
    companies.forEach(company => {
      const managersInCompany = hiringManagers.filter(mgr => mgr.org === company.company_name);
      const filteredManagersInCompany = hiringManagers.filter(mgr => mgr.org === company.company_name); // Same for now, can be filtered later

      if (managersInCompany.length > 0) {
        counts.set(company.id, {
          total: managersInCompany.length,
          filtered: filteredManagersInCompany.length
        });
      }
    });

    setCompanyManagerCounts(counts);
  }, [activeDataType, companies, hiringManagers, fullHiringManagers]);

  // Detect when table headers scroll out of view
  useEffect(() => {
    const handleScroll = () => {
      const tableContainer = document.getElementById('main-table-container');
      if (tableContainer) {
        // Show sticky headers when scrolled down more than 50px
        setShowStickyHeaders(tableContainer.scrollTop > 50);
      }
    };

    const tableContainer = document.getElementById('main-table-container');
    if (tableContainer) {
      tableContainer.addEventListener('scroll', handleScroll);
      return () => tableContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Sync temp radius search state when panel opens
  useEffect(() => {
    if (showRadiusSearch) {
      // Sync new dual-filter temp states
      setTempCompanyOfficeRadius({
        location: companyOfficeRadius.location,
        distance: companyOfficeRadius.distance
      });
      setTempCandidateLocationRadius({
        location: candidateLocationRadius.location,
        distance: candidateLocationRadius.distance
      });
    }
  }, [showRadiusSearch, companyOfficeRadius, candidateLocationRadius]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleToggleColumn = (columnKey: string) => {
    setVisibleColumns(prev => 
      prev.includes(columnKey) 
        ? prev.filter(k => k !== columnKey)
        : [...prev, columnKey]
    );
  };

const handleApplyColumns = (newVisibleColumns: string[]) => {
  // Force-include locked columns (the row identifier) — they can't be hidden.
  // Primary columns are NOT force-included anymore: the user's toggle in the
  // selector is the source of truth.
  const allCols: Array<{ key: string; isLocked?: boolean }> = activeDataType === 'companies'
    ? getCompanyColumns()
    : activeDataType === 'managers'
      ? getManagerColumns()
      : getCandidateColumns();
  const lockedKeys = allCols.filter(col => col.isLocked).map(col => col.key);

  const combined = [...lockedKeys, ...newVisibleColumns];
  const allVisible = Array.from(new Set(combined));
  setVisibleColumns(allVisible);
};

const handleAISearch = (
  filterState: FilterState,
  explanation: string,
  canWiden: boolean = false,
  isWidened: boolean = false,
  searchQuery: string = '',
  radiusSearchParam?: { location: string; distance: number; type: 'candidate' | 'company' },
  dualRadiusSearch?: {
    companyRadiusSearch?: { location: string; distance: number };
    candidateRadiusSearch?: { location: string; distance: number };
  }
) => {
  tourEvent('ai-search-applied');
  console.log('AI Search Results:', { filterState, explanation, canWiden, isWidened, radiusSearchParam, dualRadiusSearch });

  // Track activity for admin dashboard
  trackActivity();

  // Auto-switch tabs when the AI clearly targeted a different entity than
  // the one the user is on. Heuristic: if the result has at least one
  // manager-typed group and the user isn't on the managers tab, switch to
  // managers. Mirrors the same intent for candidate/company asymmetries
  // already handled by their tabs' cross-filter logic.
  // We don't switch when the result also has candidate-typed groups —
  // candidates tab can cross-filter via company groups, and a mixed result
  // is most usefully shown on the active tab.
  const hasManagerGroups = filterState.groups.some((g: any) => g.entityType === 'manager');
  const hasNonManagerGroups = filterState.groups.some((g: any) => g.entityType && g.entityType !== 'manager');
  if (hasManagerGroups && !hasNonManagerGroups && activeDataType !== 'managers') {
    console.log('🎯 AI returned manager-only filter groups — switching to managers tab');
    handleDataTypeChange('managers', filterState);
  } else if (!hasManagerGroups && activeDataType === 'managers' && filterState.groups.length > 0) {
    // Inverse case: user on managers tab but AI returned only candidate/
    // company groups. Switch back so they see results on the right surface.
    const onlyCompany = filterState.groups.every((g: any) => g.entityType === 'company');
    handleDataTypeChange(onlyCompany ? 'companies' : 'candidates', filterState);
  }

  // Handle DUAL radius search (both company and candidate)
  if (dualRadiusSearch && (dualRadiusSearch.companyRadiusSearch || dualRadiusSearch.candidateRadiusSearch)) {
    console.log('📍📍 AI detected DUAL radius search:', dualRadiusSearch);

    // Mark that AI radius search is in progress - prevents filter useEffect from overwriting counts
    aiRadiusSearchInProgressRef.current = true;

    // Set the filter state for any non-location filters
    setAiFilterState(filterState);
    setAiExplanation(explanation);

    // Set widen search state
    setCanWidenSearch(canWiden);
    setIsWidenedSearch(isWidened);
    if (searchQuery) {
      setCurrentSearchQuery(searchQuery);
    }

    // Apply both radius filters
    // For dual filter: geocode company location first, then pass coords to candidate search
    // Also pass filter groups if present (e.g., job title filter)
    const dualFilterGroups = filterState.groups.length > 0 ? filterState.groups : undefined;

    if (dualRadiusSearch.companyRadiusSearch && dualRadiusSearch.candidateRadiusSearch) {
      // Both filters present - need to geocode company first, then pass to candidate
      setTempCompanyOfficeRadius({
        location: dualRadiusSearch.companyRadiusSearch.location,
        distance: dualRadiusSearch.companyRadiusSearch.distance
      });
      setTempCandidateLocationRadius({
        location: dualRadiusSearch.candidateRadiusSearch.location,
        distance: dualRadiusSearch.candidateRadiusSearch.distance
      });

      // Geocode company location first to get coords
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      fetch(`/api/companies/geocode?location=${encodeURIComponent(dualRadiusSearch.companyRadiusSearch.location)}`, {
        headers,
        cache: 'no-store'
      })
        .then(response => response.json())
        .then(({ lat: companyLat, lng: companyLng }) => {
          console.log('📍 Geocoded company location:', { companyLat, companyLng });

          // Also set the company office radius state with coords
          setCompanyOfficeRadius({
            location: dualRadiusSearch.companyRadiusSearch!.location,
            distance: dualRadiusSearch.companyRadiusSearch!.distance,
            coords: { lat: companyLat, lng: companyLng }
          });

          // Now apply candidate radius, passing company filter coords as override
          applyRadiusSearch(
            'candidate',
            dualRadiusSearch.candidateRadiusSearch!.location,
            dualRadiusSearch.candidateRadiusSearch!.distance,
            // Pass company filter info so candidate search can combine them
            {
              type: 'company',
              lat: companyLat,
              lng: companyLng,
              distance: dualRadiusSearch.companyRadiusSearch!.distance
            },
            dualFilterGroups
          );
        })
        .catch(err => {
          console.error('Failed to geocode company location for dual search:', err);
          // Fallback: just apply candidate search without company filter
          applyRadiusSearch('candidate', dualRadiusSearch.candidateRadiusSearch!.location, dualRadiusSearch.candidateRadiusSearch!.distance, undefined, dualFilterGroups);
        });
    } else if (dualRadiusSearch.companyRadiusSearch) {
      // Only company radius
      setTempCompanyOfficeRadius({
        location: dualRadiusSearch.companyRadiusSearch.location,
        distance: dualRadiusSearch.companyRadiusSearch.distance
      });
      applyRadiusSearch('company', dualRadiusSearch.companyRadiusSearch.location, dualRadiusSearch.companyRadiusSearch.distance, undefined, dualFilterGroups);
    } else if (dualRadiusSearch.candidateRadiusSearch) {
      // Only candidate radius
      setTempCandidateLocationRadius({
        location: dualRadiusSearch.candidateRadiusSearch.location,
        distance: dualRadiusSearch.candidateRadiusSearch.distance
      });
      applyRadiusSearch('candidate', dualRadiusSearch.candidateRadiusSearch.location, dualRadiusSearch.candidateRadiusSearch.distance, undefined, dualFilterGroups);
    }

    setAiSearchLoading(false);
    return; // Early return - applyRadiusSearch handles everything
  }

  // Handle AI-provided single radius search (legacy)
  if (radiusSearchParam) {
    console.log('📍 AI detected radius search:', radiusSearchParam);

    // Mark that AI radius search is in progress - prevents filter useEffect from overwriting counts
    aiRadiusSearchInProgressRef.current = true;

    // Update the legacy radius search state
    setRadiusLocation(radiusSearchParam.location);
    setRadiusDistance(radiusSearchParam.distance);
    setRadiusType(radiusSearchParam.type);

    // Update the new dual-filter state based on type
    if (radiusSearchParam.type === 'company') {
      setTempCompanyOfficeRadius({
        location: radiusSearchParam.location,
        distance: radiusSearchParam.distance
      });
    } else {
      setTempCandidateLocationRadius({
        location: radiusSearchParam.location,
        distance: radiusSearchParam.distance
      });
    }

    // Set the filter state for any non-location filters
    setAiFilterState(filterState);
    setAiExplanation(explanation);

    // Apply the radius search - this handles fetching data and updating counts
    // Don't call applyFilterState as it would race with applyRadiusSearch
    // Pass filter groups directly so applyRadiusSearch can include them in counts
    const hasFilterGroups = filterState.groups.length > 0;
    applyRadiusSearch(
      radiusSearchParam.type,
      radiusSearchParam.location,
      radiusSearchParam.distance,
      undefined, // no other filter override
      hasFilterGroups ? filterState.groups : undefined
    );

    // Set widen search state
    setCanWidenSearch(canWiden);
    setIsWidenedSearch(isWidened);
    if (searchQuery) {
      setCurrentSearchQuery(searchQuery);
    }
    setAiSearchLoading(false);
    return; // Early return - applyRadiusSearch handles everything
  }

  // New AI searches REPLACE the previous filter state. Stacking filters
  // across consecutive searches confuses users — they expect a clean slate.
  // Widening is its own dedicated path that also replaces.
  if (isWidened) {
    console.log('🔍 Replacing filters with widened search');
  } else {
    console.log('📝 Replacing filters with new AI search (clearing any prior radius state)');
    // Wipe any lingering radius filters from a previous search. If this new
    // search carries its own radius, it returned early above and never reaches
    // here, so this only runs when the new search has no location component.
    setRadiusLocation('');
    setRadiusCoords(null);
    setRadiusDistance(25);
    setCompanyOfficeRadius({ location: '', distance: 25, coords: null });
    setCandidateLocationRadius({ location: '', distance: 25, coords: null });
    setManagerLocationRadius({ location: '', distance: 25, coords: null });
    setTempCompanyOfficeRadius({ location: '', distance: 25 });
    setTempCandidateLocationRadius({ location: '', distance: 25 });
    setTempManagerLocationRadius({ location: '', distance: 25 });
  }
  setAiFilterState(filterState);
  setAiExplanation(explanation);
  applyFilterState(filterState);

  // Set widen search state
  setCanWidenSearch(canWiden);
  setIsWidenedSearch(isWidened);
  if (searchQuery) {
    setCurrentSearchQuery(searchQuery);
  }

  setAiSearchLoading(false);
};

// Learn tab — Filters lesson: "Build the example for me" =
// (Current role contains Engineer OR Developer) AND (Role description contains GenAI).
useLessonAction('lesson-filters-example', () => {
  if (activeDataType !== 'candidates') handleDataTypeChange('candidates');
  applyFilterState({
    employmentTypes: [],
    groups: [
      { id: 'lesson-g1', name: 'Engineer or developer', logic: 'OR', entityType: 'candidate', conditions: [
        { id: 'lesson-c1', column: 'current_position', operator: 'contains', value: 'Engineer', entityType: 'candidate' },
        { id: 'lesson-c2', column: 'current_position', operator: 'contains', value: 'Developer', entityType: 'candidate' },
      ] },
      { id: 'lesson-g2', name: 'GenAI in their role', logic: 'OR', entityType: 'candidate', conditions: [
        { id: 'lesson-c3', column: 'current_position_description', operator: 'contains', value: 'GenAI', entityType: 'candidate' },
      ] },
    ],
  } as FilterState);
});

const applyFilterState = (filterState: FilterState) => {
  console.log('📝 Setting filter state:', filterState);
  tourEvent('filters-applied');

  // In job spec match mode, update preferred filters for scoring
  // The separation of required vs preferred happens when we fetch data
  if (isJobSpecMatchMode && filterState.groups.length > 0) {
    const preferredFilters = filterState.groups.filter(g => g.priority === 'preferred');
    setPreferredFilterGroups(preferredFilters);
    console.log('📋 Job Spec Mode - Updated preferred filters for scoring:', preferredFilters.length);
  }

  // Store ALL filters for display in the UI
  setAiFilterState(filterState);
  // The useEffect watching aiFilterState will handle the actual filtering
  // It will filter out preferred groups when sending to backend
};

const handleClearAIFilters = () => {
  const emptyState: FilterState = {
    groups: [],
    employmentTypes: [],
  };
  setAiFilterState(emptyState);
  setAiExplanation(null);
  setCanWidenSearch(false);
  setIsWidenedSearch(false);
  setCurrentSearchQuery('');
  setIsJobSpecMatchMode(false); // Clear job spec match mode
  setPreferredFilterGroups([]); // Clear preferred filters for scoring
  setCandidateMatchScores({}); // Clear match scores
  setJevMatch(null); // Clear Job Spec Match v2 AI scores
  jevMatchRef.current = null;
  applyFilterState(emptyState);
  clearConnectionsFilter();

  // Also clear radius search if active - this refetches data
  if (radiusLocation || isFiltered) {
    clearRadiusSearch();
  }
};

// WEIGHTED SCORING SYSTEM:
// - 10 points for first match in each preferred filter group
// - 1 point for each additional occurrence of the keyword across all fields
// This rewards candidates who have keywords appearing in multiple places

// Count occurrences of a term in a string (case-insensitive)
const countOccurrences = (text: string, term: string): number => {
  if (!text || !term) return 0;
  const regex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
  const matches = text.match(regex);
  return matches ? matches.length : 0;
};

// Get all text fields from a candidate for keyword counting
const getAllTextFields = (candidate: any): string[] => {
  const textFields = [
    'headline', 'summary', 'all_skills_text',
    'current_position', 'current_position_description',
    'previous_position_1', 'previous_position_1_description',
    'previous_position_2', 'previous_position_2_description',
    'previous_position_3', 'previous_position_3_description',
    'certification_1', 'degree_1'
  ];

  return textFields
    .map(field => candidate[field])
    .filter(val => val && typeof val === 'string');
};

// Calculate match score for a single candidate against preferred filter groups
// WEIGHTED: 10 points for first match per group, 1 point for additional occurrences
const calculateCandidateMatchScore = (candidate: any, preferredGroups: FilterGroup[]): number => {
  if (preferredGroups.length === 0) return 0; // No preferred filters = base score

  let totalScore = 0;
  const allTextFields = getAllTextFields(candidate);
  const allText = allTextFields.join(' ').toLowerCase();

  for (const group of preferredGroups) {
    // Get all unique search terms from this group's conditions
    const searchTerms = new Set<string>();

    group.conditions?.forEach(condition => {
      if (condition.values && Array.isArray(condition.values)) {
        condition.values.forEach((v: string) => searchTerms.add(v.toLowerCase()));
      } else if (condition.value) {
        searchTerms.add(String(condition.value).toLowerCase());
      }
    });

    // For OR groups: any match counts, score based on occurrences
    // For AND groups: all must match for any points
    let groupHasMatch = false;
    let groupOccurrences = 0;

    if (group.logic === 'OR') {
      // OR group: count total occurrences of any matching term
      searchTerms.forEach(term => {
        const occurrences = countOccurrences(allText, term);
        if (occurrences > 0) {
          groupHasMatch = true;
          groupOccurrences += occurrences;
        }
      });
    } else {
      // AND group: all terms must appear
      let allTermsFound = true;
      searchTerms.forEach(term => {
        const occurrences = countOccurrences(allText, term);
        if (occurrences === 0) {
          allTermsFound = false;
        } else {
          groupOccurrences += occurrences;
        }
      });
      groupHasMatch = allTermsFound;
    }

    // Apply weighted scoring:
    // - 10 points for first match (group matched)
    // - 1 point for each additional occurrence beyond the first
    if (groupHasMatch) {
      totalScore += 10; // First match bonus
      if (groupOccurrences > 1) {
        totalScore += (groupOccurrences - 1); // Additional occurrences
      }
    }
  }

  return totalScore;
};

// Check if a candidate matches a filter group (for backward compatibility)
const checkGroupMatch = (candidate: any, group: FilterGroup): boolean => {
  if (!group.conditions || group.conditions.length === 0) return true;

  const results = group.conditions.map(condition => checkConditionMatch(candidate, condition));

  // Apply group logic (AND/OR)
  if (group.logic === 'OR') {
    return results.some(r => r);
  } else {
    return results.every(r => r);
  }
};

// Check if a candidate matches a single condition
const checkConditionMatch = (candidate: any, condition: any): boolean => {
  // Any role (title/keyword) held within the last N years: scan position_history
  // for a role whose window overlaps [now - N years, now] and whose
  // title/description/company matches the keyword.
  if (condition.column === 'role_in_last_years') {
    const history = candidate.position_history;
    if (!Array.isArray(history)) return true; // no data client-side; server is authoritative
    const kw = (condition.value || '').toLowerCase().trim();
    const years = Number(condition.withinYears) > 0 ? Number(condition.withinYears) : 5;
    const cutoffYear = new Date().getFullYear() - years;
    return history.some((pos: any) => {
      if (!pos) return false;
      const hay = `${pos.title || ''} ${pos.description || ''} ${pos.companyName || ''}`.toLowerCase();
      if (kw && !hay.includes(kw)) return false;
      const end = pos.startEndDate?.end;
      // Ongoing ("Present") role always overlaps the window.
      if (end === null || end === undefined) return true;
      const endYear = Number(end.year);
      return Number.isFinite(endYear) && endYear >= cutoffYear;
    });
  }

  // Language + minimum proficiency: paired match over languages_detail.
  if (condition.column === 'language_min_proficiency') {
    const detail = candidate.languages_detail;
    if (!Array.isArray(detail)) return true; // no data client-side; server is authoritative
    const RANK: Record<string, number> = { NATIVE_OR_BILINGUAL: 5, FULL_PROFESSIONAL: 4, PROFESSIONAL_WORKING: 3, LIMITED_WORKING: 2, ELEMENTARY: 1 };
    const minRank = RANK[condition.minProficiency] || 0;
    const lang = (condition.value || '').toLowerCase().trim();
    return detail.some((l: any) => l &&
      (!lang || String(l.name || '').toLowerCase().includes(lang)) &&
      (RANK[l.proficiency] || 0) >= minRank);
  }

  // Boolean flag columns (e.g. phone_searched): "Yes" = strictly true;
  // "No / never" = false OR unset. Handled before the null-guard so "never"
  // correctly matches candidates whose flag was never set.
  if (condition.operator === 'is_true' || condition.operator === 'is_false') {
    const v = candidate[condition.column];
    const truthy = v === true || v === 'true' || v === 't' || v === 1;
    return condition.operator === 'is_true' ? truthy : !truthy;
  }

  // Emptiness must be decided before the null-guard, or "is empty" could
  // never match a null field (and is_not_empty fell through to a default).
  if (condition.operator === 'is_empty' || condition.operator === 'is_not_empty') {
    const v = candidate[condition.column];
    const empty = v === null || v === undefined || String(v).trim() === '' || String(v) === '{}';
    return condition.operator === 'is_empty' ? empty : !empty;
  }

  const value = candidate[condition.column];
  if (value === null || value === undefined) return false;

  const stringValue = String(value).toLowerCase();
  const filterValue = (condition.value || '').toLowerCase();

  // Handle multi-word terms (values array)
  if (condition.values && Array.isArray(condition.values) && condition.values.length > 0) {
    // All values must appear in the field
    return condition.values.every((term: string) =>
      stringValue.includes(term.toLowerCase())
    );
  }

  switch (condition.operator) {
    case 'contains':
      return stringValue.includes(filterValue);
    case 'not_contains':
      return !stringValue.includes(filterValue);
    case 'equals':
      return stringValue === filterValue;
    case 'not_equals':
      return stringValue !== filterValue;
    case 'starts_with':
      return stringValue.startsWith(filterValue);
    case 'ends_with':
      return stringValue.endsWith(filterValue);
    case 'greater_than':
      return parseFloat(value) > parseFloat(condition.value);
    case 'less_than':
      return parseFloat(value) < parseFloat(condition.value);
    case 'greater_equal':
      return parseFloat(value) >= parseFloat(condition.value);
    case 'less_equal':
      return parseFloat(value) <= parseFloat(condition.value);
    default:
      return stringValue.includes(filterValue);
  }
};

// Calculate match scores for all candidates
const calculateAllMatchScores = (candidateList: any[], preferredGroups: FilterGroup[]) => {
  const scores: Record<number, number> = {};

  for (const candidate of candidateList) {
    scores[candidate.id] = calculateCandidateMatchScore(candidate, preferredGroups);
  }

  setCandidateMatchScores(scores);
  console.log('📊 Calculated match scores for', Object.keys(scores).length, 'candidates');
};

// Handle filters applied from Job Spec Match modal
// Legacy v1 handler (filter breakdown + keyword scoring). Kept wired-ready so
// the old JobSpecMatchModal can be re-mounted for rollback while v2 beds in.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleJobSpecFiltersApplied = (filters: FilterGroup[], explanation: string) => {
  console.log('📋 Job Spec filters applied:', filters.length, 'groups');

  // Separate required (must have) and preferred (nice to have) filters for logging
  const requiredFilters = filters.filter(f => f.priority === 'required' || !f.priority);
  const preferredFilters = filters.filter(f => f.priority === 'preferred');

  console.log('📋 Required filters:', requiredFilters.length, 'Preferred (scoring):', preferredFilters.length);

  // Store preferred filters for scoring calculation
  setPreferredFilterGroups(preferredFilters);

  // Store ALL filters in aiFilterState - both required and preferred
  // The effectiveFilterGroups memo will filter out preferred ones when sending to backend
  // This allows ALL filters to be displayed in the filter bar with their priority indicators
  const filterState: FilterState = {
    groups: filters,  // ALL filters - required AND preferred
    employmentTypes: [],
  };

  setAiFilterState(filterState);
  setAiExplanation(explanation + (preferredFilters.length > 0
    ? `\n\n📊 Weighted scoring: 10 pts per matched "Nice to Have" criteria (${preferredFilters.length} total = ${preferredFilters.length * 10} pts max), +1 pt for each additional keyword occurrence.`
    : ''));
  setIsJobSpecMatchMode(true); // Enable job spec match mode to show priority toggles
  setCanWidenSearch(false);
  setIsWidenedSearch(false);
  applyFilterState(filterState);
};

// Job Spec Match v2: ranked AI results applied to the table. Composites go
// into candidateMatchScores (reusing the Score column + sort plumbing);
// jevMatch flags the display as 0-100 AI scores rather than keyword points.
const handleJevMatchApplied = (spec: JsSpec, results: JsResult[]) => {
  const scores: Record<number, number> = {};
  for (const r of results) scores[r.candidate_id] = r.composite;
  setCandidateMatchScores(scores);
  jevMatchRef.current = { specId: spec.id, title: spec.title };
  setJevMatch(jevMatchRef.current);
  setPreferredFilterGroups([]);
  setIsJobSpecMatchMode(true);
  // Best matches first, immediately — server-paginated projects sort via the
  // stored composite (matchSpecId param), small projects via the client branch.
  handleApplySort('_matchScore', 'desc');
  console.log(`📋 Job Spec Match v2 applied: "${spec.title}" — ${results.length} candidates scored`);
};

// Clear ALL filters including radius search
const handleClearAllFilters = () => {
  // Clear AI filters
  const emptyState: FilterState = {
    groups: [],
    employmentTypes: [],
  };
  setAiFilterState(emptyState);
  setAiExplanation(null);
  setCanWidenSearch(false);
  setIsWidenedSearch(false);
  setCurrentSearchQuery('');
  setIsJobSpecMatchMode(false); // Clear job spec match mode
  setPreferredFilterGroups([]); // Clear preferred filters for scoring
  setCandidateMatchScores({}); // Clear match scores
  setJevMatch(null); // Clear Job Spec Match v2 AI scores
  jevMatchRef.current = null;
  applyFilterState(emptyState);

  // Clear connections filter
  clearConnectionsFilter();

  // Clear radius search - call the function which refetches data
  if (radiusLocation || isFiltered) {
    clearRadiusSearch();
  }

  // Clear filter history
  setFilterHistory([]);
};

// Calculate match scores when candidates change and there are preferred filters
useEffect(() => {
  if (jevMatch) return; // v2 AI scores are set once from stored judgments — never overwrite with keyword counts
  if (preferredFilterGroups.length > 0 && candidates.length > 0) {
    console.log('📊 Calculating match scores for', candidates.length, 'candidates against', preferredFilterGroups.length, 'preferred filters');
    calculateAllMatchScores(candidates, preferredFilterGroups);
  }
}, [candidates, preferredFilterGroups]);

const handleWidenSearch = async () => {
  if (!selectedProject || !currentSearchQuery) return;
  tourEvent('search-widened');

  setAiSearchLoading(true);
  try {
    const token = localStorage.getItem('token');
    const response = await fetch('/api/ai-search/analyze', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        query: currentSearchQuery,
        projectId: selectedProject.id,
        widenSearch: true
      })
    });

    if (response.ok) {
      const data = await response.json();
      const filterState: FilterState = {
        groups: data.filterGroups || [],
        employmentTypes: [],
      };

      // Build dual radius search if present
      const dualRadiusSearch = (data.companyRadiusSearch || data.candidateRadiusSearch) ? {
        companyRadiusSearch: data.companyRadiusSearch,
        candidateRadiusSearch: data.candidateRadiusSearch
      } : undefined;

      handleAISearch(filterState, data.explanation, false, true, currentSearchQuery, data.radiusSearch, dualRadiusSearch);
    }
  } catch (err) {
    console.error('Widen search error:', err);
  } finally {
    setAiSearchLoading(false);
  }
};

const handleApplyFilters = (filters: FilterCondition[], employmentTypes: string[]) => {
  // Legacy function - kept for backwards compatibility but not actively used
  setAppliedEmploymentTypes(employmentTypes);
  setCurrentPage(1);
};

  const handleApplySort = async (column: string, direction: 'asc' | 'desc') => {
    setSortConfig({ column, direction });

    // Trigger server-side sort - refetch data with new sort params
    if (serverPagination.enabled && selectedProject?.id) {
      setCurrentPage(1); // Reset to page 1
      setPrefetchedData(new Map()); // Clear prefetch cache
      setPageLoading(true);

      // Build radius params if radius search is active
      const sortRadiusParams = (radiusLocation && radiusCoords)
        ? { lat: radiusCoords.lat, lng: radiusCoords.lng, distance: radiusDistance, type: radiusType }
        : undefined;

      try {
        const result = await fetchPaginatedData(
          activeDataType,
          1,
          rowLimit,
          column,
          direction,
          aiFilterState.groups,
          aiFilterState.employmentTypes,
          undefined,
          undefined,
          undefined,
          sortRadiusParams
        );

        if (activeDataType === 'companies') {
          setCompanies(result.data);
        } else if (activeDataType === 'candidates') {
          setCandidates(result.data);
        } else {
          setHiringManagers(result.data);
          setStarredManagerIds(new Set(result.data.filter((m: any) => m.is_starred).map((m: any) => m.id)));
        }

        // Update pagination info
        setServerPagination({
          enabled: true,
          total: result.pagination.total,
          totalPages: result.pagination.totalPages,
          hasNext: result.pagination.hasNext,
          hasPrev: result.pagination.hasPrev
        });

        // Update server totals for tab count
        if (activeDataType === 'companies') {
          setServerTotals(prev => ({
            ...prev,
            companies: result.pagination.uniqueTotal || result.pagination.total
          }));
        }

        // Prefetch next page with sort params explicitly (setSortConfig state hasn't propagated yet)
        if (result.pagination.hasNext) {
          prefetchNextPage(activeDataType, 2, effectiveFilterGroups, aiFilterState.employmentTypes, sortRadiusParams, column, direction);
        }
      } catch (error) {
        console.error('Failed to sort data:', error);
      } finally {
        setPageLoading(false);
      }
    }
  };

  const handlePaginationChange = async (start: number, limit: number) => {
    // The start row is EXACT: the window is anchored at `start` (0-based
    // offset) and the last page simply ends early if fewer rows remain.
    // Server mode sends startRow to the API (offset = anchor + page math);
    // client mode slices with the same anchor.
    const anchor = Math.max(0, start);
    setStartRow(anchor);
    setRowLimit(limit);
    setCurrentPage(1);

    if (serverPagination.enabled && selectedProject?.id) {
      setPrefetchedData(new Map()); // Clear prefetch cache
      setPageLoading(true);
      try {
        const sortColumn = sortConfig.column || (activeDataType === 'companies' ? 'candidate_count' : 'name');
        const result = await fetchPaginatedData(
          activeDataType, 1, limit, sortColumn, sortConfig.direction,
          effectiveFilterGroups, aiFilterState.employmentTypes,
          undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined,
          anchor
        );

        if (activeDataType === 'companies') {
          setCompanies(result.data);
        } else if (activeDataType === 'candidates') {
          setCandidates(result.data);
        } else {
          setHiringManagers(result.data);
          setStarredManagerIds(new Set(result.data.filter((m: any) => m.is_starred).map((m: any) => m.id)));
        }

        setServerPagination({
          enabled: true,
          total: result.pagination.total,
          totalPages: result.pagination.totalPages,
          hasNext: result.pagination.hasNext,
          hasPrev: result.pagination.hasPrev
        });

        if (activeDataType === 'companies') {
          setServerTotals(prev => ({
            ...prev,
            companies: result.pagination.uniqueTotal || result.pagination.total
          }));
        }
      } catch (error) {
        console.error('Failed to fetch with new row range:', error);
      } finally {
        setPageLoading(false);
      }
    }
  };

// New dual radius search function - handles both company office and candidate location filters independently
const applyDualRadiusSearch = async (
  filterType?: 'company' | 'candidate' | 'manager',
  location?: string,
  distance?: number
) => {
  if (location) tourEvent('radius-applied');
  console.log('🎯 applyDualRadiusSearch called!', { filterType, location, distance });

  if (filterType === 'manager' && location && distance) {
    // Hiring manager radius — direct filter on managers, no cross-filter to other tabs.
    try {
      const headers = getAuthHeaders();
      const geocodeResponse = await fetch(`/api/companies/geocode?location=${encodeURIComponent(location)}`, {
        headers,
        cache: 'no-store'
      });
      if (!geocodeResponse.ok) {
        console.error('Geocoding failed for manager location radius');
        return;
      }
      const { lat, lng } = await geocodeResponse.json();
      setManagerLocationRadius({ location, distance, coords: { lat, lng } });
      setTempManagerLocationRadius({ location, distance });

      setLoading(true);
      const managersResult = await fetchPaginatedData(
        'managers', 1, rowLimit, 'name', 'asc',
        aiFilterState.groups, aiFilterState.employmentTypes,
        undefined, undefined, undefined,
        { lat, lng, distance, managerLat: lat, managerLng: lng, managerDistance: distance }
      );
      if (managersResult) {
        setHiringManagers(managersResult.data);
        setFullHiringManagers(managersResult.data);
        setServerTotals(prev => ({ ...prev, managers: managersResult.pagination.total }));
        setServerPagination(prev => ({
          ...prev,
          enabled: true,
          total: managersResult.pagination.total,
          totalPages: managersResult.pagination.totalPages,
          hasNext: managersResult.pagination.hasNext,
          hasPrev: managersResult.pagination.hasPrev
        }));
        setIsFiltered(true);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Manager radius search failed:', err);
    } finally {
      setLoading(false);
    }
    return;
  }

  // If a specific filter is being applied, geocode and apply immediately
  if (filterType === 'company' && location && distance) {
    // Geocode the location first
    try {
      const headers = getAuthHeaders();
      const geocodeResponse = await fetch(`/api/companies/geocode?location=${encodeURIComponent(location)}`, {
        headers,
        cache: 'no-store'
      });

      if (geocodeResponse.ok) {
        const { lat, lng } = await geocodeResponse.json();
        // Update state for UI display
        setCompanyOfficeRadius({ location, distance, coords: { lat, lng } });
        setTempCompanyOfficeRadius({ location, distance });
        // Also set legacy state for compatibility
        setRadiusType('company');
        setRadiusLocation(location);
        setRadiusDistance(distance);
        setRadiusCoords({ lat, lng });
        console.log(`📍 Company office radius set: ${distance} miles of ${location} (${lat}, ${lng})`);

        // Apply the radius search immediately with the values (don't rely on state)
        await applyRadiusSearch('company', location, distance);
      } else {
        const body = await geocodeResponse.json().catch(() => ({} as any));
        if (geocodeResponse.status === 404) {
          alert(`Couldn't find "${location}". Try a more specific place name (e.g. "Reading, UK").`);
        } else {
          alert(`Location search is temporarily unavailable (${body.status || geocodeResponse.status}). This is usually a Google Maps API key/quota issue on the server — please check the backend logs.`);
        }
      }
    } catch (err) {
      console.error('Geocoding failed for company office radius:', err);
      return;
    }
  } else if (filterType === 'candidate' && location && distance) {
    // Geocode the location first
    try {
      const headers = getAuthHeaders();
      const geocodeResponse = await fetch(`/api/companies/geocode?location=${encodeURIComponent(location)}`, {
        headers,
        cache: 'no-store'
      });

      if (geocodeResponse.ok) {
        const { lat, lng } = await geocodeResponse.json();
        // Update state for UI display
        setCandidateLocationRadius({ location, distance, coords: { lat, lng } });
        setTempCandidateLocationRadius({ location, distance });
        // Also set legacy state
        setRadiusType('candidate');
        setRadiusLocation(location);
        setRadiusDistance(distance);
        setRadiusCoords({ lat, lng });
        console.log(`📍 Candidate location radius set: ${distance} miles of ${location} (${lat}, ${lng})`);

        // Apply the radius search immediately with the values (don't rely on state)
        await applyRadiusSearch('candidate', location, distance);
      } else {
        const body = await geocodeResponse.json().catch(() => ({} as any));
        if (geocodeResponse.status === 404) {
          alert(`Couldn't find "${location}". Try a more specific place name (e.g. "Reading, UK").`);
        } else {
          alert(`Location search is temporarily unavailable (${body.status || geocodeResponse.status}). This is usually a Google Maps API key/quota issue on the server — please check the backend logs.`);
        }
      }
    } catch (err) {
      console.error('Geocoding failed for candidate location radius:', err);
      return;
    }
  } else if (!filterType) {
    // No filter type specified - this is a "re-apply" call, use legacy applyRadiusSearch
    if (companyOfficeRadius.location) {
      await applyRadiusSearch('company', companyOfficeRadius.location, companyOfficeRadius.distance);
    } else if (candidateLocationRadius.location) {
      await applyRadiusSearch('candidate', candidateLocationRadius.location, candidateLocationRadius.distance);
    } else {
      // No filters active, clear
      await clearRadiusSearch();
    }
  }
};

// Apply radius filters using the current state (called after state updates)
const applyRadiusFilters = async () => {
  setLoading(true);
  const headers = getAuthHeaders();

  try {
    // Build API params based on active filters
    const params = new URLSearchParams();

    // Add company office radius filter if active
    if (companyOfficeRadius.coords) {
      params.append('companyRadiusLat', companyOfficeRadius.coords.lat.toString());
      params.append('companyRadiusLng', companyOfficeRadius.coords.lng.toString());
      params.append('companyRadiusDistance', companyOfficeRadius.distance.toString());
    }

    // Add candidate location radius filter if active
    if (candidateLocationRadius.coords) {
      params.append('candidateRadiusLat', candidateLocationRadius.coords.lat.toString());
      params.append('candidateRadiusLng', candidateLocationRadius.coords.lng.toString());
      params.append('candidateRadiusDistance', candidateLocationRadius.distance.toString());
    }

    console.log('📡 Applying dual radius filters:', {
      companyOffice: companyOfficeRadius.location ? `${companyOfficeRadius.distance}mi of ${companyOfficeRadius.location}` : 'none',
      candidateLocation: candidateLocationRadius.location ? `${candidateLocationRadius.distance}mi of ${candidateLocationRadius.location}` : 'none'
    });

    // For now, fall back to the legacy applyRadiusSearch behavior
    // TODO: Update to use new dual-filter API endpoints
    if (companyOfficeRadius.location && companyOfficeRadius.coords) {
      await applyRadiusSearch('company', companyOfficeRadius.location, companyOfficeRadius.distance);
    } else if (candidateLocationRadius.location && candidateLocationRadius.coords) {
      await applyRadiusSearch('candidate', candidateLocationRadius.location, candidateLocationRadius.distance);
    } else {
      // No filters active, clear
      await clearRadiusSearch();
    }
  } catch (err) {
    console.error('Error applying radius filters:', err);
  } finally {
    setLoading(false);
  }
};

const applyRadiusSearch = async (
  overrideType?: 'candidate' | 'company',
  overrideLocation?: string,
  overrideDistance?: number,
  // Optional: pass the OTHER filter's info directly (for dual filter AI search)
  otherFilterOverride?: { type: 'company' | 'candidate'; lat: number; lng: number; distance: number },
  // Optional: pass filter groups directly (for AI search with job title + location)
  filterGroupsOverride?: any[]
) => {
  // Use override values if provided (for direct calls from button), otherwise use state
  const effectiveType = overrideType || radiusType;
  const effectiveLocation = overrideLocation || radiusLocation;
  const effectiveDistance = overrideDistance ?? radiusDistance;

  console.log('🎯 applyRadiusSearch called!', { effectiveType, effectiveLocation, effectiveDistance });

  if (!effectiveLocation || effectiveDistance <= 0) {
    console.log('❌ Validation failed:', { effectiveLocation, effectiveDistance });
    return;
  }

  // Also update the state so it persists for display
  setRadiusType(effectiveType);
  setRadiusLocation(effectiveLocation);
  setRadiusDistance(effectiveDistance);

  try {
    setLoading(true);
    const headers = getAuthHeaders();

    console.log('✅ Passed validation, radiusType:', effectiveType);

    // Helper function to calculate distance between two lat/lng points in miles
    const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
      const R = 3959; // Earth's radius in miles
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLng = (lng2 - lng1) * Math.PI / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    };

    // Use companies endpoint for BOTH company and candidate radius filtering
    // The backend already supports both via filterType parameter
    const filterType = effectiveType === 'candidate' ? 'candidates' : 'companies';

    const params = new URLSearchParams({
      filterType: filterType,
      radiusType: effectiveType,
      radiusLocation: effectiveLocation,
      distance: effectiveDistance.toString()
    });

    console.log(`🔍 Calling companies API with filterType: ${filterType}`);
    const response = await fetch(`/api/companies/${selectedProject.id}/filtered?${params.toString()}`, {
      headers: headers,
      cache: 'no-store'
    });

    if (response.ok) {
      const filteredCompaniesFromAPI = await response.json();
      const companyNames = new Set(filteredCompaniesFromAPI.map((c: Company) => c.company_name));

      let filteredCandidates: Candidate[] = [];
      let candidateSource: Candidate[] = fullCandidates;
      let uniqueCompaniesFromCandidates: Company[] = [];

      // For company mode only: fetch all candidates for client-side filtering
      // (candidate mode now uses server-side filtering)
      if (effectiveType !== 'candidate' && serverPagination.enabled && selectedProject?.id) {
        console.log('📡 Company radius mode: fetching all candidates for client-side filtering...');
        try {
          const allCandidatesResponse = await fetch(
            `/api/candidates/${selectedProject.id}/export${campaignScopeId ? `?campaignId=${campaignScopeId}` : ''}`,
            { headers, cache: 'no-store' }
          );
          if (allCandidatesResponse.ok) {
            candidateSource = await allCandidatesResponse.json();
            console.log(`📡 Fetched ${candidateSource.length} candidates for company radius filtering`);
          }
        } catch (err) {
          console.error('Failed to fetch all candidates for radius filtering:', err);
        }
      }

      if (effectiveType === 'candidate') {
        // For candidate radius: use server-side filtering for efficient large dataset handling
        const geocodeResponse = await fetch(`/api/companies/geocode?location=${encodeURIComponent(effectiveLocation)}`, {
          headers: headers,
          cache: 'no-store'
        });

        if (geocodeResponse.ok) {
          const { lat: centerLat, lng: centerLng } = await geocodeResponse.json();
          console.log(`📍 Geocoded "${effectiveLocation}" to: ${centerLat}, ${centerLng}`);

          // Store coords for use in pagination
          setRadiusCoords({ lat: centerLat, lng: centerLng });

          // Update the applied state for UI display (so the radius button shows the active filter)
          setCandidateLocationRadius({ location: effectiveLocation, distance: effectiveDistance, coords: { lat: centerLat, lng: centerLng } });
          setTempCandidateLocationRadius({ location: effectiveLocation, distance: effectiveDistance });

          // Use server-side radius filtering via the paginated endpoint
          console.log('📡 Using server-side radius filtering...');

          // Check if company office filter is also active - if so, combine both filters
          // Use override if provided (for AI dual search), otherwise check state
          const hasCompanyFilterFromOverride = otherFilterOverride && otherFilterOverride.type === 'company';
          const hasCompanyFilter = hasCompanyFilterFromOverride || companyOfficeRadius.coords !== null;

          // Get company filter coords from override or state
          const companyFilterCoords = hasCompanyFilterFromOverride
            ? { lat: otherFilterOverride!.lat, lng: otherFilterOverride!.lng }
            : companyOfficeRadius.coords;
          const companyFilterDistance = hasCompanyFilterFromOverride
            ? otherFilterOverride!.distance
            : companyOfficeRadius.distance;

          const radiusParams = hasCompanyFilter && companyFilterCoords
            ? {
                // Dual filter mode - use new param format
                lat: centerLat, lng: centerLng, distance: effectiveDistance,
                candidateLat: centerLat,
                candidateLng: centerLng,
                candidateDistance: effectiveDistance,
                companyLat: companyFilterCoords.lat,
                companyLng: companyFilterCoords.lng,
                companyDistance: companyFilterDistance
              }
            : { lat: centerLat, lng: centerLng, distance: effectiveDistance, type: 'candidate' as const };

          if (hasCompanyFilter) {
            console.log('🔀 Combining with company filter:', hasCompanyFilterFromOverride ? 'from override' : 'from state', { companyFilterCoords, companyFilterDistance });
          }

          // Use filter groups from override (AI search) or from state
          const effectiveFilterGroups = filterGroupsOverride || aiFilterState.groups;
          const hasFilterGroups = effectiveFilterGroups.length > 0;
          if (hasFilterGroups) {
            console.log('📊 Including filter groups in radius search:', effectiveFilterGroups.length, 'groups');
          }

          const candidatesResult = await fetchPaginatedData(
            'candidates',
            1,
            rowLimit,
            'name',
            'asc',
            effectiveFilterGroups,
            aiFilterState.employmentTypes,
            undefined,
            undefined,
            undefined,
            radiusParams
          );

          if (candidatesResult) {
            filteredCandidates = candidatesResult.data;
            console.log(`📍 Server returned ${filteredCandidates.length} candidates within ${effectiveDistance} miles (total: ${candidatesResult.pagination.total})`);

            // Keep server-side pagination enabled with the new filtered total
            setServerPagination(prev => ({
              ...prev,
              enabled: true,
              total: candidatesResult.pagination.total,
              totalPages: candidatesResult.pagination.totalPages,
              hasNext: candidatesResult.pagination.hasNext,
              hasPrev: candidatesResult.pagination.hasPrev
            }));

            // Call filtered-counts API to get accurate counts for ALL tabs
            // This ensures Companies and Hiring Managers tabs show correct cross-filtered counts
            // Use dual filter params if company filter is also active
            // Also include filter groups if they exist (e.g., job title filter from AI search)
            const countsParams = new URLSearchParams();

            // Add radius params
            if (hasCompanyFilter && companyFilterCoords) {
              countsParams.append('candidateRadiusLat', centerLat.toString());
              countsParams.append('candidateRadiusLng', centerLng.toString());
              countsParams.append('candidateRadiusDistance', effectiveDistance.toString());
              countsParams.append('companyRadiusLat', companyFilterCoords.lat.toString());
              countsParams.append('companyRadiusLng', companyFilterCoords.lng.toString());
              countsParams.append('companyRadiusDistance', companyFilterDistance.toString());
            } else {
              countsParams.append('radiusLat', centerLat.toString());
              countsParams.append('radiusLng', centerLng.toString());
              countsParams.append('radiusDistance', effectiveDistance.toString());
              countsParams.append('radiusType', 'candidate');
            }

            // Add filter groups if present (for combined job title + location search)
            if (hasFilterGroups) {
              countsParams.append('filterGroups', JSON.stringify(effectiveFilterGroups));
              console.log('📊 Including filter groups in counts request');
            }

            if (campaignScopeId) countsParams.append('campaignId', String(campaignScopeId));
            const countsUrl = `/api/projects/${selectedProject.id}/filtered-counts`;
            console.log('📊 Fetching accurate counts from filtered-counts API for candidate radius...', { hasCompanyFilter, hasCompanyFilterFromOverride, hasFilterGroups });

            try {
              // POST — countsParams can carry large AI-search filterGroups
              const countsResponse = await fetch(countsUrl, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                cache: 'no-store',
                body: JSON.stringify(paramsToObject(countsParams))
              });

              if (countsResponse.ok) {
                const counts = await countsResponse.json();
                console.log('📊 Accurate counts from API:', counts);

                // Set accurate counts for all tabs
                setServerTotals({
                  companies: counts.companies,
                  candidates: counts.candidates,
                  managers: counts.managers
                });
              } else {
                // Fallback: use pagination total for candidates
                setServerTotals(prev => ({
                  ...prev,
                  candidates: candidatesResult.pagination.total
                }));
              }
            } catch (countsErr) {
              console.error('Failed to fetch accurate counts:', countsErr);
              // Fallback: use pagination total for candidates
              setServerTotals(prev => ({
                ...prev,
                candidates: candidatesResult.pagination.total
              }));
            }

            // For candidate radius: use distinctCompanies from server (all companies for ALL candidates in radius)
            // This is more accurate than deriving from just the first page of candidates
            const candidateCompanyNames = candidatesResult.distinctCompanies
              ? new Set(candidatesResult.distinctCompanies)
              : new Set(filteredCandidates.map((c: Candidate) => c.company).filter(Boolean));

            console.log(`📊 Filtering companies: ${candidateCompanyNames.size} distinct company names from ${candidatesResult.distinctCompanies ? 'server' : 'page 1'}`);

            const companiesForCandidates = filteredCompaniesFromAPI.filter((company: Company) =>
              candidateCompanyNames.has(company.company_name)
            );

            // Update uniqueCompanies to only show companies where filtered candidates work
            uniqueCompaniesFromCandidates = companiesForCandidates.filter((company: Company, index: number, self: Company[]) =>
              index === self.findIndex((c: Company) => c.company_name === company.company_name)
            );

            console.log(`📊 Filtered to ${uniqueCompaniesFromCandidates.length} unique companies`);

            // Set companies based on candidate filter results
            setCompanies(uniqueCompaniesFromCandidates);
            setFullCompanies(uniqueCompaniesFromCandidates);

            // Prefetch page 2 if there's more data
            if (candidatesResult.pagination.hasNext) {
              prefetchNextPage('candidates', 2, [], [], radiusParams);
            }
          }
        } else {
          console.error('Geocoding failed for candidate radius search');
          filteredCandidates = [];
        }
      } else {
        // For company radius mode: use server-side filtering for companies
        const geocodeResponse = await fetch(`/api/companies/geocode?location=${encodeURIComponent(effectiveLocation)}`, {
          headers: headers,
          cache: 'no-store'
        });

        if (geocodeResponse.ok) {
          const { lat: centerLat, lng: centerLng } = await geocodeResponse.json();
          console.log(`📍 Geocoded "${effectiveLocation}" to: ${centerLat}, ${centerLng}`);

          // Store coords for use in pagination
          setRadiusCoords({ lat: centerLat, lng: centerLng });

          // Update the applied state for UI display (so the radius button shows the active filter)
          setCompanyOfficeRadius({ location: effectiveLocation, distance: effectiveDistance, coords: { lat: centerLat, lng: centerLng } });
          setTempCompanyOfficeRadius({ location: effectiveLocation, distance: effectiveDistance });

          // Use server-side radius filtering via the paginated endpoint
          console.log('📡 Using server-side radius filtering for companies...');

          // Check if candidate location filter is also active - if so, combine both filters
          const hasCandidateFilter = candidateLocationRadius.coords !== null;
          const radiusParams = hasCandidateFilter
            ? {
                // Dual filter mode - use new param format
                lat: centerLat, lng: centerLng, distance: effectiveDistance,
                companyLat: centerLat,
                companyLng: centerLng,
                companyDistance: effectiveDistance,
                candidateLat: candidateLocationRadius.coords!.lat,
                candidateLng: candidateLocationRadius.coords!.lng,
                candidateDistance: candidateLocationRadius.distance
              }
            : { lat: centerLat, lng: centerLng, distance: effectiveDistance };

          if (hasCandidateFilter) {
            console.log('🔀 Combining with existing candidate filter:', candidateLocationRadius);
          }

          const companiesResult = await fetchPaginatedData(
            'companies',
            1,
            rowLimit,
            'company_name',
            'asc',
            [],
            [],
            undefined,
            undefined,
            undefined,
            radiusParams
          );

          if (companiesResult) {
            const uniqueCompanies = companiesResult.data;
            console.log(`📍 Server returned ${uniqueCompanies.length} companies within ${effectiveDistance} miles (total: ${companiesResult.pagination.uniqueTotal})`);

            setCompanies(uniqueCompanies);
            setFullCompanies(uniqueCompanies);

            // Update the displayed total count for companies
            setServerTotals(prev => ({
              ...prev,
              companies: companiesResult.pagination.uniqueTotal || companiesResult.pagination.total
            }));

            // Keep server-side pagination enabled with the new filtered total
            setServerPagination(prev => ({
              ...prev,
              enabled: true,
              total: companiesResult.pagination.uniqueTotal || companiesResult.pagination.total,
              totalPages: companiesResult.pagination.totalPages,
              hasNext: companiesResult.pagination.hasNext,
              hasPrev: companiesResult.pagination.hasPrev
            }));

            // For company radius mode: use the filtered-counts API to get accurate counts
            // The client-side filtering is inaccurate because uniqueCompanies only has first page (250)
            // Instead, call filtered-counts which uses proper server-side cross-filtering
            // Use dual filter params if candidate filter is also active
            const campaignQ = campaignScopeId ? `&campaignId=${campaignScopeId}` : '';
            const countsUrl = hasCandidateFilter
              ? `/api/projects/${selectedProject.id}/filtered-counts?companyRadiusLat=${centerLat}&companyRadiusLng=${centerLng}&companyRadiusDistance=${effectiveDistance}&candidateRadiusLat=${candidateLocationRadius.coords!.lat}&candidateRadiusLng=${candidateLocationRadius.coords!.lng}&candidateRadiusDistance=${candidateLocationRadius.distance}${campaignQ}`
              : `/api/projects/${selectedProject.id}/filtered-counts?radiusLat=${centerLat}&radiusLng=${centerLng}&radiusDistance=${effectiveDistance}&radiusType=company${campaignQ}`;
            console.log('📊 Fetching accurate counts from filtered-counts API...', { hasCandidateFilter });

            try {
              const countsResponse = await fetch(countsUrl, {
                headers: headers,
                cache: 'no-store'
              });

              if (countsResponse.ok) {
                const counts = await countsResponse.json();
                console.log('📊 Accurate counts from API:', counts);

                // Set accurate counts for tabs
                setServerTotals({
                  companies: counts.companies,
                  candidates: counts.candidates,
                  managers: counts.managers
                });

                // Also update serverPagination total based on active data type
                const activeTotal = activeDataType === 'companies' ? counts.companies
                  : activeDataType === 'candidates' ? counts.candidates
                  : counts.managers;
                setServerPagination(prev => ({
                  ...prev,
                  total: activeTotal,
                  totalPages: Math.ceil(activeTotal / rowLimit),
                  hasNext: activeTotal > rowLimit,
                  hasPrev: false
                }));

                // For display, just show what we have for now
                // Proper data will be fetched when user switches tabs
                filteredCandidates = candidateSource.slice(0, 250);
              } else {
                // Fallback to client-side filtering (inaccurate but better than nothing)
                const companyNamesInRadius = new Set(uniqueCompanies.map((c: Company) => c.company_name));
                filteredCandidates = candidateSource.filter((cand: Candidate) =>
                  companyNamesInRadius.has(cand.company || '') || companyNamesInRadius.has(cand.current_company || '')
                );
              }
            } catch (countsErr) {
              console.error('Failed to fetch accurate counts:', countsErr);
              // Fallback to client-side filtering
              const companyNamesInRadius = new Set(uniqueCompanies.map((c: Company) => c.company_name));
              filteredCandidates = candidateSource.filter((cand: Candidate) =>
                companyNamesInRadius.has(cand.company || '') || companyNamesInRadius.has(cand.current_company || '')
              );
            }
          }
        } else {
          console.error('Geocoding failed for company radius search');
          // Fall back to showing candidates from API-filtered companies
          filteredCandidates = candidateSource.filter((cand: Candidate) =>
            companyNames.has(cand.company || '') || companyNames.has(cand.current_company || '')
          );
        }
      }

      // For candidate radius, companies were already set above via uniqueCompaniesFromCandidates
      // For company radius in server-side mode, companies were also set above
      // Only set from filteredCompaniesFromAPI if not already handled
      if (effectiveType !== 'candidate' && !radiusCoords) {
        // Deduplicate companies by company_name (a company may have multiple offices in the radius)
        const uniqueCompanies = filteredCompaniesFromAPI.filter((company: Company, index: number, self: Company[]) =>
          index === self.findIndex((c: Company) => c.company_name === company.company_name)
        );
        setCompanies(uniqueCompanies);
        setFullCompanies(uniqueCompanies);
      }

      // For hiring managers, filter based on the mode
      // Note: For candidate mode, we use the company names derived above (which uses server's distinctCompanies if available)
      // For company mode, we already got accurate counts from the API, so just slice for display
      const filteredManagers = effectiveType === 'candidate'
        ? fullHiringManagers.filter((mgr: HiringManager) =>
            mgr.org && uniqueCompaniesFromCandidates?.some((c: Company) => c.company_name === mgr.org)
          )
        : fullHiringManagers.slice(0, 250); // Just show some for now, actual data fetched on tab switch

      // Set the DISPLAYED data
      setCandidates(filteredCandidates);
      setHiringManagers(filteredManagers);

      // Note: serverTotals already set accurately from filtered-counts API above for both modes

      // For company mode with fallback (non-paginated): store filtered candidates
      if (effectiveType !== 'candidate' && !radiusCoords) {
        setFullCandidates(filteredCandidates);
      }

      console.log('✅ Radius filter applied:', {
        filterType,
        radiusType: effectiveType,
        companiesFromAPI: filteredCompaniesFromAPI.length,
        candidatesDisplayed: filteredCandidates.length,
        managersDisplayed: filteredManagers.length
      });
    }

    setIsFiltered(true);
    setShowRadiusSearch(false);

  } catch (error) {
    console.error('Radius search error:', error);
  } finally {
    setLoading(false);
    // Clear the AI radius search in progress flag
    aiRadiusSearchInProgressRef.current = false;
  }
};

const clearRadiusSearch = async () => {
  console.log('🔄 clearRadiusSearch called');
  // Clear legacy state
  setRadiusLocation('');
  setRadiusDistance(25);
  setRadiusCoords(null);
  // Clear new dual-filter state
  setCompanyOfficeRadius({ location: '', distance: 25, coords: null });
  setCandidateLocationRadius({ location: '', distance: 25, coords: null });
  setManagerLocationRadius({ location: '', distance: 25, coords: null });
  setTempCompanyOfficeRadius({ location: '', distance: 25 });
  setTempCandidateLocationRadius({ location: '', distance: 25 });
  setTempManagerLocationRadius({ location: '', distance: 25 });

  setIsFiltered(false);
  setLoading(true);

  try {
    // First, check if this is a large dataset that should use server-side pagination
    const statsResponse = await fetch(`/api/projects/${selectedProject.id}`, {
      headers: getAuthHeaders()
    });

    let shouldUseServerSide = false;
    let totalCandidates = 0;
    let totalCompanies = 0;
    if (statsResponse.ok) {
      const projectData = await statsResponse.json();
      totalCandidates = projectData.stats?.candidates || 0;
      totalCompanies = projectData.stats?.companies || 0;
      shouldUseServerSide = totalCandidates >= LARGE_DATASET_THRESHOLD || totalCompanies >= LARGE_DATASET_THRESHOLD;
      console.log(`🔄 Project has ${totalCandidates} candidates, ${totalCompanies} companies, shouldUseServerSide: ${shouldUseServerSide}`);
    }

    if (shouldUseServerSide) {
      // Re-enable server-side pagination and fetch first page only
      console.log('🔄 Clearing radius search - restoring server-side pagination');

      // Fetch first page of each data type
      const [companiesResult, candidatesResult, managersResult] = await Promise.all([
        fetchPaginatedData('companies', 1, rowLimit, 'candidate_count', 'desc', [], []),
        fetchPaginatedData('candidates', 1, rowLimit, 'name', 'asc', [], []),
        fetchPaginatedData('managers', 1, rowLimit, 'name', 'asc', [], [])
      ]);

      console.log('🔄 Fetched data:', {
        companies: companiesResult?.pagination,
        candidates: candidatesResult?.pagination,
        managers: managersResult?.pagination
      });

      if (companiesResult) {
        setCompanies(companiesResult.data);
        setFullCompanies(companiesResult.data);
      }

      if (candidatesResult) {
        setCandidates(candidatesResult.data);
        setFullCandidates(candidatesResult.data);
      }

      if (managersResult) {
        setHiringManagers(managersResult.data);
        setFullHiringManagers(managersResult.data);
      }

      // Update server totals - this is what the tab counts use
      setServerTotals({
        companies: companiesResult?.pagination.uniqueTotal || companiesResult?.pagination.total || 0,
        candidates: candidatesResult?.pagination.total || 0,
        managers: managersResult?.pagination.total || 0
      });

      // Re-enable server pagination
      setServerPagination({
        enabled: true,
        total: candidatesResult?.pagination.total || 0,
        totalPages: candidatesResult?.pagination.totalPages || 1,
        hasNext: candidatesResult?.pagination.hasNext || false,
        hasPrev: false
      });

      setCurrentPage(1);
      console.log('🔄 Server-side pagination restored');
    } else {
      // Small dataset - restore full data client-side
      console.log('🔄 Clearing radius search - restoring client-side data');

      const companiesResponse = await fetch(`/api/companies/${selectedProject.id}`, {
        headers: getAuthHeaders()
      });
      if (companiesResponse.ok) {
        const companiesData = await companiesResponse.json();
        setFullCompanies(companiesData);
        setCompanies(companiesData);
      }

      const candidatesResponse = await fetch(`/api/candidates/${selectedProject.id}`, {
        headers: getAuthHeaders()
      });
      if (candidatesResponse.ok) {
        const candidatesData = await candidatesResponse.json();
        setFullCandidates(candidatesData);
        setCandidates(candidatesData);
      }

      const managersResponse = await fetch(`/api/hiring-managers/${selectedProject.id}`, {
        headers: getAuthHeaders()
      });
      if (managersResponse.ok) {
        const managersData = await managersResponse.json();
        setFullHiringManagers(managersData);
        setHiringManagers(managersData);
      }
    }

  } catch (error) {
    console.error('Error restoring datasets:', error);
  } finally {
    setLoading(false);
  }
};

// Handle adding a single row with optional enrichment
const handleAddSingle = async () => {
  if (!addSingleUrl.trim()) {
    alert('Please enter a LinkedIn URL');
    return;
  }

  setAddingSingle(true);

  try {
    const token = localStorage.getItem('token');
    const endpoint = activeDataType === 'companies'
      ? `/api/companies/${selectedProject?.id}/add-single`
      : activeDataType === 'managers'
        ? `/api/hiring-managers/${selectedProject?.id}/add-single`
        : `/api/candidates/${selectedProject?.id}/add-single`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        linkedinUrl: addSingleUrl.trim(),
        enrich: addSingleEnrich
      })
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 409) {
        alert(`This ${activeDataType === 'companies' ? 'company' : activeDataType === 'managers' ? 'hiring manager' : 'candidate'} already exists in the project`);
      } else if (response.status === 402) {
        alert('Insufficient credits for enrichment');
      } else {
        throw new Error(data.error || 'Failed to add');
      }
      return;
    }

    // Add to local state (only if we got the data back - candidates are sync, companies via Clay are async)
    if (activeDataType === 'companies' && data.company) {
      setCompanies(prev => [data.company, ...prev]);
      setFullCompanies(prev => [data.company, ...prev]);
    } else if (activeDataType === 'managers' && data.manager) {
      setHiringManagers(prev => [data.manager, ...prev]);
      setFullHiringManagers(prev => [data.manager, ...prev]);
    } else if (activeDataType === 'candidates' && data.candidate) {
      setCandidates(prev => [data.candidate, ...prev]);
      setFullCandidates(prev => [data.candidate, ...prev]);
    }

    // Clear form and close modal
    setAddSingleUrl('');
    setShowAddSingleModal(false);

    // Show prominent confirmation
    if (activeDataType === 'companies' && data.enriched) {
      // Company enrichment is async via Clay
      alert(`Company sent for enrichment.\n\nCredits remaining: ${data.creditsRemaining}\n\nThe enriched data will appear in a few seconds. Refresh the page to see it.`);
    } else if (data.enriched) {
      const matchMsg = data.companyMatched
        ? `\n\n✓ Matched to existing company in project!`
        : '';
      alert(`Successfully added and enriched!${matchMsg}\n\nCredits remaining: ${data.creditsRemaining}`);
    } else {
      alert('Added successfully (not enriched)');
    }

    // For companies, trigger a refresh after a short delay since Clay is async
    if (activeDataType === 'companies' && data.enriched) {
      setTimeout(() => {
        // Refresh the companies list by incrementing the trigger
        setRefreshTrigger(prev => prev + 1);
      }, 5000); // 5 second delay for Clay to process
    }
  } catch (error) {
    console.error('Error adding single:', error);
    alert(`Failed to add: ${error instanceof Error ? error.message : 'Unknown error'}`);
  } finally {
    setAddingSingle(false);
  }
};

const handleAddManualHm = async () => {
  if (!manualHm.firstName.trim() || !manualHm.lastName.trim() || !manualHm.jobTitle.trim() || !manualHm.linkedinUrl.trim()) {
    alert('Please fill in all required fields (First Name, Last Name, Job Title, LinkedIn URL)');
    return;
  }
  if (!manualHm.linkedinUrl.includes('linkedin.com/in/')) {
    alert('Please enter a valid LinkedIn profile URL (must contain linkedin.com/in/)');
    return;
  }

  setAddingSingle(true);
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/hiring-managers/${selectedProject?.id}/add-manual`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({
        firstName: manualHm.firstName.trim(),
        lastName: manualHm.lastName.trim(),
        jobTitle: manualHm.jobTitle.trim(),
        companyId: manualHm.companyId || null,
        email: manualHm.email.trim() || null,
        linkedinUrl: manualHm.linkedinUrl.trim(),
      })
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 409) {
        alert('This hiring manager already exists in the project');
      } else {
        throw new Error(data.error || 'Failed to add');
      }
      return;
    }

    if (data.manager) {
      setHiringManagers(prev => [data.manager, ...prev]);
      setFullHiringManagers(prev => [data.manager, ...prev]);
    }

    setManualHm({ firstName: '', lastName: '', jobTitle: '', companyId: 0, email: '', linkedinUrl: '' });
    setCompanySearch('');
    setShowAddSingleModal(false);

    const matchMsg = data.companyMatched ? '\n\nLinked to selected company.' : '';
    alert(`Hiring manager added successfully!${matchMsg}`);
  } catch (error) {
    console.error('Error adding manual HM:', error);
    alert(`Failed to add: ${error instanceof Error ? error.message : 'Unknown error'}`);
  } finally {
    setAddingSingle(false);
  }
};

const exportToCSV = async (opts: { includeOffices?: boolean } = {}) => {
  const includeOffices = opts.includeOffices === true;
  // Build the export column list to match what's currently shown in the table:
  // 1. order = user's drag-arranged columnOrder (falls back to default order)
  // 2. only include columns that are currently visible
  // 3. preserve isCustom so we can read custom_data values, not item[key]
  const allColumns = getCurrentColumns() as Array<{ key: string; label: string; type: string; isCustom?: boolean }>;
  const colByKey = new Map(allColumns.map(c => [c.key, c]));
  const orderedKeys = (columnOrder.length > 0 ? columnOrder : allColumns.map(c => c.key))
    .filter(key => visibleColumns.includes(key));
  const baseColumns = orderedKeys
    .map(key => colByKey.get(key))
    .filter((c): c is { key: string; label: string; type: string; isCustom?: boolean } => Boolean(c));
  // Office columns are only appended when the user explicitly opts
  // into "one row per office" on the companies tab. In that mode each
  // matching office row carries its own address breakdown plus a
  // Primary Office flag — important e.g. when a radius search around
  // New York returns BBVA via its New York office, where the row
  // address belongs to that office and Primary Office is blank,
  // since BBVA's actual HQ is in Bilbao.
  const officeColumns: Array<{ key: string; label: string; type: string }> = includeOffices
    ? [
        { key: 'formatted_address', label: 'Office Address', type: 'text' },
        { key: 'locality', label: 'Office City', type: 'text' },
        { key: 'admin_district', label: 'Office Region', type: 'text' },
        { key: 'country', label: 'Office Country', type: 'text' },
        { key: 'is_primary_office', label: 'Primary Office', type: 'boolean' }
      ]
    : [];
  // Avoid emitting an office column twice if the user already has it visible
  // in the table (e.g. Address / City columns).
  const existingKeys = new Set(baseColumns.map(c => c.key));
  const columns = [...baseColumns, ...officeColumns.filter(c => !existingKeys.has(c.key))];
  const headers = columns.map(col => col.label);

    // Show loading message
    setEnrichmentMessage('📊 Preparing export...');

    try {
      // Fetch ALL data from export endpoint (no pagination limit)
      const endpoint = activeDataType === 'companies'
        ? 'companies'
        : activeDataType === 'candidates'
          ? 'candidates'
          : 'hiring-managers';

      const params = new URLSearchParams();

      if (effectiveFilterGroups.length > 0) {
        params.append('filterGroups', JSON.stringify(effectiveFilterGroups));
      }

      // Add employment types filter
      if (aiFilterState.employmentTypes.length > 0) {
        params.append('employmentTypes', aiFilterState.employmentTypes.join(','));
      }

      // Add radius search filters
      if (companyOfficeRadius.coords) {
        params.append('companyRadiusLat', companyOfficeRadius.coords.lat.toString());
        params.append('companyRadiusLng', companyOfficeRadius.coords.lng.toString());
        params.append('companyRadiusDistance', companyOfficeRadius.distance.toString());
      }
      if (candidateLocationRadius.coords) {
        params.append('candidateRadiusLat', candidateLocationRadius.coords.lat.toString());
        params.append('candidateRadiusLng', candidateLocationRadius.coords.lng.toString());
        params.append('candidateRadiusDistance', candidateLocationRadius.distance.toString());
      }

      if (includeOffices && endpoint === 'companies') {
        params.append('includeOffices', '1');
      }

      // Forward the quick-search term so the export reflects what's
      // actually on screen. Without this, typing "sales manager" filters
      // the visible table to 1117 rows but the export still pulls the
      // full 2219 because the backend never received the search.
      if (searchTerm && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      // Campaign Leads List: scope the export to this campaign's members.
      if (campaignScopeId) params.append('campaignId', String(campaignScopeId));

      // POST — filterGroups can exceed nginx's request-URI limit as a query string
      const response = await fetch(
        `/api/${endpoint}/${selectedProject.id}/export`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(paramsToObject(params))
        }
      );

      if (response.status === 402) { setEnrichmentMessage(''); return; } // free trial: the upgrade prompt explains
      if (!response.ok) {
        throw new Error('Failed to fetch export data');
      }

      const allData: any[] = await response.json();

      setEnrichmentMessage(`📊 Exporting ${allData.length} ${activeDataType}...`);

      const rows = allData.map((item: any) => {
        return columns.map(col => {
          // Custom columns live in custom_data JSONB; standard fields live
          // directly on the row. Without this branch, custom column cells
          // export as empty strings.
          let value = (col as any).isCustom
            ? item.custom_data?.[col.key]
            : item[col.key];
          if (Array.isArray(value)) value = value.join(', ');
          if (col.type === 'date' && value) value = new Date(value).toLocaleDateString();
          // Boolean columns (e.g. Primary Office flag from is_primary_office)
          // render as Yes / blank, not true / false, so the CSV is filterable
          // by humans in Excel.
          if (col.type === 'boolean') return value === true ? 'Yes' : '';
          return value || '';
        });
      });

      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const fileName = `${selectedProject.name}-${activeDataType}-${new Date().toISOString().split('T')[0]}.csv`;
      a.href = url;
      a.download = fileName;
      a.click();

      setEnrichmentMessage(`✅ Exported ${allData.length} ${activeDataType}`);
      setTimeout(() => setEnrichmentMessage(null), 3000);

      // Log export to backend for audit trail
      try {
        const exportTypeMap: { [key: string]: string } = {
          'companies': 'companies',
          'candidates': 'candidates',
          'managers': 'hiring_managers'
        };

        await fetch('/api/exports/log', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            projectId: selectedProject.id,
            exportType: exportTypeMap[activeDataType],
            rowCount: allData.length,
            columnsExported: columns.map(col => col.label),
            filtersApplied: {
              hasFilters: aiFilterState.groups.length > 0,
              filterGroupsCount: aiFilterState.groups.length,
              isFiltered: isFiltered
            },
            fileName: fileName
          })
        });
      } catch (error) {
        console.error('Failed to log export:', error);
        // Don't show error to user - logging failure shouldn't block export
      }

      // Track activity for admin dashboard
      trackActivity();

    } catch (error) {
      console.error('Export failed:', error);
      setEnrichmentMessage('❌ Export failed. Please try again.');
      setTimeout(() => setEnrichmentMessage(null), 5000);
    }
  };

  const handleDataTypeChange = async (type: 'companies' | 'candidates' | 'managers', filterStateOverride?: FilterState, associatedCompanyOverride?: string | null) => {
    setActiveDataType(type);
    setCurrentPage(1);
    setSelectedRows(new Set());
    setAllResultsSelected(false);
    // Clear association filter unless explicitly provided (e.g. from handleCompanyCandidatesClick)
    if (associatedCompanyOverride === undefined) {
      setAssociatedCompanyFilter(null);
    }
    const tabSort = defaultSortFor(type, !!selectedProject?.created_by_agent);
    setSortConfig(tabSort);

    // Reset search origin to the new tab so the search bar always reflects
    // the active tab's context. Must run unconditionally (not only when
    // searchTerm is set): otherwise leaving managers without a search term
    // leaves searchOriginTab='managers', so when the user lands on
    // companies and types, the search renders as "companies at orgs
    // matching X" (the cross-filter wording) and runs through the
    // managers-origin code path. A page refresh resets it; we should too.
    setSearchOriginTab(type);

    // Use override if provided, otherwise use current state.
    // When no override is given, drop filter groups whose entityType isn't
    // relevant for the target tab — otherwise a contextual filter set by
    // e.g. handleCompanyManagersClick (entityType='manager') sticks around
    // when the user clicks back to Companies and shadows the radius filter.
    const sourceFilterState = filterStateOverride || aiFilterState;
    const allowedEntityTypesByTab: Record<'companies' | 'candidates' | 'managers', Set<string | undefined>> = {
      companies: new Set<string | undefined>(['company', 'candidate', undefined]),
      candidates: new Set<string | undefined>(['candidate', 'company', undefined]),
      // 'company' must survive the switch to managers: a company filter (e.g.
      // employees <= 20) cross-filters the managers tab to those companies'
      // contacts (sent as companyFilterGroups). Stripping it here silently
      // showed ALL managers despite the filtered tab counts.
      managers: new Set<string | undefined>(['manager', 'candidate', 'company', undefined]),
    };
    const allowed = allowedEntityTypesByTab[type];
    const sanitizedGroups = filterStateOverride
      ? sourceFilterState.groups
      : sourceFilterState.groups.filter((g: any) => allowed.has(g.entityType));
    const effectiveFilterState: FilterState = sanitizedGroups === sourceFilterState.groups
      ? sourceFilterState
      : { groups: sanitizedGroups, employmentTypes: sourceFilterState.employmentTypes };

    // If we stripped any groups, reflect that in component state so subsequent
    // effects (filter useEffect, page-change useEffect) don't re-introduce the
    // dropped group via the stale aiFilterState reference.
    if (!filterStateOverride && sanitizedGroups.length !== sourceFilterState.groups.length) {
      setAiFilterState(effectiveFilterState);
    }

    // When server pagination is enabled, fetch pagination info for the new data type
    if (serverPagination.enabled) {
      setPrefetchedData(new Map()); // Clear prefetched data for previous type

      // If connections filter is active and switching to candidates, skip fetch and use existing data
      if (type === 'candidates' && selectedConnectionsFilter && connectedCandidateIds.length > 0) {
        console.log('📄 Switching to candidates with connections filter - using existing connected candidates');
        // The connected candidates are already in fullCandidates, just make sure they're displayed
        setCandidates(fullCandidates);
        setServerPagination(prev => ({
          ...prev,
          total: fullCandidates.length,
          totalPages: Math.ceil(fullCandidates.length / rowLimit),
          hasNext: fullCandidates.length > rowLimit,
          hasPrev: false
        }));
        return;
      }

      setPageLoading(true);
      try {
        // If connections filter is active and switching to companies
        let connectionCandidateIdsArray: number[] | undefined;
        let excludeCompanyName: string | undefined;

        if (type === 'companies' && selectedConnectionsFilter && connectedCandidateIds.length > 0) {
          // Use candidate IDs for filtering (more reliable than name matching)
          // DON'T pass companyNames - candidateIds with normalized matching is sufficient
          connectionCandidateIdsArray = connectedCandidateIds;

          // Get the selected candidate's company to exclude (stored when we selected the filter)
          excludeCompanyName = selectedConnectionsFilter.company;

          console.log('🏢 Connections filter for companies:', {
            candidateIds: connectionCandidateIdsArray.length,
            excludeCompany: excludeCompanyName
          });
        }

        // Build radius params if radius search is active
        // Check for BOTH filters being active and use dual-param format if so
        const hasBothFilters = companyOfficeRadius.coords && candidateLocationRadius.coords;
        const hasManagerOnlyFilter = type === 'managers' && managerLocationRadius.coords;
        let activeRadiusParams: {
          lat: number; lng: number; distance: number; type?: string;
          companyLat?: number; companyLng?: number; companyDistance?: number;
          candidateLat?: number; candidateLng?: number; candidateDistance?: number;
          managerLat?: number; managerLng?: number; managerDistance?: number;
        } | undefined;

        if (hasManagerOnlyFilter) {
          activeRadiusParams = {
            lat: managerLocationRadius.coords!.lat, lng: managerLocationRadius.coords!.lng, distance: managerLocationRadius.distance,
            managerLat: managerLocationRadius.coords!.lat,
            managerLng: managerLocationRadius.coords!.lng,
            managerDistance: managerLocationRadius.distance
          };
          console.log('📍 Tab switch to managers with manager radius filter:', activeRadiusParams);
        } else if (hasBothFilters) {
          // Dual filter mode
          activeRadiusParams = {
            lat: radiusCoords!.lat, lng: radiusCoords!.lng, distance: radiusDistance,
            companyLat: companyOfficeRadius.coords!.lat,
            companyLng: companyOfficeRadius.coords!.lng,
            companyDistance: companyOfficeRadius.distance,
            candidateLat: candidateLocationRadius.coords!.lat,
            candidateLng: candidateLocationRadius.coords!.lng,
            candidateDistance: candidateLocationRadius.distance
          };
          console.log('📍 Tab switch with DUAL radius filters active:', activeRadiusParams);
        } else if (radiusLocation && radiusCoords) {
          // Single filter mode - use legacy format
          activeRadiusParams = {
            lat: radiusCoords.lat,
            lng: radiusCoords.lng,
            distance: radiusDistance,
            type: radiusType
          };
          console.log('📍 Tab switch with single radius filter active:', activeRadiusParams);
        }

        const newSortColumn = tabSort.column;
        const newSortDirection = tabSort.direction;
        // Use the override if provided, otherwise use current state (which may not be updated yet)
        const effectiveAssociatedCompany = associatedCompanyOverride !== undefined ? associatedCompanyOverride : associatedCompanyFilter;

        const result = await fetchPaginatedData(
          type, 1, rowLimit, newSortColumn, newSortDirection,
          effectiveFilterState.groups, effectiveFilterState.employmentTypes,
          undefined, // Don't use companyNames - use candidateIds instead (has normalized matching)
          connectionCandidateIdsArray,
          excludeCompanyName,
          activeRadiusParams, // Pass radius params if active
          searchTerm, // Pass current search term when switching tabs
          undefined, // crossFilterCompanies
          undefined, // crossFilterOrgs
          effectiveAssociatedCompany || undefined // Associated company name (junction table filter)
        );

        if (type === 'companies') {
          setCompanies(result.data);
          setFullCompanies(result.data);

          setServerPagination({
            enabled: true,
            total: result.pagination.uniqueTotal || result.pagination.total,
            totalPages: result.pagination.totalPages,
            hasNext: result.pagination.hasNext,
            hasPrev: result.pagination.hasPrev
          });
        } else if (type === 'candidates') {
          setCandidates(result.data);
          setServerPagination({
            enabled: true,
            total: result.pagination.total,
            totalPages: result.pagination.totalPages,
            hasNext: result.pagination.hasNext,
            hasPrev: result.pagination.hasPrev
          });
        } else {
          setHiringManagers(result.data);
          setStarredManagerIds(new Set(result.data.filter((m: any) => m.is_starred).map((m: any) => m.id)));
          setServerPagination({
            enabled: true,
            total: result.pagination.total,
            totalPages: result.pagination.totalPages,
            hasNext: result.pagination.hasNext,
            hasPrev: result.pagination.hasPrev
          });
        }

        // Prefetch page 2 (only if not filtered by connections)
        if (result.pagination.hasNext && !(selectedConnectionsFilter && type === 'companies')) {
          prefetchNextPage(type, 2, effectiveFilterGroups, aiFilterState.employmentTypes, activeRadiusParams);
        }
      } catch (error) {
        console.error('Failed to fetch data for new tab:', error);
      } finally {
        setPageLoading(false);
      }
    }
  };

  // Filter history management for back button
  const pushToHistory = (description: string) => {
    setFilterHistory(prev => [...prev, {
      dataType: activeDataType,
      filterState: aiFilterState,
      description,
      page: currentPage,
      associatedCompany: associatedCompanyFilter,
      sort: { column: sortConfig.column, direction: sortConfig.direction }
    }]);
  };

  const handleBackButton = async () => {
    if (filterHistory.length === 0) return;

    const previousState = filterHistory[filterHistory.length - 1];
    setFilterHistory(prev => prev.slice(0, -1));

    // Restore previous state including page position and sort
    setActiveDataType(previousState.dataType);
    setAiFilterState(previousState.filterState);
    setCurrentPage(previousState.page);
    setAssociatedCompanyFilter(previousState.associatedCompany || null);

    // Restore sort config (fall back to default sort for the data type)
    const restoredSort = previousState.sort || defaultSortFor(previousState.dataType, false);
    setSortConfig(restoredSort);

    // Force refetch data for the restored state
    if (serverPagination.enabled && selectedProject?.id) {
      setPrefetchedData(new Map()); // Clear prefetch cache
      setPageLoading(true);

      try {
        const sortColumn = restoredSort.column;

        // Carry radius filter through history navigation — otherwise hitting
        // the back button drops radius and reverts to the unfiltered list.
        const hasBothRadius = companyOfficeRadius.coords && candidateLocationRadius.coords;
        const hasManagerRadius = previousState.dataType === 'managers' && managerLocationRadius.coords;
        let restoreRadiusParams;
        if (hasManagerRadius) {
          restoreRadiusParams = {
            lat: managerLocationRadius.coords!.lat, lng: managerLocationRadius.coords!.lng, distance: managerLocationRadius.distance,
            managerLat: managerLocationRadius.coords!.lat,
            managerLng: managerLocationRadius.coords!.lng,
            managerDistance: managerLocationRadius.distance,
          };
        } else if (hasBothRadius) {
          restoreRadiusParams = {
            lat: radiusCoords!.lat, lng: radiusCoords!.lng, distance: radiusDistance,
            companyLat: companyOfficeRadius.coords!.lat,
            companyLng: companyOfficeRadius.coords!.lng,
            companyDistance: companyOfficeRadius.distance,
            candidateLat: candidateLocationRadius.coords!.lat,
            candidateLng: candidateLocationRadius.coords!.lng,
            candidateDistance: candidateLocationRadius.distance,
          };
        } else if (radiusLocation && radiusCoords) {
          restoreRadiusParams = { lat: radiusCoords.lat, lng: radiusCoords.lng, distance: radiusDistance, type: radiusType };
        }

        const result = await fetchPaginatedData(
          previousState.dataType,
          previousState.page,
          rowLimit,
          sortColumn,
          restoredSort.direction,
          previousState.filterState.groups,
          previousState.filterState.employmentTypes,
          undefined,
          undefined,
          undefined,
          restoreRadiusParams
        );

        if (previousState.dataType === 'companies') {
          setCompanies(result.data);
        } else if (previousState.dataType === 'candidates') {
          setCandidates(result.data);
        } else {
          setHiringManagers(result.data);
          setStarredManagerIds(new Set(result.data.filter((m: any) => m.is_starred).map((m: any) => m.id)));
        }

        // Update pagination info
        setServerPagination({
          enabled: true,
          total: result.pagination.total,
          totalPages: result.pagination.totalPages,
          hasNext: result.pagination.hasNext,
          hasPrev: result.pagination.hasPrev
        });

        // Update server totals for tab count
        if (previousState.dataType === 'companies') {
          setServerTotals(prev => ({
            ...prev,
            companies: result.pagination.uniqueTotal || result.pagination.total
          }));
        }
      } catch (error) {
        console.error('Failed to restore previous state:', error);
      } finally {
        setPageLoading(false);
      }
    }
  };

  const handleCompanyCandidatesClick = (companyName: string, preserveFilters: boolean = false) => {
    tourEvent('company-candidates-opened');
    // Save current state (label reflects the tab they'll return to)
    pushToHistory('Companies');

    // Use junction table association filter — only includes candidates currently working
    // at this company (endDate is null), matching the count shown
    setAssociatedCompanyFilter(companyName);

    let newFilterState: FilterState;

    if (preserveFilters) {
      // Preserve existing filters (but remove any old company field filters)
      const existingGroups = aiFilterState.groups.filter(g =>
        !g.conditions.some(c => (c.column === 'current_company' || c.column === 'company') && c.operator === 'equals')
      );

      newFilterState = {
        groups: existingGroups,
        employmentTypes: aiFilterState.employmentTypes
      };
    } else {
      newFilterState = {
        groups: [],
        employmentTypes: []
      };
    }

    // Switch to candidates tab with association filter
    setAiFilterState(newFilterState);
    handleDataTypeChange('candidates', newFilterState, companyName);
  };

  const handleCompanyManagersClick = (companyName: string) => {
    tourEvent('company-managers-opened');
    // Save current state (label reflects the tab they'll return to)
    pushToHistory('Companies');

    // Use the junction-table association filter rather than an
    // `org equals "<company_name>"` text filter. The junction
    // (hiring_manager_company_associations) is the source of truth and
    // includes HMs linked by LinkedIn ID or normalized name — so e.g. a
    // manager whose org is "Gomboc.ai" still shows under company
    // "Gomboc AI", matching the count on the companies tab. A plain org
    // text match would miss those. Mirrors handleCompanyCandidatesClick.
    setAssociatedCompanyFilter(companyName);

    const emptyFilter: FilterState = { groups: [], employmentTypes: [] };

    // Switch to managers tab with the association override - pass it
    // through handleDataTypeChange so the fetch uses it immediately
    // (avoids a race with state updates).
    setAiFilterState(emptyFilter);
    handleDataTypeChange('managers', emptyFilter, companyName);
  };

  // Handle tag click to filter by that tag
  const handleTagClick = (tag: string, entityType: 'candidate' | 'manager' | 'company') => {
    tourEvent('tag-filter-applied');
    // Save current state
    pushToHistory(`Filtering by tag: ${tag}`);

    // Create tag filter group
    const tagFilterGroup: FilterGroup = {
      id: `tag-${Date.now()}`,
      name: `Tag: ${tag}`,
      logic: 'AND' as const,
      entityType: entityType,
      conditions: [{
        id: Date.now().toString(),
        column: 'tags',
        operator: 'contains' as const,
        value: tag,
        entityType: entityType
      }]
    };

    // Filter out any existing tag filters for the same tag to avoid duplicates
    const existingGroups = aiFilterState.groups.filter(g =>
      !(g.conditions.some(c => c.column === 'tags' && c.value === tag))
    );

    const newFilterState: FilterState = {
      groups: [...existingGroups, tagFilterGroup],
      employmentTypes: aiFilterState.employmentTypes
    };

    setAiFilterState(newFilterState);
    setCurrentPage(1);
  };

  // Handle country click to filter candidates by country
  const handleCountryClick = (country: string) => {
    pushToHistory(`Filtering by country: ${country}`);

    // Build filter based on active data type
    const isManagers = activeDataType === 'managers';
    const countryFilterGroup: FilterGroup = isManagers ? {
      id: `country-${Date.now()}`,
      name: `Country: ${country}`,
      logic: 'AND' as const,
      entityType: 'manager' as const,
      conditions: [
        {
          id: `${Date.now()}-1`,
          column: 'location',
          operator: 'contains' as const,
          value: country,
          entityType: 'manager' as const
        }
      ]
    } : {
      id: `country-${Date.now()}`,
      name: `Country: ${country}`,
      logic: 'OR' as const,
      entityType: 'candidate' as const,
      conditions: [
        {
          id: `${Date.now()}-1`,
          column: 'location_country',
          operator: 'equals' as const,
          value: country,
          entityType: 'candidate' as const
        },
        {
          id: `${Date.now()}-2`,
          column: 'location_full',
          operator: 'contains' as const,
          value: country,
          entityType: 'candidate' as const
        }
      ]
    };

    // Remove any existing country filters to avoid duplicates
    const existingGroups = aiFilterState.groups.filter(g =>
      !(g.name && g.name.startsWith('Country:'))
    );

    const newFilterState: FilterState = {
      groups: [...existingGroups, countryFilterGroup],
      employmentTypes: aiFilterState.employmentTypes
    };

    setAiFilterState(newFilterState);
    setCurrentPage(1);
  };

  // Handle pattern tag click to filter by that pattern tag (companies only)
  const handlePatternTagClick = (patternTag: string) => {
    // Save current state
    pushToHistory(`Filtering by pattern tag: ${patternTag}`);

    // Create pattern tag filter group
    const patternTagFilterGroup: FilterGroup = {
      id: `pattern-tag-${Date.now()}`,
      name: `Pattern: ${patternTag}`,
      logic: 'AND' as const,
      entityType: 'company',
      conditions: [{
        id: Date.now().toString(),
        column: 'pattern_tags',
        operator: 'contains' as const,
        value: patternTag,
        entityType: 'company'
      }]
    };

    // Filter out any existing pattern tag filters for the same tag to avoid duplicates
    const existingGroups = aiFilterState.groups.filter(g =>
      !(g.conditions.some(c => c.column === 'pattern_tags' && c.value === patternTag))
    );

    const newFilterState: FilterState = {
      groups: [...existingGroups, patternTagFilterGroup],
      employmentTypes: aiFilterState.employmentTypes
    };

    setAiFilterState(newFilterState);
    setCurrentPage(1);
  };

  // Handle removing a single tag from an item
  const handleRemoveTag = async (rowId: number, tagToRemove: string, entityType: 'candidate' | 'manager' | 'company') => {
    // Track activity for admin dashboard
    trackActivity();

    const token = localStorage.getItem('token');

    // Find the current item and its tags
    let currentTags: string[] = [];
    if (entityType === 'candidate') {
      const candidate = candidates.find(c => c.id === rowId);
      currentTags = candidate?.tags && Array.isArray(candidate.tags) ? candidate.tags : [];
    } else if (entityType === 'manager') {
      const manager = hiringManagers.find(m => m.id === rowId);
      currentTags = manager?.tags && Array.isArray(manager.tags) ? manager.tags : [];
    } else {
      const company = companies.find(c => c.id === rowId);
      currentTags = company?.tags && Array.isArray(company.tags) ? company.tags : [];
    }

    const newTags = currentTags.filter(t => t !== tagToRemove);

    try {
      let endpoint = '';
      if (entityType === 'candidate') {
        endpoint = `/api/candidates/${rowId}/tags`;
      } else if (entityType === 'manager') {
        endpoint = `/api/hiring-managers/${rowId}/tags`;
      } else {
        endpoint = `/api/companies/${rowId}/tags`;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tags: newTags })
      });

      if (response.ok) {
        // Update local state
        if (entityType === 'candidate') {
          setCandidates(candidates.map(c => c.id === rowId ? { ...c, tags: newTags } : c));
          setFullCandidates(fullCandidates.map(c => c.id === rowId ? { ...c, tags: newTags } : c));
        } else if (entityType === 'manager') {
          setHiringManagers(hiringManagers.map(m => m.id === rowId ? { ...m, tags: newTags } : m));
          setFullHiringManagers(fullHiringManagers.map(m => m.id === rowId ? { ...m, tags: newTags } : m));
        } else {
          setCompanies(companies.map(c => c.id === rowId ? { ...c, tags: newTags } : c));
          setFullCompanies(fullCompanies.map(c => c.id === rowId ? { ...c, tags: newTags } : c));
        }
      }
    } catch (error) {
      console.error('Error removing tag:', error);
    }
  };

  const handleToggleStar = async (managerId: number) => {
    // Track activity for admin dashboard
    trackActivity();

    const isCurrentlyStarred = starredManagerIds.has(managerId);

    // Optimistically update UI
    const newStarredIds = new Set(starredManagerIds);
    if (isCurrentlyStarred) {
      newStarredIds.delete(managerId);
    } else {
      newStarredIds.add(managerId);
    }
    setStarredManagerIds(newStarredIds);

    try {
      const token = localStorage.getItem('token');

      // Call API to persist starred state
      const response = await fetch(`/api/hiring-managers/${managerId}/star`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          is_starred: !isCurrentlyStarred
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update star status');
      }

      // Auto-tag as "decision maker" when starring, remove when unstarring
      const manager = displayData.find((m: any) => m.id === managerId);
      const existingTags = Array.isArray(manager?.tags) ? manager.tags : [];

      if (!isCurrentlyStarred) {
        // Starring - add "decision maker" tag if not present
        if (!existingTags.includes('decision maker')) {
          const tagResponse = await fetch(`/api/hiring-managers/${managerId}/tags`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              tags: [...existingTags, 'decision maker']
            })
          });

          if (tagResponse.ok) {
            const updatedManagers = hiringManagers.map(m =>
              m.id === managerId ? { ...m, tags: [...existingTags, 'decision maker'] } : m
            );
            setHiringManagers(updatedManagers);
            setFullHiringManagers(fullHiringManagers.map(m =>
              m.id === managerId ? { ...m, tags: [...existingTags, 'decision maker'] } : m
            ));
          }
        }
      } else {
        // Unstarring - remove "decision maker" tag if present
        if (existingTags.includes('decision maker')) {
          const newTags = existingTags.filter((t: string) => t !== 'decision maker');
          const tagResponse = await fetch(`/api/hiring-managers/${managerId}/tags`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              tags: newTags
            })
          });

          if (tagResponse.ok) {
            const updatedManagers = hiringManagers.map(m =>
              m.id === managerId ? { ...m, tags: newTags } : m
            );
            setHiringManagers(updatedManagers);
            setFullHiringManagers(fullHiringManagers.map(m =>
              m.id === managerId ? { ...m, tags: newTags } : m
            ));
          }
        }
      }
    } catch (error) {
      console.error('Error toggling star:', error);
      // Revert on error
      setStarredManagerIds(starredManagerIds);
    }
  };

  const [allResultsSelected, setAllResultsSelected] = useState(false);
  const [loadingAllIds, setLoadingAllIds] = useState(false);
  // The exact request body of the current tab's paginated fetch (filter groups,
  // employment types, radius, connections, etc.). "Select all matching" reuses
  // it so the id fetch matches the FILTERED view — without it, select-all
  // selected the whole project regardless of the active filter.
  const lastMainFetchBodyRef = useRef<Record<string, any> | null>(null);

  const handleSelectAll = () => {
    if (selectedRows.size === displayData.length) {
      setSelectedRows(new Set());
      setAllResultsSelected(false);
    } else {
      setSelectedRows(new Set(displayData.map((row: any) => row.id)));
      setAllResultsSelected(false);
    }
  };

  const handleSelectAllResults = async () => {
    setLoadingAllIds(true);
    try {
      const endpoint = activeDataType === 'companies' ? 'companies' : activeDataType === 'candidates' ? 'candidates' : 'hiring-managers';
      // Reuse the current tab's EXACT filter body so the id set matches the
      // filtered view. Falls back to search-only if no fetch has run yet.
      const base = lastMainFetchBodyRef.current || { ...(searchTerm && { search: searchTerm }) };
      const response = await fetch(
        `/api/${endpoint}/${selectedProject?.id}/paginated?idsOnly=true`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          cache: 'no-store',
          body: JSON.stringify({ ...base, page: 1, pageSize: 1, idsOnly: 'true' })
        }
      );
      if (response.ok) {
        const data = await response.json();
        setSelectedRows(new Set(data.ids));
        setAllResultsSelected(true);
      }
    } catch (err) {
      console.error('Failed to fetch all matching IDs:', err);
    } finally {
      setLoadingAllIds(false);
    }
  };

  const handleSelectRow = (id: number, index: number, event: React.MouseEvent<HTMLInputElement>) => {
    const newSelected = new Set(selectedRows);

    // Shift-click: select range between last selected and current
    if (event.shiftKey && lastSelectedIndexRef.current !== null) {
      const start = Math.min(lastSelectedIndexRef.current, index);
      const end = Math.max(lastSelectedIndexRef.current, index);

      // Select all rows in the range
      for (let i = start; i <= end; i++) {
        const rowId = displayData[i]?.id;
        if (rowId !== undefined) {
          newSelected.add(rowId);
        }
      }
    } else {
      // Regular click: toggle single row
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
    }

    lastSelectedIndexRef.current = index;
    setSelectedRows(newSelected);
  };

// Copy/move selected candidates into Hiring Managers (contacts). Carries the
// full enriched profile over (position history, education, summary become the
// contact's enrichment_data) and associates the contact with every company the
// candidate holds a current/Present role at.
const [copyToContactsModal, setCopyToContactsModal] = useState(false);
const [copyToContactsMode, setCopyToContactsMode] = useState<'copy' | 'move'>('copy');
const [copyingToContacts, setCopyingToContacts] = useState(false);
const [copyToContactsMsg, setCopyToContactsMsg] = useState<string | null>(null);

const handleCopyToContacts = async () => {
  if (selectedRows.size === 0 || !selectedProject?.id) return;
  setCopyingToContacts(true); setCopyToContactsMsg(null);
  try {
    const ids = Array.from(selectedRows);
    const res = await fetch(`/api/hiring-managers/${selectedProject.id}/from-candidates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ candidateIds: ids, mode: copyToContactsMode }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed');
    const parts = [];
    if (data.created) parts.push(`${data.created} contact${data.created === 1 ? '' : 's'} created`);
    if (data.updated) parts.push(`${data.updated} existing upgraded`);
    if (data.associations) parts.push(`${data.associations} company link${data.associations === 1 ? '' : 's'}`);
    setCopyToContactsMsg((parts.join(', ') || 'Done') + '. See the Hiring Managers tab.');
    if (copyToContactsMode === 'move') {
      const idSet = new Set(ids);
      setCandidates(prev => prev.filter((r: any) => !idSet.has(r.id)));
      setFullCandidates(prev => prev.filter((r: any) => !idSet.has(r.id)));
      setSelectedRows(new Set());
    }
  } catch (e: any) {
    setCopyToContactsMsg(e.message || 'Failed to copy to contacts');
  } finally {
    setCopyingToContacts(false);
  }
};

const handleBulkEnrich = async (enrichType: 'work_email' | 'personal_email' | 'both_emails' | 'phone' | 'work_email_phone' | 'personal_email_phone' | 'all') => {
  if (selectedRows.size === 0) return;

  // Track activity for admin dashboard
  trackActivity();

  setEnriching(true);
  setShowEnrichMenu(false);

  let costPerContact = 0;
  switch (enrichType) {
    case 'work_email': costPerContact = 1; break;
    case 'personal_email': costPerContact = 3; break;
    case 'both_emails': costPerContact = 4; break;
    case 'phone': costPerContact = 12; break;
    case 'work_email_phone': costPerContact = 13; break;
    case 'personal_email_phone': costPerContact = 15; break;
    case 'all': costPerContact = 16; break;
  }

  const totalCost = selectedRows.size * costPerContact;
  const endpoint = activeDataType === 'candidates' ? 'candidates' : 'managers';
  const ids = Array.from(selectedRows);
  const BATCH_SIZE = 100; // FullEnrich supports up to 100 contacts per API call
  const DELAY_BETWEEN_BATCHES_MS = 1100; // Slight delay between batches to stay under 60 req/min

  try {
    let successCount = 0;
    let failCount = 0;
    const instantMessages: string[] = [];
    let skippedInProgressTotal = 0;
    let skippedAlreadyEnrichedTotal = 0;
    let isInstantProvider = false;
    let enrichProvider = '';
    const totalBatches = Math.ceil(ids.length / BATCH_SIZE);

    // Process in batches of up to 100 contacts per API call
    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      const batchStart = batchIndex * BATCH_SIZE;
      const batchEnd = Math.min(batchStart + BATCH_SIZE, ids.length);
      const batchIds = ids.slice(batchStart, batchEnd);

      setEnrichmentMessage(`🔍 Sending batch ${batchIndex + 1}/${totalBatches} (${batchIds.length} contacts)...`);

      try {
        const response = await fetch(`/api/fullenrich/enrich/${endpoint}/bulk`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            [activeDataType === 'candidates' ? 'candidateIds' : 'managerIds']: batchIds,
            enrichType,
            skipAlreadyEnriched: true
          })
        });

        if (response.ok) {
          const result = await response.json();
          successCount += result.sentCount || result.count || 0;
          failCount += result.failedCount || 0;
          skippedInProgressTotal += result.skippedInProgress || 0;
          skippedAlreadyEnrichedTotal += result.skippedAlreadyEnriched || 0;
          if (result.instant) isInstantProvider = true;
          if (result.provider) enrichProvider = result.provider;
          // Instant providers (Cognism, Lusha) return the actual outcome —
          // found / not found / refused — show it instead of "started".
          if (result.instant && result.message) instantMessages.push(String(result.message));
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error(`Batch ${batchIndex + 1} failed:`, errorData);
          failCount += batchIds.length;
          if (errorData && errorData.error) instantMessages.push(String(errorData.error));
        }
      } catch (err) {
        console.error(`Batch ${batchIndex + 1} error:`, err);
        failCount += batchIds.length;
      }

      // Add delay between batches (but not after the last one)
      if (batchIndex < totalBatches - 1) {
        await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES_MS));
      }
    }

    // Build result message
    let message = '';
    if (successCount > 0) {
      message = `✅ Enrichment started for ${successCount} contacts!`;

      // Optimistic UI: immediately show "Searching..." in the relevant columns
      const searchFlags: Record<string, boolean> = {};
      if (['work_email', 'both_emails', 'work_email_phone', 'all'].includes(enrichType)) searchFlags.email_searched = true;
      if (['personal_email', 'both_emails', 'personal_email_phone', 'all'].includes(enrichType)) searchFlags.personal_email_searched = true;
      if (['phone', 'work_email_phone', 'personal_email_phone', 'all'].includes(enrichType)) searchFlags.phone_searched = true;

      const selectedIdSet = new Set(ids);
      const updateRows = (rows: any[]) => rows.map(row =>
        selectedIdSet.has(row.id) ? { ...row, enrichment_status: 'in_progress', ...searchFlags } : row
      );

      if (activeDataType === 'candidates') {
        setCandidates(prev => updateRows(prev));
      } else if (activeDataType === 'managers') {
        setHiringManagers(prev => updateRows(prev));
      }
    } else {
      message = `ℹ️ No new contacts to enrich.`;
    }

    if (skippedInProgressTotal > 0) {
      message += ` ⏳ ${skippedInProgressTotal} already in progress.`;
    }
    if (skippedAlreadyEnrichedTotal > 0) {
      message += ` ✓ ${skippedAlreadyEnrichedTotal} already enriched.`;
    }
    if (failCount > 0) {
      message += ` ⚠️ ${failCount} failed.`;
    }
    if (successCount > 0 && !isInstantProvider) {
      message += ' Results will appear in 30-90 seconds.';
    }

    setEnrichmentMessage(instantMessages.length
      ? `${failCount ? '⚠️' : 'ℹ️'} ${Array.from(new Set(instantMessages)).join(' ')}`
      : message);

    // Build radius params so post-enrich refresh respects an active radius
    // filter — without this the table reverts to the unfiltered set.
    const buildEnrichRefreshRadiusParams = () => {
      const hasBothRadius = companyOfficeRadius.coords && candidateLocationRadius.coords;
      if (hasBothRadius) {
        return {
          lat: radiusCoords!.lat, lng: radiusCoords!.lng, distance: radiusDistance,
          companyLat: companyOfficeRadius.coords!.lat,
          companyLng: companyOfficeRadius.coords!.lng,
          companyDistance: companyOfficeRadius.distance,
          candidateLat: candidateLocationRadius.coords!.lat,
          candidateLng: candidateLocationRadius.coords!.lng,
          candidateDistance: candidateLocationRadius.distance,
        };
      }
      if (radiusCoords && radiusDistance > 0) {
        return { lat: radiusCoords.lat, lng: radiusCoords.lng, distance: radiusDistance, type: radiusType };
      }
      return undefined;
    };

    // Only set up refresh if there were successful enrichments
    if (successCount > 0) {
      if (isInstantProvider) {
        // Instant providers (Cognism, Lusha): data is already saved, re-fetch current page with filters
        const sortColumn = sortConfig.column || (activeDataType === 'companies' ? 'candidate_count' : 'name');
        const refreshRadiusParams = buildEnrichRefreshRadiusParams();
        const result = await fetchPaginatedData(
          activeDataType,
          currentPage,
          rowLimit,
          sortColumn,
          sortConfig.direction,
          effectiveFilterGroups,
          aiFilterState.employmentTypes,
          undefined,
          undefined,
          undefined,
          refreshRadiusParams
        );
        if (activeDataType === 'candidates') {
          setCandidates(result.data);
        } else if (activeDataType === 'managers') {
          setHiringManagers(result.data);
          setStarredManagerIds(new Set(result.data.filter((m: any) => m.is_starred).map((m: any) => m.id)));
        } else {
          setCompanies(result.data);
        }
        // Update pagination state to stay in sync
        if (result.pagination) {
          setServerPagination({
            enabled: true,
            total: result.pagination.total,
            totalPages: result.pagination.totalPages,
            hasNext: result.pagination.hasNext,
            hasPrev: result.pagination.hasPrev
          });
        }
        setEnrichmentMessage(`✅ Enrichment complete! Data updated.`);
        setSelectedRows(new Set());

        // Apollo: phone numbers arrive ~3-5s later via webhook, do a silent re-fetch
        if (enrichProvider === 'apollo') {
          setTimeout(async () => {
            try {
              const refreshResult = await fetchPaginatedData(
                activeDataType,
                currentPage,
                rowLimit,
                sortColumn,
                sortConfig.direction,
                effectiveFilterGroups,
                aiFilterState.employmentTypes,
                undefined,
                undefined,
                undefined,
                buildEnrichRefreshRadiusParams()
              );
              if (activeDataType === 'candidates') {
                setCandidates(refreshResult.data);
              } else if (activeDataType === 'managers') {
                setHiringManagers(refreshResult.data);
                setStarredManagerIds(new Set(refreshResult.data.filter((m: any) => m.is_starred).map((m: any) => m.id)));
              }
            } catch (e) { /* silent refresh */ }
          }, 6000);
        }

        setTimeout(() => {
          setEnrichmentMessage(null);
        }, 3000);
      } else {
        // Async providers (FullEnrich): wait for webhook, then refresh
        setTimeout(async () => {
          const sortColumn = sortConfig.column || (activeDataType === 'companies' ? 'candidate_count' : 'name');
          const result = await fetchPaginatedData(
            activeDataType,
            currentPage,
            rowLimit,
            sortColumn,
            sortConfig.direction,
            effectiveFilterGroups,
            aiFilterState.employmentTypes,
            undefined,
            undefined,
            undefined,
            buildEnrichRefreshRadiusParams()
          );
          if (activeDataType === 'candidates') {
            setCandidates(result.data);
          } else if (activeDataType === 'managers') {
            setHiringManagers(result.data);
            setStarredManagerIds(new Set(result.data.filter((m: any) => m.is_starred).map((m: any) => m.id)));
          } else {
            setCompanies(result.data);
          }
          if (result.pagination) {
            setServerPagination({
              enabled: true,
              total: result.pagination.total,
              totalPages: result.pagination.totalPages,
              hasNext: result.pagination.hasNext,
              hasPrev: result.pagination.hasPrev
            });
          }
          setEnrichmentMessage(`✅ Enrichment complete! Data refreshed.`);
          setSelectedRows(new Set());
          setTimeout(() => {
            setEnrichmentMessage(null);
          }, 3000);
        }, 65000);
      }
    } else {
      setTimeout(() => {
        setEnrichmentMessage(null);
        setSelectedRows(new Set());
      }, 5000);
    }

  } catch (error) {
    console.error('Bulk enrichment error:', error);
    setEnrichmentMessage('❌ Failed to enrich contacts. Please try again.');
    setTimeout(() => setEnrichmentMessage(null), 5000);
  } finally {
    setEnriching(false);
  }
};

// ─── Shortlist handlers ────────────────────────────────────────────
const fetchOpenJobs = async () => {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    const resp = await fetch('/api/campaigns?mode=delivery&status=open', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (resp.ok) {
      const data = await resp.json();
      // Unified campaigns expose `name`; keep the `title` shape callers expect.
      setShortlistJobs(data.map((c: any) => ({ id: c.id, title: c.name, company_name: c.company_name })));
    }
  } catch (err) {
    console.error('Failed to fetch open jobs:', err);
  }
};

const handleShortlist = async (jobId: number) => {
  if (selectedRows.size === 0) return;
  setShortlisting(true);
  setShowShortlistDropdown(false);

  const entityType = activeDataType === 'managers' ? 'contact' : 'candidate';
  const entities = Array.from(selectedRows).map(id => ({ type: entityType, id }));

  try {
    const token = localStorage.getItem('token');
    // Unified API: bulk-add to the campaign's first (Shortlist) stage.
    const resp = await fetch(`/api/campaigns/${jobId}/cards/bulk`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ entities }),
    });
    if (!resp.ok) throw new Error('Failed to shortlist');
    const data = await resp.json();

    const jobName = shortlistJobs.find(j => j.id === jobId)?.title || 'job';
    if (data.added > 0) {
      setShortlistMessage(`Added ${data.added} to campaign "${jobName}"`);
    } else {
      setShortlistMessage(`Already in campaign "${jobName}"`);
    }
    setTimeout(() => setShortlistMessage(null), 4000);
    setSelectedRows(new Set());
  } catch (err) {
    console.error('Shortlist error:', err);
    setShortlistMessage('Failed to shortlist. Please try again.');
    setTimeout(() => setShortlistMessage(null), 4000);
  } finally {
    setShortlisting(false);
  }
};

// ─── BD Campaign handlers ────────────────────────────────────────────
const fetchBdCampaigns = async () => {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    const resp = await fetch('/api/campaigns?mode=bd&status=active', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (resp.ok) {
      const data = await resp.json();
      setBdCampaignsList(data.map((c: any) => ({
        id: c.id,
        name: c.name,
        status: c.status,
        stages: c.stages,
      })));
    }
  } catch (err) {
    console.error('Failed to fetch BD campaigns:', err);
  }
};

const handleAddToBdCampaign = async (campaignId: number) => {
  if (selectedRows.size === 0) return;
  setAddingToCampaign(true);
  setShowBdCampaignDropdown(false);

  const entityType = activeDataType === 'companies' ? 'company' : 'contact';
  const entities = Array.from(selectedRows).map(id => ({ type: entityType, id }));

  // Find the first stage (Target / position 0) for the selected campaign
  const campaign = bdCampaignsList.find(c => c.id === campaignId);
  const firstStage = campaign?.stages?.sort((a, b) => a.position - b.position)?.[0];

  if (!firstStage) {
    setBdCampaignMessage('Campaign has no stages configured');
    setTimeout(() => setBdCampaignMessage(null), 4000);
    setAddingToCampaign(false);
    return;
  }

  try {
    const token = localStorage.getItem('token');
    const resp = await fetch(`/api/campaigns/${campaignId}/cards/bulk`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage_id: firstStage.id, entities }),
    });
    if (!resp.ok) throw new Error('Failed to add to campaign');
    const data = await resp.json();

    const campaignName = campaign?.name || 'campaign';
    if (data.added > 0) {
      setBdCampaignMessage(`Added ${data.added} to BD Campaign "${campaignName}"`);
    } else {
      setBdCampaignMessage(`Already in BD Campaign "${campaignName}"`);
    }
    setTimeout(() => setBdCampaignMessage(null), 4000);
    setSelectedRows(new Set());
  } catch (err) {
    console.error('BD Campaign error:', err);
    setBdCampaignMessage('Failed to add to campaign. Please try again.');
    setTimeout(() => setBdCampaignMessage(null), 4000);
  } finally {
    setAddingToCampaign(false);
  }
};

const handleDelete = async (includeCascade: boolean = false) => {
  if (selectedRows.size === 0) return;
  if (deleting) return; // Prevent duplicate submissions while a delete is in flight

  // Track activity for admin dashboard
  trackActivity();

  const rowCount = selectedRows.size;
  const dataType = activeDataType;

  setDeleting(true);
  setShowDeleteConfirm(false);

  // Campaign Leads List: "delete" removes members FROM THE CAMPAIGN only —
  // it must never delete the underlying candidate/company/contact from the
  // project. (Outside a campaign, it's a real delete, handled below.)
  if (campaignScopeId) {
    setDeleteMessage(`Removing ${rowCount} from campaign…`);
    try {
      const entityType = activeDataType === 'candidates' ? 'candidate'
        : activeDataType === 'managers' ? 'contact' : 'company';
      const res = await fetch(`/api/campaigns/${campaignScopeId}/cards/remove`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityType, entityIds: Array.from(selectedRows) })
      });
      if (!res.ok) throw new Error('Failed to remove from campaign');
      setSelectedRows(new Set());
      setCurrentPage(1);
      setRefreshTrigger(prev => prev + 1);
      setDeleteMessage(`✅ Removed ${rowCount} from campaign.`);
      setTimeout(() => setDeleteMessage(null), 2000);
    } catch (error) {
      console.error('Remove-from-campaign error:', error);
      setDeleteMessage('❌ Failed to remove from campaign.');
      setTimeout(() => setDeleteMessage(null), 5000);
    } finally {
      setDeleting(false);
    }
    return;
  }

  setDeleteMessage(`🗑️ Deleting ${rowCount} ${dataType}...`);

  try {
    const ids = Array.from(selectedRows);
    let endpoint = '';
    
    if (dataType === 'companies') {
      // Single bulk call — backend expands the selected IDs to "all offices
      // of those companies" and deletes them in one transaction. Replaces
      // the old per-ID loop with 100ms sleeps that took 5+ min for 750 rows.
      endpoint = 'companies';
      const response = await fetch(`/api/companies/bulk/${selectedProject.id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, cascade: includeCascade })
      });
      if (!response.ok) {
        throw new Error('Failed to delete companies');
      }
    } else if (dataType === 'candidates') {
      // Bulk delete in batches of 500
      const batchSize = 500;
      for (let i = 0; i < ids.length; i += batchSize) {
        const batch = ids.slice(i, i + batchSize);
        setDeleteMessage(`🗑️ Deleting ${dataType}... (${Math.min(i + batchSize, ids.length)}/${ids.length})`);
        const response = await fetch(`/api/candidates/bulk/${selectedProject.id}`, {
          method: 'DELETE',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: batch })
        });
        if (!response.ok) {
          throw new Error(`Failed to delete candidates batch`);
        }
      }
    } else {
      // Connected-projects check: these contacts may also exist in the
      // original project / sibling subprojects (family sync). Ask whether to
      // remove them there too before deleting.
      let includeFamily = false;
      try {
        const chk = await fetch(`/api/hiring-managers/${selectedProject.id}/family-delete-check`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids })
        });
        if (chk.ok) {
          const { matches } = await chk.json();
          if (Array.isArray(matches) && matches.length > 0) {
            const lines = matches.map((m: any) => `• ${m.count} in "${m.projectName}"`).join('\n');
            includeFamily = window.confirm(
              `Some of these contacts also appear in connected projects:\n${lines}\n\n` +
              `OK = remove them from those projects too\nCancel = only remove from this project`
            );
          }
        }
      } catch { /* check is best-effort; fall through to normal delete */ }

      // Bulk delete in batches of 500
      const batchSize = 500;
      for (let i = 0; i < ids.length; i += batchSize) {
        const batch = ids.slice(i, i + batchSize);
        setDeleteMessage(`🗑️ Deleting ${dataType}... (${Math.min(i + batchSize, ids.length)}/${ids.length})`);
        const response = await fetch(`/api/hiring-managers/bulk/${selectedProject.id}`, {
          method: 'DELETE',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: batch, includeFamily })
        });
        if (!response.ok) {
          throw new Error(`Failed to delete managers batch`);
        }
      }
    }
    
    setDeleteMessage(`✅ Deleted ${rowCount} ${dataType}. Refreshing...`);

    // Clear selection immediately
    setSelectedRows(new Set());

    // Reset to page 1 and trigger paginated data refresh
    setCurrentPage(1);
    setRefreshTrigger(prev => prev + 1);

    // Also refresh the full (non-paginated) data for map/analytics
    await refreshAllData();
    
    setDeleteMessage(`✅ Successfully deleted ${rowCount} ${dataType}!`);
    
    setTimeout(() => {
      setDeleteMessage(null);
    }, 2000);
    
  } catch (error) {
    console.error('Delete error:', error);
    setDeleteMessage(`❌ Failed to delete ${dataType}. Please try again.`);
    setTimeout(() => setDeleteMessage(null), 5000);
  } finally {
    setDeleting(false);
  }
};

const handleDeleteAllOffices = async (includeCascade: boolean = false) => {
  if (selectedRows.size !== 1 || activeDataType !== 'companies') return;
  
  // Get the selected company
  const companyId = Array.from(selectedRows)[0];
  const company = companies.find(c => c.id === companyId);
  if (!company) return;
  
  setDeleting(true);
  setShowDeleteConfirm(false);
  setDeleteMessage(`🗑️ Deleting ALL offices of ${company.company_name}...`);
  
  try {
    const cascadeParam = includeCascade ? '?cascade=true' : '';
    const url = `/api/companies/${selectedProject.id}/by-name/${encodeURIComponent(company.company_name)}${cascadeParam}`;
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete all offices');
    }
    
    const result = await response.json();
    
    setDeleteMessage(`✅ Deleted ${result.deletedOffices} offices of ${company.company_name}. Refreshing...`);

    // Clear selection
    setSelectedRows(new Set());

    // Reset to page 1 and trigger paginated data refresh
    setCurrentPage(1);
    setRefreshTrigger(prev => prev + 1);

    // Also refresh the full (non-paginated) data for map/analytics
    await refreshAllData();
    
    setDeleteMessage(`✅ Successfully deleted all offices of ${company.company_name}!`);
    
    setTimeout(() => {
      setDeleteMessage(null);
    }, 2000);
    
  } catch (error) {
    console.error('Delete all offices error:', error);
    setDeleteMessage(`❌ Failed to delete all offices. Please try again.`);
    setTimeout(() => setDeleteMessage(null), 5000);
  } finally {
    setDeleting(false);
  }
};

const refreshAllData = async () => {
  setLoading(true);
  try {
    // Add cache-busting query parameter
    const timestamp = new Date().getTime();
    
    const companiesResponse = await fetch(`/api/companies/${selectedProject.id}?_=${timestamp}`, {
      headers: getAuthHeaders(),
      cache: 'no-store'  // Force no caching
    });
    if (companiesResponse.ok) {
      const companiesData = await companiesResponse.json();
      // Show all companies in data table (not just geocoded ones)
      setFullCompanies(companiesData);
    }

    const candidatesResponse = await fetch(`/api/candidates/${selectedProject.id}?_=${timestamp}`, {
      headers: getAuthHeaders(),
      cache: 'no-store'
    });
    if (candidatesResponse.ok) {
      const candidatesData = await candidatesResponse.json();
      setFullCandidates(candidatesData);
    }

    const managersResponse = await fetch(`/api/hiring-managers/${selectedProject.id}?_=${timestamp}`, {
      headers: getAuthHeaders(),
      cache: 'no-store'
    });
    if (managersResponse.ok) {
      const managersData = await managersResponse.json();
      setFullHiringManagers(managersData);
    }
  } catch (error) {
    console.error('Error refreshing data:', error);
  } finally {
    setLoading(false);
  }
};

// Refresh current page with filters preserved (used by manual refresh button)
const handleManualRefresh = async () => {
  if (!selectedProject?.id || !serverPagination.enabled) {
    // Fallback for non-server-side mode
    setRefreshTrigger(prev => prev + 1);
    return;
  }

  setPageLoading(true);
  try {
    const sortColumn = sortConfig.column || (activeDataType === 'companies' ? 'candidate_count' : 'name');

    // Carry radius filter through the manual refresh — without this the
    // table reverts to the unfiltered set after a user enriches a contact
    // and hits the refresh icon.
    const hasBothRadius = companyOfficeRadius.coords && candidateLocationRadius.coords;
    let manualRefreshRadiusParams;
    if (hasBothRadius) {
      manualRefreshRadiusParams = {
        lat: radiusCoords!.lat, lng: radiusCoords!.lng, distance: radiusDistance,
        companyLat: companyOfficeRadius.coords!.lat,
        companyLng: companyOfficeRadius.coords!.lng,
        companyDistance: companyOfficeRadius.distance,
        candidateLat: candidateLocationRadius.coords!.lat,
        candidateLng: candidateLocationRadius.coords!.lng,
        candidateDistance: candidateLocationRadius.distance,
      };
    } else if (radiusCoords && radiusDistance > 0) {
      manualRefreshRadiusParams = { lat: radiusCoords.lat, lng: radiusCoords.lng, distance: radiusDistance, type: radiusType };
    }

    const result = await fetchPaginatedData(
      activeDataType,
      currentPage,
      rowLimit,
      sortColumn,
      sortConfig.direction,
      effectiveFilterGroups,
      aiFilterState.employmentTypes,
      undefined,
      undefined,
      undefined,
      manualRefreshRadiusParams
    );

    if (activeDataType === 'companies') {
      setCompanies(result.data);
    } else if (activeDataType === 'candidates') {
      setCandidates(result.data);
    } else {
      setHiringManagers(result.data);
      setStarredManagerIds(new Set(result.data.filter((m: any) => m.is_starred).map((m: any) => m.id)));
    }

    setServerPagination({
      enabled: true,
      total: result.pagination.total,
      totalPages: result.pagination.totalPages,
      hasNext: result.pagination.hasNext,
      hasPrev: result.pagination.hasPrev
    });
  } catch (error) {
    console.error('Error refreshing data:', error);
  } finally {
    setPageLoading(false);
  }
};

// Fetch all tags for autocomplete
const fetchAllTags = async () => {
  try {
    const endpoint = activeDataType === 'companies'
      ? 'companies'
      : activeDataType === 'managers'
        ? 'hiring-managers'
        : 'candidates';
    const response = await fetch(`/api/${endpoint}/${selectedProject.id}/tags`, {
      headers: getAuthHeaders()
    });

    if (response.ok) {
      const data = await response.json();
      setAllTags(data.tags || []);
    }
  } catch (error) {
    console.error('Error fetching tags:', error);
  }
};

// Handle add tag - ALWAYS tags all offices of a company
const handleAddTag = async () => {
  if (!newTag.trim()) return;

  // Track activity for admin dashboard
  trackActivity();

  setTagging(true);
  
  try {
    const ids = Array.from(selectedRows);
    const endpoint = activeDataType === 'companies' ? 'companies' : 'candidates';
    
    if (activeDataType === 'companies') {
      // Send the selected IDs in ONE request; the backend expands to every
      // office by company name. The old per-company loop derived office IDs
      // from `fullCompanies`, which only holds loaded rows — so a selected
      // company on another page produced an empty ID list and a 400.
      const response = await fetch(`/api/companies/${selectedProject.id}/bulk-tag`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          companyIds: ids,
          tags: [newTag.trim()]
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to add tag');
      }
    } else if (activeDataType === 'managers') {
      // For hiring managers, use the hiring managers bulk-tag endpoint
      const response = await fetch(`/api/hiring-managers/${selectedProject.id}/bulk-tag`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          managerIds: ids,
          tags: [newTag.trim()]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add tag to hiring managers');
      }
    } else {
      // For candidates, tag individually
      const response = await fetch(`/api/candidates/${selectedProject.id}/bulk-tag`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          candidateIds: ids,
          tags: [newTag.trim()]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add tag');
      }
    }
    
    // Close modal and refresh
    tourEvent('tag-added');
    setShowTagModal(false);
    const tagValue = newTag.trim();
    setNewTag('');
    setSelectedRows(new Set());

    // Show success message
    const itemType = activeDataType === 'companies' ? 'companies (all offices)' : activeDataType === 'managers' ? 'hiring managers' : 'candidates';
    setDeleteMessage(`✅ Added tag "${tagValue}" to ${ids.length} ${itemType}`);
    setTimeout(() => setDeleteMessage(null), 3000);

    // Refresh the autocomplete list so a brand-new tag added in this
    // batch shows up immediately the next time the picker opens.
    fetchAllTags();

    // Update local state directly — no full page refresh needed.
    // This preserves scroll position and is instant.
    if (activeDataType === 'candidates') {
      const updateCand = (c: any) => ids.includes(c.id)
        ? { ...c, tags: [...(Array.isArray(c.tags) ? c.tags : []), tagValue].filter((t, i, a) => a.indexOf(t) === i) }
        : c;
      setCandidates(candidates.map(updateCand));
      setFullCandidates(fullCandidates.map(updateCand));
    } else if (activeDataType === 'managers') {
      const updateMgr = (m: any) => ids.includes(m.id)
        ? { ...m, tags: [...(Array.isArray(m.tags) ? m.tags : []), tagValue].filter((t, i, a) => a.indexOf(t) === i) }
        : m;
      setHiringManagers(hiringManagers.map(updateMgr));
      setFullHiringManagers(fullHiringManagers.map(updateMgr));
    } else if (activeDataType === 'companies') {
      const taggedNames = new Set(ids.map(id => companies.find(c => c.id === id)?.company_name).filter(Boolean));
      const updateComp = (c: any) => taggedNames.has(c.company_name)
        ? { ...c, tags: [...(Array.isArray(c.tags) ? c.tags : []), tagValue].filter((t, i, a) => a.indexOf(t) === i) }
        : c;
      setCompanies(companies.map(updateComp));
      setFullCompanies(fullCompanies.map(updateComp));
    }
    
  } catch (error) {
    console.error('Error adding tag:', error);
    setDeleteMessage(`❌ Failed to add tag. Please try again.`);
    setTimeout(() => setDeleteMessage(null), 5000);
  } finally {
    setTagging(false);
  }
};

// Handle bulk remove tag
const handleBulkRemoveTag = async () => {
  if (!tagToRemove.trim()) return;

  // Track activity for admin dashboard
  trackActivity();

  setRemovingTag(true);

  try {
    const ids = Array.from(selectedRows);

    if (activeDataType === 'companies') {
      // Send the selected company ids directly — the backend expands each to
      // ALL offices sharing its company_name and removes the tag from them.
      // (Previously we expanded to office ids client-side from `fullCompanies`,
      // which returned an EMPTY list whenever fullCompanies didn't hold every
      // office — e.g. large server-paginated projects — and the backend then
      // rejected the empty companyIds with a 400, so the untag silently failed.)
      const response = await fetch(`/api/companies/${selectedProject.id}/bulk-remove-tag`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          companyIds: ids,
          tags: [tagToRemove.trim()]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove tag');
      }
    } else if (activeDataType === 'candidates') {
      const response = await fetch(`/api/candidates/${selectedProject.id}/bulk-remove-tag`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          candidateIds: ids,
          tags: [tagToRemove.trim()]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove tag');
      }
    } else {
      // Hiring managers
      const response = await fetch(`/api/hiring-managers/${selectedProject.id}/bulk-remove-tag`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          managerIds: ids,
          tags: [tagToRemove.trim()]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove tag');
      }
    }

    // Close modal and refresh
    setShowRemoveTagModal(false);
    setTagToRemove('');
    setSelectedRows(new Set());

    // Show success message
    const itemType = activeDataType === 'companies' ? 'companies' : activeDataType === 'candidates' ? 'candidates' : 'hiring managers';
    setDeleteMessage(`✅ Removed tag "${tagToRemove}" from ${ids.length} ${itemType}`);
    setTimeout(() => setDeleteMessage(null), 3000);

    // Refresh the autocomplete list so a tag that was removed from
    // its last row stops appearing as a suggestion. The backend's
    // /:projectId/tags endpoint already derives the list from
    // `array_length(tags,1) > 0`, so this fetch returns the correct
    // post-removal state; we just need to ask for it.
    fetchAllTags();

    // Refresh data
    await refreshAllData();

  } catch (error) {
    console.error('Error removing tag:', error);
    setDeleteMessage(`❌ Failed to remove tag. Please try again.`);
    setTimeout(() => setDeleteMessage(null), 5000);
  } finally {
    setRemovingTag(false);
  }
};

// Handle change color - ALWAYS changes all offices of a company
const handleChangeColor = async () => {
  setChangingColor(true);
  
  try {
    const ids = Array.from(selectedRows);
    
    // For each selected company, change ALL offices by company name
    for (const id of ids) {
      const company = companies.find(c => c.id === id);
      if (!company) continue;
      
      const requestBody = {
        companyName: company.company_name,
        allOffices: true,
        color: selectedColor
      };
      
      const response = await fetch(`/api/companies/${selectedProject.id}/bulk-color`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to change color for ${company.company_name}`);
      }
      
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Close modal and refresh
    setShowColorModal(false);
    setSelectedRows(new Set());
    
    // Show success message
    setDeleteMessage(`✅ Changed pin color for ${ids.length} ${ids.length === 1 ? 'company' : 'companies'} (all offices)`);
    setTimeout(() => setDeleteMessage(null), 3000);
    
    // Refresh data
    await refreshAllData();
    
  } catch (error) {
    console.error('Error changing color:', error);
    setDeleteMessage(`❌ Failed to change color. Please try again.`);
    setTimeout(() => setDeleteMessage(null), 5000);
  } finally {
    setChangingColor(false);
  }
};

// Load tags when modal opens
useEffect(() => {
  if (showTagModal || showRemoveTagModal) {
    fetchAllTags();
  }
}, [showTagModal, showRemoveTagModal, activeDataType]);

// Load color palette on mount
useEffect(() => {
  const fetchColorPalette = async () => {
    try {
      const response = await fetch('/api/companies/color-palette');
      if (response.ok) {
        const data = await response.json();
        setColorPalette(data.palette || []);
      }
    } catch (error) {
      console.error('Error fetching color palette:', error);
    }
  };
  
  fetchColorPalette();
}, []);

// Check for unenriched companies (for showing Enrich Companies button)
const checkUnenrichedCompanies = async () => {
  try {
    const response = await fetch(`/api/clay/check-unenriched-companies/${selectedProject.id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (response.ok) {
      const result = await response.json();
      setUnenrichedCompanyCount(result.companiesFound || 0);
    }
  } catch (error) {
    console.error('Error checking unenriched companies:', error);
  }
};

// Check for unenriched companies when candidates change
useEffect(() => {
  if (!IS_RECMAPPER && activeDataType === 'candidates' && candidates.length > 0) {
    checkUnenrichedCompanies();
  }
}, [candidates.length, activeDataType, selectedProject.id]);

const handleEnrichCompaniesClick = async () => {
  // Track activity for admin dashboard
  trackActivity();

  // Show confirmation FIRST, before calling the API
  // This prevents the old bug where enrichment started before user confirmed
  if (unenrichedCompanyCount === 0) {
    alert('All companies are already enriched!');
    return;
  }

  const batchSize = 2500;
  const willBatch = unenrichedCompanyCount > batchSize;

  const confirmed = window.confirm(
    willBatch
      ? `Enrich ${unenrichedCompanyCount} unique companies?\n\nCompanies will be processed in batches of ${batchSize.toLocaleString()}. The first batch will start now — click again after it completes for the next batch.\n\nCompanies already enriched in other projects will be populated for free. Only new companies cost 1 credit each.`
      : `Enrich ${unenrichedCompanyCount} unique companies?\n\nCompanies already enriched in other projects will be populated for free. Only new companies cost 1 credit each.\n\nCompanies will be enriched with office locations and automatically mapped.`
  );

  if (!confirmed) {
    return;
  }

  // User confirmed - now proceed with enrichment
  await proceedWithEnrichment();
};

// Proceed with actual company enrichment (only called after user confirms)
const proceedWithEnrichment = async () => {
  try {
    setEnrichmentProgress({ current: 0, total: 0, isEnriching: true, phase: 'Starting...' });

    const token = localStorage.getItem('token');
    const response = await fetch('/api/clay/enrich-companies', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        projectId: selectedProject.id
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to start enrichment');
    }

    const result = await response.json();

    if (result.companiesFound === 0) {
      alert('All companies are already enriched!');
      setEnrichmentProgress({ current: 0, total: 0, isEnriching: false });
      return;
    }

    // Track remaining for batch messaging
    const batchRemaining = result.remaining || 0;

    // Reset button count since enrichment started
    setUnenrichedCompanyCount(batchRemaining);
    setEnrichmentProgress(prev => ({ ...prev, total: result.companiesFound, phase: 'Checking cache...' }));

    // Poll for progress if we got an operationId
    if (result.operationId) {
      const pollInterval = setInterval(async () => {
        try {
          const progressRes = await fetch(`/api/clay/enrich-companies-progress/${result.operationId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (progressRes.ok) {
            const progress = await progressRes.json();
            const processed = (progress.cachedCount || 0) + (progress.enrichedCount || 0);
            setEnrichmentProgress(prev => ({
              ...prev,
              current: processed,
              phase: progress.phase || 'Processing...',
              cachedCount: progress.cachedCount || 0,
              enrichedCount: progress.enrichedCount || 0,
              creditsUsed: progress.creditsUsed || 0
            }));

            if (progress.status === 'completed' || progress.status === 'error') {
              clearInterval(pollInterval);
              enrichmentPollRef.current = null;

              // Show summary
              const cached = progress.cachedCount || 0;
              const enriched = progress.enrichedCount || 0;
              const credits = progress.creditsUsed || 0;
              const parts = [];
              if (cached > 0) parts.push(`${cached} from cache (free)`);
              if (enriched > 0) parts.push(`${enriched} enriched (${credits} credits)`);
              if (progress.failedCount > 0) parts.push(`${progress.failedCount} failed`);
              if (batchRemaining > 0) parts.push(`\n${batchRemaining} companies remaining — click "Enrich Companies" again to process the next batch.`);
              alert(`Company enrichment complete!\n\n${parts.join('\n')}\n\nCompanies will appear on the map shortly.`);

              setEnrichmentProgress({ current: 0, total: 0, isEnriching: false });
              setTimeout(() => { window.location.reload(); }, 3000);
            }
          }
        } catch (e) {
          // Ignore polling errors
        }
      }, 3000);
      enrichmentPollRef.current = pollInterval;
    } else {
      // Fallback for old backend without operationId
      alert(`Started enriching ${result.companiesFound} companies!\n\nThis will take a few minutes. Companies will appear on the map as they're enriched.`);
      setEnrichmentProgress({ current: 0, total: 0, isEnriching: false });
      setTimeout(() => { window.location.reload(); }, 5000);
    }

  } catch (error: any) {
    console.error('Enrichment error:', error);
    alert(`Failed to enrich companies: ${error.message}`);
    setEnrichmentProgress({ current: 0, total: 0, isEnriching: false });
  }
};


  // ============================================================================
  // COUNT CALCULATIONS (for tab buttons)
  // ============================================================================

  const totalRows = (() => {
    if (activeDataType === 'companies') {
      const seen = new Set();
      return companies.filter(c => {
        if (seen.has(c.company_name)) return false;
        seen.add(c.company_name);
        return true;
      }).length;
    }
    return activeDataType === 'candidates' ? candidates.length : hiringManagers.length;
  })();

  // Use server total when server pagination is enabled, otherwise use client-side count
  // For companies, use the unique count (serverTotals.companies) instead of total offices
  const filteredTotal = serverPagination.enabled
    ? (activeDataType === 'companies' ? serverTotals.companies : serverPagination.total)
    : totalRows;

  // Unique company count (deduplicated)
  const uniqueCompanyCount = (() => {
    const seen = new Set();
    return companies.filter(c => {
      if (seen.has(c.company_name)) return false;
      seen.add(c.company_name);
      return true;
    }).length;
  })();

  // Full unfiltered counts for "filtered from X" display
  const fullUniqueCompanyCount = (() => {
    const seen = new Set();
    return fullCompanies.filter(c => {
      if (seen.has(c.company_name)) return false;
      seen.add(c.company_name);
      return true;
    }).length;
  })();

const hasActiveFilters = aiFilterState.groups.length > 0 || appliedEmploymentTypes.length > 0;

// Comprehensive check for any type of filter being active
const hasAnyFilters = aiFilterState.groups.length > 0 ||
                      aiFilterState.employmentTypes.length > 0 ||
                      selectedConnectionsFilter !== null ||
                      radiusLocation !== '';

  // Wire the Ada action handlers into the ref each render — refs hold the
  // latest closures over applyFilterState / handleDataTypeChange /
  // applyDualRadiusSearch / setSearchTerm so the stable wrappers in
  // adaActions always invoke the current versions.
  useEffect(() => {
    adaActionsRef.current = {
      applyFilterGroups: (groups: any[], mode: 'replace' | 'add' = 'replace') => {
        // Normalise the shape: the manual filter builder sets `entityType`
        // on EACH condition (not just the group), and the cell-highlight
        // logic in analyzeMatches() checks condition.entityType to decide
        // whether to highlight. Ada often omits it on conditions because
        // the tool schema marks it optional. Without this normalisation,
        // Ada-applied filters work for SQL but never highlight in cells,
        // and the user loses a key piece of "what matched here" feedback.
        const safe = (Array.isArray(groups) ? groups : []).map((g: any) => ({
          ...g,
          conditions: Array.isArray(g.conditions)
            ? g.conditions.map((c: any) => ({
                ...c,
                entityType: c.entityType || g.entityType,
              }))
            : [],
        }));
        setAiFilterState(prev => ({
          ...prev,
          groups: mode === 'add' ? [...(prev.groups || []), ...safe] : safe,
        }));
      },
      switchTab: (tab) => {
        handleDataTypeChange(tab);
      },
      applyRadiusSearch: ({ type, location, distance }) => {
        return applyDualRadiusSearch(type, location, distance);
      },
      setKeywordSearch: (term) => {
        // The search input is bound to searchInputValue (the controlled
        // input state) and committed to searchTerm separately; updating
        // only searchTerm leaves the visible input field empty, which is
        // confusing. Set both so the user sees the term in the bar AND
        // the table refetches with it.
        const next = term || '';
        setSearchInputValue(next);
        setSearchTerm(next);
      },
      setEmploymentTypes: (types: string[]) => {
        // Drives the dedicated Employment Type multi-select picker. The
        // table re-fetches automatically when aiFilterState.employmentTypes
        // changes (see effect on line ~2189).
        const safe = Array.isArray(types) ? types : [];
        setAiFilterState(prev => ({ ...prev, employmentTypes: safe }));
      },
      runSmartSearch: async (query: string) => {
        // Mirror the AISearchBar flow: POST /api/ai-search/analyze, then
        // open the preview modal. The promise returned here resolves when
        // the modal closes — either via apply (filters get pushed through
        // the existing handleAISearch path) or via cancel.
        if (!selectedProject?.id) {
          return { applied: false, error: 'No active project' };
        }
        return new Promise(async (resolve) => {
          adaSmartSearchResolveRef.current = resolve;

          // Educational moment: type the query into the AI Smart Search bar
          // character by character so the user can see what manual path
          // Ada is using on their behalf. ~25ms per char ≈ a fast but
          // readable type. Skip if the query is empty.
          if (query) {
            setAiSearchBarOverride('');
            for (let i = 1; i <= query.length; i++) {
              setAiSearchBarOverride(query.slice(0, i));
              await new Promise(r => setTimeout(r, Math.max(15, 30 - i * 0.5)));
            }
            // Hold for a beat so the user can read the full query before
            // the modal pops up.
            await new Promise(r => setTimeout(r, 350));
          }

          setAdaSmartSearchModal({
            open: true,
            query,
            loading: true,
            filterGroups: null,
            explanation: null,
            radius: null,
            companyRadius: null,
            candidateRadius: null,
            canWiden: false,
          });
          try {
            const r = await fetch('/api/ai-search/analyze', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
              },
              body: JSON.stringify({
                query,
                projectId: selectedProject.id,
                currentDataType: activeDataType,
              }),
            });
            if (!r.ok) {
              const text = await r.text().catch(() => '');
              setAdaSmartSearchModal(m => ({ ...m, open: false }));
              setAiSearchBarOverride(null);
              adaSmartSearchResolveRef.current = null;
              resolve({ applied: false, error: `Smart Search failed: ${text || r.status}` });
              return;
            }
            const data = await r.json();
            setAdaSmartSearchModal(m => ({
              ...m,
              loading: false,
              filterGroups: Array.isArray(data.filterGroups) ? data.filterGroups : [],
              explanation: data.explanation || null,
              radius: data.radiusSearch || null,
              companyRadius: data.companyRadiusSearch || null,
              candidateRadius: data.candidateRadiusSearch || null,
              canWiden: !!data.canWiden,
            }));
          } catch (err: any) {
            setAdaSmartSearchModal(m => ({ ...m, open: false }));
            setAiSearchBarOverride(null);
            adaSmartSearchResolveRef.current = null;
            resolve({ applied: false, error: err?.message || 'Smart Search failed' });
          }
        });
      },
    };
  });

  // Cumulative sticky-left offset (px) for each pinned column: the leading
  // checkbox+# gutter, plus any locked (company) or user-frozen columns, in
  // display order. Consumed by both the header <th> and body <td> so they line
  // up. Non-pinned columns aren't in the map.
  const frozenLeftOffsets = React.useMemo(() => {
    const offsets: { [key: string]: number } = {};
    const matchScoreW = (isJobSpecMatchMode && activeDataType === 'candidates' && preferredFilterGroups.length > 0) ? 90 : 0;
    let acc = LEADING_GUTTER_WIDTH + matchScoreW;
    const ordered = columnOrder.filter(k => visibleColumns.includes(k) && !isColumnHiddenByGroup(k));
    for (const key of ordered) {
      if (frozenColumns.has(key)) {
        offsets[key] = acc;
        acc += (columnWidths[key] || FROZEN_DEFAULT_WIDTH);
      }
    }
    return offsets;
  }, [frozenColumns, columnWidths, columnOrder, visibleColumns, activeDataType, isJobSpecMatchMode, preferredFilterGroups, collapsedColumnGroups]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg">Loading data...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold">Data Table</h2>
            <button
              onClick={handleManualRefresh}
              disabled={loading || pageLoading}
              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <svg className={`w-5 h-5 ${loading || pageLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-600">
            {selectedRows.size > 0 ? (
              <span className="font-semibold text-blue-600">{selectedRows.size} selected</span>
            ) : (
              <>
                Showing {startRow + ((currentPage - 1) * rowLimit) + 1}-{Math.min(startRow + currentPage * rowLimit, filteredTotal)} of {filteredTotal} {activeDataType}
                {(hasAnyFilters || searchTerm) && (
                  <span className="text-blue-600">
                    {' '}(filtered from {
                      serverPagination.enabled
                        ? (activeDataType === 'companies' ? originalProjectTotals.companies : activeDataType === 'candidates' ? originalProjectTotals.candidates : originalProjectTotals.managers)
                        : (activeDataType === 'companies' ? fullUniqueCompanyCount : activeDataType === 'candidates' ? fullCandidates.length : fullHiringManagers.length)
                    })
                  </span>
                )}
              </>
            )}
          </p>
{(enrichmentMessage || deleteMessage || shortlistMessage || bdCampaignMessage) && (
  <p className="text-xs text-blue-600 mt-1">{enrichmentMessage || deleteMessage || shortlistMessage || bdCampaignMessage}</p>
)}
        </div>

        {/* Enrich Companies Button - Only show for candidates tab when there are unenriched companies */}
        {SHOW_ENRICH_COMPANIES_BUTTON && activeDataType === 'candidates' && unenrichedCompanyCount > 0 && (
          <button
            onClick={handleEnrichCompaniesClick}
            disabled={enrichmentProgress.isEnriching}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 text-sm font-medium flex items-center gap-2 shadow-md"
          >
            {enrichmentProgress.isEnriching ? (
              <>
                <span className="animate-spin">⏳</span>
                {enrichmentProgress.total > 0 && enrichmentProgress.current > 0
                  ? `${enrichmentProgress.current}/${enrichmentProgress.total} companies...`
                  : (enrichmentProgress.phase || 'Enriching...')}
              </>
            ) : (
              <>
                <span>🏢</span>
                Enrich {unenrichedCompanyCount} {unenrichedCompanyCount === 1 ? 'Company' : 'Companies'}
              </>
            )}
          </button>
        )}
      </div>

      {/* Tab Buttons */}
      <div data-tour="project-data-tabs" className="px-4 py-2 border-b flex space-x-1 flex-shrink-0">
        <button
          onClick={() => handleDataTypeChange('candidates')}
          className={`flex-1 px-3 py-2 rounded-md text-sm font-medium ${
            activeDataType === 'candidates'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Candidates ({serverPagination.enabled ? serverTotals.candidates : candidates.length}{(hasAnyFilters || searchTerm) && ` from ${serverPagination.enabled ? originalProjectTotals.candidates : fullCandidates.length}`})
        </button>
        <button
          data-tour="project-tab-companies"
          onClick={() => handleDataTypeChange('companies')}
          className={`flex-1 px-3 py-2 rounded-md text-sm font-medium ${
            activeDataType === 'companies'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Companies ({serverPagination.enabled ? serverTotals.companies : uniqueCompanyCount}{(hasAnyFilters || searchTerm) && ` from ${serverPagination.enabled ? originalProjectTotals.companies : fullUniqueCompanyCount}`})
        </button>
        <button
          data-tour="project-tab-managers"
          onClick={() => handleDataTypeChange('managers')}
          className={`flex-1 px-3 py-2 rounded-md text-sm font-medium ${
            activeDataType === 'managers'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Hiring Managers ({serverPagination.enabled ? serverTotals.managers : hiringManagers.length}{(hasAnyFilters || searchTerm) && ` from ${serverPagination.enabled ? originalProjectTotals.managers : fullHiringManagers.length}`})
        </button>
      </div>

      {/* Back to Jobs button — only shown when the user arrived here by
          clicking a company name from the Jobs tab. Restores their
          scroll position via the sessionStorage key set in
          ProjectJobWatches.tsx. */}
      {backToJobs && (
        <div className="px-4 py-2 border-b bg-blue-50 flex items-center justify-between">
          <button
            onClick={backToJobs}
            className="text-sm font-medium text-blue-700 hover:text-blue-900 flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Jobs
          </button>
          <span className="text-xs text-blue-600">
            Filtered to a company you were tracking
          </span>
        </div>
      )}

      {/* Quick Search Box */}
      <div className="px-4 py-2 border-b bg-gray-50">
        <div className="relative">
          <input
            type="text"
            data-tour="project-quick-search"
            placeholder="🔍 Search names, emails, companies... (separate multiple keywords with commas or OR)"
            value={searchInputValue}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInputValue(value);
              // Set origin tab when user starts typing (lock in the search context)
              if (value.trim() && !searchOriginTab) {
                setSearchOriginTab(activeDataType);
              }
              // Debounce the actual search
              if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
              }
              searchDebounceRef.current = setTimeout(() => {
                setSearchTerm(value);
              }, 300);
            }}
            className="input pl-10"
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchInputValue && (
            <button
              data-tour="quick-search-clear"
              onClick={() => {
                setSearchInputValue('');
                setSearchTerm('');
                setSearchOriginTab(null); // Clear the origin tab when search is cleared
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="mt-1 text-xs text-gray-500 flex items-center gap-2">
            <span>
              Found <span className="font-semibold text-blue-600">
                {activeDataType === 'candidates'
                  ? serverTotals.candidates
                  : activeDataType === 'companies'
                    ? serverTotals.companies
                    : serverTotals.managers}
              </span> {activeDataType === 'managers' ? 'hiring managers' : activeDataType}
              {searchOriginTab && searchOriginTab !== activeDataType ? (
                <> at {searchOriginTab === 'managers' ? 'orgs' : 'companies'} matching "{searchTerm}"</>
              ) : (
                <> matching "{searchTerm}"</>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Back Button - Shows when filter history exists */}
      {filterHistory.length > 0 && (
        <div className="px-4 py-2 border-b bg-gray-50">
          <button
            onClick={handleBackButton}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-white flex items-center gap-2 text-gray-700"
          >
            <span>←</span>
            <span>Back to {filterHistory[filterHistory.length - 1].description}</span>
          </button>
        </div>
      )}

      {/* Filtered Candidates Info Bar - Shows when viewing filtered candidates for a specific company */}
      {activeDataType === 'candidates' && (() => {
        // Check if we have a company association filter (junction table based)
        if (associatedCompanyFilter) {
          const companyData = fullCompanies.find(c =>
            c.company_name?.toLowerCase() === associatedCompanyFilter.toLowerCase()
          );
          // Get the TOTAL count from the candidate counts map (not filtered)
          const companyCountData = companyData ? companyCandidateCounts.get(companyData.id) : null;
          const totalAtCompany = companyCountData?.total || companyData?.candidate_count || serverPagination.total;

          // Check if there are additional filters beyond the association
          const hasAdditionalFilters = aiFilterState.employmentTypes.length > 0 || aiFilterState.groups.length > 0;

          if (hasAdditionalFilters) {
            return (
              <div className="px-4 py-2 bg-orange-50 border-b border-orange-200">
                <p className="text-sm text-orange-800">
                  Showing <span className="font-semibold">{serverPagination.total}</span> of <span className="font-semibold">{totalAtCompany}</span> candidates matching filters at <span className="font-semibold">{associatedCompanyFilter}</span>.
                  <button
                    onClick={() => {
                      setAiFilterState({ groups: [], employmentTypes: [] });
                    }}
                    className="ml-2 text-orange-600 hover:text-orange-800 underline font-medium"
                  >
                    View all {totalAtCompany}
                  </button>
                </p>
              </div>
            );
          }

          return (
            <div data-tour="association-banner" className="px-4 py-2 bg-blue-50 border-b border-blue-200">
              <p className="text-sm text-blue-800">
                Showing <span className="font-semibold">{serverPagination.total}</span> candidates associated with <span className="font-semibold">{associatedCompanyFilter}</span>
                <button
                  onClick={() => {
                    setAssociatedCompanyFilter(null);
                    setAiFilterState({ groups: [], employmentTypes: [] });
                    handleDataTypeChange('candidates');
                  }}
                  className="ml-2 text-blue-600 hover:text-blue-800 underline font-medium"
                >
                  Clear filter
                </button>
              </p>
            </div>
          );
        }

        // Legacy: Check if we have a company field filter
        const companyFilterGroup = aiFilterState.groups.find(g =>
          g.conditions.some(c => c.column === 'company' && c.operator === 'equals')
        );
        const companyName = companyFilterGroup?.conditions.find(c => c.column === 'company')?.value;

        // Check if there are additional filters beyond the company filter
        const hasAdditionalFilters = aiFilterState.employmentTypes.length > 0 ||
          aiFilterState.groups.filter(g => g !== companyFilterGroup).length > 0;

        if (companyName && hasAdditionalFilters) {
          // Get the full candidate count for this company
          const companyData = fullCompanies.find(c => c.company_name === companyName);
          // In server-side mode, fullCandidates only has current page, so use company's candidate_count
          const totalAtCompany = companyData?.candidate_count ||
            fullCandidates.filter(c => c.company === companyName).length;

          return (
            <div className="px-4 py-2 bg-orange-50 border-b border-orange-200">
              <p className="text-sm text-orange-800">
                Showing <span className="font-semibold">{candidates.length}</span> of <span className="font-semibold">{totalAtCompany}</span> candidates matching filters at <span className="font-semibold">{companyName}</span>.
                <button
                  onClick={() => {
                    // Clear all filters except the company filter
                    const companyOnlyFilter: FilterState = {
                      groups: companyFilterGroup ? [companyFilterGroup] : [],
                      employmentTypes: []
                    };
                    setAiFilterState(companyOnlyFilter);
                  }}
                  className="ml-2 text-orange-600 hover:text-orange-800 underline font-medium"
                >
                  View all {totalAtCompany}
                </button>
              </p>
            </div>
          );
        }
        return null;
      })()}

{/* STICKY HEADER SECTION - Stays at top when scrolling */}
      <div className="bg-white border-b relative z-40">
        <div className="flex items-center gap-2 max-md:flex-wrap max-md:pb-2">
          <div className="flex-1 max-md:w-full max-md:flex-none">
            <AISearchBar
              onSearch={handleAISearch}
              onClearFilters={handleClearAIFilters}
              appliedFilterState={aiFilterState}
              explanation={aiExplanation}
              isLoading={aiSearchLoading || (adaSmartSearchModal.open && adaSmartSearchModal.loading)}
              projectId={selectedProject?.id}
              currentDataType={activeDataType}
              canWiden={canWidenSearch}
              isWidened={isWidenedSearch}
              onWidenSearch={handleWidenSearch}
              savedSearches={savedSearches}
              onLoadSavedSearch={handleLoadSavedSearch}
              onDeleteSavedSearch={handleDeleteSavedSearch}
              query={aiSearchBarOverride ?? undefined}
            />
          </div>
          {/* Job Spec Match Button */}
          <button
            onClick={() => setShowJobSpecModal(true)}
            className="mr-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg hover:from-orange-600 hover:to-amber-600 font-medium text-sm transition-all shadow-sm flex items-center gap-2 whitespace-nowrap"
            title="Match candidates against a job specification"
          >
            <span>📋</span>
            Job Spec Match
          </button>
          {/* Extraction Trainer — admin-only (visible to Alex directly or while
              impersonating via the adminEmail marker); clients never see it. */}
          {isAdmin && (
            <button
              onClick={() => { if (selectedProject) window.location.href = `/extraction/${selectedProject.id}`; }}
              className="mr-4 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-lg hover:from-violet-600 hover:to-purple-600 font-medium text-sm transition-all shadow-sm flex items-center gap-2 whitespace-nowrap"
              title="Train the company-extraction judge on sample profiles, then run it across all candidates"
            >
              <span>🎓</span>
              Extract Companies
            </button>
          )}
        </div>

        {/* Job Spec Match Mode Indicator */}
        {isJobSpecMatchMode && aiFilterState.groups.length > 0 && (
          <div className="px-4 py-2 bg-gradient-to-r from-orange-50 to-amber-50 border-t border-orange-200">
            <div className="flex items-center gap-3">
              <span className="text-sm text-orange-700 font-medium">
                📋 Job Spec Match Mode Active
              </span>
              <span className="text-xs text-orange-600">
                {aiFilterState.groups.filter(g => g.priority === 'required').length} Must Have,
                {' '}{aiFilterState.groups.filter(g => g.priority === 'preferred').length} Nice to Have
              </span>
              <span className="text-xs text-gray-500">
                (Toggle priorities in the filter groups below)
              </span>
            </div>
          </div>
        )}

        {/* Create Project / Save Search Buttons */}
        {hasAnyFilters && (
          <div className="px-4 py-2 bg-gradient-to-r from-purple-50 to-blue-50 border-t border-purple-100 flex items-center gap-2 flex-wrap">
            {/* Radius-only searches count: the create-from-filtered endpoint
                fully supports radiusSearch alone, but this button was gated
                on AI filters only — hiding subproject creation for radius
                searches even though the pipeline supported it end to end. */}
            {(aiFilterState.groups.length > 0 || aiFilterState.employmentTypes.length > 0 || (radiusCoords !== null && radiusDistance > 0)) && (
              <button
                onClick={async () => {
                  setShowCreateProjectModal(true);
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch('/api/projects', {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                      const data = await response.json();
                      const otherProjects = data.filter((p: {id: number; name: string}) => p.id !== selectedProject?.id);
                      setUserProjects(otherProjects);
                    }
                  } catch (error) {
                    console.error('Error fetching projects:', error);
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm transition-colors shadow-sm"
                data-tour="create-subproject-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create New Project with Filtered Data
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded text-xs">
                  {filteredTotal} records
                </span>
              </button>
            )}
            <button
              data-tour="save-search-button"
              onClick={() => {
                setSaveSearchName('');
                setShowSaveSearchModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-50 font-medium text-sm transition-colors shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              Save Search
            </button>
          </div>
        )}

        <FilterBarV2
          columns={getCompanyColumns()}
          candidateColumns={getCandidateColumns()}
          managerColumns={getManagerColumns()}
          customColumns={customColumns}
          onApplyFilters={applyFilterState}
          currentDataType={activeDataType}
          projectId={selectedProject.id}
          initialFilterState={aiFilterState}
          totalRows={filteredTotal}
          currentStartRow={startRow}
          currentLimit={rowLimit}
          onPaginationChange={handlePaginationChange}
          currentSort={sortConfig}
          onApplySort={handleApplySort}
          onOpenColumnSelector={() => setIsColumnSelectorOpen(true)}
          onOpenColumnOrder={() => setShowColumnOrderPanel(true)}
          onToggleRadiusSearch={() => setShowRadiusSearch(!showRadiusSearch)}
          radiusLocation={radiusLocation}
          radiusDistance={radiusDistance}
          onClearAllFilters={handleClearAllFilters}
          hasAnyFilters={hasAnyFilters}
          showPriorityToggle={isJobSpecMatchMode}
        />

        {/* View Analytics/Map Buttons - Shows when filters OR a radius are active.
            The radius is carried in the FilterState so Map/Analytics reflect the
            same radius-filtered subset the table is showing. */}
        {(() => {
          const navRadius: RadiusSearch | undefined =
            candidateLocationRadius.coords ? { location: candidateLocationRadius.location, distance: candidateLocationRadius.distance, type: 'candidate', coords: candidateLocationRadius.coords }
            : companyOfficeRadius.coords ? { location: companyOfficeRadius.location, distance: companyOfficeRadius.distance, type: 'company', coords: companyOfficeRadius.coords }
            : managerLocationRadius.coords ? { location: managerLocationRadius.location, distance: managerLocationRadius.distance, type: 'manager', coords: managerLocationRadius.coords }
            : undefined;
          const hasAnyFilter = aiFilterState.groups.length > 0 || aiFilterState.employmentTypes.length > 0 || !!navRadius;
          const navFilterState: FilterState = { ...aiFilterState, radiusSearch: navRadius };
          return (onNavigateToAnalytics || onNavigateToMap) && hasAnyFilter && (
          <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-blue-700 font-medium">
                  📊 {navRadius && aiFilterState.groups.length === 0 && aiFilterState.employmentTypes.length === 0 ? 'Radius active' : 'Filters active'} - View this filtered dataset
                </span>
              </div>
              <div className="flex gap-2">
                {onNavigateToMap && (
                  <button
                    onClick={() => onNavigateToMap(navFilterState)}
                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                  >
                    🗺️ View on Map
                  </button>
                )}
                {onNavigateToAnalytics && (
                  <button
                    onClick={() => onNavigateToAnalytics(navFilterState)}
                    className="btn-primary"
                  >
                    📊 View Analytics →
                  </button>
                )}
              </div>
            </div>
          </div>
          );
        })()}

        {/* Action Bar with Connections Filter and Buttons */}
        <div className="px-4 py-2 border-b">
          <div className="flex items-center justify-between gap-4">
            {/* Left side - Connections Filter (candidates only) */}
            {activeDataType === 'candidates' ? (
              <div className="flex-1 max-w-md">
                <label className="block text-xs font-medium mb-1 text-gray-700">
                  Show connections of:
                </label>
              <div className="relative">
                <input
                  type="text"
                  value={connectionsFilterQuery}
                  onChange={(e) => {
                    setConnectionsFilterQuery(e.target.value);
                    searchCandidatesForConnections(e.target.value);
                  }}
                  onFocus={() => {
                    if (connectionsSuggestions.length > 0) {
                      setShowConnectionsSuggestions(true);
                    }
                  }}
                  placeholder="Type a candidate name (e.g., 'alex')"
                  className="w-full px-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {selectedConnectionsFilter && (
                  <button
                    onClick={clearConnectionsFilter}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Clear filter"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}

                {/* Autocomplete dropdown */}
                {showConnectionsSuggestions && connectionsSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto z-50">
                    {connectionsSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.id}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          // Collect previous companies from the suggestion
                          const prevCompanies = [
                            suggestion.previous_company_1,
                            suggestion.previous_company_2,
                            suggestion.previous_company_3
                          ].filter(Boolean) as string[];
                          console.log('🔗 Selected connection filter:', suggestion.name, suggestion.company, 'Previous:', prevCompanies);
                          fetchConnectionsForFilter(suggestion.id, suggestion.name, suggestion.company, prevCompanies);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 cursor-pointer"
                      >
                        <div className="font-medium text-sm">{suggestion.name}</div>
                        {suggestion.position && (
                          <div className="text-xs text-gray-600">{suggestion.position}</div>
                        )}
                        {suggestion.company && (
                          <div className="text-xs text-gray-500">{suggestion.company}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedConnectionsFilter && (
                <div className="mt-2 text-xs text-gray-600">
                  Showing {connectedCandidateIds.length} {connectedCandidateIds.length === 1 ? 'connection' : 'connections'} of <span className="font-medium">{selectedConnectionsFilter.name}</span>
                  {hiddenConnectionCompanies.size > 0 && (
                    <span className="ml-2 text-gray-400">
                      ({hiddenConnectionCompanies.size} {hiddenConnectionCompanies.size === 1 ? 'company' : 'companies'} hidden)
                    </span>
                  )}
                </div>
              )}
              {hiddenConnectionCompanies.size > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {Array.from(hiddenConnectionCompanies).map(company => (
                    <span key={company} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-600 border border-gray-200">
                      <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                      {company}
                      <button
                        onClick={() => setHiddenConnectionCompanies(prev => {
                          const next = new Set(prev);
                          next.delete(company);
                          return next;
                        })}
                        className="ml-0.5 text-gray-400 hover:text-gray-700"
                        title={`Show connections from ${company}`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
              </div>
            ) : activeDataType === 'companies' && selectedConnectionsFilter ? (
              <div className="flex-1 max-w-lg">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-700">
                    Companies where connections of <span className="font-medium">{selectedConnectionsFilter.name}</span> work
                  </div>
                  {selectedConnectionsFilter.previousCompanies.length > 0 && (
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={excludePreviousCompanies}
                        onChange={(e) => setExcludePreviousCompanies(e.target.checked)}
                        className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-gray-600">
                        Hide {selectedConnectionsFilter.name}'s previous employers
                        <span className="text-xs text-gray-400 ml-1">({selectedConnectionsFilter.previousCompanies.length})</span>
                      </span>
                    </label>
                  )}
                  <button
                    onClick={clearConnectionsFilter}
                    className="text-gray-400 hover:text-gray-600 text-xs"
                    title="Clear connections filter"
                  >
                    Clear
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1"></div>
            )}

            {/* Right side - Action Buttons. flex-wrap: with rows selected there
                can be 10+ buttons — wrap onto extra lines rather than running
                off the right edge of the screen. */}
            <div className="flex flex-wrap gap-2 items-center justify-end min-w-0">
              {/* Enrich Contact Button (candidates and managers only, when rows selected) */}
              {!IS_RECMAPPER && selectedRows.size > 0 && activeDataType === 'candidates' && !campaignScopeId && (
                <button
                  onClick={() => { setCopyToContactsModal(true); setCopyToContactsMsg(null); setCopyToContactsMode('copy'); }}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 text-xs flex items-center gap-1"
                  title="Copy or move the selected candidates into Hiring Managers, keeping their full enriched profile and linking them to every company they currently work at"
                >
                  👔 To Hiring Managers ({selectedRows.size})
                </button>
              )}
              {!isLibrary && selectedRows.size > 0 && (activeDataType === 'candidates' || activeDataType === 'managers') && (
                <div className="relative">
                  <button
                    onClick={() => setShowEnrichMenu(!showEnrichMenu)}
                    disabled={enriching}
                    className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-xs disabled:bg-gray-400 flex items-center gap-1"
                  >
                    {enriching ? '⏳ Enriching...' : `✨ Enrich ${selectedRows.size}`}
                    {!enriching && <span className="text-[10px]">▼</span>}
                  </button>

                  {showEnrichMenu && (
                    <div className="absolute top-full right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 min-w-[220px]">
                      <button
                        onClick={() => handleBulkEnrich('work_email')}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 border-b"
                      >
                        💼 Work Email <span className="text-gray-500">({selectedRows.size} credits)</span>
                      </button>
                      <button
                        onClick={() => handleBulkEnrich('personal_email')}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 border-b"
                      >
                        🏠 Personal Email <span className="text-gray-500">({selectedRows.size * 3} credits)</span>
                      </button>
                      <button
                        onClick={() => handleBulkEnrich('phone')}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 border-b"
                      >
                        📱 Phone <span className="text-gray-500">({selectedRows.size * 12} credits)</span>
                      </button>
                      <button
                        onClick={() => handleBulkEnrich('work_email_phone')}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 border-b"
                      >
                        💼+📱 Work + Phone <span className="text-gray-500">({selectedRows.size * 13} credits)</span>
                      </button>
                      <button
                        onClick={() => handleBulkEnrich('personal_email_phone')}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 border-b"
                      >
                        🏠+📱 Personal + Phone <span className="text-gray-500">({selectedRows.size * 15} credits)</span>
                      </button>
                      <button
                        onClick={() => handleBulkEnrich('all')}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        🔍 Everything <span className="text-gray-500">({selectedRows.size * 16} credits)</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Shortlist Button (candidates and managers only, when rows selected) */}
              {!IS_RECMAPPER && selectedRows.size > 0 && (activeDataType === 'candidates' || activeDataType === 'managers') && (
                <div className="relative">
                  <button
                    onClick={() => {
                      if (!showShortlistDropdown) fetchOpenJobs();
                      setShowShortlistDropdown(!showShortlistDropdown);
                      setShowBdCampaignDropdown(false);
                    }}
                    disabled={shortlisting}
                    className="px-3 py-1.5 bg-amber-600 text-white rounded hover:bg-amber-700 text-xs disabled:bg-gray-400 flex items-center gap-1"
                  >
                    {shortlisting ? 'Adding...' : `Add to Campaign (${selectedRows.size})`}
                    {!shortlisting && <span className="text-[10px]">▼</span>}
                  </button>

                  {showShortlistDropdown && (
                    <div className="absolute top-full right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 min-w-[260px] max-h-[300px] overflow-y-auto">
                      <div className="px-3 py-2 border-b border-gray-100">
                        <p className="text-xs font-medium text-gray-500">Select a campaign</p>
                      </div>
                      {shortlistJobs.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">
                          No open jobs found
                        </div>
                      ) : (
                        shortlistJobs.map(job => (
                          <button
                            key={job.id}
                            onClick={() => handleShortlist(job.id)}
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-amber-50 border-b border-gray-50 last:border-b-0"
                          >
                            <span className="font-medium text-gray-900">{job.title}</span>
                            {job.company_name && (
                              <span className="text-gray-400 ml-1">— {job.company_name}</span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* BD Campaign Button (companies and managers only, when rows selected) */}
              {!IS_RECMAPPER && selectedRows.size > 0 && (activeDataType === 'companies' || activeDataType === 'managers') && (
                <div className="relative">
                  <button
                    onClick={() => {
                      if (!showBdCampaignDropdown) fetchBdCampaigns();
                      setShowBdCampaignDropdown(!showBdCampaignDropdown);
                      setShowShortlistDropdown(false);
                    }}
                    disabled={addingToCampaign}
                    className="px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-xs disabled:bg-gray-400 flex items-center gap-1"
                  >
                    {addingToCampaign ? 'Adding...' : `BD Campaign (${selectedRows.size})`}
                    {!addingToCampaign && <span className="text-[10px]">▼</span>}
                  </button>

                  {showBdCampaignDropdown && (
                    <div className="absolute top-full right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 min-w-[260px] max-h-[300px] overflow-y-auto">
                      <div className="px-3 py-2 border-b border-gray-100">
                        <p className="text-xs font-medium text-gray-500">Select a BD Campaign to add to</p>
                      </div>
                      {bdCampaignsList.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">
                          No active campaigns found
                        </div>
                      ) : (
                        bdCampaignsList.map(campaign => (
                          <button
                            key={campaign.id}
                            onClick={() => handleAddToBdCampaign(campaign.id)}
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-indigo-50 border-b border-gray-50 last:border-b-0"
                          >
                            <span className="font-medium text-gray-900">{campaign.name}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Create prospect pages (admin only, candidates) */}
              {isProspectAdmin && selectedRows.size > 0 && activeDataType === 'candidates' && !campaignScopeId && (
                <button
                  onClick={handleCreateProspectPages}
                  disabled={prospectGenBusy}
                  className="px-3 py-1.5 bg-violet-600 text-white rounded hover:bg-violet-700 text-xs disabled:bg-gray-400 flex items-center gap-1"
                  title="Generate a personalised prospect page for each selected person (their own profile is the brief). Pages appear as 👁 on the row and flow to lemlist as {{prospectPage}}."
                >
                  {prospectGenBusy ? '⏳ Generating pages…' : `⚡ Prospect Pages (${Math.min(selectedRows.size, 25)})`}
                </button>
              )}

              {/* Send to SourceWhale Button (candidates and managers only, when rows selected and connected) */}
              {athenaConnected && selectedRows.size > 0 && activeDataType === 'candidates' && !campaignScopeId && (
                <button
                  onClick={handleSendToAthena}
                  disabled={athenaPushing}
                  className="px-3 py-1.5 bg-slate-800 text-white rounded hover:bg-slate-900 text-xs disabled:bg-gray-400 flex items-center gap-1"
                  title="Push the selected candidates into Athena CRM (deduped by LinkedIn URL and email; already-linked records are updated)"
                >
                  {athenaPushing ? '⏳ Sending…' : `Send to Athena (${selectedRows.size})`}
                </button>
              )}
              {/* Add to native ATS/CRM (user 1 while the module is gated) */}
              {!IS_RECMAPPER && userId === 1 && selectedRows.size > 0 && activeDataType === 'candidates' && !campaignScopeId && (
                <span className="relative inline-block">
                  <button
                    onClick={() => setAtsAddOpen(!atsAddOpen)}
                    disabled={atsAddBusy}
                    className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-xs disabled:bg-gray-400 flex items-center gap-1"
                    title="Add the selected people to your ATS/CRM (deduped by email/LinkedIn; full profile attaches automatically)"
                  >
                    {atsAddBusy ? '⏳ Adding…' : `Add to ATS (${selectedRows.size})`}
                  </button>
                  {atsAddOpen && (
                    <span className="absolute z-30 mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-40">
                      <button onClick={() => handleAddToAts('candidate')} className="block w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">As candidates</button>
                      <button onClick={() => handleAddToAts('contact')} className="block w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">As contacts</button>
                    </span>
                  )}
                </span>
              )}
              {/* Org chart — whole-project (candidates tab, single-company projects) */}
              {orgChartEnabled && activeDataType === 'candidates' && !campaignScopeId && selectedProject?.id && (
                <button
                  onClick={() => setOrgChartScope({})}
                  className="px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-xs flex items-center gap-1"
                  title="AI-inferred org chart from ALL candidates in this project — use this when the whole project is one company's employees"
                >
                  Org Chart
                </button>
              )}
              {/* Org chart — one selected company (the normal multi-company case) */}
              {orgChartEnabled && activeDataType === 'companies' && !campaignScopeId && selectedProject?.id && selectedRows.size === 1 && (
                <button
                  onClick={() => {
                    const id = Array.from(selectedRows)[0] as number;
                    const row: any = displayData.find((r: any) => r.id === id);
                    if (row) setOrgChartScope({ companyId: id, companyName: row.name || row.company_name || 'Company' });
                  }}
                  className="px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-xs flex items-center gap-1"
                  title="AI-inferred org chart for this company, built from its candidates in this project — who reports to whom, with your corrections kept across refreshes"
                >
                  Generate Org Chart
                </button>
              )}
              {/* Send to Spott — shows when this user has connected Spott in Settings → Integrations */}
              {spottConnected && selectedRows.size > 0 && activeDataType === 'candidates' && !campaignScopeId && (
                <button
                  onClick={handleSendToSpott}
                  disabled={spottPushing}
                  className="px-3 py-1.5 bg-emerald-700 text-white rounded hover:bg-emerald-800 text-xs disabled:bg-gray-400 flex items-center gap-1"
                  title="Push the selected candidates into Spott ATS with full career history (duplicates by LinkedIn URL/email/phone are skipped by Spott)"
                >
                  {spottPushing ? `⏳ ${spottProgress || 'Sending…'}` : `Send to Spott (${selectedRows.size})`}
                </button>
              )}
              {/* Spott v2: companies → clients */}
              {spottConnected && selectedRows.size > 0 && activeDataType === 'companies' && !campaignScopeId && (
                <button
                  onClick={() => handleSendToSpottV2('companies')}
                  disabled={spottV2Pushing !== null}
                  className="px-3 py-1.5 bg-emerald-700 text-white rounded hover:bg-emerald-800 text-xs disabled:bg-gray-400 flex items-center gap-1"
                  title="Push the selected companies into Spott as clients (industry, size, specialties, HQ; already-sent skipped)"
                >
                  {spottV2Pushing === 'companies' ? `⏳ ${spottV2Progress || 'Sending…'}` : `Send to Spott (${selectedRows.size})`}
                </button>
              )}
              {/* Spott v2: hiring managers → client contacts */}
              {spottConnected && selectedRows.size > 0 && activeDataType === 'managers' && !campaignScopeId && (
                <button
                  onClick={() => handleSendToSpottV2('contacts')}
                  disabled={spottV2Pushing !== null}
                  className="px-3 py-1.5 bg-emerald-700 text-white rounded hover:bg-emerald-800 text-xs disabled:bg-gray-400 flex items-center gap-1"
                  title="Push the selected hiring managers into Spott as client contacts, linked to their client company (kept as candidates too if they exist there)"
                >
                  {spottV2Pushing === 'contacts' ? `⏳ ${spottV2Progress || 'Sending…'}` : `Send to Spott (${selectedRows.size})`}
                </button>
              )}
              {/* Send to Atlas — candidates → people, companies → companies, hiring managers → company contacts */}
              {atlasConnected && selectedRows.size > 0 && (activeDataType === 'candidates' || activeDataType === 'companies' || activeDataType === 'managers') && !campaignScopeId && (() => {
                const kind = activeDataType === 'candidates' ? 'candidates' : activeDataType === 'companies' ? 'companies' : 'contacts';
                return (
                  <button
                    onClick={() => handleSendToAtlas(kind)}
                    disabled={atlasPushing !== null}
                    className="px-3 py-1.5 bg-gray-900 text-white rounded hover:bg-black text-xs disabled:bg-gray-400 flex items-center gap-1"
                    title={kind === 'candidates' ? 'Send the selected candidates to Atlas with contact details, current role, location, work history and education (anyone already in Atlas is linked, not overwritten)'
                      : kind === 'companies' ? 'Send the selected companies to Atlas (each needs a website or LinkedIn page; ones already in Atlas are linked)'
                      : 'Send the selected hiring managers to Atlas as company contacts, linked to their company'}
                  >
                    {atlasPushing === kind ? `⏳ ${atlasProgress || 'Sending…'}` : `Send to Atlas (${selectedRows.size})`}
                  </button>
                );
              })()}
              {/* Send to Giig — candidates, companies, hiring managers → contacts */}
              {giigConnected && selectedRows.size > 0 && (activeDataType === 'candidates' || activeDataType === 'companies' || activeDataType === 'managers') && !campaignScopeId && (() => {
                const kind = activeDataType === 'candidates' ? 'candidates' : activeDataType === 'companies' ? 'companies' : 'contacts';
                return (
                  <button onClick={() => handleSendToGiig(kind)} disabled={giigPushing !== null}
                    className="px-3 py-1.5 text-white rounded text-xs disabled:bg-gray-400 flex items-center gap-1 hover:opacity-90"
                    style={{ background: giigPushing !== null ? undefined : '#ff6600' }}
                    title="Send the selected rows to Giig (anyone already there by LinkedIn / email is linked, not duplicated)">
                    {giigPushing === kind ? `⏳ ${giigProgress || 'Sending…'}` : `Send to Giig (${selectedRows.size})`}
                  </button>
                );
              })()}
              {sourceWhaleConnected && selectedRows.size > 0 && (activeDataType === 'candidates' || activeDataType === 'managers') && (
                <button
                  onClick={async () => {
                    setShowSourceWhaleModal(true);
                    setSourceWhaleLoading(true);
                    try {
                      const token = localStorage.getItem('token');
                      // Check connection status
                      const statusRes = await fetch('/api/sourcewhale/status', {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const statusData = await statusRes.json();
                      setSourceWhaleConnected(statusData.connected);

                      if (statusData.connected) {
                        // Fetch campaigns and projects
                        const [campaignsRes, projectsRes] = await Promise.all([
                          fetch('/api/sourcewhale/campaigns', {
                            headers: { 'Authorization': `Bearer ${token}` }
                          }),
                          fetch('/api/sourcewhale/projects', {
                            headers: { 'Authorization': `Bearer ${token}` }
                          })
                        ]);

                        if (campaignsRes.ok) {
                          const campaignsData = await campaignsRes.json();
                          setSourceWhaleCampaigns(campaignsData.campaigns || []);
                        }
                        if (projectsRes.ok) {
                          const projectsData = await projectsRes.json();
                          setSourceWhaleProjects(projectsData.projects || []);
                        }
                      }
                    } catch (error) {
                      console.error('Error loading SourceWhale data:', error);
                    } finally {
                      setSourceWhaleLoading(false);
                    }
                  }}
                  className="px-3 py-1.5 text-white text-xs rounded hover:opacity-90 flex items-center gap-1"
                  style={{ backgroundColor: '#1aa2d9' }}
                >
                  Send to SourceWhale ({selectedRows.size})
                </button>
              )}

              {/* Send to lemlist Button (candidates and managers only, when rows selected and connected).
                  Hidden inside a campaign Leads List — campaign pushes go through the Launch tab
                  (the single, tracked path); this ad-hoc send stays on the main project data table. */}
              {!campaignScopeId && lemlistConnected && selectedRows.size > 0 && (activeDataType === 'candidates' || activeDataType === 'managers') && (
                <button
                  onClick={async () => {
                    setShowLemlistModal(true);
                    setLemlistLoading(true);
                    try {
                      const token = localStorage.getItem('token');
                      const statusRes = await fetch('/api/lemlist/status', {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const statusData = await statusRes.json();
                      setLemlistConnected(statusData.connected);

                      if (statusData.connected) {
                        const campaignsRes = await fetch('/api/lemlist/campaigns', {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (campaignsRes.ok) {
                          const campaignsData = await campaignsRes.json();
                          setLemlistCampaigns(Array.isArray(campaignsData) ? campaignsData : []);
                        }
                      }
                    } catch (error) {
                      console.error('Error loading lemlist data:', error);
                    } finally {
                      setLemlistLoading(false);
                    }
                  }}
                  className="px-3 py-1.5 text-white text-xs rounded hover:opacity-90 flex items-center gap-1"
                  style={{ backgroundColor: '#3355FF' }}
                >
                  Send to lemlist ({selectedRows.size})
                </button>
              )}

              {/* Add Tag Button (when rows selected) */}
              {selectedRows.size > 0 && (
                <button
                  data-tour="tag-button"
                  onClick={() => setShowTagModal(true)}
                  className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1"
                >
                  🏷️ Tag ({selectedRows.size})
                </button>
              )}

              {/* Remove Tag Button (when rows selected) */}
              {selectedRows.size > 0 && (
                <button
                  onClick={() => setShowRemoveTagModal(true)}
                  className="px-3 py-1.5 bg-orange-600 text-white text-xs rounded hover:bg-orange-700 flex items-center gap-1"
                >
                  🏷️ Untag ({selectedRows.size})
                </button>
              )}

              {/* Change Color Button (companies only, when rows selected) */}
              {selectedRows.size > 0 && activeDataType === 'companies' && (
                <button
                  onClick={() => setShowColorModal(true)}
                  className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 flex items-center gap-1"
                >
                  🎨 Color ({selectedRows.size})
                </button>
              )}

              {/* Delete Button (when rows selected) */}
              {selectedRows.size > 0 && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className={`text-xs px-3 py-1.5 flex items-center gap-1 ${campaignScopeId ? 'btn-secondary' : 'btn-danger'}`}
                >
                  {campaignScopeId ? `✕ Remove from campaign (${selectedRows.size})` : `🗑️ Delete (${selectedRows.size})`}
                </button>
              )}

              {/* Add Single Row Button (always visible) */}
              <button
                onClick={() => setShowAddSingleModal(true)}
                className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-xs flex items-center gap-1"
              >
                + Add {activeDataType === 'companies' ? 'Company' : activeDataType === 'candidates' ? 'Candidate' : 'Hiring Manager'}
              </button>

              {/* Find Contacts on LinkedIn - Companies only, requires selection */}
              {SHOW_LINKEDIN_FIND_HIRING_MANAGERS && activeDataType === 'companies' && (
                <button
                  onClick={() => {
                    if (selectedRows.size === 0) {
                      alert('Please select (check) one or more companies first to search for contacts.');
                      return;
                    }
                    const selected = Array.from(selectedRows);
                    const companiesWithOrgId = companies.filter(c => selected.includes(c.id) && c.org_id);
                    if (companiesWithOrgId.length === 0) {
                      alert('Selected companies do not have Org IDs. Enrich companies first.');
                      return;
                    }
                    if (companiesWithOrgId.length > 99) {
                      alert(`LinkedIn Classic supports a maximum of 99 companies per search. You have ${companiesWithOrgId.length} companies with Org IDs selected. Only the first 99 will be used.`);
                    }
                    setFindContactsTitles('');
                    setFindContactsMsg(null);
                    loadContactTemplates();
                    setShowFindContactsModal(true);
                  }}
                  className="px-3 py-1.5 bg-[#0077B5] text-white rounded hover:bg-[#006097] text-xs flex items-center gap-1"
                  title="Open a LinkedIn search for people at the selected companies"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  Find Hiring Managers{selectedRows.size > 0 ? ` (${selectedRows.size})` : ''}
                </button>
              )}

              {/* Find Contacts (universe) — search everyone with a current
                  role at the selected companies (user 1 while staged). The
                  user then says what they're hunting: candidates or hiring
                  managers. Contact-detail enrichment (Apollo/Clay) stays the
                  step after. */}
              {activeDataType === 'companies' && (userId === 1 || (user as any)?.universeAccess) && (
                <button
                  data-tour="project-find-contacts"
                  onClick={async () => {
                    if (selectedRows.size === 0) {
                      alert('Please select (check) one or more companies first.');
                      return;
                    }
                    const selected = Array.from(selectedRows);
                    // The table only holds the loaded page(s); when the user
                    // has selected everything visible and the project has
                    // more, they mean ALL companies — fetch the full list
                    // from the server instead of seeding just one page.
                    const allLoadedSelected = companies.length > 0 && companies.every((c: any) => selectedRows.has(c.id));
                    if (allLoadedSelected && selectedProject?.id && serverTotals.companies > companies.length) {
                      try {
                        const resp = await fetch(`/api/universe/project-company-entries/${selectedProject.id}`, {
                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                        });
                        const d = await resp.json();
                        if (resp.ok && Array.isArray(d.entries) && d.entries.length) {
                          setContactIntentCompanyIds(null);
                          setContactIntentEntries(d.entries);
                          return;
                        }
                      } catch { /* fall through to the selected rows */ }
                    }
                    // Prefer the LinkedIn company URL (exact slug match in the
                    // universe), then domain, then name.
                    const entries = companies
                      .filter(c => selected.includes(c.id))
                      .map((c: any) => c.linkedin_numeric_url || c.url || c.domain || c.company_domain || c.company_name)
                      .filter(Boolean);
                    if (!entries.length) {
                      alert('Selected companies have no LinkedIn URL, domain or name to search with.');
                      return;
                    }
                    setContactIntentCompanyIds(selected);
                    setContactIntentEntries(entries);
                  }}
                  className="px-3 py-1.5 bg-violet-600 text-white rounded hover:bg-violet-700 text-xs flex items-center gap-1"
                  title="Search the universe for everyone with a current role at the selected companies"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Find Contacts{selectedRows.size > 0 ? ` (${selectedRows.size})` : ''}
                </button>
              )}

              {/* What are we hunting at these companies? */}
              {contactIntentEntries && (
                <div className="modal-backdrop flex items-center justify-center" onClick={() => setContactIntentEntries(null)}>
                  <div className="modal-dialog p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                    <h3 className="text-lg font-semibold mb-1">Find contacts at {contactIntentEntries.length} compan{contactIntentEntries.length === 1 ? 'y' : 'ies'}</h3>
                    <p className="text-sm text-gray-500 mb-4">What are you looking for? This sets up the search — you can refine it there.</p>
                    <div className="space-y-2">
                      {([
                        { intent: 'candidates', label: 'Candidates', desc: 'Source people to place — opens a blank search across everyone at these companies.' },
                        { intent: 'hiring-managers', label: 'Hiring managers', desc: 'Find the people who hire — opens with a hiring-manager title preset offered.' },
                      ] as const).map((opt) => (
                        <button
                          key={opt.intent}
                          data-tour={opt.intent === 'hiring-managers' ? 'contacts-intent-hm' : undefined}
                          className="w-full text-left border border-gray-200 rounded-lg px-4 py-3 hover:border-violet-400 hover:bg-violet-50/50"
                          onClick={() => {
                            try {
                              sessionStorage.setItem('universe_search_seed', JSON.stringify({
                                entries: contactIntentEntries,
                                intent: opt.intent,
                                label: `${contactIntentEntries.length} compan${contactIntentEntries.length === 1 ? 'y' : 'ies'} from this project`,
                                ...(opt.intent === 'hiring-managers' && selectedProject?.id
                                  ? { review: { projectId: selectedProject.id, projectName: selectedProject.name } }
                                  : {}),
                              }));
                            } catch { /* storage unavailable — the page just opens blank */ }
                            window.location.assign('/contact-search');
                          }}
                        >
                          <span className="text-sm font-medium text-gray-900">{opt.label}</span>
                          <span className="block text-xs text-gray-500 mt-0.5">{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                    {!IS_RECMAPPER && (
                      <button
                        data-tour="contacts-intent-discover"
                        className="w-full text-left border border-violet-300 bg-violet-50/40 rounded-lg px-4 py-3 mt-2 hover:border-violet-500 hover:bg-violet-50"
                        onClick={() => {
                          const isAdmin = !user?.teamId || user?.teamRole === 'admin';
                          if (isAdmin) setDiscoverCompanyIds(contactIntentCompanyIds);
                          else setDiscoverApprovalFor(contactIntentCompanyIds);
                          setContactIntentEntries(null);
                        }}
                      >
                        <span className="text-sm font-medium text-gray-900">Auto-discover hiring managers <span className="ml-1 text-[10px] uppercase tracking-wide bg-violet-600 text-white rounded px-1.5 py-0.5">AI</span></span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          We read your mapped candidates to learn what they do, then find the people at each company who hire them —
                          hiring managers by level, talent acquisition, and owners at smaller firms. Each pick shows why. Free up to your plan’s monthly allowance, then 1 credit per company.
                        </span>
                      </button>
                    )}
                    <div className="flex justify-end mt-4">
                      <button className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800" onClick={() => setContactIntentEntries(null)}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}
              {discoverApprovalFor !== undefined && selectedProject?.id && (
                <FeatureApprovalPrompt
                  feature="discovery"
                  projectId={selectedProject.id}
                  onAllowed={() => { const ids = discoverApprovalFor; setDiscoverApprovalFor(undefined); setDiscoverCompanyIds(ids); }}
                  onClose={() => setDiscoverApprovalFor(undefined)}
                />
              )}
              {discoverCompanyIds !== undefined && selectedProject?.id && (
                <HmDiscoveryModal
                  projectId={selectedProject.id}
                  companyIds={discoverCompanyIds}
                  onClose={() => setDiscoverCompanyIds(undefined)}
                  onDone={() => { refreshAllData(); }}
                />
              )}

              {/* Export to CSV Button (always visible). On the companies
                  tab we open a small modal first to let the user pick
                  one-row-per-company vs one-row-per-office; other tabs
                  export straight away. */}
              <button
                onClick={() => {
                  if (activeDataType === 'companies') {
                    setShowExportOptionsModal(true);
                  } else {
                    exportToCSV();
                  }
                }}
                className="btn-primary text-xs px-3 py-1.5"
                data-tour="export-csv"
              >
                Export CSV
              </button>

              {/* Sales Navigator Search - Admin only, Companies only */}
              {isAdmin && activeDataType === 'companies' && (
                <button
                  onClick={() => {
                    const companiesWithOrgId = companies.filter(c => c.org_id);
                    if (companiesWithOrgId.length === 0) {
                      alert('No companies with Org IDs found in the current filtered view. Enrich companies first.');
                      return;
                    }
                    setSalesNavTitles('');
                    setShowSalesNavModal(true);
                  }}
                  className="px-3 py-1.5 bg-[#0077B5] text-white rounded hover:bg-[#006097] text-xs flex items-center gap-1"
                  title="Open LinkedIn Sales Navigator with filtered companies"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  Sales Nav Search
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Radius Search Panel - Two Independent Filters */}
        {showRadiusSearch && (
          <div className="px-4 py-2 border-b">
            <div className="flex gap-4 flex-wrap">
              {/* Company Office Radius Filter */}
              <div data-tour="radius-company" className="p-3 border rounded bg-blue-50 flex-1 min-w-[280px]">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-blue-800">🏢 Company Office Location</label>
                  {companyOfficeRadius.location && (
                    <button
                      type="button"
                      onClick={() => {
                        setCompanyOfficeRadius({ location: '', distance: 25, coords: null });
                        setTempCompanyOfficeRadius({ location: '', distance: 25 });
                        // Clear legacy state too
                        if (radiusType === 'company') {
                          setRadiusLocation('');
                          setRadiusCoords(null);
                        }
                        applyDualRadiusSearch();
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-600 mb-2">Filter companies by office location, then show candidates at those companies</p>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tempCompanyOfficeRadius.location}
                    onChange={(e) => setTempCompanyOfficeRadius(prev => ({ ...prev, location: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyDualRadiusSearch('company', tempCompanyOfficeRadius.location, Number.isFinite(tempCompanyOfficeRadius.distance) && tempCompanyOfficeRadius.distance > 0 ? tempCompanyOfficeRadius.distance : 25);
                      }
                    }}
                    placeholder="e.g. London, UK"
                    className="flex-1 px-2 py-1 text-xs border rounded"
                  />
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={Number.isFinite(tempCompanyOfficeRadius.distance) && tempCompanyOfficeRadius.distance > 0 ? tempCompanyOfficeRadius.distance : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '') {
                        setTempCompanyOfficeRadius(prev => ({ ...prev, distance: NaN }));
                        return;
                      }
                      const n = parseInt(v, 10);
                      setTempCompanyOfficeRadius(prev => ({ ...prev, distance: Number.isFinite(n) ? Math.max(1, Math.min(500, n)) : NaN }));
                    }}
                    className="w-20 px-2 py-1 text-xs border rounded"
                    placeholder="Miles"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => applyDualRadiusSearch('company', tempCompanyOfficeRadius.location, Number.isFinite(tempCompanyOfficeRadius.distance) && tempCompanyOfficeRadius.distance > 0 ? tempCompanyOfficeRadius.distance : 25)}
                  disabled={!tempCompanyOfficeRadius.location.trim()}
                  className="btn-primary w-full text-xs px-3 py-1.5"
                >
                  Apply Company Filter
                </button>
                {companyOfficeRadius.location && (
                  <div className="mt-2 text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded">
                    Active: {companyOfficeRadius.distance} miles of {companyOfficeRadius.location}
                  </div>
                )}
              </div>

              {/* Candidate Location Radius Filter */}
              <div data-tour="radius-candidate" className="p-3 border rounded bg-green-50 flex-1 min-w-[280px]">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-green-800">👤 Candidate Personal Location</label>
                  {candidateLocationRadius.location && (
                    <button
                      type="button"
                      onClick={() => {
                        setCandidateLocationRadius({ location: '', distance: 25, coords: null });
                        setTempCandidateLocationRadius({ location: '', distance: 25 });
                        // Clear legacy state too
                        if (radiusType === 'candidate') {
                          setRadiusLocation('');
                          setRadiusCoords(null);
                        }
                        applyDualRadiusSearch();
                      }}
                      className="text-xs text-green-600 hover:text-green-800"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-600 mb-2">Filter candidates by their personal/home location</p>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tempCandidateLocationRadius.location}
                    onChange={(e) => setTempCandidateLocationRadius(prev => ({ ...prev, location: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyDualRadiusSearch('candidate', tempCandidateLocationRadius.location, Number.isFinite(tempCandidateLocationRadius.distance) && tempCandidateLocationRadius.distance > 0 ? tempCandidateLocationRadius.distance : 25);
                      }
                    }}
                    placeholder="e.g. Manchester, UK"
                    className="flex-1 px-2 py-1 text-xs border rounded"
                  />
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={Number.isFinite(tempCandidateLocationRadius.distance) && tempCandidateLocationRadius.distance > 0 ? tempCandidateLocationRadius.distance : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '') {
                        setTempCandidateLocationRadius(prev => ({ ...prev, distance: NaN }));
                        return;
                      }
                      const n = parseInt(v, 10);
                      setTempCandidateLocationRadius(prev => ({ ...prev, distance: Number.isFinite(n) ? Math.max(1, Math.min(500, n)) : NaN }));
                    }}
                    className="w-20 px-2 py-1 text-xs border rounded"
                    placeholder="Miles"
                  />
                </div>
                <button
                  type="button"
                  data-tour="radius-candidate-apply"
                  onClick={() => applyDualRadiusSearch('candidate', tempCandidateLocationRadius.location, Number.isFinite(tempCandidateLocationRadius.distance) && tempCandidateLocationRadius.distance > 0 ? tempCandidateLocationRadius.distance : 25)}
                  disabled={!tempCandidateLocationRadius.location.trim()}
                  className="w-full px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply Candidate Filter
                </button>
                {candidateLocationRadius.location && (
                  <div className="mt-2 text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                    Active: {candidateLocationRadius.distance} miles of {candidateLocationRadius.location}
                  </div>
                )}
              </div>

              {/* Hiring Manager Location Radius Filter — managers tab only,
                  since it doesn't cross-filter to companies or candidates. */}
              {activeDataType === 'managers' && (
              <div className="p-3 border rounded bg-purple-50 flex-1 min-w-[280px]">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-purple-800">👔 Hiring Manager Location</label>
                  {managerLocationRadius.location && (
                    <button
                      type="button"
                      onClick={async () => {
                        setManagerLocationRadius({ location: '', distance: 25, coords: null });
                        setTempManagerLocationRadius({ location: '', distance: 25 });
                        // Re-fetch managers without the manager radius filter
                        setLoading(true);
                        try {
                          const result = await fetchPaginatedData(
                            'managers', 1, rowLimit, 'name', 'asc',
                            aiFilterState.groups, aiFilterState.employmentTypes
                          );
                          if (result) {
                            setHiringManagers(result.data);
                            setFullHiringManagers(result.data);
                            setServerTotals(prev => ({ ...prev, managers: result.pagination.total }));
                          }
                        } finally {
                          setLoading(false);
                        }
                      }}
                      className="text-xs text-purple-600 hover:text-purple-800"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-600 mb-2">Filter hiring managers by their personal/home location (Managers tab only)</p>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tempManagerLocationRadius.location}
                    onChange={(e) => setTempManagerLocationRadius(prev => ({ ...prev, location: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyDualRadiusSearch('manager', tempManagerLocationRadius.location, Number.isFinite(tempManagerLocationRadius.distance) && tempManagerLocationRadius.distance > 0 ? tempManagerLocationRadius.distance : 25);
                      }
                    }}
                    placeholder="e.g. Bristol, UK"
                    className="flex-1 px-2 py-1 text-xs border rounded"
                  />
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={Number.isFinite(tempManagerLocationRadius.distance) && tempManagerLocationRadius.distance > 0 ? tempManagerLocationRadius.distance : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '') {
                        setTempManagerLocationRadius(prev => ({ ...prev, distance: NaN }));
                        return;
                      }
                      const n = parseInt(v, 10);
                      setTempManagerLocationRadius(prev => ({ ...prev, distance: Number.isFinite(n) ? Math.max(1, Math.min(500, n)) : NaN }));
                    }}
                    className="w-20 px-2 py-1 text-xs border rounded"
                    placeholder="Miles"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => applyDualRadiusSearch('manager', tempManagerLocationRadius.location, Number.isFinite(tempManagerLocationRadius.distance) && tempManagerLocationRadius.distance > 0 ? tempManagerLocationRadius.distance : 25)}
                  disabled={!tempManagerLocationRadius.location.trim()}
                  className="w-full px-3 py-1.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply Manager Filter
                </button>
                {managerLocationRadius.location && (
                  <div className="mt-2 text-xs text-purple-700 bg-purple-100 px-2 py-1 rounded">
                    Active: {managerLocationRadius.distance} miles of {managerLocationRadius.location}
                  </div>
                )}
              </div>
              )}
            </div>

            {/* Clear All Button */}
            {(companyOfficeRadius.location || candidateLocationRadius.location || managerLocationRadius.location) && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={clearRadiusSearch}
                  className="px-3 py-1.5 text-xs border rounded hover:bg-gray-50"
                >
                  Clear All Radius Filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* View Toggle + Pagination Top */}
        <div className="px-4 py-2 border-b flex items-center justify-between">
          <div className="text-sm text-gray-600 flex items-center gap-2">
            {filteredTotal > rowLimit && <>Page {currentPage} of {Math.max(1, Math.ceil(Math.max(0, filteredTotal - startRow) / rowLimit))}</>}
            {pageLoading && <span className="text-blue-500">(Loading...)</span>}
          </div>
          <div className="flex items-center gap-3">
            {/* View mode toggle */}
            <div className="relative flex items-center border border-gray-200 rounded-lg overflow-hidden">
              {showCardViewHint && (
                <div className="absolute -top-8 right-0 bg-blue-600 text-white text-[10px] font-semibold px-2 py-1 rounded-md whitespace-nowrap animate-fade-in z-10">
                  New! Try card view
                  <div className="absolute top-full right-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-blue-600"></div>
                </div>
              )}
              <button
                onClick={() => handleSetViewMode('table')}
                className={`p-1.5 transition-all duration-200 ${viewMode === 'table' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                title="Table view"
                data-tour="view-table"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M3 6h18M3 18h18" />
                </svg>
              </button>
              <button
                onClick={() => handleSetViewMode('card')}
                className={`p-1.5 transition-all duration-200 ${viewMode === 'card' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                title="Card view"
                data-tour="view-card"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
            </div>
            {filteredTotal > rowLimit && (
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || pageLoading}
                  className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(p => {
                    const maxPage = Math.max(1, Math.ceil(Math.max(0, filteredTotal - startRow) / rowLimit));
                    return Math.min(maxPage, p + 1);
                  })}
                  disabled={currentPage >= Math.max(1, Math.ceil(Math.max(0, filteredTotal - startRow) / rowLimit)) || pageLoading}
                  className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

{/* Select all results banner - appears when all items on current page are selected but more exist */}
{selectedRows.size > 0 && selectedRows.size === displayData.length && filteredTotal > displayData.length && !allResultsSelected && (
  <div className="px-4 py-2 bg-blue-50 border-b border-blue-200/60 text-center text-sm animate-fade-in">
    <span className="text-blue-800">
      All <strong>{displayData.length}</strong> {activeDataType} on this page are selected.
    </span>
    <button
      onClick={handleSelectAllResults}
      disabled={loadingAllIds}
      className="ml-2 text-blue-700 font-semibold hover:text-blue-900 underline transition-colors"
    >
      {loadingAllIds ? 'Loading...' : `Select all ${filteredTotal.toLocaleString()} ${activeDataType}`}
    </button>
  </div>
)}
{allResultsSelected && (
  <div className="px-4 py-2 bg-blue-50 border-b border-blue-200/60 text-center text-sm animate-fade-in">
    <span className="text-blue-800">
      All <strong>{selectedRows.size.toLocaleString()}</strong> {activeDataType} are selected.
    </span>
    <button
      onClick={() => { setSelectedRows(new Set()); setAllResultsSelected(false); }}
      className="ml-2 text-blue-700 font-semibold hover:text-blue-900 underline transition-colors"
    >
      Clear selection
    </button>
  </div>
)}

{/* Card View */}
{viewMode === 'card' && (
  <div className="overflow-auto pb-8 px-4 pt-2 max-md:px-1" style={{ maxHeight: 'calc(100vh - 100px)' }}>
    {/* Company drilldown banner */}
    {companyDrilldown && (
      <div className="mb-3 flex items-center gap-2 bg-blue-50 border border-blue-200/60 rounded-xl px-4 py-2.5 animate-fade-in">
        <span className="text-sm text-blue-800">
          Showing <strong>{activeDataType}</strong> at <strong>{companyDrilldown}</strong>
        </span>
        <button
          onClick={() => {
            setCompanyDrilldown(null);
            setSearchTerm('');
            setAssociatedCompanyFilter(null);
            handleDataTypeChange('companies');
          }}
          className="ml-auto btn-secondary text-xs px-3 py-1"
        >
          ← Back to Companies
        </button>
      </div>
    )}
    {displayData.length === 0 && !loading ? (
      <div className="p-12 text-center">
        <div className="text-6xl mb-4">🔍</div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No {activeDataType} found</h3>
      </div>
    ) : (
      <CardView
        data={displayData}
        dataType={activeDataType}
        selectedRows={selectedRows}
        onToggleSelect={(id: number) => {
          setSelectedRows(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          });
        }}
        onSelectAll={handleSelectAll}
        onRowClick={(row: any) => {
          const entityType = activeDataType === 'managers' ? 'contact' : activeDataType === 'companies' ? 'company' : 'candidate';
          setDetailPanel({ open: true, entityType, entityId: row.id });
        }}
        onRowHover={(row: any) => {
          // Warm the detail-panel cache so the profile opens instantly.
          const entityType = activeDataType === 'managers' ? 'contact' : activeDataType === 'companies' ? 'company' : 'candidate';
          if (entityType !== 'company') prefetchEntity(entityType, row.id, localStorage.getItem('token') || '');
        }}
        onCompanyAssociationClick={(companyName: string, tab: 'candidates' | 'managers') => {
          setCompanyDrilldown(companyName);
          if (tab === 'candidates') {
            setAssociatedCompanyFilter(companyName);
            handleDataTypeChange('candidates', undefined, companyName);
          } else {
            // Filter hiring managers via the junction-table association
            // (not org-name text) so HMs linked by LinkedIn ID / normalized
            // name are included — matches the HM-count on the companies tab.
            setAssociatedCompanyFilter(companyName);
            const emptyFilter: FilterState = { groups: [], employmentTypes: aiFilterState.employmentTypes };
            setAiFilterState(emptyFilter);
            handleDataTypeChange('managers', emptyFilter, companyName);
          }
        }}
        companyCandidateCounts={companyCandidateCounts}
        hasActiveFilters={aiFilterState.employmentTypes.length > 0
          || aiFilterState.groups.length > 0
          || !!(radiusCoords && radiusDistance > 0)
          || !!companyOfficeRadius.coords
          || !!candidateLocationRadius.coords}
        totalCount={filteredTotal}
      />
    )}
  </div>
)}

{/* Data Table - Natural page flow, no height constraints */}
{viewMode === 'table' && (<>
  <div
    id="main-table-container"
    className="overflow-auto pb-8"
    // Sticky scroll region. As the page scrolls, the chrome above (AI Smart
    // Search, toolbar, buttons) scrolls away and this container pins to the
    // top of the viewport. Capped at ~580px (~17 rows) so the horizontal
    // scrollbar at the bottom stays easily reachable, while min() shrinks it
    // to fit smaller viewports. The internal `thead` (position: sticky top-0)
    // keeps the column headers frozen while the body scrolls inside here.
    style={{ height: 'min(calc(100vh - 16px), 580px)', position: 'sticky', top: 8 }}
  >
    {/* No Results Message */}
    {displayData.length === 0 && !loading && (
      <div className="p-12 text-center">
        <div className="text-6xl mb-4">🔍</div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No {activeDataType} found</h3>
        
{aiFilterState.groups.length > 0 && (
          <div className="mt-4 inline-block bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
            <p className="text-sm font-medium text-blue-900 mb-2">📋 Active Filters:</p>
            <ul className="text-sm text-blue-800 space-y-1 mb-3">
              {aiFilterState.groups.map((group, i) => (
  <li key={i}>
    <strong>{group.name || `Filter Group ${i + 1}`}</strong>:
    {group.conditions.map(c => {
      const displayValue = c.values && Array.isArray(c.values) ? c.values.join(' + ') : c.value;
      return ` ${c.column} ${c.operator} "${displayValue}"`;
    }).join(` ${group.logic} `)}
  </li>
))}
            </ul>
            <button
              onClick={handleClearAIFilters}
              className="btn-primary"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    )}

    {/* Only show table if we have results.
        NOTE: borderCollapse:'separate' (not Tailwind's default 'collapse') is
        required for the sticky `top-0` <th> headers to stick while the body
        scrolls — Chrome/Safari don't honour position:sticky on cells of a
        border-collapsed table. borderSpacing:0 keeps the layout identical. */}
    {displayData.length > 0 && (
      <table className="w-full text-sm" style={{ minWidth: 'max-content', borderCollapse: 'separate', borderSpacing: 0 }}>
<thead className="bg-gray-50 border-b-2 border-gray-300">
          <tr>
            {(activeDataType === 'candidates' || activeDataType === 'managers' || activeDataType === 'companies') && (
              <th data-tour="project-select-all" className="px-3 py-2 text-left border-r border-gray-200 w-12 bg-gray-50 sticky top-0" style={{ left: 0, zIndex: 32 }}>
                <input
                  type="checkbox"
                  checked={selectedRows.size === displayData.length && displayData.length > 0}
                  onChange={handleSelectAll}
                  className="cursor-pointer"
                />
              </th>
            )}
            {/* Row Number Column */}
            <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-r border-gray-200 w-12 bg-gray-50 sticky top-0" style={{ left: 48, zIndex: 32 }}>
              #
            </th>
            {/* Match Score Column - job spec match mode (keyword v1 or AI v2 scores) */}
            {isJobSpecMatchMode && activeDataType === 'candidates' && (preferredFilterGroups.length > 0 || jevMatch) && (
              <th
                className="px-3 py-2 text-left text-xs font-medium text-white uppercase border-r border-green-400 bg-green-600 whitespace-nowrap sticky top-0 cursor-pointer hover:bg-green-700"
                style={{ width: '90px', minWidth: '90px', left: 96, zIndex: 32 }}
                title={jevMatch
                  ? `AI match score (0-100) against "${jevMatch.title}". Click to sort.`
                  : "Weighted match score: 10 pts per matched 'Nice to Have' criteria, +1 pt per additional keyword occurrence. Click to sort."}
                onClick={() => handleApplySort('_matchScore', sortConfig.column === '_matchScore' && sortConfig.direction === 'desc' ? 'asc' : 'desc')}
              >
                <div className="flex items-center gap-1">
                  <span>📊</span>
                  <span>{jevMatch ? 'Match' : 'Score'}</span>
                  {sortConfig.column === '_matchScore' && (
                    <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
            )}
            {columnOrder
              .filter(colKey => visibleColumns.includes(colKey))
              .filter(colKey => !isColumnHiddenByGroup(colKey))
              .map(colKey => {
                const col = (getCurrentColumns() as Array<{ key: string; label: string; type: string; isLocked?: boolean; groupKey?: string | null; groupLabel?: string | null }>)
                  .find(c => c.key === colKey);
                if (!col) return null;

                const isLocked = col.isLocked && activeDataType === 'companies';
                const isFrozen = frozenColumns.has(col.key);
                const colColor = columnColors[col.key] || null;
                const colGroupKey = (col as any).groupKey as string | null;
                const colGroupLabel = (col as any).groupLabel as string | null;
                const isGroupLeader = !!colGroupKey && columnGroupMembers[colGroupKey]?.[0] === col.key;
                const isGroupCollapsed = !!colGroupKey && collapsedColumnGroups.has(colGroupKey);
                // When the group is collapsed, the leader shows the group label instead of its own label
                const displayLabel = isGroupLeader && isGroupCollapsed
                  ? (colGroupLabel || col.label)
                  : col.label;

                return (
                  <th
                    key={col.key}
                    draggable={!isLocked}
                    onDragStart={!isLocked ? (e) => handleDragStart(e, col.key) : undefined}
                    onDragOver={!isLocked ? handleDragOver : undefined}
                    onDrop={!isLocked ? (e) => handleDrop(e, col.key) : undefined}
                    onDragEnd={!isLocked ? handleDragEnd : undefined}
                    onClick={() => {
                      // When a group is collapsed, sort by the group's overall score
                      // (stored in custom_data as `${groupKey}__score`), not the
                      // individual criterion value.
                      const isGroupCollapsedNow = !!colGroupKey && collapsedColumnGroups.has(colGroupKey);
                      const isNumeric = col.type === 'number';
                      const isCustom = !!(col as any).isCustom;
                      let sortKey: string;
                      if (isGroupCollapsedNow && colGroupKey) {
                        // Sort by the group's overall score
                        sortKey = `custom:${colGroupKey}__score`;
                      } else {
                        sortKey = isCustom ? `custom:${col.key}` : col.key;
                      }
                      const isSameColumn = sortConfig.column === sortKey;
                      let newDirection: 'asc' | 'desc';
                      if (isSameColumn) {
                        newDirection = sortConfig.direction === 'asc' ? 'desc' : 'asc';
                      } else {
                        // Default to desc for scores/numbers/custom, asc for text
                        newDirection = (isNumeric || isCustom || isGroupCollapsedNow) ? 'desc' : 'asc';
                      }
                      handleApplySort(sortKey, newDirection);
                    }}
                    className={`px-3 py-2 text-left text-xs font-medium uppercase border-r whitespace-nowrap sticky top-0 z-30 ${
                      colColor ? 'text-white border-white/30' : 'text-gray-700 border-gray-200 hover:bg-gray-100 bg-gray-50'
                    } ${
                      isLocked ? 'cursor-default' : 'cursor-pointer'
                    } ${!colColor && isGroupLeader && colGroupKey ? 'bg-blue-50' : ''}`}
                    style={{
                      width: columnWidths[col.key] || 'auto',
                      minWidth: columnWidths[col.key] || 'auto',
                      // Must be 'sticky' (not 'relative') so the `sticky top-0`
                      // class actually freezes the header on vertical scroll.
                      // sticky still establishes a containing block for the
                      // absolutely-positioned resize handle below.
                      position: 'sticky',
                      top: 0,
                      opacity: draggedColumn === col.key ? 0.5 : 1,
                      // Pinned (frozen/locked) columns stick to the left at their
                      // computed gutter offset and sit above normal headers.
                      left: isFrozen ? (frozenLeftOffsets[col.key] ?? 0) : 'auto',
                      zIndex: isFrozen ? 31 : undefined,
                      backgroundColor: colColor || undefined
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        {isLocked && <span className="text-gray-400">🔒</span>}
                        {isGroupLeader && colGroupKey && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleColumnGroup(colGroupKey); }}
                            className="text-blue-600 hover:text-blue-800 font-bold"
                            title={isGroupCollapsed
                              ? `Expand group: ${colGroupLabel}`
                              : `Collapse ${columnGroupMembers[colGroupKey]?.length || 0} criteria into one column`}
                          >
                            {isGroupCollapsed ? '»' : '«'}
                          </button>
                        )}
                        {displayLabel}
                      </span>
                      <span className="flex items-center gap-0.5">
                        {(sortConfig.column === col.key || sortConfig.column === `custom:${col.key}` || (colGroupKey && sortConfig.column === `custom:${colGroupKey}__score`)) && (
                          <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                        {!campaignScopeId && aiColumnKeys.has(col.key) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); runColumn(col.key, displayLabel, 'empty'); }}
                            className={`ml-1 p-0.5 rounded ${colColor ? 'text-white/80 hover:text-white' : 'text-violet-500 hover:text-violet-700'}`}
                            title="Run AI on blank cells (rows not yet filled). Use the ⋮ menu to re-run all."
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (customColMenu === col.key) {
                              setCustomColMenu(null);
                              setCustomColMenuPos(null);
                            } else {
                              const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                              setCustomColMenuPos({
                                top: rect.bottom + 4,
                                right: window.innerWidth - rect.right,
                                anchorTop: rect.top,
                              } as any);
                              setCustomColMenu(col.key);
                            }
                          }}
                          className={`ml-1 p-0.5 rounded ${colColor ? 'text-white/80 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                          title="Column options"
                        data-tour="column-menu"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                          </svg>
                        </button>
                      </span>
                    </div>
                    {/* Resize handle */}
                    <div
                      onMouseDown={(e) => handleResizeStart(e, col.key)}
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 bg-gray-300"
                      style={{ zIndex: 31 }}
                    />
                  </th>
                );
              })}
          </tr>
        </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {displayData.map((row: any, idx) => {
          // Check if this company was a previous employer of the selected candidate
          const isPreviousEmployer = activeDataType === 'companies' &&
            selectedConnectionsFilter?.previousCompanies?.some(
              prevCompany => prevCompany.toLowerCase().replace(/[™®©]/g, '').trim() ===
                            (row.company_name || '').toLowerCase().replace(/[™®©]/g, '').trim()
            );


          return (
          <tr
            key={row.id || idx}
            className={`hover:bg-gray-50
              ${selectedRows.has(row.id) ? 'bg-blue-50' : ''}
              ${activeDataType === 'managers' && !selectedRows.has(row.id) && (row as any).discovery_kind
                ? DISCOVERY_ROW_CLASS[(row as any).discovery_kind as string] || ''
                : activeDataType === 'managers' && starredManagerIds.has(row.id) ? 'bg-yellow-50' : ''}
              ${isPreviousEmployer ? 'bg-amber-50 border-l-4 border-l-amber-400' : ''}
            `}
          >
            {(activeDataType === 'candidates' || activeDataType === 'managers' || activeDataType === 'companies') && (
              <td
                className="px-3 py-2 border-r border-gray-200 bg-white"
                style={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 10,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.05)'
                }}
              >
                <input
                  type="checkbox"
                  data-tour="row-checkbox"
                  checked={selectedRows.has(row.id)}
                  onClick={(e) => handleSelectRow(row.id, idx, e)}
                  onChange={() => {}} // Controlled by onClick for shift-click support
                  className="cursor-pointer"
                />
              </td>
            )}
            {/* Row Number Cell */}
            <td className="px-2 py-2 text-center text-xs text-gray-400 border-r border-gray-200 bg-white" style={{ position: 'sticky', left: 48, zIndex: 10 }}>
              {startRow + ((currentPage - 1) * rowLimit) + idx + 1}
            </td>
            {/* Match Score Cell - job spec match mode (keyword v1 or AI v2 scores) */}
            {isJobSpecMatchMode && activeDataType === 'candidates' && (preferredFilterGroups.length > 0 || jevMatch) && (
              <td
                className="px-3 py-2 border-r border-gray-200 text-center font-medium bg-white"
                style={{ width: '90px', minWidth: '90px', position: 'sticky', left: 96, zIndex: 10 }}
              >
                {(() => {
                  const score = candidateMatchScores[row.id];
                  if (score === undefined) return <span className="text-gray-400">-</span>;

                  // v2 AI scores are 0-100 composites of per-criterion judgments.
                  // Clickable: opens the breakdown popover (criteria bars +
                  // rationale, written on demand) for this candidate.
                  if (jevMatch) {
                    const aiColor = score >= 75 ? 'bg-green-100 text-green-800' :
                                    score >= 55 ? 'bg-yellow-100 text-yellow-800' :
                                    score >= 35 ? 'bg-orange-100 text-orange-800' :
                                    'bg-gray-100 text-gray-600';
                    return (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMatchPopover({ candidateId: row.id, name: row.name, x: e.clientX, y: e.clientY });
                        }}
                        className={`px-2 py-1 rounded text-sm cursor-pointer hover:ring-2 hover:ring-green-300 ${aiColor}`}
                        title={`AI match vs "${jevMatch.title}" — click for the breakdown and explanation`}
                      >
                        {score}
                      </button>
                    );
                  }

                  // Max possible base score = 10 points per preferred group
                  const maxBaseScore = preferredFilterGroups.length * 10;

                  // Color based on score relative to max possible
                  // High: matched all groups + multiple occurrences
                  // Medium: matched some groups
                  // Low: matched few/no groups
                  const bgColor = score >= maxBaseScore ? 'bg-green-100 text-green-800' :
                                  score >= maxBaseScore * 0.5 ? 'bg-yellow-100 text-yellow-800' :
                                  score > 0 ? 'bg-orange-100 text-orange-800' :
                                  'bg-gray-100 text-gray-600';

                  return (
                    <span className={`px-2 py-1 rounded text-sm ${bgColor}`} title={`Score: ${score} pts (10 per matched criteria + 1 per extra occurrence)`}>
                      {score} pts
                    </span>
                  );
                })()}
              </td>
            )}
{columnOrder
              .filter(colKey => visibleColumns.includes(colKey))
              .filter(colKey => !isColumnHiddenByGroup(colKey))
              .map(colKey => {
                const col = (getCurrentColumns() as Array<{ key: string; label: string; type: string; groupKey?: string | null; groupLabel?: string | null }>)
                  .find(c => c.key === colKey);
                if (!col) return null;

                let value = row[col.key];

                if (col.key === 'years_at_current_company') {
                  // Live: counted from the role start date (utils/tenure.ts)
                  value = formatLiveTenure(row) || '-';
                } else if (Array.isArray(value)) {
                  value = value.join(', ');
                } else if (col.type === 'date' && value) {
                  value = new Date(value).toLocaleDateString();
                } else if (col.type === 'number' && value != null) {
                  value = TENURE_COLUMNS.has(col.key) ? (formatTenure(value) || '-') : value.toLocaleString();
                } else if (col.type === 'boolean') {
                  value = value === true ? 'Yes' : value === false ? 'No' : '-';
                }

                const cellFrozen = frozenColumns.has(col.key);
                const cellStyle: React.CSSProperties = {
                  width: columnWidths[col.key] || 'auto',
                  minWidth: columnWidths[col.key] || 'auto',
                  // Pinned columns stick to the left; opaque bg so scrolled cells
                  // don't show through, and z-index above normal body cells.
                  ...(cellFrozen ? { position: 'sticky' as const, left: frozenLeftOffsets[col.key] ?? 0, zIndex: 10, background: '#fff' } : {})
                };

                // If this is the leader of a collapsed group, render a
                // 0-100 score badge instead of individual verdicts.
                const colGroupKey = (col as any).groupKey as string | null;
                const colGroupLabel = (col as any).groupLabel as string | null;
                if (colGroupKey && collapsedColumnGroups.has(colGroupKey) && columnGroupMembers[colGroupKey]?.[0] === col.key) {
                  const score = getGroupScore(row, colGroupKey);
                  const reasoning = getGroupReasoning(row, colGroupKey);
                  const groupConfidence = getGroupConfidence(row, colGroupKey);
                  const isLowConfidence = groupConfidence === 'low';
                  // Colour bands:
                  // 80+: strong match (green)
                  // 60-79: Near-Match — partial fit, adjacent skills, close on thresholds (blue)
                  // 40-59: Unclear — mixed signals (amber)
                  // <40: Miss (red)
                  const getBadgeClass = (s: number) =>
                    s >= 80 ? 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200'
                    : s >= 60 ? 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200'
                    : s >= 40 ? 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200'
                    : 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200';
                  // Map score to verdict label for the modal header
                  const scoreToVerdict = (s: number): 'Match' | 'Near-Match' | 'Unclear' | 'Miss' =>
                    s >= 80 ? 'Match' : s >= 60 ? 'Near-Match' : s >= 40 ? 'Unclear' : 'Miss';
                  const entityName = activeDataType === 'companies' ? row.company_name : row.name;
                  return (
                    <td key={col.key} className="px-3 py-2 border-r border-gray-200" style={cellStyle}>
                      {score !== null ? (
                        <button
                          onClick={() => setVerdictModal({
                            entityName: entityName || 'Unknown',
                            columnLabel: colGroupLabel || col.label,
                            verdict: scoreToVerdict(score),
                            reasoning: reasoning || 'No reasoning available for this score.',
                            confidence: groupConfidence,
                            score: score
                          })}
                          className={`inline-flex items-center justify-center min-w-[40px] px-2 py-0.5 rounded-full text-xs font-bold border cursor-pointer transition-colors ${getBadgeClass(score)} ${isLowConfidence ? 'border-dashed opacity-80' : ''}`}
                          title={isLowConfidence
                            ? 'Low confidence — worth reviewing manually'
                            : (reasoning ? 'Click to see match reasoning' : 'Click to expand criteria')}
                        >
                          {score}
                          {isLowConfidence && <span className="ml-0.5 text-[10px] opacity-70">?</span>}
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  );
                }

                // Campaign Leads List: lemlist status badge.
                if (col.key === 'lemlist_status') {
                  const s = (row as any).lemlist_status as string | null | undefined;
                  const cls: Record<string, string> = {
                    interested: 'bg-emerald-100 text-emerald-700',
                    replied: 'bg-blue-100 text-blue-700',
                    clicked: 'bg-indigo-100 text-indigo-700',
                    opened: 'bg-sky-100 text-sky-700',
                    sent: 'bg-gray-100 text-gray-600',
                    bounced: 'bg-red-100 text-red-700',
                    unsubscribed: 'bg-amber-100 text-amber-700',
                  };
                  return (
                    <td key={col.key} className="px-3 py-2 border-r border-gray-200" style={cellStyle}>
                      {s
                        ? <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${cls[s] || 'bg-gray-100 text-gray-500'}`}>{s}</span>
                        : <span className="text-gray-300">—</span>}
                    </td>
                  );
                }

                // Handle custom columns from custom_data JSONB
                if ((col as any).isCustom) {
                  if (!row.custom_data) {
                    // No custom data at all - show spinner if Ada / AI column working, else dash.
                    // Still double-clickable so custom fields can be filled in on rows that
                    // were added after the custom columns existed (custom_data is null here).
                    const aiPending = aiColPending(col.key);
                    return (
                      <td
                        key={col.key}
                        className="px-3 py-2 border-r border-gray-200 text-gray-400 group relative"
                        style={cellStyle}
                        onDoubleClick={() => handleCellDoubleClick('', col.label, row.id, col.key)}
                      >
                        {renderPendingCell(!!adaWorking, aiPending)}
                        {!aiPending && renderCellRunBtn(col.key, row.id)}
                      </td>
                    );
                  }
                  const customValue = row.custom_data[col.key];
                  if (!customValue) {
                    // Show subtle spinner when Ada / an AI-column job is filling this cell
                    const aiPending = aiColPending(col.key);
                    return (
                      <td
                        key={col.key}
                        className="px-3 py-2 border-r border-gray-200 text-gray-400 group relative"
                        style={cellStyle}
                        onDoubleClick={() => handleCellDoubleClick('', col.label, row.id, col.key)}
                      >
                        {renderPendingCell(!!adaWorking, aiPending)}
                        {!aiPending && renderCellRunBtn(col.key, row.id)}
                      </td>
                    );
                  }

                  // Re-run in progress on this row: show the current value dimmed
                  // with a spinner so it reads as "being re-processed".
                  if (aiCellRefreshing(col.key, row.id)) {
                    return (
                      <td key={col.key} className="px-3 py-2 border-r border-gray-200" style={cellStyle}>
                        <span className="inline-flex items-center gap-1 text-violet-500 text-[11px]">
                          <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                          </svg>
                          <span className="text-gray-400 line-through">{String(customValue)}</span>
                        </span>
                      </td>
                    );
                  }

                  // Check if this is a criteria verdict (Match:/Miss:/Unclear:)
                  // Near-Match is split by a hyphen but stored as "Near-Match: reason"
                  // so we match it carefully and fall through to Match/Miss/Unclear.
                  const isCriteriaVerdict = typeof customValue === 'string' && /^(Near-Match|Match|Miss|Unclear):/.test(customValue);

                  if (isCriteriaVerdict) {
                    const colonIdx = customValue.indexOf(':');
                    const verdict = customValue.substring(0, colonIdx) as 'Match' | 'Near-Match' | 'Miss' | 'Unclear';
                    const reason = customValue.substring(colonIdx + 1).trim();
                    const badgeColors: Record<string, string> = {
                      'Match': 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
                      'Near-Match': 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
                      'Miss': 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200',
                      'Unclear': 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200'
                    };
                    const badgeIcons: Record<string, string> = {
                      'Match': '✓',
                      'Near-Match': '~',
                      'Miss': '✗',
                      'Unclear': '?'
                    };
                    const entityName = activeDataType === 'companies' ? row.company_name : row.name;

                    // Step 5: low-confidence indicator. Confidence lives in a
                    // parallel `<key>__confidence` field in custom_data.
                    // Missing/high/medium = no indicator (trusted), low =
                    // dashed border + subtle opacity to flag for manual review.
                    const confidenceRaw = row.custom_data?.[`${col.key}__confidence`];
                    const confidence = typeof confidenceRaw === 'string' ? confidenceRaw.toLowerCase() : null;
                    const isLowConfidence = confidence === 'low';

                    return (
                      <td
                        key={col.key}
                        className="px-3 py-2 border-r border-gray-200"
                        style={cellStyle}
                      >
                        <button
                          onClick={() => setVerdictModal({
                            entityName: entityName || 'Unknown',
                            columnLabel: col.label,
                            verdict: verdict as any,
                            reasoning: reason,
                            confidence: confidence as any
                          })}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border cursor-pointer transition-colors ${badgeColors[verdict] || badgeColors['Unclear']} ${isLowConfidence ? 'border-dashed opacity-80' : ''}`}
                          title={isLowConfidence ? 'Low confidence — worth reviewing manually' : undefined}
                        >
                          <span>{badgeIcons[verdict] || '?'}</span>
                          {verdict}
                          {isLowConfidence && <span className="ml-0.5 text-[10px] opacity-70">?</span>}
                        </button>
                      </td>
                    );
                  }

                  // Format based on type
                  let displayValue = customValue;
                  if (col.type === 'number') {
                    displayValue = Number(customValue).toLocaleString();
                  } else if (col.type === 'date') {
                    displayValue = new Date(customValue).toLocaleDateString();
                  }

                  // Check if this custom cell matches the filter
                  const customMatches = matchData.get(row.id);
                  const isCustomPrimaryMatch = customMatches?.columns.has(col.key);
                  const isCustomSecondaryMatch = customMatches?.secondaryColumns?.has(col.key);
                  const hasCustomMatch = isCustomPrimaryMatch || isCustomSecondaryMatch;

                  // Render http(s):// or www. values as clickable links so
                  // custom URL columns (LinkedIn, website, etc.) work without
                  // copy-paste. Highlight matches still render for non-URL
                  // values; for URLs we keep the link text plain so the
                  // <a> stays usable.
                  const customHref = typeof displayValue === 'string' ? detectUrlHref(displayValue) : null;

                  // AI-column cells carry a `<key>__reasoning` sibling (why the
                  // model gave this value). Already stored — surface it on click.
                  const aiReasoning = row.custom_data?.[`${col.key}__reasoning`];
                  const aiMeta = row.custom_data?.[`${col.key}__meta`] || null;
                  const aiEntityName = activeDataType === 'companies' ? row.company_name : row.name;

                  return (
                    <td
                      key={col.key}
                      className={`px-3 py-2 border-r border-gray-200 text-gray-600 max-w-xs truncate ${
                        isCustomPrimaryMatch ? 'bg-green-50' : isCustomSecondaryMatch ? 'bg-yellow-50' : ''
                      }`}
                      title={aiReasoning ? undefined : String(customValue)}
                      style={cellStyle}
                      onDoubleClick={() => handleCellDoubleClick(String(customValue), col.label, row.id, col.key)}
                    >
                      {customHref ? (
                        <a
                          href={customHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-600 hover:underline"
                        >
                          {displayValue}
                        </a>
                      ) : aiReasoning ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); setAiReasoningModal({
                            entityName: aiEntityName || 'Unknown',
                            columnLabel: col.label,
                            columnKey: col.key,
                            rowId: row.id,
                            value: String(displayValue),
                            reasoning: String(aiReasoning),
                            meta: aiMeta,
                          }); }}
                          className="text-left text-gray-700 hover:text-violet-700 border-b border-dotted border-gray-300 cursor-pointer"
                          title="Click to see why"
                        >
                          {hasCustomMatch && typeof displayValue === 'string' && displayValue
                            ? highlightMatchedText(displayValue, row.id)
                            : displayValue}
                        </button>
                      ) : (
                        hasCustomMatch && typeof displayValue === 'string' && displayValue
                          ? highlightMatchedText(displayValue, row.id)
                          : displayValue
                      )}
                    </td>
                  );
                }

                // Handle company_name with Find Competitors button
                if (col.key === 'company_name' && activeDataType === 'companies') {
                  const matches = matchData.get(row.id);
                  const isPrimaryMatch = matches?.columns.has(col.key);
                  const isSecondaryMatch = matches?.secondaryColumns?.has(col.key);
                  const hasAnyMatch = isPrimaryMatch || isSecondaryMatch;

                  return (
                    <td
                      key={col.key}
                      className={`px-3 py-2 border-r border-gray-200 ${
                        isPreviousEmployer ? 'bg-amber-50' :
                        isPrimaryMatch ? 'bg-green-50' : isSecondaryMatch ? 'bg-yellow-50' : ''
                      }`}
                      style={cellStyle}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="flex-1 truncate cursor-pointer hover:text-blue-600 hover:underline"
                          title={value}
                          onClick={() => setDetailPanel({ open: true, entityType: 'company', entityId: row.id })}
                          onDoubleClick={() => handleCellDoubleClick(value, col.label, row.id, col.key)}
                        >
                          {hasAnyMatch && typeof value === 'string' && value ? highlightMatchedText(value, row.id) : (value || '-')}
                        </span>
                        {isPreviousEmployer && (
                          <span
                            className="px-1.5 py-0.5 bg-amber-200 text-amber-800 text-xs rounded whitespace-nowrap"
                            title={`${selectedConnectionsFilter?.name} previously worked here`}
                          >
                            ⚠️ Prev
                          </span>
                        )}
                        {row.cached_from_project_id != null && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleCacheFilter(); }}
                            className={`px-1.5 py-0.5 text-[10px] font-semibold rounded whitespace-nowrap border transition-colors ${
                              cacheFilterActive
                                ? 'bg-sky-600 text-white border-sky-600'
                                : 'bg-sky-100 text-sky-700 border-sky-200 hover:bg-sky-200'
                            }`}
                            title={cacheFilterActive
                              ? 'Showing only cache-added companies — click to clear the filter'
                              : 'Added automatically from your company cache (previously enriched in another project). Click to see ONLY cache-added companies, for review or removal.'}
                          >
                            ⚡ Cache
                          </button>
                        )}
                        {row.added_via_monitoring === true && (
                          <span
                            className="px-1.5 py-0.5 bg-violet-100 text-violet-700 border border-violet-200 text-[10px] font-semibold rounded whitespace-nowrap"
                            title="Added automatically by monitoring: a monitored candidate moved to this company. Filter with the 'Added by Monitoring' column to review or delete these."
                          >
                            📡 Monitoring
                          </span>
                        )}
                        {(row as any).added_via_jobs === true && (
                          <span
                            className="px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-semibold rounded whitespace-nowrap"
                            title="Added through Jobs: a job watch found a vacancy here and added the company to find its hiring managers. Filter with the 'Added through Jobs' column to review or delete these."
                          >
                            💼 Jobs
                          </span>
                        )}
                        <button
                          onClick={async () => {
                            const searchQuery = `competitors of ${row.company_name}`;

                            setAiSearchLoading(true);
                            try {
                              const token = localStorage.getItem('token');
                              const response = await fetch('/api/ai-search/analyze', {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                  query: searchQuery,
                                  projectId: selectedProject?.id
                                })
                              });

                              if (response.ok) {
                                const data = await response.json();
                                const filterState: FilterState = {
                                  groups: data.filterGroups || [],
                                  employmentTypes: [],
                                };

                                // Set filters directly - replace any existing (no merge)
                                // Avoids race condition from clearing filters before API call
                                trackActivity();
                                setAiFilterState(filterState);
                                setAiExplanation(data.explanation);
                                setCanWidenSearch(data.canWiden || false);
                                setIsWidenedSearch(false);
                                setCurrentSearchQuery(searchQuery);
                                tourEvent('competitors-found');
                                // useEffect watching aiFilterState handles data fetching
                              }
                            } catch (err) {
                              console.error('Competitor search error:', err);
                            } finally {
                              setAiSearchLoading(false);
                            }
                          }}
                          className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded hover:bg-purple-200 transition-colors flex items-center gap-1 whitespace-nowrap"
                          title="Find similar companies"
                          data-tour="company-competitors"
                        >
                          <span>🔍</span>
                          <span className="hidden xl:inline">Competitors</span>
                        </button>
                      </div>
                    </td>
                  );
                }

                // Handle "Worked Together At" column (only appears when connections filter is active)
                if (col.key === 'worked_together_at' && selectedConnectionsFilter) {
                  const companyName = connectionCompanyMap[row.id] || '-';
                  const isHidden = hiddenConnectionCompanies.has(companyName.toLowerCase());
                  return (
                    <td key={col.key} className="px-3 py-2 border-r border-gray-200 bg-purple-50" style={cellStyle}>
                      <div className="flex items-center gap-2 overflow-hidden">
                        <svg className="w-4 h-4 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="text-purple-800 font-medium truncate min-w-0 flex-1" title={companyName}>
                          {companyName}
                        </span>
                        {companyName !== '-' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setHiddenConnectionCompanies(prev => {
                                const next = new Set(prev);
                                next.add(companyName.toLowerCase());
                                return next;
                              });
                            }}
                            className="flex-shrink-0 p-0.5 rounded hover:bg-purple-200 text-purple-400 hover:text-purple-700 transition-colors"
                            title={`Hide connections from ${companyName}`}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  );
                }

                // Handle "Position at Company" column (only appears when connections filter is active)
                if (col.key === 'position_at_company' && selectedConnectionsFilter) {
                  const positionTitle = connectionPositionMap[row.id] || '-';
                  return (
                    <td key={col.key} className="px-3 py-2 border-r border-gray-200 bg-purple-50" style={cellStyle}>
                      <span className="text-purple-800 truncate min-w-0 text-sm" title={positionTitle}>
                        {positionTitle}
                      </span>
                    </td>
                  );
                }

                // Handle star column for hiring managers
                if (col.key === 'is_starred' && activeDataType === 'managers') {
                  const isStarred = starredManagerIds.has(row.id);
                  return (
                    <td key={col.key} className="px-3 py-2 border-r border-gray-200" style={cellStyle}>
                      <button
                        onClick={() => handleToggleStar(row.id)}
                        className="text-2xl hover:scale-110 transition-transform cursor-pointer"
                        title={isStarred ? 'Remove star' : 'Star as decision maker'}
                      >
                        {isStarred ? '⭐' : '☆'}
                      </button>
                    </td>
                  );
                }

                // Handle tags column for all data types
                if (col.key === 'tags') {
                  const entityType = activeDataType === 'candidates' ? 'candidate' : activeDataType === 'managers' ? 'manager' : 'company';
                  return (
                    <td
                      key={col.key}
                      className="px-3 py-2 border-r border-gray-200"
                      style={cellStyle}
                      onDoubleClick={() => handleCellDoubleClick(Array.isArray(row.tags) ? row.tags.join(', ') : '', col.label, row.id, col.key)}
                    >
                      {Array.isArray(row.tags) && row.tags.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {row.tags.map((tag: string) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full group"
                            >
                              <button
                                data-tour="row-tag"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTagClick(tag, entityType);
                                }}
                                className="hover:underline cursor-pointer"
                                title={`Filter by tag: ${tag}`}
                              >
                                {tag}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveTag(row.id, tag, entityType);
                                }}
                                className="ml-0.5 text-blue-600 hover:text-red-600 hover:bg-red-100 rounded-full w-3.5 h-3.5 flex items-center justify-center transition-colors"
                                title={`Remove tag: ${tag}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                  );
                }

                // Handle pattern_tags column for companies - clickable to filter
                if (col.key === 'pattern_tags' && activeDataType === 'companies') {
                  const patternTags = row.pattern_tags;
                  const tagsArray = Array.isArray(patternTags)
                    ? patternTags.map((t: string) => t.trim()).filter((t: string) => t.length > 0)
                    : (typeof patternTags === 'string' && patternTags ? patternTags.split(',').map((t: string) => t.trim()).filter((t: string) => t.length > 0) : []);

                  return (
                    <td
                      key={col.key}
                      className="px-3 py-2 border-r border-gray-200"
                      style={cellStyle}
                      onDoubleClick={() => handleCellDoubleClick(tagsArray.join(', '), col.label, row.id, col.key)}
                    >
                      {tagsArray.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {tagsArray.map((tag: string) => (
                            <button
                              key={tag}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePatternTagClick(tag);
                              }}
                              className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full hover:bg-purple-200 transition-colors cursor-pointer"
                              title={`Filter by pattern: ${tag}`}
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                  );
                }

                // Handle location_country - clickable to filter by country
                if (col.key === 'location_country' && activeDataType === 'candidates') {
                  // Use location_country if available, otherwise extract last segment from location_full
                  let country = row.location_country;
                  if (!country && row.location_full) {
                    const parts = row.location_full.split(',').map((p: string) => p.trim());
                    country = parts[parts.length - 1] || '';
                  }

                  return (
                    <td key={col.key} className="px-3 py-2 border-r border-gray-200" style={cellStyle}>
                      {country ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCountryClick(country);
                          }}
                          className="text-xs text-gray-700 hover:text-blue-600 hover:underline cursor-pointer"
                          title={`Filter by country: ${country}`}
                        >
                          {country}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                  );
                }

                // Handle hm_country - extract country from hiring manager location
                if (col.key === 'hm_country' && activeDataType === 'managers') {
                  let country = '';
                  if (row.location) {
                    const parts = row.location.split(',').map((p: string) => p.trim());
                    country = parts[parts.length - 1] || '';
                  }

                  return (
                    <td key={col.key} className="px-3 py-2 border-r border-gray-200" style={cellStyle}>
                      {country ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCountryClick(country);
                          }}
                          className="text-xs text-gray-700 hover:text-blue-600 hover:underline cursor-pointer"
                          title={`Filter by country: ${country}`}
                        >
                          {country}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                  );
                }

                // Handle connection_count with click to open modal
                if (col.key === 'connection_count' && activeDataType === 'candidates') {
                  const count = row.connection_count || 0;
                  return (
                    <td key={col.key} className="px-3 py-2 border-r border-gray-200" style={cellStyle}>
                      {count > 0 ? (
                        <button
                          onClick={() => {
                            setSelectedCandidateForConnections({ id: row.id, name: row.name });
                            setShowConnectionsModal(true);
                          }}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded hover:bg-blue-200 transition-colors cursor-pointer"
                        >
                          🔗 {count}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                  );
                }

                if (col.key === 'candidate_count' && activeDataType === 'companies') {
                  const counts = companyCandidateCounts.get(row.id);
                  const fallbackCount = row.candidate_count || 0;
                  const displayTotal = counts ? counts.total : fallbackCount;

                  if (displayTotal > 0) {
                    // Treat radius filters as active too — the candidate-counts
                    // endpoint already applies them, so the UI must surface
                    // the filtered count, not just the unchanged total.
                    const hasRadiusFilter = !!(radiusCoords && radiusDistance > 0) ||
                      !!companyOfficeRadius.coords || !!candidateLocationRadius.coords;
                    const hasActiveFilters = aiFilterState.employmentTypes.length > 0
                      || aiFilterState.groups.length > 0
                      || hasRadiusFilter;
                    const isFiltered = counts && hasActiveFilters && counts.filtered < counts.total;
                    const displayCount = counts && hasActiveFilters ? `${counts.filtered}/${counts.total}` : displayTotal.toString();

                    return (
                      <td key={col.key} className="px-3 py-2 border-r border-gray-200" style={cellStyle}>
                        <button
                          data-tour="company-candidate-count"
                          onClick={() => handleCompanyCandidatesClick(row.company_name, hasActiveFilters)}
                          className={`px-2 py-1 text-xs rounded hover:shadow-sm transition-all cursor-pointer ${
                            isFiltered
                              ? 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                              : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                          }`}
                          title={counts && hasActiveFilters
                            ? `${counts.filtered} of ${counts.total} candidates match filters at ${row.company_name}`
                            : `View ${displayTotal} candidate${displayTotal > 1 ? 's' : ''} at ${row.company_name}`
                          }
                        >
                          {displayCount}
                        </button>
                      </td>
                    );
                  }
                }

                if (col.key === 'hiring_manager_count' && activeDataType === 'companies') {
                  // Use the count from backend (calculated correctly across all offices)
                  const hmCount = row.hiring_manager_count || 0;
                  if (hmCount > 0) {
                    return (
                      <td key={col.key} className="px-3 py-2 border-r border-gray-200" style={cellStyle}>
                        <button
                          data-tour="company-hm-count"
                          onClick={() => handleCompanyManagersClick(row.company_name)}
                          className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded hover:bg-purple-200 hover:shadow-sm transition-all cursor-pointer"
                          title={`View ${hmCount} hiring manager${hmCount > 1 ? 's' : ''} at ${row.company_name}`}
                        >
                          {hmCount}
                        </button>
                      </td>
                    );
                  }
                  // 0 associations → review this one company's employees in
                  // Contact Search and hand-pick hiring managers. Intent is
                  // inferred from the route, so no modal.
                  if (userId === 1 || (user as any)?.universeAccess) {
                    const entry = (row as any).linkedin_numeric_url || row.url || (row as any).domain || row.company_domain || row.company_name;
                    return (
                      <td key={col.key} className="px-3 py-2 border-r border-gray-200" style={cellStyle}>
                        <button
                          onClick={() => {
                            if (!entry) { alert('This company has no LinkedIn URL, domain or name to search with.'); return; }
                            try {
                              sessionStorage.setItem('universe_search_seed', JSON.stringify({
                                entries: [entry],
                                intent: 'hiring-managers',
                                label: row.company_name || String(entry),
                                review: { projectId: selectedProject?.id, companyId: row.id, companyName: row.company_name },
                              }));
                            } catch { /* storage unavailable — the page just opens blank */ }
                            window.location.assign('/contact-search');
                          }}
                          data-tour="company-zero-hm"
                          className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded hover:bg-violet-100 hover:text-violet-700 transition-all cursor-pointer"
                          title={`No hiring managers yet — review ${row.company_name}'s employees in the universe and pick who to add`}
                        >
                          0
                        </button>
                      </td>
                    );
                  }
                }


                if ((col.key === 'website' || col.key === 'company_website' || col.key === 'url' || col.key === 'li_profile_url' || col.key === 'li_company_url') && value) {
                  const isLinkedIn = col.key === 'li_profile_url' || col.key === 'li_company_url';
                  // Websites often lack a protocol (e.g. "acme.com"); normalise so
                  // the link is absolute and actually clickable.
                  const href = isLinkedIn ? value : (detectUrlHref(String(value)) || value);
                  const showUrlText = col.key === 'website' || col.key === 'company_website';
                  return (
                    <td key={col.key} className="px-3 py-2 border-r border-gray-200 max-w-xs truncate" style={cellStyle}>
                      <a href={href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1">
                        {isLinkedIn ? (
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#0A66C2">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                        ) : (
                          <span className="truncate">{showUrlText ? String(value).replace(/^https?:\/\//, '').replace(/\/$/, '') : 'Link'}</span>
                        )}
                      </a>
                    </td>
                  );
                }

                // Handle enrichable columns with enrichment status (candidates and managers only)
                if ((col.key === 'email' || col.key === 'personal_email' || col.key === 'phone') && (activeDataType === 'candidates' || activeDataType === 'managers')) {
                  const enrichmentStatus = row.enrichment_status;

                  // Show spinner if enrichment is in progress
                  if (enrichmentStatus === 'in_progress') {
                    return (
                      <td key={col.key} className="px-3 py-2 border-r border-gray-200" style={cellStyle}>
                        <div className="flex items-center gap-2 text-blue-600">
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-xs">Searching...</span>
                        </div>
                      </td>
                    );
                  }

                  // Show "Not found" only if this specific field was searched and came back empty
                  const fieldSearched = col.key === 'email' ? row.email_searched
                    : col.key === 'personal_email' ? row.personal_email_searched
                    : col.key === 'phone' ? row.phone_searched
                    : false;

                  if (fieldSearched && !value) {
                    return (
                      <td
                        key={col.key}
                        className="px-3 py-2 border-r border-gray-200 text-gray-400 italic text-xs"
                        style={cellStyle}
                        title="Enrichment attempted - no data found"
                      >
                        Not found
                      </td>
                    );
                  }
                }

                // Check if this cell matches the filter
                const matches = matchData.get(row.id);
                const isPrimaryMatch = matches?.columns.has(col.key);  // Green - in filtered column
                const isSecondaryMatch = matches?.secondaryColumns?.has(col.key);  // Yellow - in other column
                const hasAnyMatch = isPrimaryMatch || isSecondaryMatch;

                // Name column: add single-click to open detail panel
                if (col.key === 'name' && (activeDataType === 'candidates' || activeDataType === 'managers')) {
                  const entityType = activeDataType === 'candidates' ? 'candidate' as const : 'contact' as const;
                  // When filtering by associated company, candidates who are here
                  // via a position_history match (not their primary current_company)
                  // get an `association_role` field showing the role at THIS
                  // company. Show a subtle hint so users understand the link.
                  const assocRole = (row as any).association_role;
                  return (
                    <td
                      key={col.key}
                      className={`px-3 py-2 text-gray-600 border-r border-gray-200 max-w-xs ${
                        isPrimaryMatch ? 'bg-green-50' : isSecondaryMatch ? 'bg-yellow-50' : ''
                      }`}
                      style={cellStyle}
                    >
                      <span
                        data-tour="record-name"
                        className="cursor-pointer hover:text-blue-600 hover:underline truncate block"
                        title={value}
                        onClick={() => setDetailPanel({ open: true, entityType, entityId: row.id })}
                        onMouseEnter={() => prefetchEntity(entityType, row.id, localStorage.getItem('token') || '')}
                        onDoubleClick={() => handleCellDoubleClick(value, col.label, row.id, col.key)}
                      >
                        {hasAnyMatch && typeof value === 'string' && value ? highlightMatchedText(value, row.id) : (value || '-')}
                      </span>
                      {activeDataType === 'candidates' && isProspectAdmin && prospectLinks[row.id] && (
                        <a
                          href={prospectLinks[row.id]}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="ml-1.5 align-middle text-sm hover:opacity-70"
                          title="This person has a prospect page — click to open it"
                        >
                          👁️
                        </a>
                      )}
                      {activeDataType === 'candidates' && <NewFromRefreshBadge row={row} />}
                      {/* Auto-discovered: the kind as a coloured chip, with
                          the reasons on hover — in place of the Enriched chip
                          (a discovered person always has the full profile). */}
                      {activeDataType === 'managers' && (row as any).discovery_kind && (() => {
                        const kind = (row as any).discovery_kind as string;
                        const level = (row as any).discovery_level as number | null;
                        const conf = (row as any).discovery_confidence as number | null;
                        const label = kind === 'hiring_manager' ? `Hiring manager${level ? ` L${level}` : ''}` : kind === 'talent_acquisition' ? 'Talent acquisition' : 'Owner / founder';
                        return (
                          <span
                            className={`inline-flex items-center ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded border align-middle whitespace-nowrap ${DISCOVERY_CHIP_CLASS[kind] || ''}`}
                            title={`${label}${conf != null ? ` · ${conf}% confidence` : ''}${(row as any).discovery_why ? `\n${(row as any).discovery_why}` : ''}`}
                          >
                            {label}{conf != null ? <span className="ml-1 font-normal opacity-70">{conf}%</span> : null}
                          </span>
                        );
                      })()}
                      {activeDataType === 'managers' && row.enriched && !(row as any).discovery_kind && (
                        <span
                          className="inline-flex items-center gap-0.5 ml-1.5 px-1.5 py-0.5 text-[10px] font-medium bg-violet-50 text-violet-700 rounded border border-violet-200 align-middle"
                          title="Full LinkedIn profile available — click the name to view the experience timeline, education and more"
                        >
                          <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path d="M11.3 1.046a1 1 0 00-1.6 0L8.2 3.1a1 1 0 01-.5.36l-2.4.7a1 1 0 00-.5 1.6l1.5 1.95a1 1 0 01.2.6l-.1 2.5a1 1 0 001.36 1l2.34-.9a1 1 0 01.62 0l2.34.9a1 1 0 001.36-1l-.1-2.5a1 1 0 01.2-.6l1.5-1.95a1 1 0 00-.5-1.6l-2.4-.7a1 1 0 01-.5-.36l-1.5-2.05z"/></svg>
                          Click to view
                        </span>
                      )}
                      {assocRole && assocRole.title && assocRole.companyName && (
                        <span
                          className="inline-block mt-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded border border-blue-200 truncate max-w-full"
                          title={`This candidate is also "${assocRole.title} at ${assocRole.companyName}" (via position history)`}
                        >
                          also: {assocRole.title} @ {assocRole.companyName}
                        </span>
                      )}
                      {/* Their other current roles, at companies other than the
                          main one shown in the table — so a candidate linked to
                          a company through a second job reads correctly. */}
                      {!assocRole && activeDataType === 'candidates' && Array.isArray((row as any).other_current_roles) && (row as any).other_current_roles.length > 0 && (() => {
                        const roles = (row as any).other_current_roles as { title?: string; companyName?: string }[];
                        const first = roles[0];
                        return (
                          <span
                            className="inline-block mt-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-slate-50 text-slate-600 rounded border border-slate-200 truncate max-w-full"
                            title={`Also currently:\n${roles.map((r) => `${r.title || 'Role'} at ${r.companyName}`).join('\n')}`}
                          >
                            also at {first.companyName}{first.title ? ` — ${first.title}` : ''}{roles.length > 1 ? ` +${roles.length - 1}` : ''}
                          </span>
                        );
                      })()}
                    </td>
                  );
                }

                return (
                  <td
                    key={col.key}
                    className={`px-3 py-2 text-gray-600 border-r border-gray-200 max-w-xs truncate ${
                      isPrimaryMatch ? 'bg-green-50' : isSecondaryMatch ? 'bg-yellow-50' : ''
                    }`}
                    title={value}
                    style={cellStyle}
                    onDoubleClick={() => handleCellDoubleClick(value, col.label, row.id, col.key)}
                  >
                    {hasAnyMatch && typeof value === 'string' && value ? highlightMatchedText(value, row.id) : (value || '-')}
                  </td>
                );
              })}
          </tr>
          );
        })}
      </tbody>
    </table>
   )} 
  </div>

      {/* Pagination Bottom */}
      {filteredTotal > rowLimit && (
        <div className="px-4 py-2 border-t flex items-center justify-between">
          <div className="text-sm text-gray-600 flex items-center gap-2">
            Page {currentPage} of {Math.max(1, Math.ceil(Math.max(0, filteredTotal - startRow) / rowLimit))}
            {pageLoading && <span className="text-blue-500">(Loading...)</span>}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1 || pageLoading}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(p => {
                const maxPage = Math.max(1, Math.ceil(Math.max(0, filteredTotal - startRow) / rowLimit));
                return Math.min(maxPage, p + 1);
              })}
              disabled={currentPage >= Math.max(1, Math.ceil(Math.max(0, filteredTotal - startRow) / rowLimit)) || pageLoading}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

</>)}

{/* Selection/action modals — mounted OUTSIDE the table/card view branch so they open in BOTH views (they were table-only, which silently broke Add Company, Find Contacts, Delete etc. in grid view). */}
{/* Cell Content Modal */}
{cellModal.show && (
  <div
    className="modal-backdrop flex items-center justify-center"
    onClick={() => setCellModal({ show: false, content: '', title: '' })}
  >
    <div
      className="modal-dialog p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          {cellModal.title}
          {cellModal.isEditing && <span className="ml-2 text-sm font-normal text-blue-600">(Editing)</span>}
        </h3>
        <button
          onClick={() => setCellModal({ show: false, content: '', title: '' })}
          className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
        >
          ×
        </button>
      </div>
      <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
        {cellModal.isEditing ? (
          <textarea
            value={cellModal.editValue || ''}
            onChange={(e) => setCellModal(prev => ({ ...prev, editValue: e.target.value }))}
            className="input min-h-[150px]"
            placeholder="Enter value..."
            autoFocus
          />
        ) : (
          <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">
            {highlightTermsInText(cellModal.content, getActiveHighlightTerms())}
          </div>
        )}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        {isFieldEditable(cellModal.columnKey) && !cellModal.isEditing && (
          <button
            onClick={() => setCellModal(prev => ({ ...prev, isEditing: true, editValue: prev.content === '-' ? '' : prev.content }))}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm border border-gray-300"
          >
            Edit
          </button>
        )}
        {cellModal.isEditing ? (
          <>
            <button
              onClick={() => setCellModal(prev => ({ ...prev, isEditing: false, editValue: prev.content }))}
              disabled={cellModal.isSaving}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm border border-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveCellEdit}
              disabled={cellModal.isSaving}
              className="btn-primary flex items-center gap-2"
            >
              {cellModal.isSaving ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </>
        ) : (
          <button
            onClick={() => setCellModal({ show: false, content: '', title: '' })}
            className="btn-primary"
          >
            Close
          </button>
        )}
      </div>
    </div>
  </div>
)}

{/* Sales Navigator Modal */}
{showSalesNavModal && (
  <div className="modal-backdrop flex items-center justify-center">
    <div className="modal-dialog p-6 max-w-lg w-full mx-4">
      <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
        <svg className="w-5 h-5 text-[#0077B5]" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
        Sales Navigator Search
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        {companies.filter(c => c.org_id).length} companies will be added as current company filters.
      </p>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Job Titles <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={salesNavTitles}
          onChange={(e) => setSalesNavTitles(e.target.value)}
          placeholder="e.g. CTO, Chief Technology Officer, VP Engineering"
          className="input resize-none"
          rows={3}
        />
        <p className="text-xs text-gray-400 mt-1">
          Separate titles with commas. These will be added as current job title filters.
        </p>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => {
            const companiesWithOrgId = companies.filter(c => c.org_id);

            // Build company values
            const companyValues = companiesWithOrgId.map(c => {
              const encodedUrn = `urn%253Ali%253Aorganization%253A${c.org_id}`;
              const companyName = encodeURIComponent(c.company_name || 'Unknown')
                .replace(/\(/g, '%28')
                .replace(/\)/g, '%29')
                .replace(/%/g, '%25');
              return `(id%3A${encodedUrn}%2Ctext%3A${companyName}%2CselectionType%3AINCLUDED)`;
            }).join('%2C');

            const companyFilter = `(type%3ACURRENT_COMPANY%2Cvalues%3AList(${companyValues}))`;

            // Build title filter if titles provided
            const titles = salesNavTitles
              .split(',')
              .map(t => t.trim())
              .filter(t => t.length > 0);

            let filtersList = companyFilter;

            if (titles.length > 0) {
              const titleValues = titles.map(title => {
                const encodedTitle = encodeURIComponent(title)
                  .replace(/\(/g, '%28')
                  .replace(/\)/g, '%29')
                  .replace(/%/g, '%25');
                return `(text%3A${encodedTitle}%2CselectionType%3AINCLUDED)`;
              }).join('%2C');

              const titleFilter = `(type%3ACURRENT_TITLE%2Cvalues%3AList(${titleValues}))`;
              filtersList = `${companyFilter}%2C${titleFilter}`;
            }

            const url = `https://www.linkedin.com/sales/search/people#query=(filters%3AList(${filtersList}))&viewAllFilters=true`;
            window.open(url, '_blank');
            setShowSalesNavModal(false);
          }}
          className="flex-1 px-4 py-2 bg-[#0077B5] text-white rounded-md hover:bg-[#006097] text-sm font-medium"
        >
          Open in Sales Navigator
        </button>
        <button
          onClick={() => setShowSalesNavModal(false)}
          className="btn-secondary"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

{/* Find Contacts Modal */}
{showFindContactsModal && (
  <div className="modal-backdrop flex items-center justify-center">
    <div className="modal-dialog p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Find Contacts</h3>
        <button onClick={() => setShowFindContactsModal(false)} className="text-gray-400 hover:text-gray-600">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      {/* Option A — Auto-find via Clay (uses credits) */}
      <div className="mb-5 p-4 rounded-lg border border-violet-200 bg-violet-50/50">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-semibold text-gray-900">✨ Auto-find contacts</h4>
          <span className="text-[11px] text-gray-500">1 credit / company (only if contacts found)</span>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          {Array.from(selectedRows).length} selected — finds up to 10 hiring managers per company and adds them automatically.
        </p>

        {/* Templates */}
        {contactTemplates.length > 0 && (
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Load a saved template</label>
            <select
              onChange={(e) => {
                const t = contactTemplates.find((x: any) => String(x.id) === e.target.value);
                if (t) setContactCriteria({
                  jobTitleKeywords: t.criteria?.jobTitleKeywords || '',
                  excludeKeywords: t.criteria?.excludeKeywords || '',
                  locations: t.criteria?.locations || '',
                  excludeLocations: t.criteria?.excludeLocations || '',
                  headlineKeywords: t.criteria?.headlineKeywords || '',
                  summaryKeywords: t.criteria?.summaryKeywords || '',
                  roleDescriptionKeywords: t.criteria?.roleDescriptionKeywords || '',
                  minMonths: t.criteria?.minMonths ?? 0,
                  limit: t.criteria?.limit ?? 10,
                });
              }}
              className="input text-sm"
              defaultValue=""
            >
              <option value="" disabled>Choose a template…</option>
              {contactTemplates.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}

        <div className="space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Job title keywords</label>
            <input value={contactCriteria.jobTitleKeywords} onChange={e => setContactCriteria({ ...contactCriteria, jobTitleKeywords: e.target.value })} placeholder="e.g. Sales Director, CTO, Commercial Director" className="input text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Exclude title keywords</label>
            <input value={contactCriteria.excludeKeywords} onChange={e => setContactCriteria({ ...contactCriteria, excludeKeywords: e.target.value })} placeholder="e.g. Assistant, Intern" className="input text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Location(s)</label>
            <input value={contactCriteria.locations} onChange={e => setContactCriteria({ ...contactCriteria, locations: e.target.value })} placeholder="e.g. United Kingdom, London" className="input text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Exclude location(s)</label>
            <input value={contactCriteria.excludeLocations} onChange={e => setContactCriteria({ ...contactCriteria, excludeLocations: e.target.value })} placeholder="e.g. India, Philippines" className="input text-sm" />
          </div>

          {/* Content qualifiers — refine a generic title by what the profile says. */}
          <div className="pt-1 mt-1 border-t border-gray-100">
            <p className="text-[11px] text-gray-500 mb-2">
              Refine a broad title by profile content (optional). These stack as <span className="font-medium">AND</span> filters — a person must match every field you fill in. To qualify a sector, put the keyword in just <span className="font-medium">one</span> field (usually Summary) so you don't miss people who only mention it there.
            </p>
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Headline keywords</label>
                <input value={contactCriteria.headlineKeywords} onChange={e => setContactCriteria({ ...contactCriteria, headlineKeywords: e.target.value })} placeholder="e.g. Metallurgy, Aerospace" className="input text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Summary keywords</label>
                <input value={contactCriteria.summaryKeywords} onChange={e => setContactCriteria({ ...contactCriteria, summaryKeywords: e.target.value })} placeholder="e.g. Metallurgy, Aerospace" className="input text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Role description keywords</label>
                <input value={contactCriteria.roleDescriptionKeywords} onChange={e => setContactCriteria({ ...contactCriteria, roleDescriptionKeywords: e.target.value })} placeholder="e.g. Metallurgy, Aerospace" className="input text-sm" />
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Min months in role</label>
              <input type="number" min={0} value={contactCriteria.minMonths} onChange={e => setContactCriteria({ ...contactCriteria, minMonths: Math.max(0, parseInt(e.target.value) || 0) })} className="input text-sm w-28" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Max contacts</label>
              <input type="number" min={1} max={10} value={contactCriteria.limit} onChange={e => setContactCriteria({ ...contactCriteria, limit: Math.min(10, Math.max(1, parseInt(e.target.value) || 10)) })} className="input text-sm w-28" />
            </div>
          </div>
        </div>

        {findContactsMsg && <p className="text-xs text-violet-700 mt-2">{findContactsMsg}</p>}

        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={handleFindViaClay}
            disabled={findingContacts || Array.from(selectedRows).length === 0}
            className="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {findingContacts ? 'Starting…' : `Find contacts (${Array.from(selectedRows).length})`}
          </button>
          <button onClick={saveContactTemplate} className="text-xs text-violet-600 hover:text-violet-800">Save as template</button>
        </div>
      </div>

      {/* Option B — LinkedIn deep-link (free, manual) */}
      <div className="p-4 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 mb-1">
          <svg className="w-4 h-4 text-[#0077B5]" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          <h4 className="text-sm font-semibold text-gray-900">Search on LinkedIn</h4>
          <span className="text-[11px] text-gray-500 ml-auto">free · manual</span>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          {(() => {
            const selected = Array.from(selectedRows);
            const count = companies.filter(c => selected.includes(c.id) && c.org_id).length;
            if (count > 99) return `${count} companies — LinkedIn Classic supports max 99; only the first 99 are searched.`;
            return `Opens a LinkedIn people search across the ${count} selected ${count === 1 ? 'company' : 'companies'}.`;
          })()}
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={findContactsTitles}
            onChange={(e) => setFindContactsTitles(e.target.value)}
            placeholder="Job title (optional), e.g. CTO"
            className="input text-sm flex-1"
          />
          <button
            onClick={() => {
              const selected = Array.from(selectedRows);
              const companiesWithOrgId = companies.filter(c => selected.includes(c.id) && c.org_id).slice(0, 99);
              const companyArray = companiesWithOrgId.map(c => `%22${c.org_id}%22`).join('%2C');
              const currentCompanyParam = `%5B${companyArray}%5D`;
              const title = findContactsTitles.trim();
              const url = title.length === 0
                ? `https://www.linkedin.com/search/results/people/?origin=FACETED_SEARCH&currentCompany=${currentCompanyParam}`
                : `https://www.linkedin.com/search/results/people/?origin=FACETED_SEARCH&title=%22${encodeURIComponent(title)}%22&currentCompany=${currentCompanyParam}`;
              window.open(url, '_blank');
            }}
            className="px-4 py-2 bg-[#0077B5] text-white rounded-md hover:bg-[#006097] text-sm font-medium whitespace-nowrap"
          >
            Search LinkedIn
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Export Options Modal — companies tab. Two choices: keep the
    export simple (one row per company, HQ address shown) or fan
    each company out into one row per office, with Office Address /
    City / Region / Country / Primary Office columns appended. */}
{showExportOptionsModal && (
  <div className="modal-backdrop flex items-center justify-center">
    <div className="modal-dialog p-6 max-w-md w-full mx-4">
      <h3 className="text-lg font-semibold mb-2">Export Companies</h3>
      <p className="text-sm text-gray-600 mb-5">
        Include office locations in the export?
      </p>
      <div className="flex justify-between gap-2">
        <button
          onClick={() => setShowExportOptionsModal(false)}
          className="btn-secondary text-sm px-4 py-2"
        >
          Cancel
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setShowExportOptionsModal(false);
              exportToCSV({ includeOffices: false });
            }}
            className="btn-secondary text-sm px-4 py-2"
          >
            No office locations
          </button>
          <button
            onClick={() => {
              setShowExportOptionsModal(false);
              exportToCSV({ includeOffices: true });
            }}
            className="btn-primary text-sm px-4 py-2"
          >
            With office locations
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Delete Confirmation Modal */}
{showDeleteConfirm && (
  <div className="modal-backdrop flex items-center justify-center">
    <div className="modal-dialog p-6 max-w-md w-full mx-4">
      <h3 className="text-lg font-semibold mb-4">
        {campaignScopeId ? 'Remove from campaign' : 'Confirm Deletion'}
      </h3>
      {campaignScopeId ? (
        <>
          <p className="text-sm text-gray-700 mb-6">
            Remove <strong>{selectedRows.size} {activeDataType === 'companies' ? 'compan' + (selectedRows.size > 1 ? 'ies' : 'y') : (activeDataType === 'managers' ? 'contact' : 'candidate') + (selectedRows.size > 1 ? 's' : '')}</strong> from this campaign?
            <br />
            <span className="text-gray-500">They stay in the project — this only removes them from the campaign.</span>
          </p>
          <div className="flex gap-3">
            <button onClick={() => handleDelete(false)} disabled={deleting} className="btn-primary flex-1">
              {deleting ? 'Removing…' : 'Remove from campaign'}
            </button>
            <button onClick={() => setShowDeleteConfirm(false)} disabled={deleting} className="btn-secondary flex-1">
              Cancel
            </button>
          </div>
        </>
      ) : activeDataType === 'companies' ? (
        <>
          <p className="text-sm text-gray-700 mb-4">
            You are about to delete <strong>{selectedRows.size} company{selectedRows.size > 1 ? 'ies' : ''}</strong> (all offices).
          </p>
          <p className="text-sm text-gray-700 mb-4">
            Would you like to also delete all associated candidates and hiring managers?
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => handleDelete(true)}
              disabled={deleting}
              className="btn-danger w-full py-3 font-medium"
            >
              {deleting ? 'Deleting...' : '❌ Delete All Offices + People'}
            </button>
            <button
              onClick={() => handleDelete(false)}
              disabled={deleting}
              className="w-full px-4 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {deleting ? 'Deleting...' : '🏢 Delete All Offices Only'}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleting}
              className="btn-secondary w-full"
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-700 mb-6">
            Are you sure you want to delete <strong>{selectedRows.size} {activeDataType === 'candidates' ? 'candidate' : 'hiring manager'}{selectedRows.size > 1 ? 's' : ''}</strong>?
            <br />
            <span className="text-red-600 font-medium">This action cannot be undone.</span>
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => handleDelete(false)}
              disabled={deleting}
              className="btn-danger flex-1"
            >
              Yes, Delete
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleting}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)}
{/* Add Tag Modal */}
{showTagModal && (
  <div className="modal-backdrop flex items-center justify-center">
    <div className="modal-dialog p-6 max-w-md w-full mx-4">
      <h3 className="text-lg font-semibold mb-4">
        Add Tag to {selectedRows.size} {activeDataType === 'companies' ? 'Companies' : activeDataType === 'managers' ? 'Hiring Managers' : 'Candidates'}
      </h3>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tag Name
        </label>
        <input
          type="text"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
          placeholder="e.g., High Priority, Q4 Target"
          data-tour="tag-name"
          className="input"
          autoFocus
          list="tag-suggestions"
        />
        <datalist id="tag-suggestions">
          {allTags.map(tag => (
            <option key={tag} value={tag} />
          ))}
        </datalist>
      </div>
      
      {allTags.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-500 mb-2">Existing tags (click to use):</p>
          <div className="flex flex-wrap gap-2">
            {allTags.slice(0, 10).map(tag => (
              <button
                key={tag}
                onClick={() => setNewTag(tag)}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}
      
      <div className="flex gap-3">
        <button
          onClick={handleAddTag}
          data-tour="tag-add"
          disabled={!newTag.trim() || tagging}
          className="btn-primary flex-1"
        >
          {tagging ? 'Adding...' : 'Add Tag'}
        </button>
        <button
          onClick={() => {
            setShowTagModal(false);
            setNewTag('');
          }}
          disabled={tagging}
          className="btn-secondary flex-1"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

{/* Remove Tag Modal */}
{showRemoveTagModal && (
  <div className="modal-backdrop flex items-center justify-center">
    <div className="modal-dialog p-6 max-w-md w-full mx-4">
      <h3 className="text-lg font-semibold mb-4">
        Remove Tag from {selectedRows.size} {activeDataType === 'companies' ? 'Companies' : activeDataType === 'candidates' ? 'Candidates' : 'Hiring Managers'}
      </h3>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Tag to Remove
        </label>
        <select
          value={tagToRemove}
          onChange={(e) => setTagToRemove(e.target.value)}
          className="input"
          autoFocus
        >
          <option value="">-- Select a tag --</option>
          {allTags.map(tag => (
            <option key={tag} value={tag}>{tag}</option>
          ))}
        </select>
      </div>

      {allTags.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-500 mb-2">Or click to select:</p>
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setTagToRemove(tag)}
                className={`px-2 py-1 text-xs rounded-md transition-colors ${
                  tagToRemove === tag
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={handleBulkRemoveTag}
          disabled={!tagToRemove.trim() || removingTag}
          className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {removingTag ? 'Removing...' : 'Remove Tag'}
        </button>
        <button
          onClick={() => {
            setShowRemoveTagModal(false);
            setTagToRemove('');
          }}
          disabled={removingTag}
          className="btn-secondary flex-1"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

{/* Change Color Modal */}
{showColorModal && (
  <div className="modal-backdrop flex items-center justify-center">
    <div className="modal-dialog p-6 max-w-lg w-full mx-4">
      <h3 className="text-lg font-semibold mb-4">
        Change Pin Color for {selectedRows.size} {selectedRows.size === 1 ? 'Company' : 'Companies'}
      </h3>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Select Color
        </label>
        <div className="grid grid-cols-6 gap-2">
          {colorPalette.map(color => (
            <button
              key={color.hex}
              onClick={() => setSelectedColor(color.hex)}
              className={`w-full aspect-square rounded-md transition-all ${
                selectedColor === color.hex
                  ? 'ring-4 ring-blue-500 ring-offset-2'
                  : 'hover:scale-110'
              }`}
              style={{ backgroundColor: color.hex }}
              title={color.name}
            />
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-sm text-gray-600">Selected:</span>
          <div
            className="w-8 h-8 rounded border border-gray-300"
            style={{ backgroundColor: selectedColor }}
          />
          <span className="text-sm font-mono text-gray-700">{selectedColor}</span>
        </div>
      </div>
      
      <div className="flex flex-col gap-2">
        <button
          onClick={() => handleChangeColor()}
          disabled={changingColor}
          className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400"
        >
          {changingColor ? 'Changing...' : `🎨 Change Color for ${selectedRows.size} ${selectedRows.size === 1 ? 'Company (All Offices)' : 'Companies'}`}
        </button>
        <button
          onClick={() => setShowColorModal(false)}
          disabled={changingColor}
          className="btn-secondary w-full"
        >
Cancel
        </button>
      </div>
    </div>
  </div>
)}

{/* Add Single Modal */}
{showAddSingleModal && (
  <div
    className="modal-backdrop flex items-center justify-center"
    onClick={() => { setShowAddSingleModal(false); setShowCompanyDropdown(false); }}
  >
    <div
      className="modal-dialog p-6 max-w-md w-full mx-4"
      onClick={(e) => { e.stopPropagation(); setShowCompanyDropdown(false); }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Add {activeDataType === 'companies' ? 'Company' : activeDataType === 'managers' ? 'Hiring Manager' : 'Candidate'}
        </h3>
        <button
          onClick={() => setShowAddSingleModal(false)}
          className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
        >
          ×
        </button>
      </div>

      {/* Mode toggle for hiring managers */}
      {activeDataType === 'managers' && (
        <div className="flex mb-4 border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setAddHmMode('manual')}
            className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${addHmMode === 'manual' ? 'bg-green-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
          >
            Manual Entry (Free)
          </button>
          <button
            onClick={() => setAddHmMode('linkedin')}
            className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${addHmMode === 'linkedin' ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
          >
            LinkedIn Enrich
          </button>
        </div>
      )}

      {/* Manual entry form (hiring managers only) */}
      {activeDataType === 'managers' && addHmMode === 'manual' ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">First Name *</label>
              <input
                type="text"
                value={manualHm.firstName}
                onChange={(e) => setManualHm(prev => ({ ...prev, firstName: e.target.value }))}
                placeholder="John"
                className="input"
                disabled={addingSingle}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Surname *</label>
              <input
                type="text"
                value={manualHm.lastName}
                onChange={(e) => setManualHm(prev => ({ ...prev, lastName: e.target.value }))}
                placeholder="Smith"
                className="input"
                disabled={addingSingle}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Job Title *</label>
            <input
              type="text"
              value={manualHm.jobTitle}
              onChange={(e) => setManualHm(prev => ({ ...prev, jobTitle: e.target.value }))}
              placeholder="Head of Talent Acquisition"
              className="input"
              disabled={addingSingle}
            />
          </div>

          <div className="relative">
            <label className="block text-xs font-medium text-gray-700 mb-1">Company</label>
            <input
              type="text"
              value={companySearch}
              onChange={(e) => {
                setCompanySearch(e.target.value);
                setShowCompanyDropdown(true);
                if (!e.target.value.trim()) {
                  setManualHm(prev => ({ ...prev, companyId: 0 }));
                }
              }}
              onFocus={() => setShowCompanyDropdown(true)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Search companies..."
              className="input"
              disabled={addingSingle}
            />
            {showCompanyDropdown && companySearch.trim() && (
              <div
                className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {fullCompanies
                  .filter(c => c.company_name?.toLowerCase().includes(companySearch.toLowerCase()))
                  .slice(0, 20)
                  .map(c => (
                    <button
                      key={c.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setManualHm(prev => ({ ...prev, companyId: c.id }));
                        setCompanySearch(c.company_name);
                        setShowCompanyDropdown(false);
                      }}
                      className={`block w-full text-left px-3 py-2 text-sm hover:bg-green-50 border-b border-gray-50 last:border-b-0 ${manualHm.companyId === c.id ? 'bg-green-50 font-medium' : ''}`}
                    >
                      <span className="text-gray-900">{c.company_name}</span>
                      {c.industry && <span className="text-gray-400 ml-1 text-xs">— {c.industry}</span>}
                    </button>
                  ))}
                {fullCompanies.filter(c => c.company_name?.toLowerCase().includes(companySearch.toLowerCase())).length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-400 text-center">No matching companies</div>
                )}
              </div>
            )}
            {manualHm.companyId > 0 && (
              <p className="mt-1 text-xs text-green-600">Selected: {companySearch}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email <span className="text-gray-400 font-normal">(optional)</span></label>
            <input
              type="email"
              value={manualHm.email}
              onChange={(e) => setManualHm(prev => ({ ...prev, email: e.target.value }))}
              placeholder="john.smith@company.com"
              className="input"
              disabled={addingSingle}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">LinkedIn URL *</label>
            <input
              type="text"
              value={manualHm.linkedinUrl}
              onChange={(e) => setManualHm(prev => ({ ...prev, linkedinUrl: e.target.value }))}
              placeholder="https://www.linkedin.com/in/john-smith"
              className="input"
              disabled={addingSingle}
            />
          </div>

          <div className="bg-green-50 border border-green-200 rounded p-3">
            <p className="text-xs text-green-800">This adds a hiring manager for free — no credits used. You can enrich their full profile later if needed.</p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowAddSingleModal(false)}
              disabled={addingSingle}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddManualHm}
              disabled={addingSingle || !manualHm.firstName.trim() || !manualHm.lastName.trim() || !manualHm.jobTitle.trim() || !manualHm.linkedinUrl.trim()}
              className="px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {addingSingle ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Adding...
                </>
              ) : (
                <>Add Hiring Manager</>
              )}
            </button>
          </div>
        </div>
      ) : (
        /* LinkedIn URL mode (original — for companies, candidates, or HM linkedin mode) */
        <>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LinkedIn {activeDataType === 'companies' ? 'Company' : 'Profile'} URL
              </label>
              <input
                type="text"
                value={addSingleUrl}
                onChange={(e) => setAddSingleUrl(e.target.value)}
                placeholder={activeDataType === 'companies'
                  ? 'https://www.linkedin.com/company/acme-corp'
                  : 'https://www.linkedin.com/in/john-doe'}
                className="input"
                disabled={addingSingle}
              />
              <p className="mt-1 text-xs text-gray-500">
                {activeDataType === 'companies'
                  ? 'Paste a LinkedIn company page URL'
                  : 'Paste a LinkedIn profile URL'}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enrichCheckbox"
                checked={addSingleEnrich}
                onChange={(e) => setAddSingleEnrich(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={addingSingle}
              />
              <label htmlFor="enrichCheckbox" className="text-sm text-gray-700">
                Enrich data (uses 1 credit)
              </label>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-xs text-blue-800">
                {addSingleEnrich
                  ? (activeDataType === 'companies'
                    ? 'With enrichment: Full company data will be fetched including description, industry, employee count, location, and more.'
                    : 'With enrichment: Full profile data will be fetched including headline, experience, skills, education, and more.')
                  : 'Without enrichment: Only the LinkedIn URL will be stored. You can enrich later.'}
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={() => setShowAddSingleModal(false)}
              disabled={addingSingle}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddSingle}
              disabled={addingSingle || !addSingleUrl.trim()}
              className="px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {addingSingle ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Adding...
                </>
              ) : (
                <>Add {activeDataType === 'companies' ? 'Company' : activeDataType === 'managers' ? 'Hiring Manager' : 'Candidate'}</>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)}

 {/* Column Selector Panel */}
      <ColumnSelectorPanel
        isOpen={isColumnSelectorOpen}
        onClose={() => setIsColumnSelectorOpen(false)}
        allColumns={getCurrentColumns()}
        visibleColumns={visibleColumns}
        onApply={handleApplyColumns}
      />

      {/* Column Order Panel */}
      {showColumnOrderPanel && (
        <div
          className="modal-backdrop flex items-center justify-center"
          onClick={() => setShowColumnOrderPanel(false)}
        >
          <div
            className="modal-dialog p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Reorder Columns
              </h3>
              <button
                onClick={() => setShowColumnOrderPanel(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="text-sm text-gray-600 mb-4">
              Drag and drop to reorder columns. Changes are saved automatically.
            </div>
            <div className="space-y-2">
              {columnOrder
                .filter(colKey => visibleColumns.includes(colKey))
                .map((colKey, index) => {
                  const col = getCurrentColumns().find(c => c.key === colKey);
                  if (!col) return null;

                  return (
                    <div
                      key={colKey}
                      draggable
                      onDragStart={() => setDraggedPanelColumn(colKey)}
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (draggedPanelColumn && draggedPanelColumn !== colKey) {
                          const visibleOrder = columnOrder.filter(key => visibleColumns.includes(key));
                          const draggedIdx = columnOrder.indexOf(draggedPanelColumn);
                          const targetIdx = columnOrder.indexOf(colKey);

                          if (draggedIdx !== -1 && targetIdx !== -1) {
                            const newOrder = [...columnOrder];
                            newOrder.splice(draggedIdx, 1);
                            newOrder.splice(targetIdx, 0, draggedPanelColumn);
                            setColumnOrder(newOrder);
                            saveColumnOrder(newOrder);
                          }
                        }
                      }}
                      onDragEnd={() => setDraggedPanelColumn(null)}
                      className={`flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 cursor-move transition-opacity ${
                        draggedPanelColumn === colKey ? 'opacity-50' : 'opacity-100'
                      }`}
                    >
                      <span className="text-gray-400 text-lg">≡</span>
                      <span className="text-sm font-medium text-gray-700 flex-1">
                        {col.label}
                      </span>
                    </div>
                  );
                })}
            </div>
            {activeDataType === 'candidates' && (
              <div className="mt-5 pt-4 border-t border-gray-200" data-tour="column-order-default">
                <div className="text-sm font-semibold text-gray-800">Use this order on all my projects</div>
                <p className="text-xs text-gray-500 mt-1">
                  {tablePrefs.order
                    ? `You have a default order${tablePrefs.savedAt ? ` (set ${new Date(tablePrefs.savedAt).toLocaleDateString()})` : ''}. Every project you open uses it unless you've arranged that project yourself.`
                    : 'Every project you open will start with this order. Projects you\'ve arranged yourself keep their own.'}
                </p>
                <label className="flex items-center gap-2 mt-3 text-xs text-gray-700 cursor-pointer select-none">
                  <input type="checkbox" checked={resetOtherProjects} onChange={(e) => setResetOtherProjects(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500/30" />
                  Also reset projects I've arranged differently
                </label>
                <div className="flex items-center gap-2 mt-3">
                  <button className="btn-primary !py-1.5 text-xs" onClick={() => saveDefaultColumnOrder(resetOtherProjects)}>
                    {tablePrefs.order ? 'Update my default' : 'Make this my default'}
                  </button>
                  {tablePrefs.order && (
                    <button className="btn-ghost !py-1.5 text-xs" onClick={clearDefaultColumnOrder}>Clear my default</button>
                  )}
                </div>
                {defaultOrderNote && <p className="text-xs text-emerald-700 mt-2">{defaultOrderNote}</p>}
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowColumnOrderPanel(false)}
                className="btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connections Modal */}
      {selectedCandidateForConnections && (
        <ConnectionsModal
          candidateId={selectedCandidateForConnections.id}
          candidateName={selectedCandidateForConnections.name}
          isOpen={showConnectionsModal}
          onClose={() => {
            setShowConnectionsModal(false);
            setSelectedCandidateForConnections(null);
          }}
        />
      )}

      {/* Match column click-through: per-candidate breakdown + rationale */}
      {matchPopover && jevMatch && (
        <MatchBreakdownPopover
          specId={jevMatch.specId}
          candidateId={matchPopover.candidateId}
          candidateName={matchPopover.name}
          position={{ x: matchPopover.x, y: matchPopover.y }}
          onClose={() => setMatchPopover(null)}
        />
      )}

      {/* Job Spec Match Modal (v2 — persistent specs, AI judgment scoring) */}
      <JobSpecMatchV2Modal
        isOpen={showJobSpecModal}
        onClose={() => { setShowJobSpecModal(false); setJobSpecModalFocusId(null); }}
        onApplyToTable={handleJevMatchApplied}
        projectId={selectedProject?.id || 0}
        initialSpecId={jobSpecModalFocusId}
      />

      {/* Ada-driven AI Smart Search preview modal. Mirrors AISearchBar's
          preview UX exactly — the user gets the same chip/column editor —
          but the launch path is Ada's propose_smart_search action tool.
          Resolves adaSmartSearchResolveRef on close so Ada's apply flow
          knows whether the user applied or cancelled. */}
      {/* Loading state — show a translucent backdrop with a spinner while
          /api/ai-search/analyze is in flight; the real modal mounts when the
          response arrives. Lets the user cancel mid-fetch. */}
      {adaSmartSearchModal.open && adaSmartSearchModal.loading && (
        <div
          className="modal-backdrop flex items-center justify-center"
          onClick={() => {
            setAdaSmartSearchModal(m => ({ ...m, open: false }));
            setAiSearchBarOverride(null);
            const resolve = adaSmartSearchResolveRef.current;
            adaSmartSearchResolveRef.current = null;
            resolve?.({ applied: false });
          }}
        >
          <div className="bg-white rounded-lg shadow-xl px-6 py-4 flex items-center gap-3">
            <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-gray-700">Running smart search…</span>
          </div>
        </div>
      )}

      {adaSmartSearchModal.open && !adaSmartSearchModal.loading && adaSmartSearchModal.filterGroups && (
        <AISearchPreviewModal
          isOpen={adaSmartSearchModal.open}
          query={adaSmartSearchModal.query}
          filterGroups={adaSmartSearchModal.filterGroups}
          explanation={adaSmartSearchModal.explanation || undefined}
          radiusSearch={adaSmartSearchModal.radius || undefined}
          companyRadiusSearch={adaSmartSearchModal.companyRadius || undefined}
          candidateRadiusSearch={adaSmartSearchModal.candidateRadius || undefined}
          projectId={selectedProject?.id}
          onClose={() => {
            setAdaSmartSearchModal(m => ({ ...m, open: false }));
            setAiSearchBarOverride(null);
            const resolve = adaSmartSearchResolveRef.current;
            adaSmartSearchResolveRef.current = null;
            resolve?.({ applied: false });
          }}
          onApply={(editedGroups) => {
            const nextFilterState: FilterState = {
              groups: editedGroups,
              employmentTypes: aiFilterState.employmentTypes,
            };
            handleAISearch(
              nextFilterState,
              adaSmartSearchModal.explanation || '',
              adaSmartSearchModal.canWiden,
              false,
              adaSmartSearchModal.query,
              adaSmartSearchModal.radius || undefined,
              {
                companyRadiusSearch: adaSmartSearchModal.companyRadius || undefined,
                candidateRadiusSearch: adaSmartSearchModal.candidateRadius || undefined,
              }
            );
            setAdaSmartSearchModal(m => ({ ...m, open: false }));
            setAiSearchBarOverride(null);
            const resolve = adaSmartSearchResolveRef.current;
            adaSmartSearchResolveRef.current = null;
            // Surface what the user actually applied (chips after editing)
            // so Ada can ack precisely.
            const keywords = new Set<string>();
            for (const g of editedGroups) {
              for (const c of g.conditions || []) {
                if (Array.isArray(c.values)) c.values.forEach((v: any) => keywords.add(String(v)));
                else if (c.value) keywords.add(String(c.value));
              }
            }
            resolve?.({ applied: true, keywordsApplied: Array.from(keywords) });
          }}
        />
      )}

      {/* Save Search Modal */}
      {showSaveSearchModal && (
        <div
          className="modal-backdrop flex items-center justify-center"
          onClick={() => setShowSaveSearchModal(false)}
        >
          <div
            className="modal-dialog p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              Save Search
            </h3>
            <input
              type="text"
              value={saveSearchName}
              onChange={(e) => setSaveSearchName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && saveSearchName.trim()) handleSaveSearch(saveSearchName); }}
              placeholder="e.g. AWS engineers within 20mi of Birmingham"
              data-tour="save-search-name"
              className="input mb-3"
              autoFocus
            />
            <div className="text-xs text-gray-500 mb-4">
              Saves your current filters{radiusLocation ? `, radius search (${radiusDistance}mi of ${radiusLocation})` : ''}{aiFilterState.groups.length > 0 ? `, ${aiFilterState.groups.length} filter group${aiFilterState.groups.length > 1 ? 's' : ''}` : ''}{aiFilterState.employmentTypes.length > 0 ? `, employment type filter` : ''}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSaveSearchModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                data-tour="save-search-confirm"
                onClick={() => handleSaveSearch(saveSearchName)}
                disabled={!saveSearchName.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 text-sm font-medium"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Project from Filtered Data Modal */}
      {showCreateProjectModal && (
        <div className="modal-backdrop flex items-center justify-center">
          <div className="modal-dialog max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Create New Project from Filtered Data</h2>
                <button
                  onClick={() => {
                    setShowCreateProjectModal(false);
                    setNewProjectName('');
                    setProjectCreationMode('copy');
                    setSelectedExistingProjectId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isCreatingProject}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Project Name Input - only show for new project modes */}
              {projectCreationMode !== 'copy-existing' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Project Name
                  </label>
                  <input
                    type="text"
                    value={newProjectName}
                    data-tour="subproject-name"
                    onChange={(e) => setNewProjectName(e.target.value)}
                    placeholder="Enter project name..."
                    disabled={isCreatingProject}
                    className="input disabled:bg-gray-100"
                  />
                </div>
              )}

              {/* Mode Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  How should the data be handled?
                </label>
                <div className="space-y-3" data-tour="subproject-modes">
                  {/* Copy Mode */}
                  <div
                    onClick={() => !isCreatingProject && setProjectCreationMode('copy')}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      projectCreationMode === 'copy'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${isCreatingProject ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-start">
                      <input
                        type="radio"
                        checked={projectCreationMode === 'copy'}
                        onChange={() => setProjectCreationMode('copy')}
                        disabled={isCreatingProject}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-1">Copy to New Project</div>
                        <div className="text-sm text-gray-600">
                          Records will be added to the new project while keeping them in the current project.
                          Records can exist in multiple projects.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Move Mode */}
                  <div
                    onClick={() => !isCreatingProject && setProjectCreationMode('move')}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      projectCreationMode === 'move'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${isCreatingProject ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-start">
                      <input
                        type="radio"
                        checked={projectCreationMode === 'move'}
                        onChange={() => setProjectCreationMode('move')}
                        disabled={isCreatingProject}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-1">Move to New Project</div>
                        <div className="text-sm text-gray-600">
                          Records will be removed from the current project and moved to the new project exclusively.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Copy to Existing Project Mode */}
                  <div
                    onClick={() => !isCreatingProject && userProjects.length > 0 && setProjectCreationMode('copy-existing')}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      projectCreationMode === 'copy-existing'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${isCreatingProject || userProjects.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-start">
                      <input
                        type="radio"
                        checked={projectCreationMode === 'copy-existing'}
                        onChange={() => setProjectCreationMode('copy-existing')}
                        disabled={isCreatingProject || userProjects.length === 0}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-1">Copy to Existing Project</div>
                        <div className="text-sm text-gray-600">
                          Records will be copied to an existing project you own. Records can exist in multiple projects.
                        </div>
                        {userProjects.length === 0 && (
                          <div className="text-sm text-orange-600 mt-1">
                            No other projects available. Create a project first.
                          </div>
                        )}
                      </div>
                    </div>
                    {projectCreationMode === 'copy-existing' && userProjects.length > 0 && (
                      <div className="mt-3 ml-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select target project:
                        </label>
                        <select
                          value={selectedExistingProjectId || ''}
                          onChange={(e) => setSelectedExistingProjectId(e.target.value ? parseInt(e.target.value) : null)}
                          disabled={isCreatingProject}
                          className="input"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">-- Select a project --</option>
                          {userProjects.map((project) => (
                            <option key={project.id} value={project.id}>
                              {project.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-blue-800">
                    <div className="font-medium mb-1">What will be included:</div>
                    <ul className="list-disc list-inside space-y-1">
                      <li>
                        {activeDataType === 'companies'
                          ? `${filteredTotal} ${filteredTotal === 1 ? 'company' : 'companies'}`
                          : activeDataType === 'candidates'
                          ? `${filteredTotal} ${filteredTotal === 1 ? 'candidate' : 'candidates'}`
                          : `${filteredTotal} hiring ${filteredTotal === 1 ? 'manager' : 'managers'}`}
                        {' '}(all matching filters)
                      </li>
                      {activeDataType === 'candidates' && (
                        <li>Associated companies and hiring managers</li>
                      )}
                      {activeDataType === 'companies' && (
                        <li>Candidates and hiring managers at these companies</li>
                      )}
                      {activeDataType === 'managers' && (
                        <li>Associated companies</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCreateProjectModal(false);
                    setNewProjectName('');
                    setProjectCreationMode('copy');
                    setSelectedExistingProjectId(null);
                  }}
                  disabled={isCreatingProject}
                  className="btn-secondary flex-1 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    // Validate based on mode
                    if (projectCreationMode === 'copy-existing') {
                      if (!selectedExistingProjectId) {
                        alert('Please select a target project');
                        return;
                      }
                    } else {
                      if (!newProjectName.trim()) {
                        alert('Please enter a project name');
                        return;
                      }
                    }

                    setIsCreatingProject(true);

                    try {
                      const token = localStorage.getItem('token');

                      if (projectCreationMode === 'copy-existing') {
                        // Copy to existing project using new endpoint
                        const response = await fetch(`/api/projects/${selectedExistingProjectId}/copy-from`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            sourceProjectId: selectedProject.id,
                            recordType: activeDataType,
                            filterGroups: aiFilterState.groups,
                            employmentTypes: aiFilterState.employmentTypes,
                            searchTerm: searchTerm,
                            radiusSearch: radiusCoords ? {
                              latitude: radiusCoords.lat,
                              longitude: radiusCoords.lng,
                              radiusKm: radiusDistance
                            } : null
                          })
                        });

                        if (!response.ok) {
                          const error = await response.json();
                          throw new Error(error.error || 'Failed to copy to project');
                        }

                        const result = await response.json();
                        const targetProject = userProjects.find(p => p.id === selectedExistingProjectId);
                        const targetProjectName = targetProject?.name || 'selected project';

                        alert(`Successfully copied ${result.processedCount} ${activeDataType} to project "${targetProjectName}"${result.associationsCreated ? ` (${result.associationsCreated} associations created)` : ''}`);

                        // Close modal and reset
                        setShowCreateProjectModal(false);
                        setNewProjectName('');
                        setProjectCreationMode('copy');
                        setSelectedExistingProjectId(null);
                      } else {
                        // Create new project (copy or move)
                        const response = await fetch('/api/projects/create-from-filtered', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            sourceProjectId: selectedProject.id,
                            newProjectName: newProjectName.trim(),
                            mode: projectCreationMode,
                            recordType: activeDataType,
                            // Send filter criteria so backend can fetch ALL matching records
                            filterGroups: aiFilterState.groups,
                            employmentTypes: aiFilterState.employmentTypes,
                            searchTerm: searchTerm,
                            radiusSearch: radiusCoords ? {
                              latitude: radiusCoords.lat,
                              longitude: radiusCoords.lng,
                              radiusKm: radiusDistance
                            } : null
                          })
                        });

                        if (!response.ok) {
                          const error = await response.json();
                          throw new Error(error.error || 'Failed to create project');
                        }

                        const result = await response.json();

                        // Show success message with actual count from server
                        const totalRecords = result.processedCount || 0;
                        alert(`Successfully ${projectCreationMode === 'copy' ? 'copied' : 'moved'} ${totalRecords} ${activeDataType} to project "${newProjectName}"`);

                        // Close modal and reset
                        setShowCreateProjectModal(false);
                        setNewProjectName('');
                        setProjectCreationMode('copy');

                        // Refresh data if moved (records were removed from current project)
                        if (projectCreationMode === 'move') {
                          window.location.reload();
                        }
                      }
                    } catch (error) {
                      console.error('Error with project operation:', error);
                      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                      alert(`Failed: ${errorMessage}`);
                    } finally {
                      setIsCreatingProject(false);
                    }
                  }}
                  disabled={isCreatingProject || (projectCreationMode === 'copy-existing' ? !selectedExistingProjectId : !newProjectName.trim())}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCreatingProject ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      {projectCreationMode === 'copy-existing' ? 'Copying...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      {projectCreationMode === 'copy-existing' ? 'Copy to Project' : 'Create Project'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SourceWhale Modal */}
      {showSourceWhaleModal && (
        <div
          className="modal-backdrop flex items-center justify-center"
          onClick={() => {
            setShowSourceWhaleModal(false);
            setSelectedCampaignId('');
            setSelectedProjectIds([]);
            setSendImmediately(true);
          }}
        >
          <div
            className="modal-dialog p-6 max-w-lg w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#1aa2d9' }}>
                  <span className="text-white font-bold text-sm">SW</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Send to SourceWhale</h3>
              </div>
              <button
                onClick={() => {
                  setShowSourceWhaleModal(false);
                  setSelectedCampaignId('');
                  setSelectedProjectIds([]);
                  setSendImmediately(true);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {sourceWhaleLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#1aa2d9' }}></div>
                <p className="mt-2 text-gray-600">Loading SourceWhale...</p>
              </div>
            ) : !sourceWhaleConnected ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <span className="text-3xl">🔌</span>
                </div>
                <h4 className="font-medium text-gray-800 mb-2">SourceWhale Not Connected</h4>
                <p className="text-gray-600 text-sm mb-4">
                  Connect your SourceWhale account in Settings to send contacts directly to outreach sequences.
                </p>
                <button
                  onClick={() => {
                    setShowSourceWhaleModal(false);
                    window.location.href = '/settings?tab=integrations';
                  }}
                  className="px-4 py-2 text-white rounded-md hover:opacity-90"
                  style={{ backgroundColor: '#1aa2d9' }}
                >
                  Go to Settings
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#e8f7fc' }}>
                  <p className="text-sm" style={{ color: '#0d7a9e' }}>
                    Sending <strong>{selectedRows.size} {activeDataType === 'candidates' ? 'candidate' : 'hiring manager'}{selectedRows.size > 1 ? 's' : ''}</strong> to SourceWhale
                  </p>
                </div>

                {/* Campaign Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Campaign (optional)
                  </label>
                  <select
                    value={selectedCampaignId}
                    onChange={(e) => setSelectedCampaignId(e.target.value)}
                    className="input"
                  >
                    <option value="">No campaign - add to SourceWhale only</option>
                    {sourceWhaleCampaigns.map((campaign: any) => (
                      <option key={campaign.campaignId} value={campaign.campaignId}>
                        {campaign.campaignName}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Select a campaign to add contacts to an outreach sequence
                  </p>
                </div>

                {/* Project Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Projects (optional)
                  </label>
                  <select
                    multiple
                    value={selectedProjectIds}
                    onChange={(e) => {
                      const values = Array.from(e.target.selectedOptions, option => option.value);
                      setSelectedProjectIds(values);
                    }}
                    className="input h-24"
                  >
                    {sourceWhaleProjects.map((project: any) => (
                      <option key={project.projectId} value={project.projectId}>
                        {project.projectName}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Hold Ctrl/Cmd to select multiple projects
                  </p>
                </div>

                {/* Send Immediately Toggle */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Send Immediately</p>
                    <p className="text-xs text-gray-500">Start sequence as soon as contacts are added</p>
                  </div>
                  <button
                    onClick={() => setSendImmediately(!sendImmediately)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      sendImmediately ? '' : 'bg-gray-300'
                    }`}
                    style={{ backgroundColor: sendImmediately ? '#1aa2d9' : undefined }}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        sendImmediately ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setShowSourceWhaleModal(false);
                      setSelectedCampaignId('');
                      setSelectedProjectIds([]);
                      setSendImmediately(true);
                    }}
                    disabled={sourceWhaleSending}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setSourceWhaleSending(true);
                      try {
                        const token = localStorage.getItem('token');
                        const response = await fetch('/api/sourcewhale/send', {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            contactIds: Array.from(selectedRows),
                            contactType: activeDataType === 'candidates' ? 'candidates' : 'hiring_managers',
                            campaignId: selectedCampaignId || undefined,
                            projectIds: selectedProjectIds.length > 0 ? selectedProjectIds : undefined,
                            sendImmediately
                          })
                        });

                        const data = await response.json();

                        if (response.ok) {
                          alert(data.message || `Successfully sent ${selectedRows.size} contacts to SourceWhale`);
                          setShowSourceWhaleModal(false);
                          setSelectedCampaignId('');
                          setSelectedProjectIds([]);
                          setSendImmediately(true);
                          setSelectedRows(new Set());
                        } else {
                          alert(data.error || 'Failed to send to SourceWhale');
                        }
                      } catch (error) {
                        console.error('Error sending to SourceWhale:', error);
                        alert('Failed to send to SourceWhale');
                      } finally {
                        setSourceWhaleSending(false);
                      }
                    }}
                    disabled={sourceWhaleSending}
                    className="flex-1 px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#1aa2d9' }}
                  >
                    {sourceWhaleSending ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        Sending...
                      </>
                    ) : (
                      <>Send to SourceWhale</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* lemlist Modal */}
      {showLemlistModal && (
        <div
          className="modal-backdrop flex items-center justify-center"
          onClick={() => {
            setShowLemlistModal(false);
            setSelectedLemlistCampaignId('');
          }}
        >
          <div
            className="modal-dialog p-6 max-w-lg w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <svg viewBox="0 0 100 100" className="w-10 h-10 rounded-lg" xmlns="http://www.w3.org/2000/svg" aria-label="lemlist">
                  <rect width="100" height="100" rx="24" fill="#2F6BFF" />
                  <g fill="#ffffff">
                    <rect x="32" y="30" width="11" height="40" rx="5.5" />
                    <rect x="43" y="30" width="24" height="11" rx="5.5" />
                    <rect x="43" y="44.5" width="18" height="11" rx="5.5" />
                    <rect x="43" y="59" width="24" height="11" rx="5.5" />
                  </g>
                </svg>
                <h3 className="text-lg font-semibold text-gray-800">Send to lemlist</h3>
              </div>
              <button
                onClick={() => {
                  setShowLemlistModal(false);
                  setSelectedLemlistCampaignId('');
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {lemlistLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#3355FF' }}></div>
                <p className="mt-2 text-gray-600">Loading lemlist...</p>
              </div>
            ) : !lemlistConnected ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <span className="text-3xl">🔌</span>
                </div>
                <h4 className="font-medium text-gray-800 mb-2">lemlist Not Connected</h4>
                <p className="text-gray-600 text-sm mb-4">
                  Connect your lemlist account in Settings to send contacts directly to outreach campaigns.
                </p>
                <button
                  onClick={() => {
                    setShowLemlistModal(false);
                    window.location.href = '/settings?tab=integrations';
                  }}
                  className="px-4 py-2 text-white rounded-md hover:opacity-90"
                  style={{ backgroundColor: '#3355FF' }}
                >
                  Go to Settings
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#eef0ff' }}>
                  <p className="text-sm" style={{ color: '#2338c4' }}>
                    Sending <strong>{selectedRows.size} {activeDataType === 'candidates' ? 'candidate' : 'hiring manager'}{selectedRows.size > 1 ? 's' : ''}</strong> to lemlist
                  </p>
                </div>

                {/* Campaign Selection (required) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Campaign
                  </label>
                  <select
                    value={selectedLemlistCampaignId}
                    onChange={(e) => setSelectedLemlistCampaignId(e.target.value)}
                    className="input"
                  >
                    <option value="">Select a campaign…</option>
                    {lemlistCampaigns.map((campaign: any) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Contacts are added as leads. Custom &amp; AI-column values are pushed as lemlist variables you can use in your templates.
                  </p>
                </div>

                {/* Optional fields → lemlist variables */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowLemlistFields(v => !v)}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    {showLemlistFields ? '▾' : '▸'} Fields to send as variables
                    <span className="text-gray-400 ml-1">
                      (always: name, company, title, email, phone, LinkedIn, headline, skills{lemlistFields.size ? ` + ${lemlistFields.size} extra` : ''})
                    </span>
                  </button>
                  {showLemlistFields && (
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                      {LEMLIST_FIELD_OPTIONS.map(f => (
                        <label key={f.key} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={lemlistFields.has(f.key)}
                            onChange={e => setLemlistFields(prev => {
                              const n = new Set(prev);
                              if (e.target.checked) n.add(f.key); else n.delete(f.key);
                              return n;
                            })}
                          />
                          {f.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setShowLemlistModal(false);
                      setSelectedLemlistCampaignId('');
                    }}
                    disabled={lemlistSending}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setLemlistSending(true);
                      try {
                        const token = localStorage.getItem('token');
                        const response = await fetch('/api/lemlist/send', {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            contactIds: Array.from(selectedRows),
                            contactType: activeDataType === 'candidates' ? 'candidates' : 'hiring_managers',
                            campaignId: selectedLemlistCampaignId,
                            fields: Array.from(lemlistFields)
                          })
                        });

                        const data = await response.json();

                        if (response.ok) {
                          alert(data.message || `Sent ${selectedRows.size} contacts to lemlist`);
                          setShowLemlistModal(false);
                          setSelectedLemlistCampaignId('');
                          setSelectedRows(new Set());
                        } else {
                          alert(data.error || 'Failed to send to lemlist');
                        }
                      } catch (error) {
                        console.error('Error sending to lemlist:', error);
                        alert('Failed to send to lemlist');
                      } finally {
                        setLemlistSending(false);
                      }
                    }}
                    disabled={lemlistSending || !selectedLemlistCampaignId}
                    className="flex-1 px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#3355FF' }}
                  >
                    {lemlistSending ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        Sending...
                      </>
                    ) : (
                      <>Send to lemlist</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Entity Detail Panel (slide-out on name click) */}
      <EntityDetailPanel
        isOpen={detailPanel.open}
        onClose={() => setDetailPanel({ open: false, entityType: 'candidate', entityId: null })}
        entityType={detailPanel.entityType}
        entityId={detailPanel.entityId}
        projectId={selectedProject?.id}
        onEnrichmentComplete={() => {
          // Silently refresh just the current page data without resetting filters/view.
          // Must include radius params if a radius filter is active, otherwise the
          // refetch returns the unfiltered result set and the table appears to "revert"
          // back to all rows after a personal-email/phone enrich click.
          if (serverPagination.enabled && selectedProject?.id) {
            const sortColumn = sortConfig.column || (activeDataType === 'companies' ? 'candidate_count' : 'name');
            const hasBothFilters = companyOfficeRadius.coords && candidateLocationRadius.coords;
            const hasAnyFilter = (activeDataType === 'candidates' || activeDataType === 'companies' || activeDataType === 'managers') && radiusCoords && radiusDistance > 0;
            let radiusParams;
            if (hasBothFilters) {
              radiusParams = {
                lat: radiusCoords!.lat, lng: radiusCoords!.lng, distance: radiusDistance,
                companyLat: companyOfficeRadius.coords!.lat,
                companyLng: companyOfficeRadius.coords!.lng,
                companyDistance: companyOfficeRadius.distance,
                candidateLat: candidateLocationRadius.coords!.lat,
                candidateLng: candidateLocationRadius.coords!.lng,
                candidateDistance: candidateLocationRadius.distance
              };
            } else if (hasAnyFilter) {
              radiusParams = { lat: radiusCoords!.lat, lng: radiusCoords!.lng, distance: radiusDistance, type: radiusType };
            }
            fetchPaginatedData(activeDataType, currentPage, rowLimit, sortColumn, sortConfig.direction, effectiveFilterGroups, aiFilterState.employmentTypes, undefined, undefined, undefined, radiusParams).then(result => {
              if (result) {
                if (activeDataType === 'candidates') setCandidates(result.data);
                else if (activeDataType === 'companies') setCompanies(result.data);
                else if (activeDataType === 'managers') setHiringManagers(result.data);
              }
            });
          } else {
            setRefreshTrigger(prev => prev + 1);
          }
        }}
        searchTerm={searchTerm}
        filterTerms={aiFilterState.groups
          .flatMap(g => g.conditions || [])
          .filter((c: any) => {
            // Only highlight terms from conditions matching the panel's entity,
            // so a company filter's value doesn't light up a candidate profile.
            const et = c.entityType;
            return (detailPanel.entityType === 'company' && et === 'company') ||
                   (detailPanel.entityType === 'candidate' && (et === 'candidate' || !et)) ||
                   (detailPanel.entityType === 'contact' && et === 'manager');
          })
          // Parse each value so an "All columns" boolean ('"data center" OR
          // colo OR …') expands into individual keywords the panel can
          // highlight; plain phrases pass through unchanged.
          .flatMap((c: any) => {
            const raw = c.values?.length ? c.values : (c.value ? [c.value] : []);
            return raw.flatMap((v: any) => parseSearchTerms(String(v)));
          })
          .filter(Boolean)}
        onNavigateNext={(() => {
          if (!detailPanel.open || detailPanel.entityId == null) return undefined;
          const currentIdx = displayData.findIndex((row: any) => row.id === detailPanel.entityId);
          if (currentIdx < 0 || currentIdx >= displayData.length - 1) return undefined;
          return () => {
            const data = displayDataRef.current;
            const panel = detailPanelRef.current;
            const clickIdx = data.findIndex((row: any) => row.id === panel.entityId);
            if (clickIdx >= 0 && clickIdx < data.length - 1) {
              setDetailPanel({ open: true, entityType: panel.entityType, entityId: data[clickIdx + 1].id });
            }
          };
        })()}
        onNavigatePrev={(() => {
          if (!detailPanel.open || detailPanel.entityId == null) return undefined;
          const currentIdx = displayData.findIndex((row: any) => row.id === detailPanel.entityId);
          if (currentIdx <= 0) return undefined;
          return () => {
            const data = displayDataRef.current;
            const panel = detailPanelRef.current;
            const clickIdx = data.findIndex((row: any) => row.id === panel.entityId);
            if (clickIdx > 0) {
              setDetailPanel({ open: true, entityType: panel.entityType, entityId: data[clickIdx - 1].id });
            }
          };
        })()}
      />

      {/* Rename custom column modal */}
      {/* Copy/move candidates → contacts modal */}
      {copyToContactsModal && (
        <div className="modal-backdrop !z-[60] flex items-center justify-center" onClick={() => !copyingToContacts && setCopyToContactsModal(false)}>
          <div className="modal-dialog p-5 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold mb-1">Add {selectedRows.size || 'selected'} candidate{selectedRows.size === 1 ? '' : 's'} to Hiring Managers</h3>
            <p className="text-xs text-gray-500 mb-4">
              Their full enriched profile carries over (career history, summary, education), they'll show as
              enriched, and they'll be linked to every company they currently hold a role at.
            </p>
            <div className="space-y-2 mb-4">
              <label className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer ${copyToContactsMode === 'copy' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`}>
                <input type="radio" checked={copyToContactsMode === 'copy'} onChange={() => setCopyToContactsMode('copy')} className="mt-0.5" />
                <span>
                  <span className="block text-sm font-semibold text-gray-800">Copy</span>
                  <span className="block text-xs text-gray-500">They appear as both a candidate and a contact.</span>
                </span>
              </label>
              <label className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer ${copyToContactsMode === 'move' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`}>
                <input type="radio" checked={copyToContactsMode === 'move'} onChange={() => setCopyToContactsMode('move')} className="mt-0.5" />
                <span>
                  <span className="block text-sm font-semibold text-gray-800">Move</span>
                  <span className="block text-xs text-gray-500">They become a contact and are removed from candidates.</span>
                </span>
              </label>
            </div>
            {copyToContactsMsg && <p className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 mb-3">{copyToContactsMsg}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setCopyToContactsModal(false)} disabled={copyingToContacts} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
              <button
                onClick={handleCopyToContacts}
                disabled={copyingToContacts || selectedRows.size === 0}
                className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {copyingToContacts && <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />}
                {copyingToContacts ? 'Working…' : (copyToContactsMode === 'move' ? 'Move to contacts' : 'Copy to contacts')}
              </button>
            </div>
          </div>
        </div>
      )}

      {renameModal && (
        <div className="modal-backdrop !z-[60] flex items-center justify-center" onClick={() => { setRenameModal(null); setRenameValue(''); }}>
          <div className="modal-dialog p-5 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold mb-3">Rename Column</h3>
            <input
              type="text"
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleRenameCustomColumn(); if (e.key === 'Escape') { setRenameModal(null); setRenameValue(''); } }}
              className="input"
              placeholder={renameModal.currentLabel}
              autoFocus
            />
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleRenameCustomColumn}
                disabled={!renameValue.trim() || renameValue.trim() === renameModal.currentLabel}
                className="btn-primary flex-1 px-3 py-1.5"
              >
                Rename
              </button>
              <button
                onClick={() => { setRenameModal(null); setRenameValue(''); }}
                className="btn-secondary flex-1 px-3 py-1.5"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Column search criteria / description modal */}
      {descriptionModal && (
        <div className="modal-backdrop !z-[60] flex items-center justify-center" onClick={() => setDescriptionModal(null)}>
          <div className="modal-dialog max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="px-6 pt-5 pb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {descriptionModal.columnLabel}
                </h3>
                <button onClick={() => setDescriptionModal(null)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50 rounded-lg p-4 max-h-[50vh] overflow-y-auto">
                {descriptionModal.description}
              </div>
            </div>
            <div className="px-6 pb-4">
              <button
                onClick={() => setDescriptionModal(null)}
                className="btn-secondary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Column header dropdown (Clay-style) — portal escapes sticky <th> stacking context */}
      {customColMenu && customColMenuPos && createPortal(
        <>
          {/* Invisible backdrop to close on outside click */}
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => { setCustomColMenu(null); setCustomColMenuPos(null); }}
          />
          <div
            className="fixed bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[180px] z-[9999]"
            style={(() => {
              // Open upwards when the header sits low on the screen, and
              // scroll inside the menu rather than run off the bottom.
              const pos: any = customColMenuPos;
              const below = window.innerHeight - pos.top - 8;
              const above = (pos.anchorTop ?? pos.top) - 12;
              return below >= 360 || below >= above
                ? { top: pos.top, right: pos.right, maxHeight: below, overflowY: 'auto' as const }
                : { bottom: window.innerHeight - (pos.anchorTop ?? pos.top) + 4, right: pos.right, maxHeight: above, overflowY: 'auto' as const };
            })()}
            onClick={e => e.stopPropagation()}
          >
            {(() => {
              if (!customColMenu) return null; // narrows to string for closures below
              // Display column (works for ANY column — system or custom).
              const dispCol = (getCurrentColumns() as any[]).find((c: any) => c.key === customColMenu);
              // Custom-column metadata (rename/description/delete only apply here).
              const customCol = customColumns.find((c: any) => c.column_key === customColMenu);
              if (!dispCol && !customCol) return null;
              const label = dispCol?.label || customCol?.column_label || 'Column';
              const isCustom = !!(dispCol?.isCustom || customCol);
              const activeColor = columnColors[customColMenu] || null;
              const SWATCHES = [
                { name: 'Red', hex: '#EF4444' },
                { name: 'Yellow', hex: '#EAB308' },
                { name: 'Teal', hex: '#14B8A6' },
                { name: 'Green', hex: '#22C55E' },
                { name: 'Violet', hex: '#8B5CF6' },
                { name: 'Orange', hex: '#F97316' },
                { name: 'Pink', hex: '#EC4899' },
              ];
              const itemCls = 'w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 normal-case flex items-center gap-2';
              return (
                <>
                  <button
                    className="w-full text-left px-3 py-2 text-xs text-violet-700 hover:bg-violet-50 normal-case flex items-center gap-2 font-medium"
                    data-tour="column-menu-ai"
                    onClick={() => { setAiReRunInitialSpec(null); setAiReRunColumnKey(null); setAiColumnAnchorKey(customColMenu); setAiColumnModalOpen(true); setCustomColMenu(null); setCustomColMenuPos(null); }}
                  >
                    <span>✨</span> Insert AI column
                  </button>

                  {/* Insert a manual, editable column. Hover reveals side
                      (right/left), then a type (text/url/number). Flyouts open
                      to the left because the menu is right-anchored. */}
                  {(activeDataType === 'candidates' || activeDataType === 'companies') && (
                    <div className="relative group/insert">
                      <button className={itemCls + ' justify-between'}>
                        <span className="flex items-center gap-2"><span>➕</span> Insert column</span>
                        <span className="text-gray-400 text-[10px]">▶</span>
                      </button>
                      <div className="absolute top-0 left-full ml-0.5 hidden group-hover/insert:block bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[140px] z-[10000]">
                        {(['right', 'left'] as const).map(side => (
                          <div key={side} className="relative group/side">
                            <button className={itemCls + ' justify-between capitalize'}>
                              <span>To the {side}</span>
                              <span className="text-gray-400 text-[10px]">▶</span>
                            </button>
                            <div className="absolute top-0 left-full ml-0.5 hidden group-hover/side:block bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[110px] z-[10001]">
                              {([['text', 'Text'], ['url', 'URL'], ['number', 'Number']] as const).map(([type, tlabel]) => (
                                <button
                                  key={type}
                                  className={itemCls}
                                  onClick={() => handleInsertColumn(customColMenu, side, type)}
                                >
                                  {tlabel}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Formula column — natural language → a formula applied to
                      every row, free (no credits). */}
                  {(activeDataType === 'candidates' || activeDataType === 'companies') && (
                    <button
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 normal-case flex items-center gap-2"
                      data-tour="column-menu-formula"
                      onClick={() => { setFormulaModal({ anchorKey: customColMenu, side: 'right' }); setCustomColMenu(null); setCustomColMenuPos(null); }}
                    >
                      <span className="italic text-gray-500">ƒ</span> Insert formula column
                    </button>
                  )}
                  {customCol?.column_type === 'formula' && customCol?.formula && (
                    <button
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 normal-case flex items-center gap-2"
                      onClick={() => recomputeFormulaColumn(customColMenu)}
                    >
                      <span>↻</span> Recompute formula
                    </button>
                  )}
                  {aiColumnKeys.has(customColMenu) && (
                    <button
                      className="w-full text-left px-3 py-2 text-xs text-violet-700 hover:bg-violet-50 normal-case flex items-center gap-2"
                      onClick={() => openReRunAiColumn(customColMenu)}
                    >
                      <span>↻</span> Re-run with new prompt
                    </button>
                  )}
                  {!campaignScopeId && aiColumnKeys.has(customColMenu) && (
                    <>
                      <button
                        className="w-full text-left px-3 py-2 text-xs text-violet-700 hover:bg-violet-50 normal-case flex items-center gap-2"
                        onClick={() => { const k = customColMenu; setCustomColMenu(null); setCustomColMenuPos(null); runColumn(k, label, 'empty'); }}
                      >
                        <span>▶</span> Run blank cells only
                      </button>
                      <button
                        className="w-full text-left px-3 py-2 text-xs text-violet-700 hover:bg-violet-50 normal-case flex items-center gap-2"
                        onClick={() => { const k = customColMenu; setCustomColMenu(null); setCustomColMenuPos(null); runColumn(k, label, 'all'); }}
                      >
                        <span>↻</span> Re-run all cells (same prompt)
                      </button>
                    </>
                  )}
                  <div className="my-1 border-t border-gray-100" />

                  <button className={itemCls} onClick={() => dispCol && handleMenuSort(dispCol, 'asc')}>
                    <span className="text-gray-400">↑</span> Sort A → Z
                  </button>
                  <button className={itemCls} onClick={() => dispCol && handleMenuSort(dispCol, 'desc')}>
                    <span className="text-gray-400">↓</span> Sort Z → A
                  </button>

                  <button
                    className={itemCls}
                    data-tour="column-menu-freeze"
                    onClick={() => { toggleFreezeColumn(customColMenu); setCustomColMenu(null); setCustomColMenuPos(null); }}
                  >
                    <span className="text-gray-400">📌</span>
                    {frozenColumns.has(customColMenu) ? 'Unfreeze column' : 'Freeze column (keep visible on scroll)'}
                  </button>

                  {activeDataType === 'candidates' && (
                    <button
                      className={itemCls}
                      data-tour="column-menu-default-order"
                      title="Use the columns as they are arranged now on every project you open (projects you've arranged yourself keep their own order)"
                      onClick={() => { setCustomColMenu(null); setCustomColMenuPos(null); setShowColumnOrderPanel(true); saveDefaultColumnOrder(false); }}
                    >
                      <span className="text-gray-400">★</span> Make this column order my default
                    </button>
                  )}

                  <div className="my-1 border-t border-gray-100" />

                  <button
                    className={itemCls}
                    onClick={() => dispCol && handleFilterOnColumn({ key: dispCol.key, label })}
                  >
                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L14 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 018 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                    </svg>
                    Filter on this column
                  </button>

                  {/* Colour picker */}
                  <div className="px-3 pt-2 pb-1">
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1.5">Colour</div>
                    <div className="flex items-center gap-1.5">
                      {SWATCHES.map(s => (
                        <button
                          key={s.hex}
                          title={s.name}
                          onClick={() => handleColumnColor(customColMenu, s.hex)}
                          className="w-4 h-4 rounded-full border border-black/10 flex items-center justify-center"
                          style={{ backgroundColor: s.hex }}
                        >
                          {activeColor === s.hex && <span className="text-white text-[9px] leading-none">✓</span>}
                        </button>
                      ))}
                      <button
                        title="None"
                        onClick={() => handleColumnColor(customColMenu, null)}
                        className="w-4 h-4 rounded-full border border-gray-300 bg-white flex items-center justify-center"
                      >
                        {!activeColor && <span className="text-gray-400 text-[9px] leading-none">✓</span>}
                      </button>
                    </div>
                  </div>

                  {isCustom && customCol && (
                    <>
                      <div className="my-1 border-t border-gray-100" />
                      <button
                        className={itemCls}
                        onClick={() => {
                          setRenameModal({ columnKey: customCol.column_key, currentLabel: customCol.column_label });
                          setRenameValue(customCol.column_label);
                          setCustomColMenu(null);
                          setCustomColMenuPos(null);
                        }}
                      >
                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Rename
                      </button>
                      {customCol.column_description && (
                        <button
                          className={itemCls}
                          onClick={() => {
                            setDescriptionModal({ columnLabel: customCol.column_label, description: customCol.column_description });
                            setCustomColMenu(null);
                            setCustomColMenuPos(null);
                          }}
                        >
                          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Search criteria
                        </button>
                      )}
                      <button
                        className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 normal-case flex items-center gap-2"
                        onClick={() => {
                          handleDeleteCustomColumn(customCol.column_key, customCol.column_label);
                          setCustomColMenu(null);
                          setCustomColMenuPos(null);
                        }}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete column
                      </button>
                    </>
                  )}
                </>
              );
            })()}
          </div>
        </>,
        document.body
      )}

      {/* AI Column background-job pill — non-blocking progress, bottom-right */}
      {activeAiJob && (
        <div className="fixed bottom-4 right-4 z-[60] bg-white border border-gray-200 rounded-xl shadow-xl px-4 py-3 w-80">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
              <span className="text-violet-600">✨</span>
              {['pending', 'running'].includes(activeAiJob.status) && <span>Enriching “{activeAiJob.columnLabel}”</span>}
              {activeAiJob.status === 'completed' && <span className="text-green-700">✓ “{activeAiJob.columnLabel}” done</span>}
              {activeAiJob.status === 'failed' && <span className="text-red-600">“{activeAiJob.columnLabel}” failed</span>}
              {activeAiJob.status === 'cancelled' && <span className="text-amber-600">“{activeAiJob.columnLabel}” cancelled</span>}
            </div>
            {['pending', 'running'].includes(activeAiJob.status) ? (
              <button onClick={cancelActiveAiJob} className="text-xs text-gray-400 hover:text-red-500">Cancel</button>
            ) : (
              <button onClick={() => setActiveAiJob(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            )}
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden mt-2">
            <div
              className={`h-2 rounded-full transition-all ${activeAiJob.status === 'failed' ? 'bg-red-500' : 'bg-violet-600'}`}
              style={{ width: `${activeAiJob.status === 'completed' ? 100 : (activeAiJob.total_rows > 0 ? Math.round((activeAiJob.processed_rows / activeAiJob.total_rows) * 100) : 0)}%` }}
            />
          </div>
          <div className="text-[11px] text-gray-500 mt-1.5">
            {activeAiJob.processed_rows}/{activeAiJob.total_rows} rows
            {activeAiJob.failed_rows > 0 && ` · ${activeAiJob.failed_rows} skipped`}
            {activeAiJob.credits_charged > 0 && ` · ${activeAiJob.credits_charged} credits`}
            {['pending', 'running'].includes(activeAiJob.status) && ' · you can keep working'}
          </div>
          {activeAiJob.error && <div className="text-[11px] text-red-600 mt-1">{activeAiJob.error}</div>}
        </div>
      )}

      {/* AI Column creation modal */}
      {selectedProject?.id && (
        <AIColumnModal
          isOpen={aiColumnModalOpen}
          onClose={() => setAiColumnModalOpen(false)}
          projectId={selectedProject.id}
          entityType={activeDataType as any}
          scope={buildAiColumnScope()}
          campaignId={campaignScopeId}
          initialSpec={aiReRunInitialSpec}
          existingColumnKey={aiReRunColumnKey || undefined}
          onJobStarted={handleAiJobStarted}
          isAdmin={isAdmin}
        />
      )}

      {/* Org chart — AI-inferred reporting structure */}
      {orgChartScope && selectedProject?.id && (
        <OrgChartModal
          projectId={selectedProject.id}
          companyId={orgChartScope.companyId}
          companyName={orgChartScope.companyName}
          onClose={() => setOrgChartScope(null)}
        />
      )}

      {/* Formula column generator */}
      {formulaModal && selectedProject?.id && (activeDataType === 'candidates' || activeDataType === 'companies') && (
        <FormulaColumnModal
          projectId={selectedProject.id}
          token={localStorage.getItem('token') || ''}
          entity={activeDataType}
          columns={buildRefColumns()}
          previewRows={buildPreviewRows()}
          onClose={() => setFormulaModal(null)}
          onSaved={handleFormulaSaved}
        />
      )}

      {/* AI Column "why" modal — value + the model's reasoning for this cell */}
      {aiReasoningModal && (
        <div className="modal-backdrop !z-[60] flex items-center justify-center" onClick={() => setAiReasoningModal(null)}>
          <div className="modal-dialog max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="px-6 pt-5 pb-4">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h3 className="text-lg font-semibold text-gray-900">{aiReasoningModal.entityName}</h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-violet-100 text-violet-800 border-violet-200">
                    {aiReasoningModal.value}
                  </span>
                </div>
                <button onClick={() => setAiReasoningModal(null)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-500">{aiReasoningModal.columnLabel}</p>
            </div>
            <div className="border-t border-gray-100" />
            <div className="px-6 py-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                Why
                {aiReasoningModal.meta?.confidence && (
                  <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded uppercase tracking-wide ${
                    aiReasoningModal.meta.confidence === 'high' ? 'bg-green-50 text-green-700'
                    : aiReasoningModal.meta.confidence === 'medium' ? 'bg-amber-50 text-amber-700'
                    : 'bg-gray-100 text-gray-500'}`}>
                    {aiReasoningModal.meta.confidence} confidence
                  </span>
                )}
              </h4>
              <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {aiReasoningModal.reasoning}
              </div>
              {(aiReasoningModal.meta?.sources || []).length > 0 && (
                <div className="mt-3">
                  <h4 className="text-sm font-semibold text-gray-900 mb-1.5">
                    Sources
                    {aiReasoningModal.meta?.searches != null && (
                      <span className="ml-2 text-[11px] font-normal text-gray-400">{aiReasoningModal.meta.searches} web search{aiReasoningModal.meta.searches === 1 ? '' : 'es'}</span>
                    )}
                  </h4>
                  <ul className="space-y-1">
                    {(aiReasoningModal.meta?.sources || []).map((src, i) => (
                      <li key={i}>
                        <a href={src.url} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-600 hover:underline break-all">
                          {src.title || src.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between gap-2">
              <span className="text-xs text-gray-400">The reasoning is stored for every row.</span>
              <div className="flex items-center gap-2">
                {aiReasoningModal.rowId && aiReasoningModal.reasoning.startsWith('Judged from the selected fields') && (
                  <button
                    onClick={writeCellExplanation}
                    disabled={explainBusy}
                    className="px-3 py-1.5 text-sm text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-50 flex items-center gap-1.5 disabled:opacity-50"
                    title="Have the AI write a full explanation of this verdict from the row's fields"
                  >
                    {explainBusy ? 'Writing…' : '✍️ Write full explanation'}
                  </button>
                )}
                <button
                  onClick={() => materializeReasoningColumn(aiReasoningModal.columnKey, aiReasoningModal.columnLabel)}
                  className="px-3 py-1.5 text-sm text-violet-700 border border-violet-300 rounded-lg hover:bg-violet-50 flex items-center gap-1.5"
                >
                  <span>＋</span> Add reasoning as a column
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verdict detail modal (Match/Miss/Unclear reasoning) */}
      {verdictModal && (
        <div className="modal-backdrop !z-[60] flex items-center justify-center" onClick={() => setVerdictModal(null)}>
          <div className="modal-dialog max-w-xl w-full mx-4 flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 pt-5 pb-4 flex-shrink-0">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h3 className="text-lg font-semibold text-gray-900">{verdictModal.entityName}</h3>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                    verdictModal.verdict === 'Match' ? 'bg-green-100 text-green-800 border-green-200' :
                    verdictModal.verdict === 'Near-Match' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                    verdictModal.verdict === 'Miss' ? 'bg-red-100 text-red-800 border-red-200' :
                    'bg-amber-100 text-amber-800 border-amber-200'
                  }`}>
                    {verdictModal.verdict === 'Match' ? '✓' :
                     verdictModal.verdict === 'Near-Match' ? '~' :
                     verdictModal.verdict === 'Miss' ? '✗' : '?'} {verdictModal.verdict}
                  </span>
                  {typeof verdictModal.score === 'number' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border bg-gray-100 text-gray-700 border-gray-200">
                      {verdictModal.score}/100
                    </span>
                  )}
                  {verdictModal.confidence && (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                        verdictModal.confidence === 'high'
                          ? 'bg-gray-100 text-gray-600 border-gray-200'
                          : verdictModal.confidence === 'medium'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-red-50 text-red-700 border-red-200 border-dashed'
                      }`}
                      title={verdictModal.confidence === 'low'
                        ? 'Ada is uncertain about this verdict — worth a manual review.'
                        : verdictModal.confidence === 'medium'
                        ? 'Ada has some uncertainty on this verdict.'
                        : 'Ada is confident in this verdict.'}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {verdictModal.confidence} confidence
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setVerdictModal(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-500">{verdictModal.columnLabel}</p>
            </div>

            <div className="border-t border-gray-100 flex-shrink-0" />

            {/* Reasoning — scrollable */}
            <div className="px-6 py-4 overflow-y-auto">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Reasoning</h4>
              <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {renderReasoningWithLinkedIn(verdictModal.reasoning)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableView;
